/**
 * English catalogue - every user-facing string in the platform.
 *
 * Keys are hierarchical (`module.surface.element`). New strings go
 * here first; components reference them via `t("module.surface.element")`.
 * v2.1 RTL ships an Arabic catalogue with the same key structure.
 *
 * Don't introduce string values inline in components - even if the
 * platform never translates, the discipline keeps copy in one
 * editable place and makes the v2.1 Arabic ship a translation pass
 * rather than a rewrite.
 */
export const en = {
  /*
   * Round 9 G -- the maturity instrument's own words.
   *
   * The ten dimensions, their anchors, the thirty evidence probes, the
   * nine external-evidence items and the five tier names were composed
   * in backend/dossier/maturity_model.py and readiness.py, so
   * /readiness was the last route in the product still rendering
   * English under the pseudo-locale: 27 runs, every one of them these.
   *
   * These ARE ours to translate, and the distinction matters. The 911
   * control texts are the regulator's own words and must be SOURCED in
   * Arabic rather than rendered by us -- SAMA and CBUAE publish their
   * frameworks in Arabic as the authoritative text. This instrument is
   * MARSAD's, built from our own readiness reference. A translator
   * should still reach for SAMA's Arabic terms where the concepts
   * coincide, which is a terminology instruction rather than a reason
   * to withhold it.
   *
   * The English stays on the wire: the signed maturity pack and dossier
   * slot 110 carry it, and a signed artefact is evidence in one
   * language by design.
   */
  /* Round 9 G -- why the board is this colour, in the reader's
     language. Composed in board_composer.py until now, so the one
     artefact an executive reads in the room kept an English sentence
     under any translation. The English stays for the PDF, which is a
     signed artefact. */
  board: {
    because: {
      clear: "No open finding on any {{serviceTermShort}} reaches amber or red.",
      // Round 17 I15 -- the board's sentence on a tenant that has assessed
      // nothing; the server's own words, catalogued.
      notAssessed:
        "Nothing has been assessed yet: no service has been admitted, so there is no estate to judge. This is not a clean bill of health -- it is the absence of one.",
      /* Round 18 B17 -- the colour kept (round 14 B3), its basis said:
         nothing admitted, so it is not a verdict on the estate. */
      driverUnadmitted_one:
        "{{rag}} because of {{driverCount}} {{severity}} finding on services not yet admitted. Nothing has been admitted, so this is not an assessment of the estate: it is the worst open finding.",
      driverUnadmitted_other:
        "{{rag}} because of {{driverCount}} {{severity}} findings on services not yet admitted. Nothing has been admitted, so this is not an assessment of the estate: it is the worst open finding.",
      driver_one:
        "{{rag}} because of {{driverCount}} {{severity}} finding across {{impacted}} impacted {{serviceTermShortPlural}}. This is the worst open finding, not a tolerance verdict -- the two answer different questions.",
      driver_other:
        "{{rag}} because of {{driverCount}} {{severity}} findings across {{impacted}} impacted {{serviceTermShortPlural}}. This is the worst open finding, not a tolerance verdict -- the two answer different questions.",
      driverWithMvb_one:
        "{{rag}} because of {{driverCount}} {{severity}} finding across {{impacted}} impacted {{serviceTermShortPlural}}, {{mvbImpacted}} of {{mvbTotal}} minimum-viable {{serviceTermShortPlural}} among them. This is the worst open finding, not a tolerance verdict -- the two answer different questions.",
      driverWithMvb_other:
        "{{rag}} because of {{driverCount}} {{severity}} findings across {{impacted}} impacted {{serviceTermShortPlural}}, {{mvbImpacted}} of {{mvbTotal}} minimum-viable {{serviceTermShortPlural}} among them. This is the worst open finding, not a tolerance verdict -- the two answer different questions.",
    },
    severity: {
      critical: "critical",
      high: "high",
      any: "open",
    },
  },
  readiness: {
    dimension: {
      D1: {
        name: "Governance, Accountability and Operating Model",
        anchor: "Board-level ownership, a mandated function, clear decision rights.",
      },
      D2: {
        name: "{{serviceTermTitle}} Identification and Classification",
        anchor:
          "{{serviceTermShort}} defined by customer + market outcome, with assured completeness.",
      },
      D3: {
        name: "{{toleranceTermTitle}} Definition and Calibration",
        anchor: "Outcome-based tolerances that drive investment, board-approved.",
      },
      D4: {
        name: "End-to-End Dependency Mapping",
        anchor:
          "{{serviceTermShort}} mapped across people/process/tech/data/third parties; SPOFs addressed.",
      },
      D5: {
        name: "Resilience Data, Tooling and Single Source of Truth",
        anchor: "A governed single source of truth for resilience data.",
      },
      D6: {
        name: "Scenario Testing and Resilience Proving",
        anchor: "A severe-but-plausible scenario-testing programme.",
      },
      D7: {
        name: "Business Continuity and Disaster Recovery",
        anchor: "Continuity + recovery aligned to required outcomes, tested.",
      },
      D8: {
        name: "Third-Party and Outsourcing Resilience",
        anchor: "Governance, assurance + exit for material outsourcing + cloud.",
      },
      D9: {
        name: "Cyber Resilience, Incident and Crisis Management",
        anchor: "Detection, response + recovery for cyber + disruptive events.",
      },
      D10: {
        name: "Culture, Embedding, Self-Assessment and Regulatory Engagement",
        anchor: "Resilience embedded in culture + continuous self-assessment.",
      },
    },
    tier: {
      A: "Advanced",
      B: "Established",
      C: "Developing",
      D: "Emerging",
      E: "Foundational",
    },
    probe: {
      boardSnapshotGeneratedOnDemand: "Board snapshot generated on demand",
      boardGovernedDualSignOffInUse: "Board-governed dual sign-off in use",
      // Round 17 P1 -- names what it counts: every listed row, services
      // and functions, admitted or not; the next line counts admission.
      ibsCataloguePopulated:
        "{{serviceTermShort}} catalogue populated (services and functions listed, admitted or under assessment)",
      // Round 19 P12 -- services only; a function admitted through
      // Route 2 is the next line, shown only when there is one.
      ibsesAdmittedViaTheMethodology:
        "{{serviceTermShortPlural}} admitted via the methodology (services)",
      functionsAdmittedThroughRouteTwo:
        "Critical internal functions admitted through Route 2",
      approvedMvbDeclaration: "Approved {{mvbTermShort}} declaration",
      coreServiceClassificationInUse: "Core service classification in use",
      classificationDiscriminatesNonCriticalClassesInUse:
        "Classification discriminates (non-critical classes in use)",
      signedImpactToleranceDeclarations:
        "Signed impact-tolerance declarations on record (every version)",
      ibsesCarryingAnImpactTolerance:
        "Register rows carrying an {{toleranceTerm}} (candidates and functions included)",
      ibsToServiceDependencyEdges: "{{serviceTermShort}}-to-service dependency edges",
      serviceToServiceDependencyEdges: "Service-to-service dependency edges",
      serviceToVendorEdges: "Service-to-vendor edges",
      resilienceStateChainSealed: "Resilience-state chain sealed",
      blastRadiusChainSealed: "Blast-radius chain sealed",
      dataSourceRegisterPopulated: "Data-source register populated",
      scenarioTestsExecuted: "Scenario tests executed",
      drillsRun: "Drills run",
      serviceTargetedScenariosDefined: "Service-targeted scenarios defined",
      recoveryDrillsRun: "Recovery drills run",
      processCoverageChainSealed: "Process-coverage chain sealed",
      vendorRegisterPopulated: "Vendor register populated",
      materialOutsourcingFlagged: "Material outsourcing flagged",
      thirdPartyMaterialityDifferentiates: "Third-party materiality differentiates",
      outsourcingContractsRegistered: "Outsourcing contracts registered",
      cyberConnectorsEnabled: "Cyber connectors enabled",
      findingsFlowing: "Findings flowing",
      assessmentCyclesRun: "Assessment cycles run",
      attestationCyclesRun: "Attestation cycles run",
      obligationChainSealed: "Obligation chain sealed",
      regulatoryChangesRespondedTo: "Regulatory changes responded to",
    },
    external: {
      committeeTermsOfReference: "Committee terms of reference",
      threeLinesOperatingModelMandate: "Three-lines operating-model mandate",
      bias: "BIAs",
      continuityAndDrPlanDocuments: "Continuity + DR plan documents",
      crisisManagementPlan: "Crisis management plan",
      exitPlans: "Exit plans",
      thirdPartyAssuranceReports: "Third-party assurance reports",
      redTeamFeerReports: "Red-team / FEER reports",
      postIncidentReviews: "Post-incident reviews",
    },
  },
  /*
   * Round 9 G -- the definitions, where a translator can reach them.
   *
   * These lived in shared/glossary/terms.ts, whose docstring said
   * "translation follows the same route as the rest -- `ar.ts` wraps
   * this module's English until a real pass lands". That was not true:
   * `ar.ts` wraps `en`, and this module was not in it, so the
   * definitions rendered as bare English under the pseudo-locale.
   * A stated assumption the measurement disproved.
   *
   * The module's reasoning for keeping them together was sound --
   * these are DEFINITIONS, the same sentence wherever the term
   * appears, and splitting them across a catalogue of button labels is
   * how they drift. So they are together, under one namespace, rather
   * than scattered.
   *
   * `aka` stays in the module: it is search input, not prose a reader
   * is shown.
   */
  glossaryTerms: {
    /* Round 16 H31 -- the constructs new since round 14. */
    critical_internal_function: {
      term: "Critical internal function",
      short:
        "Something the bank cannot stop without harm that no customer sees -- treasury settlement, the ledger, sanctions screening. It can never pass the test for a customer-facing service, so it has its own register and is counted apart from {{serviceTermPlural}}.",
      more: "It is admitted by its own route, signed by the CRO and the CIO. It can fund a service, and it is never placed in a group.",
    },
    funding_edge: {
      term: "Funding",
      short:
        "A link saying a critical internal function funds a service: if the function stops, the services it funds stop with it.",
      more: "Only a critical internal function may fund a service. The cascade follows the link, so a treasury outage reaches the payments it funds.",
    },
    integrity_tolerance: {
      term: "Integrity tolerance",
      short:
        "How long the bank can keep running without a service's integrity, and how quickly it must stop safely instead. It is judged only on what a test, drill or incident observed, never projected.",
      more: "With nothing observed it reads as cannot be judged, never as within tolerance.",
    },
    fail_closed: {
      term: "Fail closed",
      short:
        "How quickly a function can stop safely when it can no longer run correctly. One of the two integrity measures.",
    },
    ibs_group: {
      term: "Group",
      short:
        "A few {{serviceTermPlural}} governed together, so the board reads a handful of lines rather than every service. A service sits in one group; the group's tolerance is its tightest member's and its colour its worst member's.",
      more: "Only admitted services are grouped, and a critical internal function never is. Indirect Services is kept for services a regulator names that are not customer-facing.",
    },
    route_2: {
      term: "Route 2",
      short:
        "How a critical internal function is admitted: assessed and challenged like any admission, then signed by the CRO and the CIO.",
    },
    contagion: {
      term: "Contagion",
      short:
        "How far a function's failure spreads to admitted services, read from what it funds and the obligations it shares with them.",
      more: "Where none of those are declared it falls back to the services they share, and says so.",
    },
    tolerance_window: {
      term: "Window",
      short:
        "A period -- an overnight batch, a month end, a season -- when a tolerance is tighter than usual. A window may only tighten a tolerance, never loosen it.",
    },
    tolerance_limit: {
      term: "Limit",
      short:
        "A point short of a tolerance at which the product raises a warning, so the breach is not the first anyone hears of it.",
    },
    stated_duration: {
      term: "Stated duration",
      short:
        "How long a scenario lasts, as the regulator states it. Where the source gives no duration, the scenario says so rather than inventing one.",
    },
    /* Round 17 R6 -- nothing said what the strip was on first meeting. */
    thread: {
      term: "The thread",
      short:
        "One matter followed in five stages, always in the same order: what the regulator expects, what the bank has, what the product judged, what a named person decided, and what can be proved. Each stage says who holds it and the next step.",
    },
    owner_roles: {
      term: "Your signatures",
      short:
        "An owner signs in three ways: as business owner, for an {{serviceTerm}}; as service owner, for a system beneath it; and as submitter, for the quarterly attestation.",
    },
    /* Round 19 G39 -- the change register's two kinds of change. */
    significant_change: {
      term: "Significant change",
      short:
        "A change the bank judges could alter its resilience -- a new product, an acquisition, a new market, a new technology system. Assessing it brings forward the review of every admitted service it reaches and raises a finding on each.",
    },
    routine_change: {
      term: "Routine change",
      short:
        "A change recorded on the register for the record. Assessing it writes down the assessment and changes nothing else: no review moves and no finding is raised.",
    },
    /* ROUND 13 H6, fourth round -- the constructs this product
       invented and never defined. A reader meeting the six-layer
       block, the capability city or a data asset for the first time
       had nowhere to look them up, on the one page whose job that is. */
    data_asset: {
      term: "Data asset",
      short:
        "A body of data the bank holds, modelled as a thing in its own right -- a customer file, a payments ledger -- rather than as a word in a description. It attaches to the service that stores or processes it.",
      more: "It is modelled because prose cannot be aggregated: 'where does customer data live?' is unanswerable against a free-text field, and residency is a SET of places rather than one country.",
    },
    dependency_layers: {
      // Round 17 H32 -- eight since P429.2: the regime's six, plus the
      // funding and the regulatory obligation it carries.
      term: "The eight layers",
      short:
        "What {{serviceTermShortA}} rests on: the six dimensions the regime asks about (technology, process, people, facilities, third parties and data), plus its funding and the regulatory obligations it carries.",
      more: "Four reach {{serviceTermShortA}} directly (process, technology, funding and regulatory obligation) and four are reached through the services it depends on. That is the correct model, not a gap -- but a layer nobody has mapped is stated as unmapped rather than left blank, because a blank reads as a clean bill of health.",
    },
    capability_city: {
      term: "Capability city",
      short:
        "The 3D view of the bank's capability map, where each capability is a tower and each district a domain. Height and colour carry the figure the view is about.",
      more: "It answers the same question as the flat capability map and is easier to read across a room, which is what it is for.",
    },
    propagation: {
      term: "Propagation",
      short:
        "Following a failure downstream: if this component goes, what else stops working. The product walks the dependency graph rather than asking anyone to remember it.",
      more: "It is why a scenario that names one server can report the {{serviceTermShortPlural}} it reaches. A component that reaches nothing is a real answer, not a missing one.",
    },
    estate: {
      term: "Estate",
      short:
        "Everything the bank has recorded in MARSAD: its services, the systems beneath them, its suppliers, its people and its sites. When a figure says 'across the estate', this is what it is counted over.",
      more: "An empty estate is not a healthy one. Several figures in the product say which they are describing, for exactly that reason.",
    },
    ibs: {
      term: "{{serviceTermTitle}}",
      short:
        "A service the bank provides to customers or the market whose failure would cause harm -- to customers, to the bank's safety, or to confidence in it. The regulator expects you to name them, and to be able to show what each one depends on.",
      more: "An {{serviceTermShort}} is not an IT system. It is what the customer is trying to do: make a payment, open an account, withdraw cash.",
    },
    admission: {
      term: "Admission",
      short:
        "The decision that a candidate service really is an {{serviceTermTitle}}. It is assessed against a written methodology, challenged by second line, and signed by two people -- so the register is a governed decision rather than an opinion.",
    },
    impact_tolerance: {
      term: "{{toleranceTerm, capitalize}}",
      short:
        "The most disruption the bank is prepared to accept before an {{serviceTermTitle}} causes unacceptable harm -- usually expressed as a length of time. It is a board-level statement of appetite, not a target.",
      more: "MARSAD projects how long recovery would actually take and compares it against this, which is what turns a declared number into a verdict.",
    },
    mvb: {
      term: "{{mvbTerm, capitalize}}",
      short:
        "The smallest set of services the bank must be able to keep running, or restore first, for it to still be functioning as a bank. It is a different cut of the estate from the {{serviceTermShort}} register: that one is about governability, this one about survival.",
    },
    /* ROUND 15 H5 -- "Gate-1 threshold" unglossed on the home MVB card,
       seventh round. */
    gate1_threshold: {
      term: "Gate-1 threshold",
      short:
        "The recovery bar the declaration sets for the {{mvbTerm}}: the hours within which its services must be running again after a total loss.",
      more: "Gate 1 is the continuity gate of the {{mvbTermShort}} rubric. A candidate whose supporting services cannot come back inside this bar fails the gate whatever its score.",
    },
    dual_signoff: {
      term: "Dual sign-off",
      short:
        "Two named people, in different roles, each signing the same decision. One cannot do both. It is what makes a record evidence rather than an assertion, and MARSAD seals the pair into a hash chain when the second one signs.",
    },
    positions: {
      term: "Positions",
      short:
        "Preparer, reviewer and approver -- the three seats work moves through. A position is what you may DO; it is separate from the job you hold, so one person can prepare in one place and approve in another.",
    },
    cascade: {
      term: "Cascade",
      short:
        "What else stops working when one thing does. MARSAD walks the dependency map outwards from a failure and shows every service, business service and channel it reaches.",
    },
    hash_chain: {
      term: "Hash chain",
      short:
        "A tamper-evident record. Each entry carries a fingerprint of the one before it, so changing any past entry breaks every fingerprint after it -- and anybody can check that with nothing but the published file.",
      more: "This is what lets an examiner verify a MARSAD dossier without a licence, a password, or trusting the bank.",
    },
    /* Round 20 Y8 -- the thread said "Admission sealed" and nothing
       said what a seal is or how to check one. */
    seal: {
      term: "Seal",
      short:
        "A seal is one entry in a hash chain: a fingerprint (its hash) of a decision as it stood, linked to the entry before it. Where a page says sealed, the hash beside it is that entry.",
      more: "To check one, download the audit dossier and run its verifier: it recomputes every entry of every chain from the first, so a changed decision no longer matches its hash.",
    },
    control_testing: {
      term: "Design, implementation, effectiveness",
      short:
        "Three separate questions about one control: is it designed to work, is it actually in place, and is it working. A control can pass the first two and fail the third, which is why they are recorded apart.",
    },
    maturity_level: {
      term: "Maturity level",
      short:
        "How embedded a control is, on the regulator's scale, from 'not done' up to 'measured and improving'. It answers a different question from compliance: a control can be in place and still immature.",
    },
    finding: {
      term: "Finding",
      short:
        "Something wrong that MARSAD has been told about -- by a scanner, a monitoring system, a drill, or a person. Findings are what turn the live twin red; they are not incidents, and MARSAD does not replace your incident system.",
    },
    /* Round 12 H2 -- sixth round of the wrong definition on the right
       control. The "?" beside four My Work plan rows and the one on the
       control drawer's effectiveness-KPI heading both resolved to
       `finding`, so the persona whose whole job runs through plans and
       metrics was told, twice, what a Finding is. The glossary had no
       entry for either thing, which is why they were pointed at a
       third. */
    cap: {
      term: "Corrective action plan",
      short:
        "The bank's written answer to something a regulator or an assessment found wrong: what will be done, by whom, by when. It moves through the three positions, and its history is sealed, so what was promised cannot be edited after the fact.",
      more: "A plan is raised against the control or the finding it answers, which is what lets the product show a regulator that the gap they named has a response attached to it.",
    },
    kpi: {
      term: "Effectiveness KPI",
      short:
        "A number the product watches to judge whether a control is working in practice, with a tolerance the bank sets. Where the readings sit inside tolerance the effectiveness test passes on measurement rather than on assertion.",
      more: "KPIs are fed from the twin where the twin can answer -- drill pass rates, time to close a finding, availability -- and by hand where it cannot.",
    },
    tier: {
      term: "Criticality tier",
      short:
        "How critical an admitted service is, which sets how often it is reviewed and rehearsed. Tier 0 is the tightest -- reviewed quarterly and expected in the {{mvbTerm}}; Tier 2 is reviewed yearly.",
      more: "The tier is derived from the methodology score and the tightest recovery time of anything the service depends on, not typed in.",
    },
    service_classification: {
      term: "Service classification",
      short:
        "How much a service matters to the bank: whether it supports an {{serviceTerm}}, merely supports something that does, or is non-critical. Confirming it is a signed decision, because everything downstream reads it.",
    },
    connector: {
      term: "Connector",
      short:
        "A scheduled pull of data from one of your own systems -- a CMDB, a scanner, a document store. The status tells you whether the last run worked and whether the next one is overdue; stale data is a silent way for a dashboard to be wrong.",
    },
    architecture_edges: {
      term: "Topology edges",
      short:
        "The links from an {{serviceTerm}} to the services and hosts it actually runs on. Without them a failure has nothing to travel along, so a service can look safe when it is only unmapped.",
      more: "Load them by CSV (the {{serviceTermShort}}-to-Service link template) or from an architecture connector.",
    },
    data_source_class: {
      term: "Data source class",
      short:
        "Whose data a source is: the bank's own, reference data we ship, or demo filler. Mark a source canonical when it is the one that wins if two sources disagree about the same thing.",
    },
    data_baseline: {
      term: "Data baseline",
      short:
        "Whether the data underneath the answers is good enough to trust them. It asks three things: are the entities linked to each other, are the regulator's mandatory fields filled, and do the classifications actually distinguish anything -- a register where everything is Critical says nothing.",
    },
    supervisory_check: {
      term: "Supervisory check",
      short:
        "A question a real examination asks, answered from the bank's live data rather than a questionnaire. FLAG means the data as it stands would support a finding; it names the rows, so it can be argued with.",
    },
    resilience_score: {
      term: "Resilience score",
      short:
        "A 0-100 score over the bank's posture, exposure and outcomes, with a letter band. Confidence says how much of the score rests on evidenced data -- a high score on thin data is capped, because the number should not outrun what supports it.",
      // Round 20 Y8 -- the method, where the reader can re-perform it.
      more: "The score card on Readiness lists every input, weight and the formula, and links to the published method.",
    },
    examiner_grant: {
      term: "Examiner grant",
      short:
        "Time-boxed access for a named examiner, limited to the examination pages. Every mint and revoke is sealed into a chain, so the bank can show exactly who had access and when, and the record cannot be edited afterwards.",
    },
    assessment_cycle: {
      term: "Assessment cycle",
      short:
        "One pass through every control in a framework, with a due date. You grade each control, then submit the cycle for review.",
    },
    control_status: {
      term: "Control status",
      short:
        "Your judgement of how well a control is met today. It can also be set from the three tests -- design, implementation and effectiveness -- in which case the worst of them wins.",
    },
    peer_workflow: {
      term: "Peer review",
      short:
        "After you submit an assessment a reviewer either approves it or sends it back with a comment saying what to change. Nothing is final until it has been through someone else.",
    },
    probe: {
      term: "Probe",
      short:
        "A live check against the twin that says whether something a control claims is actually in place -- backups running, drills executed, evidence still fresh. It is the difference between asserting a control works and observing it.",
    },
    drra: {
      term: "Attestation",
      short:
        "A named person stating, for the record and on a schedule, that a business service stands where the bank says it does. What they sign is frozen at that moment, so it can be read back later exactly as it was.",
    },
  },
  /*
   * Round 9 G -- the product's words for its own concepts.
   *
   * These lived as hard-coded TypeScript maps in shared/vocab/, which
   * `markUntranslated` never sees -- so they could not translate at
   * any price, and under the pseudo-locale they rendered as bare
   * English. They are workflow states, classifications, outcome
   * categories, ceremony seats and entity kinds: exactly the
   * vocabulary a regulator reads.
   *
   * The helpers (`stateLabel`, `roleLabel`, ...) keep their signatures
   * and their fallbacks; only the WORDS moved here.
   */
  vocab: {
    /* P418 -- what class of severe but plausible event this is, as
       distinct from the cause `area`. Catalogued rather than printed
       from the enum, because a reader should not be shown
       `pandemic_workforce`. */
    scenarioClass: {
      power_outage: "Power outage",
      technology_failure: "Technology failure",
      third_party_disruption: "Third-party disruption",
      cyber_incident: "Cyber incident",
      insider_threat: "Insider threat",
      natural_disaster: "Natural disaster",
      pandemic_workforce: "Pandemic and workforce",
      facility_loss: "Facility loss",
      demand_surge: "Demand surge",
      data_corruption: "Data corruption",
      regulatory_enforcement: "Regulatory enforcement",
    },
    governance: {
      proposal: {
        included: "Admit to the register",
        deferred: "Defer -- not admitted for now",
        excluded: "Exclude from the register",
        ibs_supporting: "Supports an {{serviceTerm}}",
        supporting: "Supporting",
        non_critical: "Not critical",
        out_of_scope: "Outside resilience scope",
      },
      tier: {
        tier_0: "Tier 0 -- critical",
        tier_1: "Tier 1 -- high",
        tier_2: "Tier 2 -- medium",
        tier_3: "Tier 3 -- standard",
        core_mvb: "Core {{mvbTerm}}",
        mvb: "{{mvbTerm, capitalize}}",
        watchlist: "Watchlist -- not in the {{mvbTermShort}}",
        out_of_scope: "Outside the {{mvbTerm}}",
      },
      pathway: {
        ces: "Customer-exposed service",
        ids: "Indirect service",
        standard: "Standard assessment",
        sms: "Sovereign / mandated service",
        // Round 16 N4.
        cif: "Critical internal function (Route 2)",
        // P416.
        critical_operation: "Critical operation (E-21)",
      },
      nextSigner: {
        cio: "The CIO",
        business_owner: "The business owner",
      },
      recommendation: {
        include: "Include",
        defer: "Defer",
        exclude: "Exclude",
        core_mvb: "Core {{mvbTerm}}",
        mvb: "{{mvbTerm, capitalize}}",
        watchlist: "Watchlist -- not in the {{mvbTermShort}}",
        out_of_scope: "Outside the {{mvbTerm}}",
      },
      members: {
        empty_set: "Nothing -- signing would put an empty set in force",
      },
      /* Round 11 F1 -- the four SAMA outcome categories. The register
         has said "Minimise customer harm" for rounds; the owner's own
         attestation panel said "Minimize customer harm", because the
         value was read through the classification vocabulary, missed,
         and fell back to the de-underscored column. */
      outcome: {
        minimize_customer_harm: "Minimise customer harm",
        retain_customer_confidence: "Retain customer confidence",
        regulatory_engagement: "Regulatory engagement",
        protect_safety_and_soundness: "Protect safety and soundness",
      },
    },
    entityKind: {
      ibs_service: "{{serviceTerm, capitalize}}",
      // Round 16 L2 -- the internal class of the same table.
      ibs_service_internal: "Critical internal function",
      service: "Service",
      capability: "Capability",
      vendor: "Supplier",
      configuration_item: "Technology asset",
      product: "Product",
      channel: "Channel",
      journey: "Customer journey",
      connector: "Connected system",
      outsourcing_contract: "Outsourcing contract",
    },
    /* The composite state My Work emits for a half-signed ceremony.
       The role half goes through `roleLabel`, so only the frame is
       here -- and in Arabic the frame moves. */
    awaitingRole: "Awaiting {{role}}",
    state: {
      draft: "Draft",
      in_review: "In review",
      in_signoff: "Awaiting sign-off",
      approved: "Approved",
      sent_back: "Sent back",
      submitted: "Submitted",
      rejected: "Rejected",
      open: "Open",
      closed: "Closed",
      challenged: "Challenged -- awaiting the CRO",
      cro_signed: "CRO signed -- awaiting the second signature",
      business_owner_signed: "Business owner signed -- awaiting the CRO",
      ops_res_signed: "Resilience lead signed -- awaiting the CRO",
      service_owner_signed: "Service owner signed -- awaiting the CRO",
      compliant: "Compliant",
      partial: "Partial",
      tested: "Tested",
      non_compliant: "Non-compliant",
      no_evidence: "No evidence",
      not_applicable: "Not applicable",
      candidate: "Candidate",
      admitted: "Admitted",
      included: "Included",
      excluded: "Excluded",
      removed: "Removed",
      deferred: "Deferred",
      superseded: "Superseded",
    },
    role: {
      preparer: "Preparer",
      reviewer: "Reviewer",
      approver: "Approver",
      cro: "Chief Risk Officer",
      business_owner: "Business owner",
      cio: "Chief Information Officer",
      service_owner: "Service owner",
      head_of_opsres: "Head of Operational Resilience",
      // One seat, one name. The API spells it `ops_res` and the realm
      // spells it `head_of_opsres`; a reader meeting both should not
      // meet two job titles, so these two entries say the same words
      // and a test asserts they keep saying them.
      ops_res: "Head of Operational Resilience",
      ae: "Accountable Executive",
      examiner: "Examiner",
      admin: "Administrator",
      submitter: "Submitter",
      challenge: "Second-line challenge",
    },
    letterSource: {
      pdf_upload: "uploaded PDF",
      pasted: "pasted text",
      internal: "raised inside the bank",
    },
    classification: {
      ibs_supporting: "{{serviceTermShort}} supporting",
      supporting: "Supporting",
      non_critical: "Non-critical",
      out_of_scope: "Out of scope",
    },
    /* Round 10 E1 -- three closed sets that were reaching the screen
       as column values with the underscores taken out. */
    postureVerdict: {
      ok: "Sufficient",
      unproven: "Unproven",
      insufficient: "Insufficient",
      no_posture: "No posture recorded",
    },
    drillStatus: {
      planned: "Planned",
      in_progress: "In progress",
      passed: "Passed",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    findingCategory: {
      cyber: "Cyber",
      technology: "Technology",
      third_party: "Third party",
      process: "Process",
      people: "People",
      facility: "Facility",
      data: "Data",
      other: "Other",
    },
    classificationInSentence: {
      ibs_supporting: "supports a business service",
      supporting: "supports services other than an {{serviceTerm}}",
      non_critical: "not critical",
      out_of_scope: "out of scope",
    },
    /* Round 11 F3 -- how a service keeps running when something
       fails. Was a hard-coded English map inside one view, about to be
       copied into a second. */
    continuityPosture: {
      active_active: "Active-active",
      active_passive: "Active-passive",
      backup_restore: "Backup-restore",
      manual_workaround: "Manual workaround",
      none: "None",
    },
    /* Round 11 F3 -- the qualifiers the six-layer block prints after
       the kind. Ten rounds of this class, and it grew on the newest
       surface: "SARIE Gateway / a supporting service / ibs_supporting".
       The kind was translated and our column value left standing
       beside it. */
    layerDetail: {
      // Round 20 V6.
      no_exit_plan: "material, no exit plan",
      // Round 20 V9.
      no_members: "no one in it",
      ibs_supporting: "supports an {{serviceTerm}}",
      /* ROUND 13 H -- "supporting" one row under "supports an
         important business service" is not an answer, it is the same
         word with the answer taken out. Both places that print this
         classification as a phrase now say what it supports. */
      supporting: "supports services other than an {{serviceTerm}}",
      non_critical: "not critical",
      out_of_scope: "out of resilience scope",
      cmdb_derived: "edge derived from the CMDB",
      architecture_connector: "edge imported from the architecture model",
      /* ROUND 13 H -- the CMDB classes, without the vendor's table
         prefix. `cmdb_ci_lb_appliance` is a ServiceNow table name, not
         a description of anything, and six detail rows ended in one. */
      linux_server: "Linux server",
      db_instance: "database",
      atm: "ATM",
      lb_appliance: "load balancer",
      app_server: "application server",
      network_gear: "network device",
      storage: "storage",
      vm: "virtual machine",
      container: "container",
      cluster: "cluster",
      public: "public",
      internal: "internal",
      confidential: "confidential",
      secret: "secret",
      customer_personal: "customer personal data",
      payment: "payment data",
      account: "account data",
      transaction: "transaction data",
      employee: "employee data",
      operational: "operational data",
      regulatory: "regulatory data",
      stores: "stored here",
      processes: "processed here",
      transmits: "transmitted here",
      // P429.2 -- what a funding resource is, and a commitment's kind
      // and the service's stance towards it.
      settlement_account: "settlement account",
      facility: "facility",
      buffer: "buffer",
      market_access: "market access",
      counterparty: "counterparty",
      return: "regulatory return",
      notification: "notification",
      limit: "limit",
      licence_condition: "licence condition",
      bound_by: "bound by it",
      generates: "produces it",
    },
    outcomeCategory: {
      minimize_customer_harm: "minimise customer harm",
      retain_customer_confidence: "retain customer confidence",
      regulatory_engagement: "regulatory engagement",
      protect_safety_and_soundness: "protect safety and soundness",
    },
    roleBadge: {
      cro: "CRO",
      head_of_opsres: "Head of OpsRes",
      ae: "AE",
      challenge: "Challenge",
    },
  },
  /* ROUND 13 I2 -- the reason a row was refused, in the reader's
     language. Every importer message was a formatted string composed
     in the backend with a Python literal inside it, reachable by no
     catalogue at all, on the screen where a person is most likely to
     need words they understand.

     The server still composes its English and sends it as `message`;
     these are what a client renders when it can. A code the catalogue
     has not learned falls back, so adding one can never blank a row
     error that used to read. */
  import: {
    /* Round 16 I14 -- how a field wants to be written. */
    form: {
      enum: "one of: {{values}}",
      bool: "true or false",
      date: "an ISO date, e.g. {{example}}",
      number: "digits only, with no currency marker or thousands separator",
      numberExample:
        "digits only, with no currency marker or thousands separator, e.g. {{example}}",
      maxLength: "at most {{max}} characters",
      example: "e.g. {{example}}",
    },
    row: {
      requiredBare: "required",
      required: "required. Every row needs a value for '{{field}}'.",
      notAccepted: "'{{value}}' is not accepted: {{form}}.",
      unresolvedCode:
        "no {{entity}} with code '{{value}}'. Load {{entity}} first, then import this file again.",
      noSuchCode: "no {{entity}} with code '{{value}}'",
      noJourney:
        "no journey for {{ibs}} via channel {{channel}} -- import the journey first",
      selfLoop: "an edge cannot point at itself",
      // Round 20 W13 -- two CMDB items sharing a name, collapsed into one.
      selfLoopSharedName:
        "both ends are '{{value}}'. Two items that share a name are held as one configuration item, so a link between them collapses into one pointing at itself. Give the two items distinct names to keep the link.",
      // P429.2 -- only a Critical Internal Function funds a service.
      fundedByNotInternal:
        "'{{value}}' is not a Critical Internal Function, so it cannot fund another service",
      // P428 -- a service sits in one group; a function in none.
      groupHoldsNoFunction:
        "'{{value}}' is a Critical Internal Function; functions sit on their own register, not in a group",
      alreadyGrouped: "'{{value}}' is already in another group; remove it there first",
      // P429.6 -- a function the bank performs for itself has no channel.
      functionHasNoChannel:
        "'{{value}}' is a Critical Internal Function; a function the bank performs for itself has no delivery channel or customer journey",
      // ROUND 15 F7 -- the ring refusal, the last row refusal with no code.
      dependencyRing:
        "this edge closes a dependency ring: {{ring}}. A ring is what the cascade walks, so it is refused. Remove one edge of the ring and import again.",
      // Round 20 W5 -- a configuration item placed under a second service.
      secondService:
        "{{name}} already rests under service {{first}} (row {{row}}); a configuration item rests under one service, so this row's {{service}} was not staged. Keep the service it supports most directly; the dependency between the two services belongs in a service dependency link.",
      // Round 20 W1 -- a row with more or fewer fields than the header.
      misshapenLong:
        "line {{line}}{{who}}: {{fields}} fields under a {{header}}-column header; check for an unquoted comma. The row was not staged, so no value in it was read into the wrong column.",
      misshapenShort:
        "line {{line}}{{who}}: {{fields}} fields under a {{header}}-column header; check for a missing value or a line break inside a cell. The row was not staged, so no value in it was read into the wrong column.",
    },
  },
  /* Round 16 O1 -- the signed group register, offered where groups are
     governed and on the examiner's seat. */
  /* Round 16 D18 -- whose a stated duration is. */
  scenarioProvenance: {
    setBySource: "Set by {{who}} on {{when}}; {{source}} states {{figure}}",
    setBy: "Set by {{who}} on {{when}}",
    sourceStates: "{{source}} states {{figure}}",
    hours: "{{n}}h",
    range: "{{from}} to {{to}}h",
    untilRecovered: "until recovery completes",
    none: "none",
  },
  groupRegister: {
    download: "Download the signed group register",
    downloading: "Preparing the group register…",
    body: "Every group, its members and their admission, its worst colour and its derived tolerance, signed. Verify it offline with the bundled verifier.",
  },
  examiner: {
    // Round 18 S3 -- a typed bank page is said to be the bank's.
    notYours: "{{path}}: examiners read the workspace; this page is the bank's.",
    notYoursHome: "Go to the workspace",
    // D11 -- one row per offender printed thirty near-identical
    // lines, each naming one, so an examiner could not tell whether he
    // was reading one finding or thirty.
    coherenceCount_one: "1 record",
    coherenceCount_other: "{{count}} records",
    // C4 -- the count names the rows listed here, not the estate the
    // check ran over. The detail sentence carries that, and the two
    // numbers were contradicting each other.
    coherenceNamed_one: "{{count}} named below",
    coherenceNamed_other: "{{count}} named below",
    /* D3 -- the server caps what it sends; a short list that does not
       say so reads as the whole answer. */
    /* Round 9 E4 -- the pointer went to a surface the examiner has no
       route to, and the count disagreed with the sentence above it.
       One number, and it is the real one. */
    coherenceMore: " -- naming {{shown}} of {{count}}",
    // CX6 -- the record a check named, opened. The links waited for
    // somewhere to land: the boundary confines an examiner to the
    // examiner surfaces, so a link into the operator's estate would
    // have refused him.
    /* ROUND 15 H8, fifteenth round -- the route to a control. */
    controls: {
      heading: "Controls",
      body: "Look a control up by its identifier to read its latest assessment, who graded it and when, the evidence attached, and the policy documents that cover it.",
      label: "Control",
      find: "Find",
      none: "No control matches \u201c{{query}}\u201d.",
      error: "This control could not be read.",
      assessmentHeading: "Latest assessment",
      notAssessed: "Not yet assessed in any cycle.",
      assessment: "{{status}}, maturity {{current}} (target {{target}}), in {{cycle}}.",
      gradedBy_one: "Graded by {{who}} on {{when}}; the judgement is sealed once.",
      gradedBy_other:
        "Graded by {{who}} on {{when}}; the judgement is sealed {{count}} times.",
      neverSealed: "No sealed judgement names who graded it.",
      evidenceHeading: "Evidence attached",
      noEvidence: "No evidence is attached to this assessment.",
      policyHeading: "Policy documents covering it",
      noPolicy: "No policy document is mapped to this control.",
    },
    /* ROUND 15 H8 -- the record card's fields, said as words. */
    field: {
      biz_crit: "Business criticality",
      cyber_crit: "Cyber criticality",
      rto_hours: "Recovery time objective (hours)",
      rpo_hours: "Recovery point objective (hours)",
      classification: "Classification",
      outcome_category: "Outcome category",
      mvb_flag: "In the {{mvbTerm}}",
      sovereign_flag: "Sovereign service",
      ci_class: "Asset class",
      filename: "File",
      sha256: "Fingerprint (SHA-256)",
      content_type: "Format",
      size_bytes: "Size (bytes)",
      extraction_status: "Text extraction",
      page_count: "Pages",
      directed_passages: "Passages addressed to an automated reader",
      hidden_passages: "Passages not visible in the original",
    },
    entity: {
      close: "Close",
      error: "Could not load this record.",
      findings: "{{count}} open findings, worst {{severity}}",
      sealsHeading: "Sealed about this record",
      noSeals: "Nothing is sealed about this record.",
    },
    // D11 -- the table showed a stamp and the only way to move it was
    // to reload the page and re-walk all sixteen.
    chainVerifyOne: {
      column: "Check",
      now: "Verify now",
      again: "Verify again",
      running: "Re-walking...",
    },
    /* Round 16 H30 (M5 fix 7) -- which figure to cite, and why each
       seal of the score was sealed. */
    score: {
      heading: "The Resilience Score: which figure to cite",
      which:
        "The tile above is computed from the live estate, read {{read}}: {{live}}. The latest seal, {{sealedAt}}, reads {{sealed}}. Cite the seal: it is the figure the chain proves, and the tile moves with the estate.",
      noSeal: "No seal of the score exists yet, so there is no sealed figure to cite.",
      // Round 17 H38 -- it rewinds the score, and only the score.
      asOfLabel: "The score as it stood at (rewinds the score only)",
      inForce:
        "At {{when}} the seal in force read {{score}} ({{tier}}), sealed {{sealedAt}}.",
      noneInForce: "No seal of the score stood at {{when}}.",
      reason: {
        first: "The first seal.",
        methodology: "The methodology changed, {{from}} to {{to}}.",
        moved: "{{pillar}} {{before}} to {{after}}",
        inputs: "No pillar moved; what the score is derived from changed.",
      },
      pillar: {
        posture: "Posture",
        exposure: "Exposure",
        outcome: "Outcome",
      },
      by: {
        worker: "sealed on the scheduled re-derive",
        seed: "sealed when the demonstration estate was loaded",
        admin: "sealed on an administrator's derive",
        other: "sealed by {{kind}}",
      },
    },
    rfi: {
      /* Round 12 H3 -- the accessible name for the ask box. */
      label: "Your question for the bank",
      heading: "Ask the bank",
      body: "Lodge a question with the bank. It is answered from the assurance surface, and a signed evidence pack comes back here.",
      placeholder: "What would you like the bank to answer?",
      ask: "Send to the bank",
      sending: "Sending…",
      /* Round 16 H30 -- where it went. Only an approver answers a
         question, so it waits on that seat. */
      sent_one:
        "Sent. It waits with the bank's Approver seat, held by {{count}} person, who answers it.",
      sent_other:
        "Sent. It waits with the bank's Approver seat, held by {{count}} people, who answer it.",
      sent_zero:
        "Sent. Nobody at the bank holds the Approver seat that answers it yet.",
      // Round 18 S4 -- who answers, by name.
      sentNamed_one:
        "Sent. It waits with the bank's Approver, {{names}}, who answers it.",
      sentNamed_other:
        "Sent. It waits with the bank's Approvers, {{names}}; any of them answers it.",
      waitsWith: "Waiting with the bank's Approver seat",
      // Round 19 S8 -- the row names the people, as the toast did.
      waitsWithNamed_one: "Waiting with the bank's Approver, {{names}}",
      waitsWithNamed_other: "Waiting with the bank's Approvers, {{names}}",
      open: "Awaiting reply",
      answered: "Answered",
      // A3 -- who answered, and what they cited, were only inside the
      // zip.
      answeredBy: "Answered by {{who}}, {{at}}",
      citedGrade: "graded {{status}} by {{who}}",
      graderUnknown: "an unnamed user",
      sealed: "sealed",
      notSealed: "not sealed",
      downloadPack: "Download the signed answer pack",
    },
    check: {
      // C5 -- a compound check names what it flagged.
      // CX6 -- the cap on a class member list, stated. A silent cut is
      // the failure this programme keeps finding.
      // D4 -- "and 3 more" told a reader there was more and gave no
      // route to it, which is a dead end wearing a number. It says what
      // is shown against the whole, and where the whole lives: the
      // signed pack, which is reachable from this page.
      /* Round 11 F4 -- the sentence acts now: it was an inert line for
         seven rounds, telling a supervisor where to stop rather than
         where to look. The pack still carries the full list and is
         still the durable copy; this is the one he can read here. */
      classMore: "Showing {{shown}} of {{count}} -- show the rest",
      /* ... and where there is no second read to offer. The class is
         grouped on a derived value rather than a column, so the pack
         is where the full list is. */
      classMoreFixed:
        "Showing {{shown}} of {{count}}. The full list is in the signed pack.",
      offendersHeading_one: "The row this check flagged:",
      offendersHeading_other: "The {{count}} rows this check flagged:",
      noSpread:
        "This check judges a combination of conditions rather than one field, so it has no single distribution to show. The sentence above states what it counted.",
    },
    verify: {
      intro:
        "The zip contains a verifier. It needs no MARSAD licence and no " +
        "secret: it recomputes every hash from the sealed bytes and checks " +
        "the signature against the key published in the manifest. Run it " +
        "on a machine of your own.",
      command: "python verify-dossier.py <the-downloaded-file>.zip",
      /* Round 19 S8 -- what the chain anchor proves: a later dossier
         checked against one kept from an earlier visit shows the
         history was only added to. */
      against:
        "Keep this dossier. A later one can be checked against it: the verifier confirms every chain head the earlier dossier anchored is still in the later one's history, so nothing sealed before has been changed or removed.",
      againstCommand:
        "python verify-dossier.py <the-later-file>.zip --against <the-kept-file>.zip",
      ok:
        "every file matches its recorded hash, every chain recomputes from " +
        "genesis, and the signature is valid. Exit code 0.",
      /* ROUND 15 K10 -- what the verifier prints on the hosted pack. */
      selfAsserted:
        "the embedded public key is the one the pack carries for itself. Pass --expected-key or --keys-url to pin it to the issuer's published key; that is the key this deployment publishes at:",
      keysLink: "the published proof keys",
      warn:
        "the contents verify, but the signing key is a labelled " +
        "demonstration key rather than a production one. Nothing is wrong " +
        "with the evidence; the signature is not one you should rely on " +
        "for a real submission.",
    },
    kicker: "Examiner workspace",
    heading: "Examination workspace",
    body: "Run the supervisory checks, re-walk every evidence chain, and download the signed dossier -- live, read-only, from the bank's running estate.",
    asOf: "Read at {{when}}. Sealed states are as at that moment; open findings are live.",
    grantLine: "Access grant for {{username}}: {{scope}} -- expires {{date}}",
    error: "Could not load the examination overview.",
    ok: "OK",
    flagged: "FLAG",
    /* Round 16 B9 -- a check with nothing to judge is neither a pass
       nor a flag, and wore the same OK as a passed one. */
    notAssessed: "Not assessed",
    downloadingDossier: "Preparing the dossier…",
    dossierUnavailable:
      "The dossier is not available while this visit cannot be read. If your access has been withdrawn, ask the bank contact who issued it.",
    downloadDossier: "Download the signed dossier",
    dossierBody:
      "The full signed audit dossier over the last 30 days. Verify it offline with the bundled stdlib verifier -- no licence, no key.",
    cards: {
      score: "Resilience score",
      scoreNotScored: "Not yet scored: nothing admitted",
      confidence: "confidence {{pct}}%",
      baseline: "Data baseline",
      checks: "Checks flagged",
      checksSub: "supervisory checks",
      // Round 18 P11 -- the tile's parts, joined: the flagged figure
      // includes the coherence flags the field exam lists apart.
      checksSubCoherence_one: "{{count}} of them a coherence flag",
      checksSubCoherence_other: "{{count}} of them coherence flags",
      checksSubNotAssessedPart: "{{count}} not assessed",
      // Round 19 P18 -- what the flagged figure is made of, as the field
      // exam counts it.
      checksParts:
        "flagged: {{distribution}} distribution of {{distributionTotal}}, {{coherence}} coherence",
      checksSubJoin: "; ",
      chains: "Chain breaks · chains",
      chainsSub: "{{rows}} rows re-walked",
      // Round 18 B22 -- a tick needs a walk: say how many chains had one.
      chainsWritten: "{{mark}} · {{written}} of {{total}} written",
    },
    sections: {
      deployment: "Where this deployment runs",
      checks: "Supervisory checks -- live",
      chains: "Evidence chains -- re-walked from genesis",
      dossier: "The signed dossier",
    },
    /* Round 13 F8 -- criterion 12. The mechanism was built in round
       12 and an EXAMINER could not reach a word of it: the panel is
       administrator-only and his token is confined to his own routes,
       where "hosted", "residency", "restore" and "backup" occurred
       zero times. A sovereignty claim nobody can read is not one. */
    deployment: {
      region: "Region",
      hosting: "Hosting",
      backup: "Backup",
      restore: "Restore last exercised",
      notStated: "not stated",
      unstated:
        "This deployment has not told the product where it runs. Nothing here is inferred: residency is a claim the bank makes to its regulator, and a screen naming a region it was not given would be worse than one saying nothing.",
      // ROUND 15 K9 -- a refusal rendered as "not stated" read, to the
      // same seat three minutes apart, as two different facts about
      // where the data lives.
      refused: "Shown once you hold a grant.",
      unavailable: "The deployment facts could not be loaded just now.",
      // ROUND 15 K6 -- the product's own record beside the operator's line.
      restoreRecorded: "Last restore recorded by the product: {{date}}.",
      restoreDisagrees:
        "The operator's restore line and the product's own record disagree. The record is what ran; the line is what was typed.",
      source:
        "Repeated from what this deployment was configured to say, not measured by the product.",
      document: "The deployment's own document",
    },
    chain: {
      /* ROUND 15 H8 -- a sealed row opens the record it seals. */
      openRecord: "Open the record",
      // D11 -- the table showed a verified-at stamp, and the only way
      // to move it was to reload the page and re-walk all seventeen.
      checkColumn: "Check",
      verifyNow: "Verify now",
      verifyAgain: "Verify again",
      verifying: "Re-walking...",
      // A5 -- the button re-walked the chain and changed only its own
      // label, so the one act on this page that proves something said
      // nothing about what it had proved.
      verifyResult:
        "{{slug}} chain intact: {{examined}} entries checked at {{at}}, {{valid}} valid, {{breaks}} breaks",
      /* Round 19 B30 -- nothing written, so nothing to verify. */
      verifyEmpty: "{{slug}}: nothing written to this chain yet; nothing to verify",
      // C5 -- 32 zero bytes read as a hash rather than as "nothing
      // precedes this row".
      genesis: "genesis",
      entityLabel: "entity",
      /* Round 17 A32 -- the whole fingerprint on request, so a row can
         be joined to the Engine's own log. */
      copyFull: "Copy whole value",
      copiedFull: "Copied",
      /* Round 14 A7 -- shown beside the seal, not covered by it. */
      notSealed: "Recorded with this entry, not covered by the seal",
      /* ROUND 15 A15 -- both model names are inside the seal now, and
         the difference between them is said rather than left to be
         noticed. */
      modelsDiffer:
        "The model that answered ({{model}}) is not the one configured ({{configured}}).",
      /* Round 16 A26 -- under a replay the two names differ by design. */
      modelsReplayed:
        "Answered by the replay configured as {{configured}}, which replays {{original}}.",
      /* ROUND 13 H -- four of seventeen chains read 0 / 0 / 0, and a
         row of zeroes is the same glyph for "nothing has happened
         here" and "nothing is wrong here". On a page built for a
         supervisor those are not the same claim. */
      neverWritten: "never written",
      neverWrittenWhy:
        "This chain is registered and has no rows: nothing it seals has happened on this estate yet. It is not a verification that passed -- there was nothing to verify.",
      slug: "Chain",
      rows: "Rows",
      valid: "Valid",
      breaks: "Breaks",
      loading: "Reading the sealed entries...",
      showing: "Showing the {{shown}} most recent of {{total}} sealed entries.",
      /* Round 12 C2 -- the panel carrying the Engine's entire record
         offered no route past its first 25 rows, and the mislabelled
         ones sat at positions 26 to 57. */
      showMore_one: "Show {{count}} more",
      showMore_other: "Show {{count}} more",
      showMoreCapped:
        "This view stops at {{max}} entries; the signed pack carries the whole chain.",
      /* B6 -- an examiner met "genesis" three entries deep, directly
         under a line saying every entry links to the one before it.
         Both were true and neither said the missing thing: these
         chains thread PER ENTITY, so one grant's first seal opens its
         own thread rather than continuing somebody else's. */
      perEntity:
        "Each entry links to the one before it FOR THE SAME RECORD; a record's first seal opens its own thread.",
      firstForEntity: "first entry for this {{kind}}",
    },
  },
  vendorPassport: {
    heading: "Resilience passport",
    none: "No passport on record. Ask the vendor to produce one with the free MARSAD passport tool and upload it here.",
    currentUntil: "current until {{date}}",
    expired: "expired",
    firstUse: "key pinned on first use",
    tofu: "Trust this key on first use (I have confirmed it with the vendor out-of-band)",
    upload: "Upload passport",
    // Round 20 Q8
    adminOnly:
      "Resilience passports are held by administrators: only an administrator can see or upload one.",
    uploading: "Verifying signature...",
    uploadFailed: "Upload failed.",
    hint: "The signature is verified against the key recorded for this vendor; the first upload pins it after out-of-band confirmation.",
  },
  mvbDrill: {
    /* Round 19 Q6 -- why there is nothing to schedule here. */
    scheduleWhyNot:
      "An administrator schedules a drill and records what it found; you can read every drill and its result here.",
    heading: "{{mvbTermShort}} validation drill",
    body: "The rehearsal that turns a declared {{mvbTerm}} into a proven one: the recovery sequence executed against total loss, actual recorded against projected per step, and the variance fed back into the rated model.",
    empty: "No validation drills yet.",
    projectedVsActual: "projected T+{{projected}}h vs actual T+{{actual}}h",
    step: "Restored",
    projected: "Projected",
    actual: "Actual",
    variance: "Variance",
    met: "met",
    missed: "missed",
    /* Round 10 F1 -- the ceremony this page could not perform. The
       recovery sequence points here to "test it"; until now the page
       could only list drills somebody else had created. */
    schedule: "Schedule a validation drill",
    scheduleConfirm: "Freeze the plan and schedule",
    scheduling: "Scheduling\u2026",
    scheduled: "Scheduled {{name}}. The plan is frozen as it stands today.",
    scheduleNote:
      "Scheduling freezes the current recovery sequence as this drill's plan, so a later model change cannot rewrite what was projected.",
    defaultName: "Total-loss rehearsal {{date}}",
    nameLabel: "What this rehearsal is called",
    dateLabel: "Scheduled for",
    cancel: "Cancel",
    start: "Start the drill",
    started: "Drill started.",
    record: "Record",
    recorded: "Recorded.",
    recordedCount: "{{recorded}} of {{total}} steps recorded",
    actualFor: "Actual finish, hours after start, for {{step}}",
    complete: "Close the drill",
    completed: "Drill closed. The verdict is computed on what was observed.",
    failed: "That did not go through.",
    apply: "Apply actuals to the model",
    applied: "Applied {{count}} observed RTO(s); {{skipped}} cycle block(s) skipped",
    note: "The plan was frozen when the drill was created, so a later model change cannot rewrite what was projected. A cycle block's observed duration is not attributed to one member, so those steps are skipped when actuals are applied.",
  },
  toleranceMetrics: {
    // Round 20 V5 -- why a reader sees no declaration form.
    noFormNoOwner:
      "No form here: only the service's owner can countersign a tolerance, and no owner is recorded, so nobody can complete a new one until one is.",
    noFormNotYours:
      "A tolerance is declared by the service's owner or the resilience desk.",
    heading: "{{toleranceTerm, capitalize}} — all dimensions",
    body: "Every declared tolerance dimension, evaluated live against the same projected outage duration the recovery model produces. Each metric shows where its number came from. A tolerance is breached if any declared dimension is breached.",
    error: "Could not load the tolerance-metrics view.",
    // Round 17 H40 -- plural-aware.
    assessed_one: "{{count}} {{serviceTermShort}} assessed",
    assessed_other: "{{count}} {{serviceTermShortPlural}} assessed",
    // Round 17 L6 -- the register's cut, where candidates were assessed too.
    assessedAdmitted: " ({{count}} admitted)",
    // Round 16 L3 -- appended after the IBS count, functions apart.
    assessedFunctions_one: " and {{count}} critical internal function",
    assessedFunctions_other: " and {{count}} critical internal functions",
    /* Round 16 H22 -- the page is filtered to her service now, so the
       sentence no longer points at rows below. */
    focusNotReached:
      "No live condition reaches this service now, so there is nothing to project against its tolerance.",
    showAll_one: "Show the {{count}} service something is affecting now",
    showAll_other: "Show all {{count}} services something is affecting now",
    breached: "breached",
    indeterminate: "cannot be judged",
    // P429.3 -- the live tolerance view judges time alone, so a breach
    // on another dimension is counted where it is seen, and said.
    beyondTime_one:
      "of which {{count}} on a dimension other than time -- the live view, which judges time, does not count it",
    beyondTime_other:
      "of which {{count}} on a dimension other than time -- the live view, which judges time, does not count them",
    multiMetric: "{{count}} with more than one dimension declared",
    mvb: "{{mvbTermShort}}",
    /* Round 20 P21 -- which model: the worst live condition, where the
       signed incident pack reads one finding's own projection. */
    projected: "projected {{hours}}h outage from the worst live condition",
    noProjection: "no live condition reaches this {{serviceTermShort}}",
    declaredCount: "{{count}} dimension(s) declared",
    showMetrics: "Show the declared dimensions",
    hideMetrics: "Hide the dimensions",
    metricBreached: "Breached",
    metricWithin: "Within",
    metricUnjudgeable: "Cannot be judged",
    sourceLabel: "Source:",
    /* A3 -- the declarable dimensions, for the declaration form. The
       evaluated rows take their labels from the API; the form has no
       row to read one from, so the wording lives here and a backend
       test pins the vocabulary it mirrors. */
    metric: {
      // ROUND 15 G4 -- "Duration of disruption: 4" with a missing key
      // and fifteen console errors: the duration is a metric too, and
      // its unit is part of its name.
      duration_hours: "Duration of disruption (hours)",
      customers_affected: "Customers affected",
      financial_loss_sar: "Financial loss (SAR)",
      transactions_lost: "Transactions lost",
      service_degradation_pct: "Diminishment of service (% capacity lost)",
      data_loss_hours: "Loss of data (hours of work)",
      integrity_breach_hours: "Running without integrity (hours, observed)",
      fail_closed_minutes: "Time to fail closed (minutes, observed)",
    },
    observedBasis: "judged on observations",
    observation: {
      open: "Record an observation",
      value: "Observed ({{unit}})",
      date: "When",
      kind: "From",
      kinds: {
        test: "A test",
        drill: "A drill",
        incident: "An incident",
      },
      source: "Which one",
      sourcePlaceholder: "e.g. Q3 sanctions-list outage test",
      save: "Record",
      cancel: "Cancel",
      error: "Could not record the observation.",
    },
    declareHeading: "Declare a tolerance",
    declareFor: "Declare a tolerance for {{ibs}}",
    verdict: {
      breached: "Breached",
      indeterminate: "Cannot be judged",
      within: "Within tolerance",
      undeclared: "No tolerance declared",
    },
    boundary:
      "Duration is the binding metric — it is what the recovery model projects against. Diminishment of service and loss of data are measured rather than projected from that clock: the first reads what a scenario states is lost, the second the widest recovery point objective the operation rests on. A dimension whose source figure is not recorded reports as unjudgeable, never as zero and never as within tolerance. Reputational and regulatory tolerance are not offered: both are qualitative and there is no figure here to project them from.",
  },
  continuityPlans: {
    heading: "Continuity plans",
    body: "Business-continuity and IT disaster-recovery plans, and running one as a playbook during a live event. An ITDR plan can be generated from the {{mvbTermShort}} recovery sequence — the restoration order derived from the live dependency graph — rather than assembled by hand. Activating a plan freezes it, so editing it afterwards cannot rewrite the record of what was followed.",
    liveHeading: "{{count}} activation(s) in progress",
    libraryHeading: "Plan library",
    lessons: {
      heading: "Lessons learned",
      none: "No lesson has been recorded from an exercise or an incident.",
      actionLabel: "Recommended:",
      declinedLabel: "Declined:",
      accept: "Accept",
      acceptedToast: "Accepted. Agreeing with it has not changed anything yet.",
      markDone: "Mark incorporated",
      doneToast: "Recorded as incorporated. That is the claim that something changed.",
      decline: "Decline",
      declineConfirm: "Confirm decline",
      reasonLabel: "Why this is not being acted on",
      /* Round 16 D22 -- "accepted 145 days ago" counted from when the
         lesson was RAISED; the register records no acceptance date. Say
         what is known, and when the plan's 90 days have run out. */
      waiting: "raised {{days}} days ago and accepted; not yet incorporated",
      waitingOverdue:
        "raised {{days}} days ago and accepted; not yet incorporated, past the {{grace}} days the plan allows",
      status: {
        open: "open",
        accepted: "accepted",
        declined: "declined",
        done: "incorporated",
      },
      note: "Accepted is not incorporated. Agreeing with a lesson changes nothing, and a register that treats the two alike would report a bank as having learned from an exercise when all it did was nod. Declining needs a reason, or the register can be emptied by clicking. A lesson that changed a plan cannot be re-opened.",
    },
    // Round 14 D4 -- tests on the plan, recorded by the Resilience Lead.
    tests: {
      approvedOn: "approved {{when}}",
      approvedUndated: "marked approved, but no approval date is recorded",
      lastTested: "last tested {{when}}",
      neverTested: "never tested",
      nextDue: "next test due {{when}}",
      show: "Tests",
      hide: "Hide tests",
      none: "No test of this plan has been recorded.",
      loadFailed:
        "The tests could not be read. This says nothing about whether the plan was tested.",
      row: "{{when}}: {{method}}, {{outcome}}",
      methodLabel: "Method",
      outcomeLabel: "Outcome",
      notesLabel: "What happened",
      record: "Record test",
      recorded: "Test recorded. The next test date moves forward from it.",
      raiseLesson: "Raise a lesson",
      lessonLabel: "What this test taught",
      lessonSave: "Raise",
      lessonRaised:
        "Lesson raised. It shows in the register below until it is acted on.",
      method: {
        walkthrough: "walkthrough",
        tabletop: "tabletop",
        simulation: "simulation",
        live: "live",
      },
      outcome: {
        effective: "effective",
        gaps_found: "gaps found",
        ineffective: "ineffective",
      },
    },
    historyHeading: "Past activations",
    noPlans: "No continuity plans yet.",
    generate: "Generate ITDR plan from the recovery sequence",
    generatedBadge: "generated — review before relying on it",
    activate: "Activate",
    reasonPlaceholder: "Why are you activating this?",
    activeBadge: "ACTIVE",
    progress: "{{done}} of {{total}} steps done",
    expected: "expected {{minutes}}m",
    actual: "actual {{minutes}}m",
    notePlaceholder: "What happened?",
    complete: "Complete activation",
    abandon: "Abandon",
    pastSummary: "{{status}}, {{done}} of {{total}} steps done",
    outcome: {
      pending: "Pending",
      done: "Done",
      skipped: "Skipped",
      blocked: "Blocked",
    },
  },
  architectureStandards: {
    /* Round 9 F1 -- a breach a reader can turn into work. */
    raise: "Raise",
    /* Round 10 F1 -- and the rows that cannot become a finding. A
       finding's subject is a closed set; a facility, a contract and a
       data asset are not in it. */
    openRecord: "Open the record",
    notAFindingSubject:
      "A finding is raised against a service, capability, vendor or configuration item, so these rows lead to the record rather than to a form that would refuse them.",
    heading: "Architecture standards",
    body: "Conformance against a curated catalogue of structural standards, derived continuously from the estate rather than collected by an annual survey answered by the people being measured. Each standard states its rule and why the bank holds it, so a breach can be argued with on the merits.",
    error: "Could not load the architecture-standards view.",
    total: "{{count}} standard(s)",
    conformant: "conformant",
    breaching: "breaching",
    conformantBadge: "Conformant",
    not_assessableBadge: "Nothing to assess",
    notAssessable: "not assessable",
    pass: "Conformant",
    breachCount_one: "{{count}} breach",
    breachCount_other: "{{count}} breaches",
    assessed: "{{count}} assessed",
    showRows: "Show the {{count}} breaching row(s)",
    hideRows: "Hide the breaching rows",
    boundary:
      "Derived on read from current estate data, never stored. The catalogue is curated and closed: a standard a bank can redefine at will is a standard nobody can assert against. Thresholds are calibratable; the rule set is not. A breach becomes a plan through its finding: raise one here, and the finding's own page drafts the corrective action plan.",
  },
  serviceDisposition: {
    heading: "Service disposition",
    body: "A proposed disposition for every service in the estate, derived from live evidence rather than typed into a form: how the service is classified, whether its continuity posture is adequate to the load it carries, how many suppliers stand behind it, and what is currently open against it. It proposes; you decide.",
    error: "Could not load the service-disposition view.",
    total: "{{count}} service(s)",
    invest: "Invest",
    migrate: "Migrate",
    eliminate: "Eliminate",
    tolerate: "Tolerate",
    mvb: "{{mvbTermShort}}",
    /* Round 11 F3 -- the evidence behind a proposal, in the reader's
       words. The server used to compose these as English with our
       column values inside: "classified ibs_supporting". */
    driver: {
      ratedRto: "rated RTO {{hours}}h",
      unratedRto: "no rated RTO",
      supportsMvb: "supports the {{mvbTerm}}",
      classified: "classified: {{term}}",
      continuityPosture: "continuity posture: {{term}}",
      openCritical_one: "{{count}} open critical finding",
      openCritical_other: "{{count}} open critical findings",
      openHigh_one: "{{count}} open high finding",
      openHigh_other: "{{count}} open high findings",
      noSupplier: "no supplier recorded",
      oneSupplier: "a single recorded supplier",
      suppliers: "{{count}} recorded suppliers",
    },
    noAction: "Nothing in the estate is currently proposed for action.",
    showTolerate: "Show the {{count}} service(s) proposed to tolerate",
    hideTolerate: "Hide the services proposed to tolerate",
    boundary:
      "Derived on read from current estate data, never stored. This proposes a disposition and shows the evidence behind it; the decision, and any lifecycle change, remain yours. Rated RTO is shown as evidence but does not drive a verdict.",
  },
  /* Technology currency (2026-10-20). */
  technologyCurrency: {
    heading: "Technology currency",
    standfirst:
      "Whether the technology each service rests on is still supported by its maker, judged against the bank's technology lifecycle register. An item the register cannot judge says why, and is never counted as current.",
    registerEmpty: "The register holds no products yet, so nothing can be judged.",
    registerLink: "Open the technology lifecycle register →",
    estate_one:
      "{{count}} configuration item judged against {{products}} products in the register.",
    estate_other:
      "{{count}} configuration items judged against {{products}} products in the register.",
    mvbLine_one:
      "Behind the services that support the {{mvbTermShort}}: {{count}} configuration item.",
    mvbLine_other:
      "Behind the services that support the {{mvbTermShort}}: {{count}} configuration items.",
    mvb: "{{mvbTermShort}}",
    items_one: "{{count}} item",
    items_other: "{{count}} items",
    noModel: "no model named",
    endsOn: "support ends {{date}}",
    endedOn: "out of support since {{date}}",
    verdict: {
      out_of_support: "out of support",
      extended_support: "extended support only",
      not_assessable: "not assessable",
      in_support: "in support",
    },
    count: {
      out_of_support_one: "{{count}} out of support",
      out_of_support_other: "{{count}} out of support",
      extended_support_one: "{{count}} in extended support",
      extended_support_other: "{{count}} in extended support",
      not_assessable_one: "{{count}} not assessable",
      not_assessable_other: "{{count}} not assessable",
      in_support_one: "{{count}} in support",
      in_support_other: "{{count}} in support",
    },
    reason: {
      no_model: "no model named",
      no_register_entry: "model not in the register",
      no_dates: "register row has no dates",
    },
  },
  continuityPosture: {
    /* Round 19 H56 -- nothing to read until an MVB is declared. */
    empty:
      "No {{mvbTermShort}}-supporting service yet: the register reads the services an approved {{mvbTermShort}} rests on, so declare the {{mvbTermShort}} first.",
    emptyLink: "Declare the {{mvbTermShort}} \u2192",
    /* Round 9 F1 -- the two moves that close an insufficient row. */
    prove: "Prove it in a drill",
    raise: "Raise it as a finding",
    heading: "Continuity posture",
    body: "The continuity-posture register for the {{mvbTermShort}}-supporting services: what capability exists (active-active through nothing), whether it can meet the tightest supported tolerance, and when it was last proven -- by declaration or by a passed drill.",
    error: "Could not load the continuity-posture register.",
    total: "{{count}} {{mvbTermShort}}-supporting service(s)",
    unproven: "unproven",
    insufficient: "insufficient",
    noPosture: "no posture",
    // E4 -- the counts were concatenated beside these words in JSX, so
    // they never passed through the interpolator and never got the
    // isolate the RTL sweep looks for: "10 ok" rendered with the
    // number on the wrong side of its own label. Through the catalogue
    // the number is isolated, and a translator can put it where Arabic
    // wants it rather than where the JSX happened to leave it.
    countOk: "{{count}} ok",
    countUnproven: "{{count}} unproven",
    countInsufficient: "{{count}} insufficient",
    countNoPosture: "{{count}} no posture",
    service: "Service",
    posture: "Posture",
    rto: "RTO",
    tolerance: "Tightest tol.",
    lastProven: "Last proven",
    verdict: "Verdict",
    supports_one: "supports {{count}} {{mvbTermShort}} {{serviceTermShort}}",
    supports_other: "supports {{count}} {{mvbTermShort}} {{serviceTermShortPlural}}",
    viaDrill: "passed drill",
    horizon: "Horizon",
    horizonMetHint: "Posture would make this recovery horizon in a total-loss event",
    // D4 -- "T", the number and "h" were three separate renders glued
    // together, so right-to-left reordered the parts.
    horizonChip: "T+{{hours}}h",
    horizonMissedHint: "Posture would MISS this recovery horizon in a total-loss event",
    declared: "declared",
    never: "never proven",
    note: "Proof is the later of the declared date and the latest passed resilience drill on a supported {{mvbTermShort}} {{serviceTermShort}}. Sufficiency compares the service's rated RTO against the tightest declared tolerance it supports; only active-active passes regardless of RTO.",
  },
  /* P440 -- the MVB target state. */
  targetState: {
    heading: "{{mvbTermShort}} target state",
    standfirst:
      "What it would take for the {{mvbTermShort}} to meet every recovery horizon the bank has set for it, what that would cost, and in what order: missed horizons first, then continuity postures, then technology out of support, then suppliers concentrated across it. Worked out from the estate as it stands; nothing here is stored until it is adopted below.",
    budgetLabel: "Budget (SAR, optional)",
    apply: "Plan",
    viability:
      "Minimum viability after a total loss: T+{{before}}h now, T+{{after}}h with these steps.",
    total: "Cost: {{total}}.",
    totalAtLeast_one: "Cost: at least {{total}}; {{count}} step has no cost yet.",
    totalAtLeast_other: "Cost: at least {{total}}; {{count}} steps have no cost yet.",
    stillMissed: "Still missed after every step: {{names}}.",
    method:
      "Each recovery step is chosen by re-running the recovery sequence with it, taking the most horizon overrun removed per SAR first. A good order, not a proved best one.",
    needsCost: "needs a cost",
    estimate: "estimate: the RTO this buys is not declared",
    closesAndAfter:
      "Meets the horizon for {{closes}}; minimum viability then T+{{hours}}h.",
    after: "Moves the critical path on; minimum viability then T+{{hours}}h.",
    noGapsInTier: "No gaps of this kind.",
    closedFree: "Closed by the steps above, at no further cost: {{names}}.",
    notBought:
      "Not bought: {{target}} to {{to}} hours ({{cost}}) would bring a horizon closer but never meet it, so it is not spent.",
    step: {
      recovery: "Make {{target}} recover faster: {{from}}h to {{to}}h",
      posture: "Give {{target}} a continuity posture sufficient for its tolerance",
      technology_one: "Replace {{count}} item: {{target}}",
      technology_other: "Replace {{count}} items: {{target}}",
      supplier_one:
        "Move {{count}} service off {{target}} to another supplier (of {{services}})",
      supplier_other:
        "Move {{count}} services off {{target}} to another supplier (of {{services}})",
    },
    tier: {
      recovery_one: "Recovery horizons: {{count}} missed",
      recovery_other: "Recovery horizons: {{count}} missed",
      posture_one: "Continuity posture: {{count}} insufficient",
      posture_other: "Continuity posture: {{count}} insufficient",
      technology_one: "Technology: {{count}} item out of support",
      technology_other: "Technology: {{count}} items out of support",
      supplier_one: "Suppliers: {{count}} concentrated across the {{mvbTermShort}}",
      supplier_other: "Suppliers: {{count}} concentrated across the {{mvbTermShort}}",
    },
    open: {
      no_lever: "{{name}}: no service made faster closes it.",
      team_capacity:
        "{{name}}: the recovery waits for people, not technology ({{teams}}); no hardening closes it.",
      not_within_budget_or_unpriced:
        "{{name}}: the steps that would close it are outside the budget or have no cost.",
      not_within_budget: "{{name}}: outside the budget.",
      needs_a_cost: "{{name}}: needs a cost before it can be planned within a budget.",
    },
    nothing: {
      no_mvb_declared: "No {{mvbTermShort}} is declared, so there is nothing to plan.",
      no_gaps: "The {{mvbTermShort}} has no gaps of the kinds this plans.",
    },
    adopt: {
      heading: "Adopted target",
      why: "The plan above moves whenever the estate does. Adopting freezes it as a record the CRO and the Head of Operational Resilience sign; the second signature seals it, and progress is then read against it.",
      none: "No target has been adopted.",
      state: {
        draft:
          "Version {{version}}, proposed {{date}}: {{total}}. Awaiting both signatures.",
        cro_signed:
          "Version {{version}}, proposed {{date}}: {{total}}. Signed by the CRO; awaiting the Head of Operational Resilience.",
        ops_res_signed:
          "Version {{version}}, proposed {{date}}: {{total}}. Signed by the Head of Operational Resilience; awaiting the CRO.",
        approved:
          "Version {{version}}, adopted from {{date}}: {{total}}. Signed and sealed.",
      },
      atLeast: "At least: some steps had no cost when adopted.",
      signatures: "CRO: {{cro}}. Head of Operational Resilience: {{opsRes}}.",
      unsigned: "not yet signed",
      progress_one: "{{done}} of {{total}} step done ({{sar}} of the adopted cost).",
      progress_other: "{{done}} of {{total}} steps done ({{sar}} of the adopted cost).",
      viability:
        "Minimum viability: T+{{adopted}}h when adopted, T+{{now}}h now, T+{{target}}h once every step is done.",
      stepDone: "Done: {{kind}}, {{target}}",
      stepOpen: "Open: {{kind}}, {{target}}",
      kind: {
        recovery: "recovery",
        posture: "continuity posture",
        technology: "technology",
        supplier: "supplier",
      },
      rationale: "Why this target (optional)",
      propose: "Propose this plan as the target",
      proposeAgain: "Propose today's plan instead",
      proposed: "Proposed for signature.",
      signed: "Signature recorded.",
      signCro: "Sign as CRO",
      signOps: "Sign as Head of Operational Resilience",
      pack: "Download the signed target pack",
    },
    toRecovery: "Open the recovery sequence →",
  },
  mvbRecovery: {
    // P440 -- from the sequence to what closing its gaps would cost.
    toTargetState: "What would close these gaps, and what it would cost →",
    // Round 20 V9 -- a recovery team with nobody to recover anything.
    unstaffed:
      "{{name}} has no one in it: the {{mvbTerm}} services it restores have nobody to restore them.",
    /* Round 9 G -- the plan's stated limits, in the reader's
       language. Composed server-side until now, so they were three of
       the four untranslated prose runs the pseudo-locale found on this
       screen. */
    assumption: {
      boundedByTeams:
        "Restoration runs in parallel only as far as declared team capacity allows. A service with no team recorded is treated as unconstrained.",
      unlimitedParallelism:
        "Restoration is assumed to run fully in parallel: no team capacity has been declared.",
      zeroRtoNoWindow:
        "A service rated 0h occupies no restoration window, so it consumes no team capacity. Observed drill durations replace rated ones.",
      unratedDefault:
        "A service with no rated recovery objective is assumed to take {{hours}}h.",
      ratedNotObserved:
        "These durations are rated recovery objectives, not observed recoveries. The {{mvbTermShort}} validation drill is what closes that gap.",
    },
    /* Round 9 F1 -- the route from a projection to its proof. */
    projectedNote:
      "This is projected from rated recovery objectives, not from a rehearsal.",
    toDrill: "Test it in a validation drill",
    heading: "{{mvbTermShort}} recovery sequence",
    body: "The ordered restoration plan for the declared {{mvbTerm}}: dependencies satisfied upstream-first, cycle blocks restored together, and the critical path that sets the horizon.",
    error: "Could not load the recovery sequence.",
    headline: "Time to minimum viability",
    summary:
      "{{services}} services in {{waves}} wave(s) · {{cycles}} cycle block(s) · {{unrated}} unrated (default RTO)",
    viability: "{{serviceTermShort}} viability vs declared tolerance",
    ibs: "{{serviceTermShort}}",
    viableAt: "Viable at",
    tolerance: "Tolerance",
    verdict: "Verdict (tolerance)",
    phases: "Recovery phases",
    phaseMembers_one: "{{count}} {{serviceTermShort}}",
    phaseMembers_other: "{{count}} {{serviceTermShortPlural}}",
    phaseWorst: "worst T+{{worst}}h vs {{deadline}}h",
    met: "met",
    missed: "missed",
    horizon: "Phase",
    declared: "declared",
    derived: "derived",
    resources: "Teams and sites the {{mvbTermShort}} needs",
    teamDemand:
      "{{services}} {{mvbTermShort}} services · peak {{demand}} at once vs capacity {{capacity}}",
    bottleneck: "bottleneck",
    siteShare: "{{services}} {{mvbTermShort}} services ({{pct}}%)",
    waited: "waited {{hours}}h for {{team}}",
    sequence: "Restoration sequence",
    cycleBlock: "cycle block",
    unrated: "{{count}} unrated",
    critical: "critical path",
    assumptions: "Stated assumptions",
  },
  mockExam: {
    /* Round 19 A41 -- a past run, read as it stood. */
    readAs: "Read",
    readToday: "today's estate",
    readRun: "the run of {{date}}, as it stood",
    asRunNote:
      "As judged by the run of {{date}}: its questions and evidence as they stood then. Today's estate may differ.",
    row: {
      // ROUND 15 A10 -- on the row itself, so a reader does not have to
      // find the count in the header.
      refused: "The Engine could not answer for this question. Nothing was recorded.",
    },
    /* Round 12 D4 -- 13 of 32 narrated notes carried a defect an
       examiner would have to correct, and the product's own
       instrumentation reported a clean sheet over all of them. Nine
       stated a number that appears nowhere in their evidence. The word
       "grounded" reads as "accurate" and means "cited". */
    /* Round 13 F3 -- the three checks a numeral check cannot make.
       Eight of twenty-five notes were wrong in a way an examiner would
       have to correct and none of the eight carried a bad figure. */
    contradictsVerdict:
      'This note says "{{phrase}}" beside a verdict of {{verdict}}. One of them is wrong.',
    reusedRatio:
      "This note reuses a figure from its evidence about something else: {{ratios}}.",
    /* Round 15 A13 -- a note that says how a grade was reached, over
       evidence that only counts grades. */
    inventedMethod:
      'This note says how the values were reached ("{{phrase}}"); its evidence does not say how.',
    worseUncited_one:
      "1 evidence row reads worse than the one this note answers ({{ids}}).",
    worseUncited_other:
      "{{count}} evidence rows read worse than the ones this note answers ({{ids}}).",
    /* Round 13 F2 -- the note is frozen and the evidence is not. */
    evidenceMoved:
      "The evidence behind this question has changed since the note was written. Checked against the evidence as it stood at {{date}}.",
    /* Round 13 F4 -- what answered, and what was asked for. */
    modelNotObserved:
      "model name repeated from the configuration, not read from the reply",
    status: {
      ok: "OK",
      flagged: "flagged",
      not_assessed: "not assessed",
    },
    narrativeNeedsCorrection: "ENGINE NOTE \u00b7 NEEDS CORRECTION",
    unsupportedFigures_one:
      "This note states a figure its evidence does not: {{figures}}. Correct it before using it.",
    unsupportedFigures_other:
      "This note states {{count}} figures its evidence does not: {{figures}}. Correct them before using it.",
    citedNotChecked:
      "The Engine cited evidence rows that exist. Nothing has checked that this paragraph states them correctly. The rows are printed above; read them.",
    /* Round 10 F4 -- the page said nothing while the report was on
       its way, and nothing at all when it never came. */
    loading: "Composing the examination report\u2026",
    /* Round 11 C4 -- an administrator arriving cold read that line and
       nothing else, on an estate that already carried a narrated run.
       A wait with no shape reads as a page that is broken. */
    loadingCount_one: "Composing the examination report -- {{count}} question.",
    loadingCount_other: "Composing the examination report -- {{count}} questions.",
    loadingPrior_one:
      "A narrated run already exists on this estate, covering {{count}} question.",
    loadingPrior_other:
      "A narrated run already exists on this estate, covering {{count}} questions.",
    /* ... and why a button is not offered, said on the button rather
       than left to be discovered by pressing it. */
    refusedWhy:
      "Composing the report is an administrator's action. Your account can read it.",
    packWhyDisabled:
      "There is no composed report to pack yet. The pack seals what is on this page.",
    error: "Could not compose the examination report.",
    refused:
      "The mock field exam is an administrator's surface. Your account cannot compose the report.",
    heading: "Mock field exam",
    body: "Rehearse the SAMA field examination against the live estate: the deterministic script derives the examiner's questions and verdicts; the Engine drafts grounded working-paper notes on top.",
    run: "Run the mock exam",
    // D5 -- the launching page never showed the result.
    runDone: "The Engine narrated {{n}} question(s). The notes are below.",
    runEnded: "The narrative pass ended: {{status}}.",
    queued: "Narrative pass queued -- the Engine is drafting the examiner notes.",
    engineOff:
      "Marsad Engine not configured -- the deterministic report below still stands.",
    failed: "Could not queue the narrative pass.",
    progress: "Drafting examiner notes... {{processed}} / {{total}}",
    /* Round 11 C4 -- the product's own name for the thing that wrote
       the note, and a date in the format every neighbour on the page
       uses. It read "Last narrated by stub-engine at
       2026-09-16T14:44". */
    lastRun: "Last narrated by the Marsad Engine{{model}} on {{date}}",
    byModel: " ({{model}})",
    pack: "Signed report pack",
    packDone: "Mock-exam pack downloaded.",
    packFailed: "Could not export the pack.",
    summary: {
      flagged: "{{flagged}} likely finding(s) of {{total}} questions",
      /* Round 19 B27 -- with questions nothing could judge, say them. */
      flaggedWithUnassessed:
        "{{flagged}} likely finding(s), {{notAssessed}} not assessed, {{passing}} passing of {{total}} questions",
      // Round 16 A26 -- the script grew after the run.
      askedByRun: "the last run asked {{asked}}",
      high: "{{count}} high",
      narrated: "{{count}} Engine-narrated",
      /* Round 12 F6 -- the page read "20 likely finding(s) of 32
         questions · 16 high · 29 Engine-narrated" while six of the
         twenty ranked findings, every one of them HIGH, carried no note
         at all: the questions the deterministic script derived after
         the last run. A reader counting notes would conclude the Engine
         had declined to comment on the six worst rows. */
      unnarrated_one:
        "1 ranked finding has no Engine note -- derived since the last run",
      unnarrated_other:
        "{{count}} ranked findings have no Engine note -- derived since the last run",
      /* ROUND 15 A10 -- eight refused notes read as "derived since the
         last run". A refusal is its own count. */
      refused_one:
        "1 ranked finding has no Engine note -- the Engine could not answer for it",
      refused_other:
        "{{count}} ranked findings have no Engine note -- the Engine could not answer for them",
      /* D4 -- notes stating a figure their own evidence does not. */
      unsupported_one: "1 note states a figure its evidence does not",
      unsupported_other: "{{count}} notes state figures their evidence does not",
    },
    findings: "Likely findings -- ranked",
    /* Round 12 F7 -- "A field examination would raise this as a named
       finding." ended every row and printed sixteen times in one
       screenful. A sentence true of every row belongs to the list. */
    findingsNote: "A field examination would raise each of these as a named finding.",
    openTheEntity: "Open the record this is about",
    clean: "No likely findings -- the estate passes the exam script.",
    detail: "Detail",
    hide: "Hide",
    /* Round 11 C4 -- what the note IS, and what it adds. Called a
       working-paper note it claims the tester's own reasoning, which
       in an audit is a specific thing and is not what this carries.
       Every one of nine opened restated the two lines above it. */
    narrativeLabel: "Engine restatement of the evidence above",
    narrativeWhat:
      "Drafted from the evidence and the verdict above, which do not depend on it. It is here so a reader can see what the Engine made of the same facts -- and it cites them, so the two can be compared.",
    cites: "cites {{ids}}",
    invented: "{{count}} invented citation(s) dropped",
    showAll: "Show passing questions",
    hideAll: "Hide passing questions",
    /* Round 19 B27 -- the questions nothing could judge, shown on ask. */
    showUnassessed_one: "Show the {{count}} not-assessed question",
    showUnassessed_other: "Show the {{count}} not-assessed questions",
    hideUnassessed: "Hide not-assessed questions",
  },
  adminExaminer: {
    heading: "Examiner access",
    body: "Mint time-boxed, username-bound grants for SAMA examiners. A grant lets a named examiner sign in for a fixed number of days and see only the examination pages -- never the bank's working screens. Every mint and revoke is audited and sealed into the examiner-grant chain, so the record of who had access cannot be edited afterwards.",
    username: "Examiner username",
    scope: "Scope note",
    days: "Days",
    mint: "Mint grant",
    mintNote:
      "The examiner's Keycloak account (with the examiner realm role) is created by your identity admin; the grant is the product-side ceremony.",
    minted: "Grant minted for {{username}} -- expires {{date}}",
    mintFailed: "Could not mint the grant.",
    // Round 18 S1 -- a grant bound to no account.
    usernameInvalid:
      "A username has no spaces: letters, digits and . _ @ - only. Use the examiner's sign-in name, not a description.",
    rowExaminer: "Examiner {{username}}",
    rowScope: "Scope: {{scope}}",
    unbound:
      "Bound to {{username}}: no one has signed in with this username (until {{date}})",
    grants: "Grants",
    empty: "No grants yet.",
    activeUntil: "active until {{date}}",
    /* ROUND 13 G10 -- two live grants for one examiner printed the
       same username, scope and expiry, so nothing on the row said
       which was which. The chain three panels below has sealed the
       minting of each since P357. */
    mintedBy: "minted {{when}} by {{who}}",
    mintedByUnrecorded: "an account this record no longer names",
    revokedPill: "revoked",
    // Round 19 S5 -- the revoker and the minute.
    revokedAtBy: "revoked {{when}} by {{who}}",
    expiredPill: "expired",
    revoke: "Revoke",
    revoked: "Grant revoked -- access closes on the examiner's next request.",
    offline: "Offline examiner pack",
    offlineBody:
      "One self-contained signed zip for an examiner who will not touch a bank system: the examiner report + a full chain re-walk summary + the complete signed dossier + the offline verifier.",
    packButton: "Export the examiner pack",
    packBuilding: "Composing the signed pack...",
    packDone: "Examiner pack downloaded.",
    packFailed: "Could not export the examiner pack.",
  },
  /** A7: who holds what -- admin / position / job role / module. */

  /* Round 10 C2 / C3 -- a register of the bank's own estate is
     readable by the seats the product attributes that estate to; only
     changing it needs the admin role. */
  adminList: {
    /* A1 -- it named its own refusal and not who could lift it, so a
       reader who needed the register changed had nowhere to go. The
       wording follows the `forbidden` sentence the registers already
       use, which round 9 C2 settled: name the seat that can grant it,
       because the person reading cannot solve an identity-management
       problem on their own. */
    readOnly:
      "You can read this register. Changing it needs the admin role -- ask your platform administrator, who can make the change or grant it.",
  },

  adminUsers: {
    heading: "Users and roles",
    explainer:
      "MARSAD separates four things a person can hold, and they do " +
      "different jobs. ADMIN runs the platform. A POSITION -- preparer, " +
      "reviewer, approver -- is the seat in the corrective-action " +
      "workflow. A JOB ROLE -- CRO, head of operational resilience, " +
      "service owner, business owner, accountable executive -- is what a " +
      "governance ceremony waits on, and admin does not stand in for it: " +
      "a signature is the signer's own. A MODULE licence decides which " +
      "parts of the product they can open at all.",
    /* Round 10 A3 -- this paragraph teaches what a person can hold and
       never mentioned the one section whose whole existence is decided
       by what they hold. An administrator setting up seats could not
       learn from here that granting an approver position or the Head of
       Operational Resilience role also opens a room. */
    explainerExecutive:
      "What somebody holds also decides which reporting section they " +
      "see. Executive -- the board, regulator and sector view -- opens " +
      "for an approver, a reviewer or the Head of Operational " +
      "Resilience, each with the rooms their own seat can open. A " +
      "preparer or a service owner does not see it at all.",
    reflectedNote:
      "These rows are recorded when someone signs in, so a user who has " +
      "never signed in does not appear here, and a role granted since " +
      "their last sign-in is not shown. Users are created and changed in " +
      "Keycloak.",
    /* Round 9 E1, ninth round of an administrator asking for a write
       path here. The page said WHAT ("users are created and changed in
       Keycloak") and never WHY, so nine rounds read it as a feature
       nobody had got to yet.
       It is a position: the identity provider owns identity, and a
       product that could create and disable people would have to hold
       credentials that can create and disable people. On a deployment
       sold on sovereignty and an auditable record, that is a decision
       for the bank, not a convenience we add quietly. Saying so is not
       a refusal -- it is the difference between a boundary and a gap. */
    whyNotHere:
      "MARSAD reads who exists; it does not create or disable anyone. " +
      "Holding credentials that can would move identity out of your " +
      "provider's audit trail and into ours, so the provider stays the " +
      "one place a person is granted or revoked.",
    // D12 -- it opened a master-realm sign-in and the label promised a
    // user list. Keycloak's admin console lives in its own realm and
    // will ask for its own administrator, which is a fact about the
    // identity provider rather than something the product can smooth
    // over -- so it says so before the click rather than after.
    manageInKeycloak:
      "Manage users in Keycloak (signs in separately, as a Keycloak administrator)",
    // ROUND 15 K3 -- on the hosted edition the console is not served on
    // the public address at all, so the link says where it IS reached
    // rather than opening on a refusal.
    /* Round 19 H55 -- "its operator" named nobody. */
    manageWhere:
      "On a hosted deployment the identity provider's administration is not served on this address. The team that runs the deployment -- MARSAD's operations team, for a deployment MARSAD hosts -- reaches it over the deployment's administrative route; ask them to make the change.",
    invitedArrivesWithWork:
      "Anything raised for a role before its holder first signs in is held for them, and lands in their bell when they do.",
    /* D1 -- the row's own management route. Managing users stays in
       the identity provider; the administrator no longer has to find
       the realm, find users and search for a name already on screen. */
    manageThisUser: "Manage",
    /* Not on this address (the hosted edition): a link would open on
       the edge's refusal, which is what an administrator found. */
    manageWithheld: "In Keycloak (administrative route)",
    /* The signed-in person's own account page: password and sessions.
       Every deployment serves it on the sign-in address. */
    yourAccount: "Your account",
    ownPasswordLead:
      "To change your own password, open your account page in the identity provider:",
    manageUnavailable: "Not yet signed in",
    manageUnknown:
      "Users are managed in your identity provider. This deployment has no identity-provider URL configured, so we cannot link you to it.",
    loading: "Loading users...",
    never: "never",
    forbidden: "Users and roles is an administrator surface.",
    col: {
      person: "Person",
      admin: "Admin",
      position: "Position",
      job: "Job role",
      modules: "Modules",
      lastLogin: "Last sign-in",
      manage: "Manage",
    },
  },
  /** B2: the Resilience Lead's own landing page. */
  opsResHome: {
    rubric: "Operational resilience",
    heading: "{{name}} -- your resilience desk",
    positioning:
      "What is waiting on your signature, what is breaching now, what is " +
      "falling due, and where the bank sits on the ladder you own.",
    waiting: {
      heading: "Waiting on you",
      empty: "Nothing is waiting on your signature.",
    },
    now: {
      heading: "Right now",
      breaching: "{{serviceTermShortPlural}} breaching tolerance",
      red: "{{serviceTermShortPlural}} red",
      reviewsOverdue: "{{serviceTermShort}} reviews overdue",
      drills: "Drills scheduled",
      unavailable: "The live portfolio could not be read.",
    },
    next: {
      heading: "Your next step",
      empty: "Nothing on the ladder is waiting on you.",
      /* Round 20 U9 -- for the reader who is told rather than telling. */
      seeOpenIncidents: "See open incidents",
    },
  },
  /** B3: the Service Owner's own landing page. */
  ownerHome: {
    rubric: "Your accountability",
    loading: "Looking up what is recorded against you...",
    // Round 17 L9 -- a treasurer owns functions, not only services.
    heading: "{{name}} -- what you own",
    noIdentity:
      "Your account has no email address on it, so MARSAD cannot tell " +
      "which services are yours. Ask an administrator to add one.",
    openIncidents_one: "{{count}} open incident",
    openIncidents_other: "{{count}} open incidents",
    noIncidents: "no open incidents",
    toleranceMissing: "No {{toleranceTerm}} declared yet",
    toleranceDeclared: "{{toleranceTerm, capitalize}}: {{hours}}h",
    notYetAdmitted: "not yet admitted as an {{serviceTerm}}",
    notYetAdmittedInternal:
      "not yet admitted to the critical internal function register",
    /* Round 17 N9 -- the admission said, once it happens. */
    admittedOn: "Admitted to the {{serviceTerm}} register, {{date}}",
    admittedOnInternal: "Admitted to the critical internal function register, {{date}}",
    functions: {
      heading: "Critical internal functions you own",
    },
    asked: {
      heading: "What you are being asked to do",
      explainer:
        "These need your confirmation. Each one records that you, as the " +
        "person accountable for the service, agree with what the bank is " +
        "about to tell its regulator.",
    },
    ibses: {
      heading: "{{serviceTermPlural, capitalize}} you own",
      empty: "No {{serviceTerm}} is recorded against you.",
    },
    services: {
      heading: "Services you own",
      empty: "No service is recorded against you.",
    },
  },
  /** B4: the owner describes, wires and declares his own service. */
  ownerPanel: {
    heading: "You own this service",
    /* Round 16 -- a Critical Internal Function serves the bank, not a
       customer through a channel, so its owner is not asked who it
       serves or how they reach it. */
    headingInternal: "You own this function",
    internalNoChannel:
      "A critical internal function has no delivery channel: it serves " +
      "the bank's own operations, not customers.",
    intro:
      "You are the person accountable for this service, so you are the " +
      "person who can say what it does, what it rests on, and how long " +
      "the bank can manage without it.",
    introInternal:
      "You are the person accountable for this function, so you are the " +
      "person who can say what it does, what it rests on, and how long " +
      "the bank can manage without it.",
    describe: {
      label: "What this service does",
      labelInternal: "What this function does",
      placeholder: "In a sentence or two, in plain language.",
    },
    serves: {
      label: "Who it serves, and how they reach it",
      placeholder: "e.g. retail customers, through POS terminals and the app",
    },
    save: "Save",
    saving: "Saving...",
    saved: "Saved.",
    depends: {
      label: "What it depends on",
      pick: "Pick a service...",
      add: "Add",
      /* D5 -- adding one reset the picker and showed nothing, so the
         owner could not tell whether it saved, what was already
         there, or how to remove one. */
      added: "Recorded: this depends on {{name}}.",
      removed: "Dependency removed.",
      theService: "that service",
      /* ROUND 13 H, ninth round -- the code, because on an estate
         where "SAMA regulatory reporting" rests on "Regulatory
         Reporting" the bare name read as the page's own subject. */
      optionWithCode: "{{name}} ({{code}})",
      none: "No dependencies recorded yet.",
      byYou: "you said so",
      fromCmdb: "from the CMDB",
      remove: "Remove",
    },
    tolerance: {
      label: "How long can the bank manage without it?",
      explainer:
        "This is your {{toleranceTerm}}: the longest outage the bank could " +
        "absorb before the harm becomes unacceptable -- to customers, to " +
        "the market, or to the bank itself. It is not a target and not a " +
        "recovery time; it is the point beyond which the answer is no.",
      /* ROUND 13 G5 -- this sentence was the tail of the explainer and
         it is not guidance, it is a claim about the figure in force.
         It rendered four lines under "In force: 2h, signed by ...",
         so the page told a reader his signed tolerance was a draft.
         Said only where it is true. */
      explainerDraft: "Your figure is a draft until the CRO signs it.",
      /* Round 16 D21 -- the figure in force is one nobody signed. */
      explainerUnsigned:
        "The {{hours}}h in force is not signed. What you declare here is a draft until it is signed.",
      /* And where one IS signed, the thing worth saying is what
         declaring again does to it. */
      explainerReplaces:
        "A tolerance is already in force. A new figure does not change it until the CRO signs that one too.",
      hours: "Hours",
      why: "Why that figure?",
      declare: "Declare tolerance",
      // D9 -- an inline confirmation and nothing else, so the owner was
      // not told his declaration had left his hands.
      declareSentToCro: "Tolerance declared. It is now with the CRO to sign.",
      declaring: "Recording...",
      limitAndWindowIntro:
        "Optional. An operational risk limit is the line inside the tolerance at which this service reads at risk -- yours to draw rather than a fraction chosen for every service at once. A window is a period where you have less room, such as month end.",
      limitLabel: "Operational risk limit (hours)",
      limitPlaceholder: "e.g. 3",
      limitNotInside:
        "A limit has to sit inside the tolerance. One equal to it would report at risk and breached at the same moment.",
      windowLabelField: "Window name",
      windowKind: "Window repeats",
      windowHours: "Tolerance in the window (hours)",
      windowNotTighter:
        "A window can only tighten. One declaring the same or longer would raise your own ceiling for that period.",
      windowKinds: {
        monthly: "Monthly",
        weekly: "Weekly",
        annual: "Annually",
      },
      windowRule: {
        monthly: "last N business days",
        weekly: "weekdays, 0=Mon (e.g. 0,4)",
        annual: "MM-DD..MM-DD",
      },
      dimensionNone: "Duration only",
      /* Round 10 F2 -- the select has no visible label, so it needs a
         spoken one. "Duration only" is the first OPTION, not a name
         for the control. */
      dimensionLabel: "A second dimension, if there is one",
      threshold: "Threshold",
      // C1 -- the form asked for a number as though none existed, so an
      // owner declared over his own tolerance without being told, and
      // the estate collected identically named drafts.
      /* ROUND 13 G7, ninth round -- these two were the second wording
         for the unsigned state and one of four for the signed one.
         The STATE is now said by `toleranceSentence`, the one function
         every surface calls; what is left here is the only thing this
         form adds, which is what declaring again would do. A
         consequence is not a state. */
      // Round 14 D6.
      windowRuleEmpty:
        "This rule cannot be read for the kind chosen, so the window would never apply.",
      limitNotInsideWindow:
        "The limit must be below the tightest figure, {{hours}}h inside the window.",
      existingReplaces: "Declaring again replaces it and needs fresh sign-off.",
      /* WHO signed it is a fact this form carries and the shared state
         sentence does not: the badge puts it in a tooltip, which a
         form asking for a replacement figure cannot rely on. A name is
         not a state either, so it gets its own clause rather than a
         fifth wording. */
      existingSigner: "Signed by {{signer}}.",
      pending:
        "A declaration of {{hours}}h is already waiting on the {{role}}. Nothing more to do here until they sign it.",
      /* ROUND 15 G3 -- "until they sign it" stayed true for ever on a
         host where no one held the seat. */
      pendingNamed:
        "A declaration of {{hours}}h is already waiting on {{who}} ({{role}}). Nothing more to do here until they sign it.",
      pendingUnheld:
        "A declaration of {{hours}}h is waiting on the {{role}}, and nobody signed in holds that seat: it cannot be signed until an administrator assigns the role in the identity provider.",
      pendingReason: "Why that figure, as declared: “{{reason}}”",
      /* Round 16 G14 -- only the service's owner countersigns, and the
         form offered Declare on a service that has none. */
      pendingNoOwner:
        "A declaration of {{hours}}h is waiting on the service's owner, and none is recorded: it cannot be countersigned until one is.",
      // Round 20 W3 -- an owner the register names but no seat holds.
      pendingOwnerNotSeat:
        "A declaration of {{hours}}h is waiting on {{owner}}, the service's owner. Owner named, not yet a seat: it cannot be countersigned until the address they sign in with is recorded in the {{serviceTermShort}} register.",
      ownerNotSeat:
        "Owner named, not yet a seat: {{owner}} owns {{name}}, and only the owner can countersign a tolerance. Record the address they sign in with in the {{serviceTermShort}} register to bind them, then declare.",
      noOwner:
        "{{name}} has no recorded owner, and only its owner can countersign a tolerance, so a declaration could never complete. Record the owner in the {{serviceTermShort}} register first.",
      noOwnerLink: "Open the {{serviceTermShort}} register",
      drafted:
        "Recorded as a draft. It becomes the bank's declared tolerance once " +
        "the CRO and you have both signed it.",
    },
  },
  /** B6: the admin console opens with the setup order. */
  adminSetup: {
    heading: "Set up this bank",
    /* Round 12 A7 -- four ladders, each counting a different set, three
       of them saying only a number. "0 of 7" and "0 of 7" about
       different sevens is worse than one ladder, because it reads as
       agreement. Each says whose steps it is counting now. */
    whose: "Your steps and the data steward's -- {{done}} of {{total}} done",
    wholeJourney: "The whole journey \u2192",
    /* A7 -- the rung that needs a shell. Bank Setup names this wall
       precisely, with the command to generate a key; the ladder that
       sends an administrator at the rung did not, and that was the rung
       still open at five o'clock. Deliberately not phrased as a block:
       a connector whose credentials come from environment variables
       still runs, so what is unavailable is entering them here. */
    needsDeploymentSetting:
      "Credentials cannot be entered in the product until {{name}} is set on the deployment. Connectors reading their credentials from environment variables still run.",
    needsDeploymentSettingCta: "What to set, and how",
    handover_one:
      "{{count}} step left before you can hand over to the resilience lead.",
    handover_other:
      "{{count}} steps left before you can hand over to the resilience lead.",
    handoverReady:
      "The plumbing is done. Hand over to the resilience lead -- the rest of " +
      "the journey is hers.",
  },
  app: {
    /* Round 12 H3 -- the skip link. A keyboard user crossed the whole
       navigation on every route change, which is the one structural
       gap the first accessibility measurement of this product found. */
    skipToContent: "Skip to the main content",
    title: "MARSAD",
    /**
     * Short one-liner. Kept for places that need a category claim in
     * 4-6 words. P79 removed it from the persistent header (the line
     * is the category claim; it belongs in page headings, not as a
     * logo subtitle).
     */
    tagline: "Operational twin of bank resilience",
    /** Arabic wordmark (مرصد). Rendered in the brass colour on navy. */
    titleArabic: "مرصد",
    /* ROUND 13 H -- no screen carried a `footer` landmark, so a reader
       navigating by landmarks had a banner, a navigation and a main
       and nowhere to jump to for what sits at the end of a document. */
    footer: {
      product: "{{product}} -- the operational twin of {{bank}}'s resilience.",
      docs: "Documentation",
    },
    /** Marketing strapline on the login splash + brand surfaces. */
    strapline: "Observe · Map · Comply",
    /** Category claim used as the LoginGate hero heading + Home h1. */
    heroClaim: "MARSAD is the operational twin of bank resilience.",
  },
  // B1 -- a stored comment nobody shows breaks the promise the box
  // was added under.
  signatures: {
    heading: "Signatures",
    noComment: "Signed without a comment",
  },
  // B1 -- the seats a signature history names. Shared, because four
  // record pages render the same ceremony.
  roles: {
    cro: "CRO",
    business_owner: "Business owner",
    cio: "CIO",
    service_owner: "Service owner",
    ae: "Accountable executive",
    // Cased to match `vocab.role.ops_res`. The signature block and the
    // ceremony toast name the same seat, and a reader who meets both
    // should not meet two job titles.
    ops_res: "Head of Operational Resilience",
    challenge: "Second-line challenge",
    submitter: "Submitted for sign-off",
  },
  common: {
    // Round 17 K18 -- a card that could not load says when it asks again.
    cardRetrying: "Trying again every {{seconds}} seconds.",
    /* Round 11 B4 -- the toast's close button carried a hard-coded
       English aria-label in a shared primitive, so the one control on
       every toast in the product was the one string no catalogue
       reached. */
    /* Round 12 A6 -- a reader who cannot do the work is told who can,
       and offered no link into a refusal. The repeat-P1 of four rounds
       was an action offered to a seat that cannot complete it. */
    emptyAskAdmin: "Your administrator loads this; the page fills in as the rows land.",
    dismiss: "Dismiss",
    // Paired with the layers "Show all N" toggle.
    hide: "Hide",
    // A1 -- a write that fails must say so. Forty-seven mutations
    // handled onSuccess and nothing else, so the default changed
    // rather than forty-seven call sites.
    writeFailed: "That did not save",
    yes: "Yes",
    no: "No",
    cancel: "Cancel",
    save: "Save",
    edit: "Edit",
    /* Referenced by the board comparison since round 1 and never
       defined, so anyone who caught that panel mid-fetch read the
       literal string "common.loading". No test had rendered the
       branch. */
    loading: "Loading...",
    /** Elapsed-seconds counter on EngineBusy (P142). */
    elapsedSeconds_one: "{{count}}s",
    elapsedSeconds_other: "{{count}}s",
    /** Grounded-AI verdict line (P153). */
    engine: {
      /* Round 12 I2 -- the Engine composes in English and there is no
         locale in the prompt path. Shown only to a reader who is NOT
         reading English, because to everyone else it is noise about a
         language they can already see. See docs/engine-locale-scope.md. */
      answersInEnglish:
        "The Engine writes its answers in English. They are not translated.",
    },
    grounding: {
      /* Round 12 D4 -- the word "grounded" reads as "accurate" and
         means "cited". What these counters check is that the Engine
         pointed at something that exists; nothing checks that it
         pointed at the right thing. The line says what it verified. */
      allGrounded_one: "The 1 result cites a source on the shortlist it was given.",
      allGrounded_other:
        "All {{count}} results cite sources on the shortlist they were given.",
      /* And the line under it, in the register the pages already use:
         what has NOT been checked, in words, because a reader who is
         told "grounded" stops reading. */
      citedNotChecked:
        "That is a citation check. Nothing has checked that these statements are correct -- the sources are listed above; read them.",
      /* D1 -- a citation drawn from a set of one is not a judgement.
         Five drafted actions all cited the one linked control, three of
         them about something else, over a line reading "all 5 results
         cite real sources". */
      nominalAttribution_one:
        "There was only ONE source to cite, so the attribution is nominal rather than judged: the result had no other choice. Re-point it by hand.",
      nominalAttribution_other:
        "There was only ONE source to cite, so all {{count}} attributions are nominal rather than judged: the Engine had no other choice. Re-point them by hand.",
      dropped_one:
        "1 ungrounded result dropped -- the Engine cited a source that does not exist.",
      dropped_other:
        "{{count}} ungrounded results dropped -- the Engine cited sources that do not exist.",
      lowConfidence:
        "Low confidence: only {{grounded}} of {{returned}} were grounded -- the " +
        "Engine invented more than it verified. Treat with caution.",
      refused:
        "Engine declined -- confidence below the floor, so no result was " +
        "returned (hard-refusal mode).",
    },
  },
  /** P268: the unified "my work" inbox + user picker. */
  errors: {
    /* A1 -- a refusal is an authority answer, not a stack trace. The
       server's own words ("realm role business_owner required") name
       our schema at a person who is simply not the signatory. */
    refused: {
      forbidden: "This is not yours to complete. Whoever it names signs it.",
      signedOut: "Your session has expired. Sign in again to continue.",
    },
  },
  /* Round 10 F5 -- a module the bank holds and this account does not.
     One sentence, said once, instead of a masthead full of links that
     refuse and a console full of 403s. */
  moduleAccess: {
    framework: { heading: "The compliance module is not part of your access." },
    twin: { heading: "The operational twin is not part of your access." },
    foundation: { heading: "The foundation register is not part of your access." },
    generic: { heading: "That module is not part of your access." },
    body: "Your colleagues may use it; your account is not set up for it. An administrator can add it to your account in the identity provider.",
  },
  /* Round 20 Y5 -- who signed a tolerance, said on the page. */
  toleranceSigners: {
    leg: {
      cro: "Chief Risk Officer",
      business_owner: "Business owner",
    },
    signed: "{{name}}, {{at}}",
    awaiting: "awaiting {{name}}",
    noOwner: "no owner recorded",
    nobodyHolds: "nobody holds the seat",
    holderUnknown: "the seat's holder",
  },
  myWork: {
    /* Round 20 Y1 -- the internal audit seat: reads everything, signs
       nothing, has no queue. */
    readsOnly:
      "Nothing waits on you: this seat reads. Internal audit can open every register and record, and is offered no signature or approval.",
    /* Round 16 I13 -- a title the server composed. */
    title: {
      maturity: "Resilience maturity assessment",
      mvbTarget: "{{mvbTermShort}} target",
    },
    /* Round 16 I11 -- a title the server composed, as a key. */
    waitingTitle: {
      cifAdmission: "Critical internal function admission: {{name}}",
      ibsAdmission: "{{serviceTermShort}} admission: {{name}}",
    },
    /* A1 -- who signs a row the reader cannot. Dropping the button is
       half the fix; the row still has to say who it is waiting for and
       where they do it, or the reader is left to work that out. */
    awaiting: {
      named: "Awaiting {{name}} ({{role}}); they sign from My Work",
      seat: "Awaiting the {{role}}; they sign from My Work",
      /* Round 19 G42 -- the leg nobody can take yet. */
      noOwner:
        "Awaiting the {{role}}: no owner is recorded for this service, so nobody can sign yet",
      nobody: "Awaiting the {{role}}: nobody holds that seat yet",
      challenge:
        "Awaiting the second-line challenge; the reviewer records it from My Work",
      // B3 -- shared work the viewer can pick up, which is a different
      // thing from work that is waiting on somebody else.
      shared: "Shared with everyone holding the {{role}} seat",
    },
    /* B1 -- the decision a signer reads before signing it. */
    sign: {
      heading: "What you are signing",
      /* A1 -- name the service and the owner, not just the signer. */
      onBehalfOf: "{{title}} — owned by {{owner}}",
      // D8 -- shown on every record with no owner FIELD, including a
      // bank-wide declaration and a board maturity assessment, which
      // have no owner to record. Asserting a gap that is not there
      // reads as a defect in the record.
      about: "{{title}}",
      openTheRecord: "Open the record",
      /* Round 11 F1 -- the signature already on the decision, named
         and dated, so the second signer is not counter-signing a
         record whose history they cannot see. Seventh round. */
      alreadySigned: "Already signed by the {{role}}, {{name}},",
      /* A leg the ledger timed and did not name. Reported as the gap
         it is rather than filled with whoever holds the seat today. */
      alreadySignedUnnamed: "Already signed by the {{role}} (name not recorded),",
      noSummary: "This record carries no summary, so it cannot be signed from here.",
      challenge: "Second-line challenge:",
      comment: "Comment (recorded with your signature)",
      signAs: "Sign as {{name}}",
      signedToast: "Signed: {{title}}",
      // D9 -- "Signed: {item}" and nothing else, so somebody who had
      // just signed could not tell whether it was finished or with whom.
      signedNowWith: "Signed: {{title}} -- now with the {{role}}.",
      signedNowWithNamed: "Signed: {{title}} -- now with {{name}} ({{role}}).",
      // Round 10 -- this said "nobody holds that role -- ask an
      // administrator to assign it", which MARSAD cannot know and
      // which was false every time the walk saw it fire: the holder
      // signed three minutes later. Role holders are reflected at
      // first sign-in and the bank's identity provider is the only
      // authority, so the product can report who it could NOTIFY and
      // nothing more. The request is not lost -- it is held and
      // released when they sign in -- so the sentence says that
      // instead of sending the signer to an administrator with
      // nothing to fix.
      // Round 11 D4 -- the substance was right and the length was not.
      // Thirty-nine words explaining MARSAD's identity reflection sat
      // on the largest signature the product has, on the first day a
      // bank is live. The FACT the signer needs is that the ceremony
      // is not stuck and why; the MECHANISM is a second glance, so it
      // moves to the toast's own detail line.
      signedRoleUnnotified:
        "Signed: {{title}}. It now needs the {{role}}. They have not opened MARSAD yet, so the request is waiting for them.",
      // The mechanism, one line smaller. It still says "could not be
      // notified" rather than "nobody holds that role", because a
      // holder who has not signed in is indistinguishable from an
      // empty seat and only one of those may be said out loud.
      signedRoleUnnotifiedDetail:
        "Role holders are recorded at first sign-in, so there was nobody to notify yet. The request is held and reaches them then.",
      // Round 14 D5 -- an owner seat nobody fills.
      signedOwnerMissing:
        "Signed: {{title}}. It now needs the {{role}}, and no owner is recorded for this service, so it cannot complete yet.",
      signedOwnerMissingDetail:
        "Record the owner on the business service register, and they can countersign it from My Work.",
      signedComplete: "Signed: {{title}}. That was the last signature.",
      failedToast: "Signature was not recorded",
    },
    // Round 14 D3 -- each window's rule in words, and what it does.
    // `_tightens` shortens the tolerance inside the window; `_noHours`
    // sets no shorter duration; `empty` is a rule that never applies.
    window: {
      daily_tightens:
        "{{label}}: every day, {{from}} to {{to}}. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      daily_noHours:
        "{{label}}: every day, {{from}} to {{to}}. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      weekly_tightens:
        "{{label}}: every {{days}}. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      weekly_noHours:
        "{{label}}: every {{days}}. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      monthlyLast_tightens:
        "{{label}}: the last {{count}} business days of each month. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      monthlyLast_noHours:
        "{{label}}: the last {{count}} business days of each month. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      monthly_tightens:
        "{{label}}: days {{from}} to {{to}} of each month. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      monthly_noHours:
        "{{label}}: days {{from}} to {{to}} of each month. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      annual_tightens:
        "{{label}}: {{from}} to {{to}} each year. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      annual_noHours:
        "{{label}}: {{from}} to {{to}} each year. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      dated_tightens:
        "{{label}}: from {{from}} to {{to}}. Inside it the service must be back within {{hours}}h, not {{base}}h.",
      dated_noHours:
        "{{label}}: from {{from}} to {{to}}. It sets no tighter duration than {{base}}h, so it changes no verdict on time.",
      empty:
        "{{label}}: its rule is empty, so it never applies. Signing it declares nothing for this period.",
    },
    limit: {
      sentence:
        "{{metric}}: {{value}}. Crossing it raises a high finding before the tolerance itself is breached.",
    },
    summary: {
      // Round 14 D3 -- what the CRO signs, window by window.
      toleranceWindow: "Window",
      // G4 -- "Operational risk limit" on the form, "Operational limit"
      // on the panel: one thing, one name.
      toleranceLimit: "Operational risk limit",
      proposal: "Proposal",
      targetCost: "Cost",
      targetSteps: "Steps",
      targetDerivedOn: "Planned on",
      tier: "Tier",
      pathway: "Pathway",
      // Round 16 N2.
      challengedBy: "Challenged by",
      nextSigner: "Signs after you",
      rationale: "Rationale",
      composite: "Score",
      recommendation: "Recommendation",
      tolerance: "Declared tolerance",
      index: "Weighted index",
      scored: "Dimensions scored",
      /* A2 -- the declaration and the attestation reached a signature
         with nothing to read. */
      effectiveFrom: "Effective from",
      threshold: "{{mvbTermShort}} threshold",
      /* B1 -- 48 alone has been read as days. It is hours. */
      thresholdHours: "Gate-1 threshold (hours)",
      /* B1 -- what signing actually puts in force, by name. The panel
         the two most senior signatories sign from named nothing. */
      members: "Puts in force",
      /* B1 -- the second signer sees the first signer's reason, rather
         than counter-signing a blank. */
      croSaid: "The CRO's reason",
      attested: "Attested",
      submittedBy: "Submitted by",
      classification: "Classification",
      /* Round 11 F1 -- the IBS's outcome category, which was being
         sent on the `classification` line and read through the service
         classification catalogue, which does not hold it. */
      outcome: "Customer outcome at risk",
      comments: "Comments",
      // B3 -- the backend emitted these two keys and the catalogue did
      // not define them, so the panel rendered the literal string
      // "myWork.summary.owner" to the Service Owner as a field label.
      description: "What it is",
      owner: "Service owner",
      mvb: "In the {{mvbTermShort}} set",
      /* Round 19 G42 -- whose tolerance, and what is in force now. */
      businessOwner: "Business owner",
      noOwnerRecorded: "None recorded -- nobody can countersign until one is",
      inForce: "In force now",
      hoursValue: "{{hours}}h",
      noneInForce: "None in force yet",
      dependencies: "Supporting services",
      // Round 9 C5. The submitter's sentence was printed on the record
      // after the fact and shown to neither signer beforehand.
      whyThisSet: "Why this set",
      due: "Due",
    },
    commits: {
      // Round 9 C3 -- the attestation was the only ceremony asking for a
      // signature without saying what it does.
      attestation:
        "Signing confirms this service is still correctly described -- its owner, classification, tolerance and what it depends on -- as at today. It goes to an approver, and the record is what the regulator is shown for this cycle.",
      maturity:
        "Signing records this as the bank's assessed operational-resilience maturity. Both signatures seal it into the maturity chain.",
      mvbTarget:
        "Signing adopts this plan as the bank's {{mvbTermShort}} target: the steps, their order and their cost as proposed. Both signatures seal it, and progress is then read against it.",
      // A3 -- one sentence per DECISION, not per ceremony. The fixed
      // sentence said "admits this service to the register" above a
      // proposal to defer, so the words on screen described the
      // opposite of the act being signed.
      ibsAdmission: {
        included:
          "Signing admits this service to the register: it becomes subject to tolerance, review and rehearsal obligations.",
        deferred:
          "Signing records that this service is deferred from the register. It stays out for now and can be re-proposed.",
        excluded:
          "Signing records that this service is excluded from the register. It takes on no tolerance, review or rehearsal obligation.",
        unknown:
          "Signing records this admission decision. Open the record to see what it proposes before you sign.",
      },
      cifAdmission: {
        included:
          "Signing admits this function to the critical internal function register: it becomes subject to tolerance, review and rehearsal obligations.",
        deferred:
          "Signing records that this function is deferred from the critical internal function register. It stays out for now and can be re-proposed.",
        excluded:
          "Signing records that this function is excluded from the critical internal function register. It takes on no tolerance, review or rehearsal obligation.",
        unknown:
          "Signing records this admission decision. Open the record to see what it proposes before you sign.",
      },
      mvbPack: {
        in: "Signing places this service in the {{mvbTerm}}: it is restored first and its recovery is rehearsed.",
        watchlist:
          "Signing places this service on the watchlist. It is NOT in the {{mvbTerm}}; it is reviewed for entry.",
        out: "Signing records that this service is outside the {{mvbTerm}}. It is not restored first.",
        unknown:
          "Signing records this evidence pack. Open the record to see the tier it proposes before you sign.",
      },
      mvbDeclaration:
        "Signing puts this declaration in force. It is what the board and the regulator will be shown.",
      serviceClassification:
        "Signing sets how critical this service is. Everything downstream -- tolerance, tiering, rehearsal -- reads it.",
      impactTolerance:
        "Signing declares the most disruption the bank will accept before this service causes unacceptable harm.",
    },
    /**
     * G1 -- these are composed by the SERVER (dossier/my_work.py) and
     * sent as keys, so the inbox reads in the reader's language rather
     * than in the language the API happens to be written in.
     */
    rowSubtitle: {
      /* Round 20 U1 -- a critical finding reached a service the reader answers for. */
      criticalFinding_one:
        "Critical finding on {{on}}: it reaches {{count}} admitted or minimum-viable service ({{names}}). Acknowledge it.",
      criticalFinding_other:
        "Critical finding on {{on}}: it reaches {{count}} admitted or minimum-viable services ({{names}}). Acknowledge it.",
      /* Round 19 G36 -- a significant change reached the reader's service. */
      changeFinding:
        "A significant change reached your service: map it again, review its tolerance, test it again",
      maturity: "Board sign-off",
      cap: "Corrective action plan",
      mvbTarget: "Target sign-off",
      attestation: "Resilience attestation",
      /* Round 19 G42 -- a function's attestation, said as one. */
      functionAttestation:
        "Critical internal function attestation: you submit it as the function's owner",
      ibsAdmission: "{{serviceTermShort}} admission",
      // Round 20 N16 -- an exclusion said as one.
      ibsExclusion:
        "{{serviceTermShort}} exclusion: signing removes it from the register",
      cifAdmission: "Critical internal function admission",
      mvbPack: "{{mvbTermShort}} pack",
      // A4 -- 27 rows all reading "MVB pack" is a wall. The kind is
      // what tells a reviewer whether he is looking at a business
      // service, something beneath one, or a supplier. The server picks
      // the key, as it does for the commitment sentence, so the stored
      // value never reaches the reader.
      mvbPackIbs: "{{mvbTermShort}} pack · {{serviceTerm}}",
      // Round 16 L5 -- a Critical Internal Function's pack.
      mvbPackFunction: "{{mvbTermShort}} pack · critical internal function",
      mvbPackService: "{{mvbTermShort}} pack · supporting service",
      mvbPackVendor: "{{mvbTermShort}} pack · supplier",
      mvbDeclaration: "{{mvbTermShort}} declaration",
      serviceClassification: "Service classification",
      controlsToGrade_one: "{{count}} control still to grade",
      controlsToGrade_other: "{{count}} controls still to grade",
      toleranceDeclared: "{{hours}}h declared",
      // C5 -- a supervisor is waiting. Nothing told the bank.
      rfi: "Question from {{examiner}}",
    },
    heading: "My work",
    subtitle: "Everything across MARSAD that is waiting on you.",
    loading: "Loading your work...",
    error: "Could not load your work.",
    empty: "Nothing is waiting on you right now.",
    nothingYours: "Nothing waiting on you.",
    /* B4 -- where the work you have already signed now sits. */
    waitingOn: "{{title}} is with the {{role}}.",
    /* Round 11 F4 -- the seat named a position where a person belongs.
       The reader's question is who to chase. */
    waitingOnWho: "{{role}}, {{who}}",
    /* ... and where NOBODY holds it, which is a fact about the bank's
       record rather than about the work, and the reason the line
       cannot name anybody. Round 10 A1 settled the wording for the
       same situation on the sign panel: report who could be notified,
       and nothing more. */
    // Round 14 D5.
    waitingOnOwnerMissing:
      "-- no owner is recorded for this service. Record one on the business service register so it can be countersigned.",
    waitingOnUnheld: "(nobody signed in holds that seat yet)",
    /* Round 11 D2 -- and since when. A line saying a thing is with the
       CRO, without saying how long it has been there, cannot support
       the only decision it exists for: whether to chase it. */
    waitingOnSince: "{{title}} is with the {{role}}, since {{since}}.",
    /* Round 16 N3 -- the second signer reads a Route 2 function before
       it reaches her, so its arrival is not her first word of it. */
    comingToYou: "{{title}}: coming to you after the {{role}}.",
    /* Round 10 F2 -- the block has its own heading now that it is no
       longer hidden inside the empty state. */
    waitingHeading: "Yours, elsewhere",
    /* Round 10 F5 -- one act of judgement over a business service.
       The panel stays; the repetition goes. `group` is taken by the
       band-heading map above, and these are a different thing. */
    /* ROUND 15 C12 -- the comment the group act takes. */
    groupAct: {
      comment: "Comment (optional; recorded on each pack)",
    },
    groupSignoff: {
      // C12 -- it read "No objection to all 5", a challenge's verb on a
      // signing control, and opened "Sign 5 packs". It signs; say so.
      review_one: "Sign the 1 pack here",
      review_other: "Sign all {{count}}",
      title: "Every {{mvbTermShort}} pack under {{subject}}",
      member: "Pack",
      commits:
        "One signature, recorded on each of these packs as part of a group decision over this business service. Each pack is still sealed and cited on its own.",
      confirm_one: "Sign 1 pack as {{name}}",
      confirm_other: "Sign {{count}} packs as {{name}}",
      done_one: "Signed 1 pack under {{subject}}.",
      done_other: "Signed {{count}} packs under {{subject}}.",
      failed: "That group sign-off did not go through.",
    },
    /* Round 14 G, eighth round -- the second line's group act. */
    groupChallenge: {
      review_one: "No objection to the 1 pack here",
      review_other: "No objection to all {{count}}",
      title: "Challenge every {{mvbTermShort}} pack under {{subject}}",
      commits:
        "One second-line challenge, recorded on each of these packs as a single no objection over this business service. The CRO reads that it was given once, over the group, before she signs any of them.",
      confirm_one: "Record no objection to 1 pack as {{name}}",
      confirm_other: "Record no objection to {{count}} packs as {{name}}",
      done_one: "Challenge recorded on 1 pack under {{subject}}. It is with the CRO.",
      done_other:
        "Challenge recorded on {{count}} packs under {{subject}}. They are with the CRO.",
    },
    band: {
      overdue: "Overdue",
      thisWeek: "Due this week",
      later: "Later",
      undated: "No date set",
      // B2 -- state the rule rather than leaving the order to be
      // inferred from a list that looks arbitrary.
      groupedRule:
        "Grouped by the service or function each belongs to, most critical tier first.",
      groupNone: "Not under a business service",
      undatedWhy:
        "None of these carries a review-by date: the bank has not declared a review cadence for them, and a date it did not choose would drive real work. Under a heading with several packs, one no objection covers them all.",
    },
    action: {
      done: "Signed.",
      working: "Signing...",
      /* Round 9 G -- the seat arrives as a VALUE; the words are
         chosen here, from the vocabulary that already names all four.
         Under `action` beside its siblings, because that is the
         namespace the backend emits -- my first attempt filed it under
         `sign` and the existing key ratchet caught it in CI. */
      signAsRole: "Sign as {{role}}",
      review: "Review and sign",
      reviewSubmit: "Review and submit",
      noIdentity:
        "Your account has no name and email on it, so a signature cannot be attributed.",
    },
    // B2 -- 24 rows in one undated band with no order and no grouping.
    // A ceremony has no deadline; when it was RAISED is a fact.
    raisedOn: "Raised {{date}}",
    queueGroup_one: "In your queue, not yours ({{count}})",
    queueGroup_other: "In your queue, not yours ({{count}})",
    group: {
      ceremony: "Governance sign-offs",
      cap: "Corrective action plans",
      assessment: "Control assessments",
      attestation: "Attestations",
    },
    card: {
      micro: "My work",
      inQueues_one: "{{count}} in your queues, none assigned to you personally.",
      inQueues_other: "{{count}} in your queues, none assigned to you personally.",
      awaiting: "items awaiting you",
      open: "Open my work",
    },
    link: {
      label: "My work",
    },
    picker: {
      placeholder: "Search people by name or email...",
    },
  },
  /** P269: MVB declaration detail (foundation workspace). */
  /* Round 18 L10 -- a frozen MVB set's members by class and admission
     today (shared/mvb/membersNow.ts), on the declaration and home's card. */
  mvbMembers: {
    // Round 18 L11 -- a function's membership, named as such.
    inDeclaration: "In the {{mvbTermShort}} declaration",
    sentence_one: "{{count}} member: {{parts}}",
    sentence_other: "{{count}} members: {{parts}}",
    admittedServices_one: "{{count}} admitted service",
    admittedServices_other: "{{count}} admitted services",
    candidates_one: "{{count}} candidate",
    candidates_other: "{{count}} candidates",
    functions_one: "{{count}} critical internal function",
    functions_other: "{{count}} critical internal functions",
    removed_one: "{{count}} since removed",
    removed_other: "{{count}} since removed",
    readFromRegister: "as the register reads today",
  },
  mvbDeclarationDetail: {
    /* Round 20 B32 -- a signature from someone not holding the seat. */
    signerUnheld:
      "{{name}} signed for the {{seat}} without holding that seat (seat at signing: {{atSigning}}). This signature does not count, and the declaration is not dual-signed.",
    unnamedSigner: "an unnamed signer",
    seatNotRecorded: "not recorded",
    effectiveBeforeSignature:
      "In force from {{when}}, a date before its last signature: a declaration cannot be in force before it is signed.",
    // Round 20 G43 -- a review cannot fall due before the declaration is in force.
    reviewBeforeEffective:
      "Its review date, {{review}}, is before it comes into force on {{effective}}. Correct the review date.",
    // Round 17 G16 -- the AE signs after the CRO.
    aeAfterCro: "Waits on {{who}}, then you.",
    /* Round 9 F1 -- signing is the start of something, not the end. */
    nowInForce: "Now in force",
    nowInForceBody:
      "This set is what the bank must be able to run. These three screens " +
      "are how you prove it can.",
    toRecovery: "The order these services come back in",
    toPosture: "Whether each one's continuity posture is sufficient",
    toDrill: "Test the plan against observed recovery times",
    loading: "Loading declaration…",
    loadError: "Failed to load declaration: {{message}}",
    rationale: "Rationale:",
    inForceSignedBy: "In force since {{when}}. Signed by {{cro}} and {{ae}}.",
    // Round 14 C2 -- what a record says once another has replaced it,
    // and before a signed declaration's start date arrives.
    supersededBy: "No longer in force: superseded on {{when}} by",
    supersededUnnamed: "No longer in force: superseded.",
    // Round 14 C5 / C6.
    withdrawSignature: "Withdraw this signature",
    withdrawnToast: "Signature withdrawn. The leg is open to be signed again.",
    setMoved:
      "The set has changed since the {{role}} signed: they signed for {{signedIbs}} {{serviceTermPlural}}, {{signedServices}} services and {{signedVendors}} vendors, and signing now would put {{nowIbs}}, {{nowServices}} and {{nowVendors}} in force. The {{role}} needs to withdraw their signature before this can be signed.",
    deleteDeclaration: "Delete this declaration",
    deleteConfirmBody:
      "Delete '{{name}}'? It has never been in force, so nothing the bank decided is lost; its signatures and requests to sign go with it.",
    deleteConfirm: "Delete it",
    deleteCancel: "Keep it",
    deletedToast: "'{{name}}' deleted. A new declaration can be started.",
    comesIntoForce: "Signed by {{cro}} and {{ae}}. Comes into force on {{when}}.",
    replaces: "Replaces",
    waitingOnOne: "Waiting on the {{role}}. They sign from My Work.",
    // Round 16 G12 -- the one person, where one person holds the seat.
    waitingOnOneNamed: "Waiting on {{who}}. They sign from My Work.",
    previewHeading: "What signing this would put in force",
    previewLoading: "Reading the current set...",
    previewUnavailable: "The current set could not be read.",
    previewEmpty:
      "Nothing is currently in scope. Signing this would put no services in force.",
    previewCount_one: "{{count}} {{serviceTerm}}",
    previewCount_other: "{{count}} {{serviceTermPlural}}",
    // D9 -- it submitted silently, so the sender was told neither that
    // it had gone nor to whom.
    /* D6 -- a submission that never asked for a reason records an
       absence, and the signatures block then reads it, correctly, as a
       signer who declined to give one. The column has existed since
       C2 and nothing ever sent one. */
    submitNoteLabel: "Why this set, in a sentence (optional)",
    submitNotePlaceholder: "What the CRO should know before signing.",
    submittedToast: "Declaration submitted. It is now with the CRO to sign.",
    threshold: "Gate-1 threshold {{hours}}h",
    effectiveWindow: "Effective window",
    // Round 9 C5. Neither sign leg took a comment, so the finished
    // record read "Signed without a comment" under three names when no
    // box had ever been offered -- which reads as three people
    // declining to explain themselves rather than as nobody asking.
    signNote: "Why you are signing (optional)",
    // On a draft both ends are empty, so the window rendered a dash, an
    // arrow and the words "open-ended", which describes nothing.
    windowOnApproval:
      "Starts when both signatures are in. It stays in force until a later declaration supersedes it.",
    openEnded: "open-ended",
    ceremony: "Dual sign-off ceremony",
    cro: "CRO",
    /* Round 9 E8 -- "Audit Executive" is a mis-expansion of AE and it
       had spread to three places. The seat is the ACCOUNTABLE
       executive: the person answerable for the declaration, not
       somebody auditing it. A record that names one seat three ways
       across four lines of the same page is a record a reader has to
       reconcile before they can read it. `seats.ae` above is the one
       spelling; this exists because the block renders the word inside
       a longer sentence. */
    ae: "Accountable executive",
    notSigned: "not yet signed",
    signed: "signed {{when}}",
    waitingOnSigners:
      "Waiting on the CRO and the accountable executive. They sign from My Work.",
    // Round 16 G12 -- each named where one person holds the seat.
    waitingOnNamedSigners: "Waiting on {{cro}} and {{ae}}. They sign from My Work.",
    signCro: "Sign as CRO",
    signAe: "Sign as AE",
    submit: "Submit for sign-off",
    frozenSnapshot: "Frozen snapshot",
    ibses: "{{serviceTermShortPlural}}",
    // Round 17 L6 -- members that are not important business services.
    functions: "Critical internal functions",
    memberFunction: "critical internal function",
    memberNotAdmitted: "not yet admitted",
    previewFunctions_one: ", of which {{count}} is a critical internal function",
    previewFunctions_other: ", of which {{count}} are critical internal functions",
    services: "Services",
    vendors: "Vendors",
  },
  /** P269: per-cycle assessment grid + control drawer (framework). */
  executive: {
    rubric: "Framing \u00b7 Executive",
    heading: "Executive",
    body: "What the board, the regulator and the sector see. Every figure here is derived from the live estate and can be opened down to the finding behind it.",
    section: "Reporting",
    /* Round 10 A3 -- the section was correctly seat-scoped and said
       nothing about it, so a Head of Operational Resilience read one
       card of four under a standfirst promising the board's, the
       regulator's and the sector's view, and a preparer read the
       promise followed by nothing at all. */
    partial:
      "You can open {{open}} of the {{total}} rooms below. The rest belong to other seats and are not shown.",
    notYours:
      "This section reports to the board, the regulator and the sector, and every room behind it is held by an approver, a reviewer or the Head of Operational Resilience. None of it is yours to open, which is why nothing is offered here.",
    // Round 18 Q3 -- the records under this question that are yours.
    notYoursButThese:
      "The board's, the regulator's and the sector's reports belong to other seats. The records behind them that are yours to open are these:",
    toMyWork: "Go to your own work",
    /* Nothing anywhere announced the section existed -- including the
       page carrying the same four cards. */
    homeNote:
      "These four cards are also gathered, with a two-minute read above them, under one heading:",
    homeNoteLink: "Executive",
    brief: {
      /* Round 16 I13 -- the brief's notes, catalogued. */
      note: {
        noSealedState_one:
          "No sealed resilience state existed {{count}} day ago, so impairment cannot be compared.",
        noSealedState_other:
          "No sealed resilience state existed {{count}} days ago, so impairment cannot be compared.",
        nothingAssessed:
          "Nothing has been assessed yet: no service has been admitted, no tolerance declared and no telemetry sampled. The figures below are the absence of evidence, not evidence of health.",
      },
      rag: "Where the bank stands",
      ragWord: {
        red: "Red",
        amber: "Amber",
        green: "Green",
        /* Round 12 B1 -- the fourth word, and it is the point of the
           whole workstream: a bank that has assessed nothing is not
           green. */
        not_assessed: "Not yet assessed",
      },
      tolerance:
        "{{breached}} of {{assessed}} declared tolerances breached, {{mvb}} of them in the {{mvbTerm}}.",
      moved: "What moved",
      /* Nothing in the product records a board meeting, so the window
         is stated rather than dressed up as a board cycle. */
      since: "Against {{date}}, {{days}} days ago",
      nothingMoved: "Nothing in this set has changed since then.",
      noEarlier: "no earlier reading, now {{after}}",
      figure: {
        impaired: "business services impaired or degraded",
        mvbImpacted: "minimum-viable-bank services caught in the cascade",
        assessmentsApproved: "control assessments approved",
      },
      cost: "What it is costing",
      /* A5 -- "SAR 0 / Across 0 products, all of them with revenue
         recorded" is technically true of an empty set and reads as a
         reassurance about work nobody has done. A clause that only
         holds vacuously is dropped rather than rephrased. */
      costComplete: "Across {{measured}} products.",
      /* B1 -- "SAR 0 / Across 0 products" sat beside a GREEN headline
         on a bank with no products at all, and a measured nothing and
         an unmeasured nothing print the same figure. */
      costNothingMeasured:
        "No product carries revenue yet, so there is nothing to price. This is not an estimate of zero.",
      /* A total over products carrying no revenue is a floor, and a
         board told a floor is a total will plan against the wrong
         number. */
      costFloor:
        "A floor, not a total: {{measured}} products carry revenue and {{unmeasured}} do not.",
      waiting: "Waiting on you",
      waitingNone: "Nothing is waiting on you.",
      waitingAll: "Open your work",
      oneNumber: "The number you will be asked about",
      /* Round 11 F3 -- "47.3C" read as one token; it is a score and
         the band it falls in. */
      /* A5 -- two corrections in one line. The leading separator was
         also emitted by the component, so the brief read "40.7 · ·
         tier C"; and a bare "tier C" says nothing about WHICH measure
         produced it, on a product carrying two that band differently
         and disagree -- the administrator's desk headlined 2.10 Tier D
         while this screen headlined 40.7 tier C, ninety seconds
         apart. */
      scoreTier: "Resilience Score tier {{tier}}",
      confidence: "{{pct}}% confidence, methodology {{version}}",
      capped:
        "The tier is capped by confidence: the estate does not yet evidence enough to claim a higher one.",
      openScore: "Open the score and its evidence",
      failed: "This read could not be composed just now. The rooms below still open.",
    },
    tiles: {
      board: {
        name: "Board snapshot",
        desc: "One page: overall posture, what is driving it, and what changed",
        // Round 14 B4 -- the route admits the Head of Operational
        // Resilience; the tile says so.
        who: "Approver, reviewer, Head of Operational Resilience or administrator",
      },
      assurance: {
        name: "Provable assurance",
        desc: "Answer a regulator's question, cited and signed",
        who: "Approver or administrator",
      },
      readiness: {
        name: "Resilience readiness",
        desc: "The maturity assessment and the evidence behind each dimension",
        who: "Approver, reviewer, Head of Operational Resilience or administrator",
      },
      /* Round 11 C4 -- the room the executives were never offered.
         The flagged count rides the tile so it is on the two-minute
         read rather than one click past it. */
      fieldExam: {
        name: "Field-exam readiness",
        desc: "What a SAMA field examiner would ask of this estate, and which answers the bank cannot yet give",
        descCount:
          "{{flagged}} of {{total}} supervisory checks flagged -- what an examiner would ask, and which answers the bank cannot yet give",
        descCountNotAssessed:
          "{{flagged}} of {{total}} assessed supervisory checks flagged, {{notAssessed}} not assessed -- what an examiner would ask, and which answers the bank cannot yet give",
        who: "For the CRO and the board",
      },
      benchmark: {
        name: "Sector benchmark",
        desc: "Where this bank sits against contributing peers",
        who: "Approver or administrator",
      },
      setup: {
        name: "Set up",
        desc: "How far the bank is through setting MARSAD up, step by step, and whose each remaining step is",
        who: "Kept in Admin; open to anyone signed in",
      },
    },
  },

  notFound: {
    heading: "That page does not exist",
    body: "Nothing is served at",
    home: "Go to the home page",
    myWork: "Go to my work",
  },

  glossary: {
    rubric: "Framing \u00b7 Glossary",
    heading: "What these words mean",
    body: "MARSAD uses the regulator's vocabulary. These are the terms the product assumes, in plain sentences.",
    showLess: "Show less",
    learnMore: "Learn more",
    // Round 19 H54 -- the door a quiet teaching line leaves behind.
    learnMoreAbout: "Learn more: {{term}}",
    /* ROUND 13 H6, thirteenth round -- the route, not only the word. */
    whereYouDoThis: "Where you do this →",
    alsoCalled: "Also called: {{names}}",
    searchHeading: "Definition",
    // Stitching review 5.5 -- a construct taught where it is first met.
    firstMeeting: {
      label: "New to you here",
      gotIt: "Got it",
    },
  },

  escalate: {
    // E3 -- the sentence an escalation carries is prose a reader reads,
    // so it belongs in the catalogue like every other sentence. These
    // two were composed in JSX and would have reached a translator
    // nowhere.
    whatNoLicence: "This MARSAD deployment has no licence installed",
    whatSentinelKey: "MARSAD is signing proofs with the sentinel key, not a bank key",
    label: "Escalate to:",
    copy: "Copy details",
    copied: "Copied",
    platformAdmin: "your platform administrator",
    chainBreak:
      "the head of operational resilience and the CRO -- do not issue a dossier until this is resolved",
    licence: "your MARSAD contact (the licence was issued to this deployment)",
    // D2 -- these two panels named nobody at all. Both faults are the
    // bank's own infrastructure, so the person to tell is inside the
    // bank, not us.
    mailRelay: "whoever operates the bank's mail relay",
    signingKey:
      "the head of operational resilience -- artefacts signed with the sentinel key cannot be attributed to the bank",
  },

  adminOnly: {
    body: "{{surface}} is an administrator surface. Your account does not hold the admin role -- ask an administrator to grant it if you need this.",
    back: "Back to my work",
  },

  asOfBanner: {
    text: "Showing sealed states as of {{when}}. Open findings and the entity register are shown LIVE -- they are not rewound, and anything raised after that time is marked below.",
    later: "raised after the chosen time",
    // D11 -- the scrubber moved the whole picture and said nothing, so
    // a reader could be looking at March believing it was today.
    returnToLive: "Return to live",
  },

  // Round 20 U4 -- findings on what a service rests on.
  restsOn: {
    heading: "On what it rests on",
    summary_one:
      "{{own}} open on this service; {{count}} on what it rests on ({{critical}} critical).",
    summary_other:
      "{{own}} open on this service; {{count}} on what it rests on ({{critical}} critical).",
    onWhat: "on {{name}}, since {{since}}",
  },
  // Round 20 V7 -- a contract and its supplier describing two estates.
  supplierCoherence: {
    contractRow:
      "Names {{services}}, which this supplier is not linked to on the vendor register.",
    vendorRow: "A contract names services this supplier is not linked to: {{list}}.",
  },
  // Round 20 V6 -- a supplier's arrangements on its own page.
  vendorArrangements: {
    heading: "Contracts and exit",
    noExitPlan:
      "Material supplier with no exit plan, behind {{services}}: if it fails or must be replaced, nothing on the record says how the service carries on.",
    exitPlanRef: "Exit plan: {{ref}}",
    noExitPlanRef: "No exit plan reference on the supplier register.",
    noContracts: "No outsourcing contract is recorded with this supplier.",
    material: "material",
    noObjection: "SAMA no-objection {{ref}}",
    noObjectionMissing: "no SAMA no-objection recorded",
    noObjectionNotNeeded: "no no-objection needed",
    exitRecorded: "exit strategy recorded",
    exitMissing: "no exit strategy",
    ends: "ends {{date}}",
  },
  // Round 20 V5 -- an admitted service with no recorded owner.
  noOwner: {
    plain: "No owner recorded: nobody is accountable for this service",
    withCritical_one:
      "No owner recorded: nobody is accountable for its open critical finding",
    withCritical_other:
      "No owner recorded: nobody is accountable for its {{count}} open critical findings",
  },
  toleranceState: {
    signed: "{{hours}}h declared and signed",
    /* Round 11 F1 -- `setUnsigned` is gone. "{{hours}}h declared, not
       yet signed" was the second wording for the state `pending`
       already describes, and the two sat one row apart on home and in
       one row on the portfolio. Every unsigned state now reads
       `pending`, which names the seat when the record names one. */
    undeclared: "No tolerance declared",
    /* C1 -- a declaration in flight. The owner who declared it was
       told on four screens that none existed. */
    pending: "{{hours}}h declared, {{awaiting}}",
    /* Round 10 F2's `replacing` folded into `unsignedDeclared` (Round 16
       D21): the figure in force says it is unsigned, and the proposal
       follows it whether it replaces the figure or confirms it. */
    /* Round 16 D21 -- one wording for a figure in force nobody signed. */
    unsigned: "{{hours}}h in force, not signed",
    unsignedDeclared:
      "{{hours}}h in force, not signed; {{proposed}}h declared, {{awaiting}}",
    awaitingCro: "awaiting the CRO",
    awaitingBusinessOwner: "awaiting the business owner",
    awaitingSignature: "awaiting signature",
    /* ROUND 15 G2 -- the holder by name where the record names one;
       the seat alone, and said so, where nobody holds it. */
    awaitingNamed: "awaiting {{who}} ({{role}})",
    awaitingNamedSince: "awaiting {{who}} ({{role}}) since {{since}}",
    awaitingUnheld: "awaiting the {{role}} -- nobody signed in holds that seat yet",
    // Round 16 G14 -- only the owner countersigns, and there is none.
    awaitingNoOwner:
      "awaiting the service's owner -- none is recorded, so nobody can countersign yet",
    // Round 20 V2 -- a signer who no longer holds the seat.
    signerLapsed:
      "Signed by {{name}} as {{role}}, who does not hold that seat on MARSAD's record today.",
    signerLapsedAtTheTime:
      "Signed by the {{role}} at the time ({{name}}), who no longer holds the seat.",
    // Round 20 W3 -- named in the register, not yet bound to a seat.
    awaitingOwnerNotSeat:
      "awaiting {{owner}}, the service's owner -- named, not yet a seat, so nobody can countersign until their sign-in address is recorded",
    awaitingSeveral: "awaiting the {{role}} ({{count}} hold the seat)",
    pendingBy: "{{hours}}h declared by {{who}} on {{when}}, {{awaiting}}",
    // B4 -- "in force" was doing two jobs and reported the wrong one.
    // The count is deliberate and correct: `set_unsigned` means a
    // number the recovery model and every breach verdict are already
    // using. But "in force" reads as GOVERNANCE, so the header called
    // eleven unsigned tolerances in force on a page that elsewhere
    // says plainly that an unsigned tolerance is not. Say both facts
    // rather than let one word carry both.
    /* D6 (round 8) -- and the fourth state.
       `tolerance_state` computes four and the sentence named three, so
       "16 IBSes: 1 signed, 11 unsigned, 0 with none" accounted for
       twelve. The four it dropped were `pending` -- a declaration in
       flight -- which on that estate included the reader's OWN
       submission, so the one number he was looking for was the one
       silently omitted. */
    /* Round 12 H1 -- and the FOURTH wording. The badges say "awaiting
       the CRO", "awaiting the business owner" or "awaiting signature",
       which is one state with the seat named where the record names
       one. This line said "awaiting sign-off", a phrase used nowhere
       else, so the summary above the rows and the rows themselves
       described the same state in words that do not match. Eighth
       round of counting wordings for this state; the count is what
       the reader is entitled to have stay still. */
    /* Round 17 P1 -- the total counted functions and candidates under
       the word "IBSes" ("15 IBSes" beside home's 5 in the register).
       It names its cut now. */
    /* Round 18 P11 -- "3 functions" beside home's "1 function": the
       functions name their own admitted count. */
    summary:
      "{{total}} on the register ({{admitted}} admitted {{serviceTermShortPlural}}; {{functions}} functions, {{admittedFunctions}} admitted; the rest under assessment): {{signed}} with a signed tolerance, {{unsigned}} using a number nobody has signed yet, {{pending}} awaiting signature, {{undeclared}} with none",
  },

  engineOff: {
    heading: "The Marsad Engine is off in this environment.",
    analyser:
      "Grade each control yourself in the assessment drawer -- open a control, set its status and maturity, and attach the evidence.",
    controlTesting:
      "Record the test result yourself: pick it from the list and save. Nothing here needs the Engine.",
    regChange:
      "Log the circular against its obligations by hand below, then draft the CAP from them.",
    letter:
      "Write the letter's observations yourself -- add one per requirement so each can be actioned separately.",
    cap: "Write the action items yourself using the form below.",
  },

  /* Round 10 F1 -- the assessments hub, when it is entered from a
     global-search hit on a control rather than by browsing. */
  assessmentsHub: {
    controlLocating: "Finding the cycle this control is graded in\u2026",
    controlOpening: "Opening {{control}} in {{cycle}}\u2026",
    controlNoCycle:
      "{{control}} belongs to {{framework}}, which has no assessment cycle yet, so there is nothing to open. Start a cycle below and the control will be in it.",
    controlNotFound:
      "That control could not be found. It may have been removed since the link was made.",
  },
  assessmentGrid: {
    /* Round 10 F1 -- a link named a control that is not in this
       framework's catalogue. */
    controlNotInThisCycle:
      "The control that link named is not in this framework's catalogue, so there is no row to open.",
    /* Round 12 F5 -- a mark on the grid row, because that row is what a
       reviewer reads before he opens anything. */
    aboveTargetMark: "above target",
    // ROUND 15 A18 -- the second press, with the plan's name on screen.
    raiseAnother: "Raise another?",
    // Round 18 A34 -- the plans remediating a control.
    remediatedBy: "Remediated by:",
    planState: "({{state}}),",
    fromLetter: "from letter",
    raisedInternally: "raised inside the bank",
    // Round 18 A33 -- beside the plan the control is already on.
    openThatPlan: "Open that plan",
    review: {
      ungraded: "This control has not been graded in this cycle yet.",
      /* Round 17 A32 -- "maturity 1 of 4" read as a scale of four beside
         "Current ML (1-5)"; the second figure is the cycle's target. */
      maturity: "maturity {{current}} (target {{target}})",
      /* Round 12 F5 -- an accepted Engine suggestion set a current
         maturity above the cycle's target and the drawer printed
         "maturity 4 of 3" with nothing said about it. Not clamped:
         exceeding a target is a real thing a bank can do, and silently
         lowering the Engine's number would be worse than printing it.
         What was missing was the sentence. */
      aboveTarget:
        "Above this cycle's target ({{current}} against {{target}}). That is allowed -- a control may be more mature than the cycle asked for -- but check it is the judgement you meant.",
      graded: "recorded {{when}}",
      requirement: "What the control requires",
      evidenceHeading: "{{count}} piece(s) of evidence attached",
      attachedBy: " — attached by {{who}}",
      /* The editing panel has no labelled input for these two, so the
         review rendering brings its own words rather than borrowing a
         key that does not exist. */
      recommendations: "Recommendations",
      notes: "Notes",
      gradedBy: "Graded by {{who}}. ",
      // Round 17 A29 -- one sentence: grader, date, and who moved it.
      gradedByWho: "Graded by {{who}}",
      gradedOnWhen: " on {{when}}",
      moved: {
        send_back: "; sent back by {{who}}",
        approve: "; approved by {{who}}",
      },
      // D2 -- distinct from gradedBy: whose queue it is in, not whose
      // judgement it is.
      assignedTo: "Assigned to {{who}}. ",
      approvedBy: "Approved by {{who}}.",
      /* D8 -- a person confirming the Engine's proposal is a different
         provenance from a person forming a view, and the examiner is
         entitled to know which. */
      engineAssisted:
        "The Marsad Engine proposed this judgement; a person confirmed it.",
      /* Round 10 C5 -- fifth round. Undo lived only as long as its
         toast: nine seconds after accepting, the drawer offered no way
         back and said only who graded it, above three paragraphs she
         did not write. The persona's stated fear is Engine output she
         cannot explain to a reviewer. */
      engineAccepted: "Set from an Engine suggestion, accepted by {{who}} at {{when}}.",
      engineUndo: "Undo",
      // D11 -- the section vanished when nothing covered the control,
      // so an absence read as a section that had not loaded.
      noDocuments:
        "No policy document is mapped to this control. The document gap " +
        "report is where that is closed.",
      /* D4 -- the sentence named the gap report and left the reader to
         find it. */
      openDocumentGap: "Open document gap analysis",
      /* D4 -- when the judgement was made, from the seal. */
      gradedOn: "on {{when}}. ",
      documentsHeading_one: "1 policy document covers this control",
      documentsHeading_other: "{{count}} policy documents cover this control",
      /* Round 14 A3 -- take a document out of this control's evidence. */
      /* Round 16 A26 -- the verb names the act, and it asks first. */
      withdrawDocument: "Withdraw from this control",
      // Round 17 A29 -- the evidence under review does not move.
      documentsFrozen:
        "Frozen while this control is in review: its documents change after a send-back or a decision.",
      withdrawDocumentAsk:
        "Withdraw {{name}} from this control's evidence? A design test that rested on it stays as it was recorded.",
      withdrawDocumentConfirm: "Withdraw",
    },
    backToCycles: "Back to {{code}} cycles",
    cycleFallback: "Cycle",
    controlsStatus: "{{count}} controls · status:",
    openCycle: "Open cycle",
    closeCycle: "Close cycle",
    autoAnalyseTooltip:
      "When ON, attaching evidence to any assessment in this cycle automatically queues a Marsad Engine analyser run.",
    autoAnalyse: "Auto-analyse on evidence attach",
    loadingCatalogue: "Loading catalogue…",
    colControl: "Control",
    colStatus: "Status",
    colMl: "ML",
    colWorkflow: "Workflow",
    aiDraftedTooltip: "Materialised by the batch analyser; review before submitting.",
    close: "Close",
    judgement: "Judgement",
    status: "Status",
    // Round 18 A35 -- one scale, named once: the header said
    // "maturity 1 (target 4)" above "Current ML (1-5)".
    currentMl: "Current maturity (1 to 5)",
    evidenceSummary: "Evidence summary",
    gaps: "Gaps",
    assignee: "Assignee (preparer)",
    approver: "Approver",
    currentlyAssigned: "Currently assigned",
    saving: "Saving…",
    saveJudgement: "Save judgement",
    drafting: "Drafting…",
    raiseCap: "Raise remediation CAP",
    /* Round 19 G41 -- the plan can be named when it is raised. */
    planTitle: "Plan title",
    planTitlePlaceholder: "Plan title (optional; the control's name if left blank)",
    comment: "Comment (required for send-back)",
    submitForReview: "Submit for review",
    approve: "Approve",
    sendBack: "Send back",
    withdraw: "Withdraw",
    // B1 -- a transition finished in silence, so the operator pressed
    // the button again and met a 409 that said nothing either. Each
    // verb says where the assessment went.
    // D9 -- naming the STATE says what the record is called, not where
    // it went. The reader's question is who has it now, and the seat is
    // what the state means.
    transition: {
      submit: "Sent for review. It is now with the reviewer.",
      approve: "Approved. Nobody else needs to act on it.",
      send_back: "Sent back. It is now with the preparer.",
      withdraw: "Withdrawn. It is back with you.",
    },
    approvedNoActions: "Approved -- no further actions",
    evidence: "Evidence",
    detach: "Detach",
    attach: "Attach",
    noEvidence: "No evidence attached.",
    derivedTooltip: "Status derived from control testing ({{tests}})",
    savedToast: "Judgement saved.",
    saveFailed: "Could not save the judgement.",
    attachedToast: "Evidence attached.",
    attachFailed: "Could not attach the evidence.",
    attachedBy: "Attached by {{who}}",
    /* Round 20 Y4 -- the artefact's date, and what it is the date of. */
    evidenceRetrieved: "retrieved {{date}}",
    evidenceUploaded: "no retrieval date; uploaded {{date}}",
    evidenceOlderThanCycle: "older than this cycle",
    attachedByUnknown: "Attached by an unrecorded user",
    evidenceDetails: "What MARSAD knows about this artefact →",
    uriPlaceholder: "https://sharepoint/.../policy.pdf",
    evidenceTextPlaceholder:
      "Optional: paste the evidence text snippet you want the Marsad Engine to analyse. Falls back to URI metadata when blank.",
  },
  benchmark: {
    /* Round 10 A4 -- the one control in the product that sends the
       bank's own data outside the bank shipped with no statement of
       what it sends, to whom, or who may authorise it. An IT
       administrator at a SAMA-regulated bank would refuse to leave
       that enabled, and would be right to. */
    disclose: {
      heading: "Before you submit: what leaves the bank",
      body: "Submitting publishes a signed contribution to the peer benchmark network. It carries four whole-bank figures and nothing else:",
      granularityLabel: "Granularity",
      granularity:
        "One number per figure for the whole bank, to two decimal places. No service, vendor, finding or business-service name is included, and nothing identifies an individual row.",
      formLabel: "Form",
      form: "A signed JSON bundle. The signature lets the recipient prove it came from this deployment unaltered; it does not conceal the figures, which are the point of sending them.",
      recipientLabel: "Recipient",
      authorityLabel: "Authorised by",
      authority:
        "An administrator of this deployment. The button is disabled for every other seat.",
      noNetwork:
        "No peer network is configured, so nothing can be submitted from here.",
      roleLocked:
        "Only an administrator of this deployment may submit to the peer network.",
      confirmBody:
        "Send {{count}} whole-bank figures, signed and anonymised, to {{destination}}. This leaves the bank. Nothing is retained by MARSAD beyond the audit record of who sent it.",
      confirmSend: "Send to the peer network",
    },
    kicker: "SECTOR BENCHMARK",
    heading: "How do we compare?",
    intro:
      "Your operational-resilience posture against the sector baseline. " +
      "Every metric is an anonymized aggregate -- the same bundle you can " +
      "contribute to a benchmark network, signed + verifiable.",
    loading: "Computing benchmark...",
    error: "Could not compute the benchmark.",
    forbidden: "The sector benchmark is for approvers + admins.",
    baseline: "baseline {{value}}{{unit}}",
    baselineSource: "Baseline source: {{source}}.",
    downloadContribution: "Download signed contribution",
    submit: "Submit to peer network",
    submitting: "Submitting...",
    submitted: "Contribution submitted to the aggregator.",
    submitError: "Could not submit the contribution.",
    livePeers: "Live peers (n={{n}})",
    syntheticChip: "Synthetic baseline -- no peer network configured",
    suppressed: "suppressed below the k-anonymity floor (n={{n}})",
    you: "you:",
    downloaded: "Signed contribution bundle downloaded.",
    downloadError: "Could not download the contribution bundle.",
    verdict: {
      ahead: "Ahead",
      on_par: "On par",
      behind: "Behind",
      /* Round 17 B14 -- no verdict over nothing. */
      not_comparable: "Cannot be compared",
    },
    population: "Cannot be compared: {{why}}",
    populationShown: "Measured {{why}}",
    card: {
      heading: "Sector benchmark",
      body: "Compare your posture to the sector baseline + export a signed contribution.",
      open: "Open benchmark",
    },
  },
  recommendations: {
    /* Round 9 G -- the "based on" line, composed in the reader's
       language rather than arriving finished from the backend. */
    citation: {
      control: "{{subdomain}} -- {{status}}",
      concentration_one: "{{reach}} downstream, {{mvb}} in the {{mvbTermShort}} set",
      concentration_other: "{{reach}} downstream, {{mvb}} in the {{mvbTermShort}} set",
      /* Pluralised on `count`, which i18next selects on -- passing
         only `suppliers` would leave the _other form always chosen and
         "1 suppliers" on the page. The number is sent twice on
         purpose: `count` chooses the form, the named param is what the
         sentence prints, and a translator may order them differently. */
      exposure_one: "{{suppliers}} supplier, RTO {{rto}}",
      exposure_other: "{{suppliers}} suppliers, RTO {{rto}}",
      scenario_one:
        "{{breached}} {{serviceTermShort}} breaches ({{mvb}} {{mvbTermShort}})",
      scenario_other:
        "{{breached}} {{serviceTermShortPlural}} breach ({{mvb}} {{mvbTermShort}})",
    },
    /* F (round 8) -- the recommendation sentences, composed on the
       SERVER and until now arriving in English whatever language the
       reader is in. The client cannot translate a sentence it did not
       build, so the server sends the key and its parameters and the
       client renders it here. The English stays on the wire for
       callers with no client. */
    item: {
      assessmentGap: {
        title: "Remediate {{framework}} {{control}}",
        // Round 9 B5. This printed the raw enum ("is non_compliant") and
        // dropped the maturity, on the one screen whose stated job is
        // telling a business reader what to do -- while the card's own
        // citation two lines below spelled it out properly, so the card
        // disagreed with itself in a single reader's eye. i18next picks
        // the _withMl variant by context, because a control with no
        // maturity recorded must not read "at maturity  of 4".
        rationale:
          "{{framework}} {{control}} ({{subdomain}}) is {{status}} in the current assessment cycle.",
        rationale_withMl:
          "{{framework}} {{control}} ({{subdomain}}) is {{status}}, at maturity {{ml}} of {{mlMax}}, in the current assessment cycle.",
        action: "Draft a remediation CAP for this control.",
      },
      concentration: {
        title: "Reduce reliance on {{node}}",
        rationale:
          "{{node}} is a concentration risk: {{reach}} downstream entities depend on it, {{mvb}} of them in the {{mvbTerm}}.",
        action:
          "Reduce concentration: add a redundant supplier or cut this dependency's recovery time.",
      },
      exposedIbs: {
        title: "Harden {{ibs}}",
        rationale_one:
          "{{ibs}} is thinly protected: {{supporting}} supporting service, tightest recovery target {{rto}}.",
        rationale_other:
          "{{ibs}} is thinly protected: {{supporting}} supporting services, tightest recovery target {{rto}}.",
        rationaleSingleSupplier_one:
          "{{ibs}} is thinly protected: {{supporting}} supporting service, and a single supplier behind it. Tightest recovery target {{rto}}.",
        rationaleSingleSupplier_other:
          "{{ibs}} is thinly protected: {{supporting}} supporting services, and a single supplier behind them. Tightest recovery target {{rto}}.",
        /* Round 9 G -- two whole sentences rather than one with a
           clause concatenated onto it. A translator cannot reorder a
           fragment that arrives already joined, and in Arabic the
           parenthetical has to move. */
        action: "Add a secondary supplier, or tighten the recovery target.",
        actionNoRedundancy:
          "Add a secondary supplier -- there is no redundancy today -- or tighten the recovery target.",
      },
      scenarioBreach: {
        title: "Mitigate scenario: {{scenario}}",
        rationale_one:
          "{{scenario}} breaches {{toleranceTerm}} for {{breached}} {{serviceTermShort}} ({{mvb}} {{mvbTermShort}}).",
        rationale_other:
          "{{scenario}} breaches {{toleranceTerm}} for {{breached}} {{serviceTermShortPlural}} ({{mvb}} {{mvbTermShort}}).",
        /* Round 9 G -- the cheapest fix, as a sentence the reader's
           catalogue supplies rather than one the backend finished. */
        actionHarden_one:
          "Harden {{service}}: cut RTO from {{from}}h to {{to}}h to clear {{cleared}} breach.",
        actionHarden_other:
          "Harden {{service}}: cut RTO from {{from}}h to {{to}}h to clear {{cleared}} of the breaches.",
        actionNoSingleFix:
          "No single-service fix clears these breaches -- several slow dependencies. See the war-room remediation plan.",
      },
    },
    kicker: "OUTCOME LAYER",
    heading: "What to do about it",
    intro:
      "Prioritised, grounded remediation across the diagnostic pillars -- " +
      "assessment gaps, {{serviceTermShort}}/{{mvbTermShort}} concentration risk, and scenario breaches. " +
      "Every recommendation cites its source; each action routes to where " +
      "you act on it. Decision-support -- nothing changes from here.",
    loading: "Assembling recommendations...",
    error: "Could not load recommendations.",
    empty: "No recommendations in this view.",
    // Round 17 I16 -- one sentence each, not a label glued to a value.
    soWhatLine: "What to do: {{action}}",
    basedOn: "Based on",
    sourceAndPriority: "{{source}} · priority {{score}}",
    revenueAtRisk: "{{sar}} at risk",
    drafting: "Drafting CAP + Engine actions...",
    drafted: "Remediation CAP drafted -- review + draft actions.",
    /* Round 14 A5 -- the control was already on a draft plan. */
    reusedDraft:
      "This control is already on a draft remediation plan, so that plan is open. Nothing was added to it.",
    draftedWithActions:
      "Remediation CAP drafted with {{n}} Engine actions -- review + submit.",
    draftedNoEngine:
      "Remediation CAP drafted (Engine not configured -- draft actions on the CAP).",
    // ROUND 15 A10 -- the plan exists and the Engine drafted nothing onto it.
    draftedEngineRefused:
      "Remediation CAP created. The Engine could not draft actions this time; add them by hand.",
    criticalCount: "{{n}} critical",
    highCount: "{{n}} high",
    totalCount: "{{n}} total",
    severity: {
      critical: "critical",
      high: "high",
      medium: "medium",
      low: "low",
    },
    source: {
      assessment_gap: "Assessment gap",
      ibs_risk: "{{serviceTermShort}} / {{mvbTermShort}} risk",
      scenario_breach: "Scenario breach",
    },
    filter: {
      all: "All",
      assessment_gap: "Assessment gaps",
      ibs_risk: "{{serviceTermShort}} / {{mvbTermShort}}",
      scenario_breach: "Scenarios",
    },
    action: {
      draft_cap: "Open assessments",
      draft_cap_do: "Draft remediation CAP",
      // Round 17 Q2 -- not a link to a page that refuses this seat.
      draft_cap_elsewhere: "Acted on by the compliance team, in Framework",
      war_room: "Open war-room",
      fragility: "Open fragility radar",
      review: "Review",
    },
    card: {
      heading: "RECOMMENDATIONS",
      body: "Prioritised remediation across assessments, {{serviceTermShort}}/{{mvbTermShort}} + scenarios.",
      summary: "{{total}} recommendations -- {{crit}} critical, {{high}} high.",
      open: "View recommendations",
      error: "Recommendations unavailable.",
    },
  },
  assurance: {
    // F -- a headline figure like "4 of 6" sat above ten unlabelled
    // rows, so the count read as a count of what followed.
    claimsHeading_one: "What this answer rests on (1 statement)",
    claimsHeading_other: "What this answer rests on ({{count}} statements)",
    kicker: "PROVABLE ASSURANCE",
    heading: "Answer the regulator",
    intro:
      "Pick a question. The answer is assembled from the operational " +
      "twin -- every figure cites its source, and chain-backed figures " +
      "are independently verifiable. No language model is in the answer.",
    pickPrompt: "Choose a question to assemble its cited answer.",
    /* ROUND 15 H11 -- the four questions were fixed English on a
       translatable surface. The server still sends its own text as
       the fallback. */
    questions: {
      "mvb-impact":
        "How many minimum-viable-bank {{serviceTermShortPlural}} are impacted right now, and which?",
      "board-posture": "What is our overall operational-resilience posture?",
      "ibs-review-readiness":
        "Which admitted {{serviceTermShortPlural}} are overdue for their methodology review?",
      "regulatory-change-responsiveness": "Are we keeping pace with regulatory change?",
    },
    forbidden: "Provable assurance is for approvers + admins.",
    loading: "Assembling the answer...",
    error: "Could not assemble the answer.",
    asOf: "As of",
    sources: "Sources",
    verifiable: "verifiable",
    downloadPack: "Download signed pack",
    downloading: "Packaging...",
    packDownloaded: "Signed answer pack downloaded.",
    downloadError: "Could not build the signed pack.",
    rfi: {
      /* E6 -- a supervisor's lodged question. The keys above it are
         P177's RFI BUNDLE, which is the pack that answers one; the two
         are different things and the names say which. */
      // Round 16 H25 -- "1 QUESTION(S)", plurals catalogued.
      inboxHeading_one: "{{count}} question from a supervisor",
      inboxHeading_other: "{{count}} questions from a supervisor",
      askedBy: "Asked by {{who}}",
      answer: "Answer this",
      notePlaceholder: "Your reply, in your own words",
      reviewAnswer: "Review the answer",
      confirmHeading: "This is what will be sent.",
      confirmCounts:
        "{{questions}} question(s) answered, {{citations}} citation(s) attached.",
      confirmSend: "Send it",
      confirmBack: "Back",
      sendAnswer: "Send the answer",
      sendingAnswer: "Sending…",
      // D7 -- this was said after a note-only answer, which produced
      // no pack at all. Every answer is signed now, and the question
      // and the note travel inside it.
      sent: "Answered. The question, your reply and the evidence behind it are in the examiner's workspace, signed.",
      /* C5 -- an answer with no question selected carries no
         citations, so its pack proves nothing. Promising evidence
         anyway is a claim the product cannot back, made to the one
         reader whose job is checking claims. */
      cite: {
        /* Round 10 F4 -- the examiner's next question after "which
           control": has it been graded, in which cycle, and is the
           judgement sealed. */
        preview: {
          check: "Check it",
          loading: "Resolving\u2026",
          failed: "Could not resolve this citation.",
          cycle: "Graded in",
          grade: "Grade",
          maturity: "(maturity {{current}}, target {{target}})",
          notGraded: "Not graded in any cycle, so there is no judgement to cite.",
          sealed: "The judgement is sealed.",
          notSealed: "The judgement is not sealed.",
        },
        heading: "Cite the records this answers with",
        placeholder: "Search a control, a document, or a service...",
        add: "Cite",
        remove: "Remove this citation",
        sealedRecords: "Sealed records",
        sealedRows_one: "{{slug}} -- {{count}} sealed entry",
        sealedRows_other: "{{slug}} -- {{count}} sealed entries",
        nothingSealed: "Nothing sealed for this record yet.",
        noResults: "Nothing matches that.",
      },
      sentNoteOnly:
        "Answered with a note only. It is signed and in the examiner's workspace, but it cites no evidence -- select the questions it answers to send those too.",
      include: "Add to RFI bundle",
      selected_one: "{{count}} question in the RFI bundle",
      selected_other: "{{count}} questions in the RFI bundle",
      download: "Download signed RFI bundle",
      downloaded_one: "Signed RFI bundle downloaded -- {{count}} answer.",
      downloaded_other: "Signed RFI bundle downloaded -- {{count}} answers.",
      clear: "Clear",
      // B3 -- the record of what the bank answered. The supervisor's
      // workspace has shown all of this since A3; the surface that
      // wrote it showed none of it.
      answeredHeading_one: "{{count}} answered",
      answeredHeading_other: "{{count}} answered",
      answeredBy: "Answered by {{who}}, {{at}}",
      answererUnknown: "an unnamed user",
      answeredNoCitations: "Sent with no citations -- a written reply only.",
      answeredPack: "Download the pack that was sent",
    },
    card: {
      heading: "Provable assurance",
      body: "Answer a regulator question with cited, verifiable evidence.",
      open: "Answer the regulator",
      // B3 -- pluralised, because one waiting question and four are
      // different situations, and this card is where an approver finds
      // out which.
      waiting_one: "{{count}} supervisor question open",
      waiting_other: "{{count}} supervisor questions open",
      answer: "Answer \u2192",
    },
  },
  auth: {
    loginRetry: "Try signing in again",
    loginStalled:
      "The sign-in page did not load. That is usually a network hiccup between here and the identity provider -- try again, and tell your administrator if it keeps happening.",
    initializing: "Connecting to identity provider...",
    loadingProfile: "Loading your MARSAD profile...",
    profileErrorTitle: "Could not load your profile",
    profileErrorBody:
      "You are signed in, but MARSAD could not load your profile, so " +
      "it does not yet know which queues and controls to show you. " +
      "This is the MARSAD API, not sign-in.",
    profileErrorRateLimited:
      "You are signed in, but MARSAD is currently rate-limiting this " +
      "connection, so it could not load your profile. This is common " +
      "when many people share one office connection. Waiting a moment " +
      "and retrying usually clears it.",
    profileErrorRetry: "Try again",
    loginRequired: "Sign in to continue",
    loginRequiredBody:
      "MARSAD is protected by Keycloak SSO. Sign in with your bank " +
      "credentials to see your queues, draft CAPs, and review the " +
      "audit trail.",
    login: "Sign in",
    logout: "Sign out",
    // Keycloak's account page: change your password, see your sessions.
    yourAccount: "Your account",
    yourAccountTitle:
      "Change your password and see your sign-in sessions. Opens in a new tab.",
    // Round 17 K20 -- the link opened a new tab without saying so.
    opensNewTab: "(opens in a new tab)",
    // Keycloak's update-password step; you come straight back here.
    changePassword: "Change password",
    changePasswordTitle:
      "Set a new password. You confirm your current one first, then return here.",
    signedInAs: "Signed in as {{username}}",
    errorTitle: "Sign-in failed",
    errorBody:
      "MARSAD could not complete the Keycloak handshake. The error " +
      "message below comes from keycloak-js - common causes are an " +
      "expired authorisation code (refresh), CORS web-origin " +
      "mismatch on the realm, or the Keycloak service being " +
      "unreachable. Check the browser DevTools Network tab for the " +
      "failing /token request.",
    errorRetry: "Back to sign-in",
  },
  nav: {
    /* Round 10 A1 -- the floating queue card on a full-bleed route
       sits over part of the canvas, so it can be moved out of the way. */
    hideQueues: "Hide queues",
    showQueues: "Queues",
    /**
     * The breadcrumb. Same story as the tab strip: hard-coded English
     * in a record, which the copy rule cannot see because it reads
     * literals in JSX children.
     *
     * Page keys are the ROUTE with slashes doubled to underscores,
     * because a nested shape would need `foundation` to be both a
     * string and an object.
     */
    crumb: {
      module: {
        foundation: "Foundation",
        twin: "Operational Twin",
        framework: "Framework + CAP",
        board: "Board",
        admin: "Admin",
      },
      page: {
        foundation__admin: "Admin",
        foundation__admin__ibses: "{{serviceTermShort}} services",
        foundation__admin__services: "Services",
        foundation__admin__capabilities: "Capabilities",
        foundation__admin__vendors: "Vendors",
        foundation__admin__products: "Products",
        "foundation__admin__configuration-items": "Configuration items",
        foundation__admin__channels: "Channels",
        foundation__admin__journeys: "Customer journeys",
        foundation__mvb: "{{mvbTermShort}} declarations",
        twin__cascade: "Cascade",
        twin__channels: "Channel heatmap",
        twin__journeys: "Customer journeys",
        twin__scenarios: "Scenarios",
        "twin__test-runs": "Test runs",
        twin__drills: "Resilience drills",
        "twin__incident-reports": "Incident reports",
        "twin__war-room": "War-room",
        twin__fragility: "Fragility radar",
        twin__telemetry: "Live telemetry",
        "twin__capability-map": "Capability map",
        twin__landscape: "Landscape",
        /* Round 11 C6 -- one name for one page. The Twin overview
           tile said "KSA capability map" and the tab strip three
           inches above it said "Capability map". */
        twin__capmap: "Capability map",
        twin__constellation: "Constellation",
        /* Round 16 -- the route serves both registers, so a Critical
           Internal Function read "IBS detail" above its own name. */
        twin__ibs: "Register entry",
        "twin__mvb-candidates": "{{mvbTermShort}} candidates",
        "twin__field-exam": "Field-exam readiness",
        twin__service: "Service detail",
        twin__capability: "Capability detail",
        twin__vendor: "Vendor detail",
        twin__ci: "Configuration item detail",
        framework__assessments: "Assessments",
        framework__attestations: "Attestations",
        "framework__regulatory-change": "Regulatory change",
        framework__letters: "Regulator letters",
        framework__letters__new: "New letter intake",
        framework__queue: "Queue",
        framework__caps: "CAP",
        admin__users: "Users and roles",
        "admin__audit-events": "Audit events",
        admin__connectors: "Connectors",
        admin__settings: "Settings audit",
        admin__setup: "Set up",
      },
    },
    /**
     * The in-module tab strip.
     *
     * These were hard-coded English in ModuleTabs: the eslint rule that
     * keeps copy in the catalogue reads literals in JSX CHILDREN, and a
     * record of tab definitions is data. So the navigation on every
     * page of the product was never translatable, through two i18n
     * sweeps that both believed they had finished.
     *
     * Keyed on the ROUTE rather than nested by segment, because `twin`
     * is both a tab and the prefix of eleven others -- a nested shape
     * would need it to be a string and an object at once.
     */
    tab: {
      admin: "Overview",
      "admin__audit-events": "Audit events",
      admin__connectors: "Connectors",
      "admin__data-dictionary": "Data dictionary",
      "admin__data-import": "Data import",
      // Round 16 H30 -- carried: the examiner's access had no tab.
      "admin__examiner-grants": "Examiner access",
      "admin__service-accounts": "Service accounts",
      admin__settings: "Settings",
      admin__users: "Users and roles",
      foundation: "Overview",
      foundation__admin__capabilities: "Capabilities",
      foundation__admin__ibses: "{{serviceTermShort}} services",
      "foundation__admin__risk-appetite-statements": "Risk appetite",
      foundation__admin__services: "Services",
      foundation__mvb: "{{mvbTermShort}} declarations",
      framework: "Overview",
      framework__assessments: "Assessments",
      // Round 16 G8 -- the quarter's attestations, given a tab.
      framework__attestations: "Attestations",
      framework__letters: "Letters",
      framework__queue__approver: "Approver queue",
      framework__queue__preparer: "Preparer queue",
      framework__queue__reviewer: "Reviewer queue",
      admin__setup: "Set up",
      twin: "Overview",
      twin__capmap: "Capability map",
      twin__cascade: "Cascade",
      twin__constellation: "Constellation",
      /* Round 11 C4 -- reachable from the Twin at last. */
      "twin__field-exam": "Field exam",
      twin__drills: "Drills",
      "twin__incident-reports": "Incident reports",
      twin__fragility: "Fragility",
      "twin__ibs-admission": "{{serviceTermShort}} admission",
      twin__cifs: "Internal functions",
      twin__changes: "Changes",
      "twin__ibs-portfolio": "{{serviceTermShort}} portfolio",
      "twin__ibs-groups": "{{serviceTermShort}} groups",
      "twin__mvb-candidates": "{{mvbTermShort}} candidates",
      twin__scenarios: "Scenarios",
      "twin__test-runs": "Test runs",
      twin__telemetry: "Telemetry",
      "twin__war-room": "War room",
    },
    foundation: "Foundation",
    twin: "Operational Twin",
    framework: "Framework + CAP",
    admin: "Admin",
    gettingStarted: "Getting Started",
    executive: "Executive",
    // Navigation re-cut, option A -- the five questions a person arrives
    // with (docs/the-story.md, section 3). The short form is the
    // masthead's; the full question heads the row beneath it.
    question: {
      matters: "What matters",
      mattersFull: "What matters most here?",
      safe: "Safe right now",
      safeFull: "Is it safe right now?",
      owe: "What we owe",
      oweFull: "What do we owe, and how are we doing?",
      do: "My work",
      doFull: "What must I do?",
      prove: "What we can prove",
      proveFull: "What can we prove?",
      continuity: "Continuity posture",
      // Round 17 Q1 -- pages a journey needed and no question claimed.
      tolerances: "Tolerances",
      groupEditor: "Group editor",
      continuityPlans: "Continuity plans",
      raiseFinding: "Raise a finding",
      /* Round 20 U9 -- what is open now, newest first. */
      openFindings: "Open incidents, newest first",
      edgeDelays: "Edge delays",
      regulatoryChange: "Regulatory change",
      documentGap: "Document gap",
      // Round 18 Q4 -- pages that sat under no question.
      documentLocker: "Document locker",
      mvbDrill: "{{mvbTermShort}} validation drill",
      attestations: "Attestations",
      recommendations: "Recommendations",
      myWork: "My work",
      overview: "Overview",
      board: "Board",
      assurance: "Assurance",
      readiness: "Readiness",
      benchmark: "Benchmark",
    },
    /* Stitching review, 2026-10-02 -- the queue is the product's real
       spine and was in no navigation strip, only a glyph beside the
       bell. docs/the-story.md, section 1. */
    myWork: "My work",
  },
  onboarding: {
    // D10 -- the ladder said what each step was and never whose it
    // is, so an administrator could not tell a governance station was
    // somebody else's to do.
    ownedBy: "{{owner}}",
    kicker: "WELCOME TO MARSAD",
    heading: "Getting Started",
    intro:
      "A short ladder to stand up your operational-resilience programme. Each step ticks off automatically as you complete it.",
    /* Round 12 A7 -- four ladders, none of which referenced any other,
       and two of them saying "0 of 7" about different sevens. This is
       the governance seven: the owner chip on every card reads OpsRes
       owner, CRO / business owner or Compliance, and an administrator
       reading it card by card had to infer that not one of the seven
       was his. Say it once, at the top. */
    whose:
      "These seven are the governance ladder -- the resilience lead's work, with the CRO, the business owner and compliance signing. The plumbing that has to be true first is on the admin console, and both are slices of the same journey.",
    wholeJourney: "See the whole journey",
    adminLadder: "The plumbing, on the admin console",
    loading: "Loading your setup progress...",
    error: "Could not load onboarding status.",
    progress: "{{completed}} of {{total}} steps complete",
    allDone: "All set up!",
    done: "Done",
    teaser: {
      /* E1 -- the ladder the VIEWER owns, not the bank-wide one. */
      headingMine_one: "{{count}} step left before hand over",
      headingMine_other: "{{count}} steps left before hand over",
      heading: "New to MARSAD? Finish setting up.",
      cta: "Continue setup",
      // D1 -- the done state. It read like the unstarted one, which
      // tells the person who did the work that it did not register.
      handOver: "Your setup is done: hand over to the resilience lead.",
      handOverCta: "Show her stations",
      doneMine: "Your steps are done.",
      doneCta: "See the whole journey",
    },
    step: {
      ibs_register: {
        title: "Bring in your {{serviceTermShort}} register",
        intent:
          "Import or add the {{serviceTermPluralTitle}} your bank runs — the backbone everything else hangs off.",
        cta: "Add {{serviceTermShort}}",
      },
      ibs_admission: {
        title: "Identify your {{serviceTermShort}}",
        intent:
          "Run the admission methodology to admit each candidate as a recognised {{serviceTermTitle}}.",
        cta: "Review admissions",
      },
      // The onboarding ladder gained these two steps when E3 expanded
      // the journey spine, and their copy was never written -- so the
      // Getting Started page rendered the KEY at the reader and logged
      // a missing-key error each time. Found by the persona walk,
      // which visits /getting-started; the route-sweep drive did not.
      mvb_declared: {
        title: "Declare the {{mvbTermShort}} set formally",
        intent:
          "Turn the scored candidates into a signed declaration, so the {{mvbTerm}} is a governed decision rather than a working list.",
        cta: "Open {{mvbTermShort}} candidates",
      },
      impact_tolerances: {
        title: "Declare an {{toleranceTerm}} per {{serviceTermShort}}",
        intent:
          "State, for each admitted service, the longest outage the bank is prepared to accept before the harm is unacceptable. Two executives sign it.",
        // D10 -- the button said "Open the IBS register" and went to
        // the tolerance page, which A3 had already moved it to for good
        // reason: the register is a list with no tolerance on it and no
        // form. A label naming the wrong destination is worse than a
        // vague one, because the reader believes they clicked the wrong
        // thing.
        cta: "Open {{toleranceTermPlural}}",
      },
      mvb_declare: {
        title: "Declare your {{mvbTermTitle}}",
        intent:
          "Flag the {{serviceTermShortPlural}} that make up the bank's minimum viable operating core.",
        cta: "Open {{mvbTermShort}}",
      },
      mvb_signoff: {
        title: "Sign off the {{mvbTermShort}} declaration",
        intent: "Get the CRO + accountable executive to approve the declaration.",
        cta: "Sign off",
      },
      obligation_map: {
        title: "Map your regulatory obligations",
        intent:
          "Derive obligations from the framework controls so each one traces to real controls + assessment status.",
        cta: "Open Framework",
      },
      assessment_cycle: {
        title: "Open your first assessment cycle",
        intent:
          "Start a framework assessment cycle to judge your controls against the bar.",
        cta: "Start a cycle",
      },
      telemetry: {
        title: "Connect your telemetry",
        intent:
          "Wire up a connector (or let findings flow) so the operational picture stays live.",
        cta: "Connect a source",
      },
    },
  },
  viewRail: {
    dismiss: "Dismiss",
    foundation: {
      audience: "For data stewards + CMDB owners",
      question:
        "Is the bank's {{serviceTermShort}} + service catalogue complete and current?",
      cta: "Manage the {{serviceTermShort}} register",
    },
    twin: {
      audience: "For resilience + operations teams",
      question: "What's degraded right now, and how far does the blast radius reach?",
      cta: "Open the cascade",
    },
    framework: {
      audience: "For compliance + CAP owners",
      question: "Which controls are assessed against the bar, and what's still a gap?",
      cta: "Open assessments",
    },
    framework_assessments: {
      audience: "For compliance + CAP owners",
      question:
        "Graded a control as non-compliant? Raise a CAP from the drawer, or see every gap to act on.",
      cta: "Open what needs attention",
    },
  },
  notifications: {
    /**
     * G1 -- sentences composed by the SERVER and sent as keys, so the
     * bell reads in the reader's language rather than in the language
     * the API happens to be written in.
     *
     * Keyed by the notification's `kind`, which already exists and is
     * already unique per type -- deriving the key from it means the
     * two cannot drift. A kind with no entry falls back to the English
     * `title` the server also sends, which is what email and SMS
     * actually deliver.
     */
    /* Round 16 I11 -- what an answered ask now reads. */
    answered: {
      cifAdmissionSigned:
        "Critical internal function admission -- signed by {{who}}: {{name}}",
      ibsAdmissionSigned:
        "{{serviceTermShort}} admission -- signed by {{who}}: {{name}}",
      cifNowWithCio: "Critical internal function admission now with the CIO: {{name}}",
    },
    kinds: {
      /* Round 16 H30 -- a supervisor's question reaches the seat that
         answers it. */
      "examiner.rfi.asked": "A supervisor has asked the bank a question",
      /* Round 10 C1 -- the accountable owner wrote into a live critical
         incident and no notification row was written to anybody. */
      "finding.comment.added": "{{who}} added to an incident record: {{finding}}",
      /* Round 20 U1 -- a connector's critical finding told nobody for 52 minutes. */
      "finding.critical.raised_one":
        "Critical finding on {{on}}: {{finding}} -- reaches {{count}} admitted or minimum-viable service",
      "finding.critical.raised_other":
        "Critical finding on {{on}}: {{finding}} -- reaches {{count}} admitted or minimum-viable services",
      /* Connector runs take minutes; the person who pressed Run now
         is told when one ends, and how. */
      "connector.run.finished":
        "{{label}}: run finished -- {{inserted}} new, {{updated}} updated, {{skipped}} skipped",
      "connector.run.failed": "{{label}}: run failed",
      "ibs.admission.awaiting_cro":
        "{{serviceTermShort}} admission awaiting CRO sign-off: {{name}}",
      "ibs.admission.awaiting_business_owner":
        "{{serviceTermShort}} admission awaiting business-owner sign-off: {{name}}",
      /* P429.1 -- a Critical Internal Function is signed by the CRO
         and the CIO (Route 2); the Board notes the register. */
      "cif.admission.awaiting_cro":
        "Critical Internal Function awaiting CRO sign-off: {{name}}",
      "cif.admission.awaiting_cio":
        "Critical Internal Function awaiting CIO sign-off: {{name}}",
      /* Round 16 N3 -- the CIO is forewarned at the challenge, and
         the function's owner is told at each leg and at admission.
         Nothing reached him at or after the admission. */
      "cif.admission.coming_cio":
        "Coming to you after the CRO: {{name}}, a Critical Internal Function",
      "cif.admission.owner_challenged":
        "{{name}} has been challenged by {{reviewer}}. It now waits on the CRO, then the CIO.",
      "cif.admission.owner_cro_signed":
        "The CRO has signed {{name}}. It now waits on the CIO.",
      "cif.admission.owner_cio_signed":
        "The CIO has signed {{name}}. It now waits on the CRO.",
      /* Round 17 N9 -- the next signer by name where one person holds
         the seat; the seat alone otherwise. */
      /* Round 19 N14 -- the treasurer's bell named no one before the CRO
         acted. */
      "cif.admission.owner_challenged_named":
        "{{name}} has been challenged by {{reviewer}}. It now waits on {{who}} (CRO), then the CIO.",
      "cif.admission.owner_cro_signed_named":
        "The CRO has signed {{name}}. It now waits on {{who}} (CIO).",
      "cif.admission.owner_cio_signed_named":
        "The CIO has signed {{name}}. It now waits on {{who}} (CRO).",
      "cif.admission.owner_admitted":
        "{{name}} is admitted as a Critical Internal Function. The CRO and the CIO have signed.",
      "mvb.declaration.awaiting_cro":
        "{{mvbTermShort}} declaration awaiting CRO sign-off: {{name}}",
      "mvb.declaration.awaiting_ae":
        "{{mvbTermShort}} declaration awaiting Accountable-Executive sign-off: {{name}}",
      "mvb.pack.awaiting_cro": "{{mvbTermShort}} pack awaiting CRO sign-off: {{name}}",
      "mvb.target.awaiting_signatures":
        "{{mvbTermShort}} target v{{version}} awaiting sign-off by the CRO and the Head of Operational Resilience",
      "mvb.pack.awaiting_ae":
        "{{mvbTermShort}} pack awaiting Accountable-Executive sign-off: {{name}}",
      "service.classification.awaiting_cro":
        "Service classification awaiting CRO sign-off: {{name}}",
      "cap.transition.submit":
        "CAP submitted for review: {{title}} -- now with the {{position}}",
      // Round 16 G9 -- like its siblings, and like the email it travels with.
      "cap.transition.approve": "CAP approved: {{title}} -- now with the {{position}}",
      /* Round 13 E5 -- the person who RAISED the plan. The fan-out
         tells whoever it now waits on, and a closed plan waits on
         nobody, so the preparer who raised it heard nothing at all
         while the approver was told everyone had been. */
      "cap.closed.approved":
        "The plan you raised has been approved and closed: {{title}}",
      "cap.transition.send_back":
        "CAP sent back: {{title}} -- now with the {{position}}",
      "cap.transition.withdraw":
        "CAP withdrawn: {{title}} -- now with the {{position}}",
      // Round 18 G24 -- the assessment hand-off, the one that was silent.
      "assessment.awaiting": "{{name}} is waiting for your review",
      "assessment.sent_back": "{{name}} was sent back by {{by}}",
      "service.classification.awaiting_service_owner":
        "Service classification awaiting service-owner sign-off: {{name}}",
      // D5 -- a declaration sat waiting on the CRO with nothing said.
      "impact_tolerance.awaiting_cro":
        "{{toleranceTerm, capitalize}} awaiting CRO sign-off: {{name}}",
      // Round 16 G9 -- the CRO's signature passed it to the owner.
      "impact_tolerance.awaiting_owner":
        "The CRO has signed your {{hours}}h for {{name}}; it now needs yours.",
      /* Round 19 G40 -- the bell named the rule, not who raised what. */
      "change.raised.new_product":
        "{{who}} raised a significant new product: {{title}}",
      "change.raised.acquisition":
        "{{who}} raised a significant acquisition: {{title}}",
      "change.raised.new_market": "{{who}} raised a significant new market: {{title}}",
      "change.raised.technology_system":
        "{{who}} raised a significant technology change: {{title}}",
      "change.raised.process_change":
        "{{who}} raised a significant process change: {{title}}",
      "change.raised.vendor_change":
        "{{who}} raised a significant vendor change: {{title}}",
      "change.raised.facility_change":
        "{{who}} raised a significant facility change: {{title}}",
      "change.raised.regulatory_rebase":
        "{{who}} raised a significant regulatory change: {{title}}",
      "change.raised.other": "{{who}} raised a significant change: {{title}}",
      /* Round 19 N13 -- an assessment handed to the preparers to draft. */
      "regulatory_change.handoff": "{{who}} asks you to draft the plan for: {{title}}",
      // Round 19 G36 -- a significant change reached the reader's service.
      "change.reached_your_service":
        "A significant change reached your service {{name}}: map it again, review its tolerance, test it again.",
      // Round 19 G32 -- a declaration somebody else made, named as theirs.
      "impact_tolerance.awaiting_owner_declared_by":
        "{{declarer}} declared {{hours}}h for {{name}}; the CRO has signed; it needs yours.",
      "impact_tolerance.awaiting_owner_unnamed":
        "The CRO has signed the {{hours}}h declared for {{name}}; it now needs yours.",
    },
    bellLabel: "Notifications",
    heading: "Notifications",
    markAll: "Mark all read",
    empty: "You're all caught up.",
    digestLabel: "Email digest",
    digestOff: "Per event",
    digestDaily: "Daily",
    digestWeekly: "Weekly",
    acknowledge: "Acknowledge",
  },
  dossier: {
    heading: "Audit dossier",
    body:
      // C6 -- "5 chain timelines" was true when the dossier proved
      // five. P291 made the proof derive from the chain registry, so
      // it has covered every chain since; the card kept quoting a
      // figure from before that, and understating what the artefact
      // does is the same defect as overstating it.
      "One-button regulator-ready signed zip. Every hash chain, " +
      "re-walked from genesis, plus workflow history, connector " +
      "runs, an integrity proof and an " +
      "embedded verify-dossier.py for offline verification. " +
      "Admin-only. Build takes 30-60 seconds for a real dataset.",
    sinceLabel: "Audit window since (UTC)",
    untilLabel: "Audit window until (UTC)",
    asOfLabel: "As-of (optional)",
    mvbOnlyLabel: "{{mvbTermShort}}-only scope",
    generateButton: "Generate dossier",
    generatingButton: "Generating dossier...",
    generatingHint:
      "Walking every chain + chain-verify gate + rendering PDFs " +
      "(plus JSON sidecars) -- 30-60s for a real dataset.",
    downloadedOk: "Downloaded {{filename}}",
    errorGeneric: "Couldn't generate dossier: {{detail}}",
    errorChainBroken:
      "Chain integrity check failed: {{detail}} " +
      "Investigate via POST /v1/admin/chains/{slug}/verify and " +
      "treat as P0. Do not retry.",
  },
  modules: {
    journey: {
      rubric: "Admin · Set up",
      heading: "Setting up the bank",
      subtitle:
        "Where the bank's data starts, what feeds what, and where it flows " +
        "out. Nine stages in four tempos -- each step's status is derived " +
        "live from the estate, so this can't drift from reality.",
      // D1 -- scoping the ladder to one seat, so a hand-over can point
      // at what the next person owns rather than at all twenty steps.
      ownerFilter: "Showing the stations owned by the {{owner}}.",
      ownerFilterClear: "Show every station",
      loading: "Reading the estate...",
      error: "Could not load the journey.",
      go: "Go →",
      view: "View →",
      countOf: "{{count}} of {{total}}",
      /* Round 18 P11 -- the denominator, named: each step counts
         against a different population, and "7 of 11" said none. */
      countOfPopulation: {
        connectors: "{{count}} of {{total}} enabled",
        data_baseline: "{{count}} of {{total}} configuration items linked",
        /* Round 20 P23 -- the count it names, and the composed baseline
           beside it: "36 of 100 configuration items linked" was the
           composite score wearing a count's label. */
        data_baselineComposed:
          "{{linked}} of {{of}} configuration items linked ({{pct}}%); baseline {{baseline}}% composed",
        ibs_derive: "{{count}} of {{total}} listed",
        ibs_challenge: "{{count}} of {{total}} assessed",
        ibs_admission: "{{count}} of {{total}} listed",
        cif_register: "{{count}} of {{total}} listed",
        mvb_score: "{{count}} of {{total}} candidates",
        tolerance_declared: "{{count}} of {{total}} admitted",
        impact_tolerances: "{{count}} of {{total}} admitted",
      },
      // Round 17 P1 -- the register station: listed, and admitted.
      listedAdmitted: "{{count}} listed, {{admitted}} admitted",
      derivedJudged: "{{count}} derived, {{judged}} judged",
      /* Round 19 B31 -- the connectors station's own cut. */
      switchedOnVerified: "{{on}} switched on, {{count}} verified",
      switchedOffUnrun_one: "{{count}} switched off without a run",
      switchedOffUnrun_other: "{{count}} switched off without a run",
      /* Round 20 W3 -- before scoring: owners named with no sign-in
         address, on whom the owner's leg will stop. */
      ownersUnbound_one:
        "Owner named, not yet a seat: {{names}} owns a listed service and has no sign-in address, so admission and its tolerances will stop at the owner's signature.",
      ownersUnbound_other:
        "Owners named, not yet seats: {{count}} owners ({{names}}) own listed services and have no sign-in address, so admission and their tolerances will stop at each owner's signature.",
      ownersUnboundAndMore_one: "{{names}} and {{count}} more",
      ownersUnboundAndMore_other: "{{names}} and {{count}} more",
      ownersUnboundCta: "Record their addresses in the register",
      // Round 20 V1.
      notIndependent_one:
        "{{count}} decision was challenged and signed by the same person: not independent, so not counted.",
      notIndependent_other:
        "{{count}} decisions were challenged and signed by the same person: not independent, so not counted.",
      blockedBy: "Do {{step}} first",
      /* ROUND 15 H6 -- under "Only my steps" a band with none of the
         reader's steps rendered as a bare heading. */
      tempoNoneMine: "Nothing in this part is yours.",
      tempo: {
        setup: "Set up",
        govern: "Govern",
        operate: "Operate + comply",
        prove: "Prove",
      },
      status: {
        done: "done",
        partial: "partial",
        todo: "to do",
        blocked: "blocked",
      },
      owner: {
        admin: "Platform admin",
        steward: "Data steward",
        opsres: "OpsRes owner",
        cro: "CRO / business owner",
        reviewer: "Second-line reviewer",
        compliance: "Compliance",
        ops: "Ops (mostly automated)",
        approver: "Approver / CRO",
        /* ROUND 13 H, thirteenth round -- the ladder had no station
           owned by the person accountable for a business service. */
        service_owner: "Service owner",
      },
      waitsOn: "Waits on {{owner}}",
      /* Round 16 N1 -- Route 2's seats sign in turn, and the station
         said "CRO / business owner" at every moment and to every seat. */
      ownerCroThenCio: "CRO, then CIO",
      waitsOnYou: "Waits on you",
      // Round 18 N11 -- a ceremony station with no leg open.
      nothingWaits: "Nothing waits on a signature",
      // Round 18 G23 -- the seat is empty, and said so.
      seatUnheld: "the {{role}} (nobody signed in holds that seat yet)",
      legThen: ", then ",
      // Round 19 G42 -- the owner's leg is each record's own owner.
      eachOwner: "each service's business owner",
      // Round 17 N9 -- which function waits on which leg.
      functionWaits: "{{name}}: waits on {{who}}",
      /* Round 12 A7 -- the four ladders in one room. This is the whole
         one; the other two are slices of it, and none of the four
         referenced any other, so a reader meeting a second had no way
         to tell whether it was the same work counted again. */
      slices: "Two shorter ladders are slices of this one:",
      sliceGovernance: "Getting Started, the governance seven",
      slicePlumbing: "Set up this bank, the plumbing",
      myStepsAll: "All steps",
      myStepsMine: "Only my steps",
      myStepsNone:
        "None of these steps are yours to do. The list above shows who each one is waiting on.",
      stage: {
        "0": "Deploy & identity",
        "1": "SSoT estate load",
        "2": "Topology & architecture",
        "3": "{{serviceTermShort}} identification & admission",
        "4": "{{mvbTermShort}} selection & declaration",
        "5": "Calibration & appetite",
        "6": "Compliance spine",
        "7": "Live twin",
        "8": "Evidence out",
      },
      step: {
        bank_profile: {
          title: "Bank profile & branding",
          feeds: "Feeds branding on every regulator-facing PDF.",
        },
        connectors: {
          title: "Connectors configured",
          feeds: "Feeds live findings + telemetry into the twin.",
        },
        services: {
          title: "Services loaded",
          feeds: "Feeds everything downstream -- the SSoT anchor.",
        },
        capabilities: {
          title: "Capabilities loaded",
          feeds: "Feeds the capability map + capability-{{serviceTermShort}} links.",
        },
        ibs_service_links: {
          title: "Topology edges ({{serviceTermShort}} ↔ service)",
          feeds: "Feeds the blast graph, cascade + war-room propagation.",
        },
        journeys: {
          title: "Customer journeys",
          feeds: "Feeds per-channel failing-step inference on the cascade.",
        },
        data_baseline: {
          title: "Data baseline established",
          feeds:
            "Feeds the resilience score's confidence -- weak source data caps the tier.",
        },
        ibs_register: {
          title: "{{serviceTermShort}} register",
          feeds: "The register everything downstream is keyed on.",
        },
        ibs_derive: {
          title: "Admission assessed",
          feeds: "Scores each candidate against the admission methodology.",
        },
        ibs_challenge: {
          title: "Admission challenged",
          feeds: "Second line reviews the assessment before anyone signs it.",
        },
        ibs_admission: {
          title: "Admission signed off",
          feeds: "Governs which services count as {{serviceTermPlural}}.",
        },
        cif_register: {
          title: "Critical Internal Functions admitted",
          feeds:
            "The functions the bank performs for itself, admitted by the CRO and the CIO once the register above is stable.",
        },
        mvb_score: {
          title: "{{mvbTermShort}} candidates scored",
          feeds: "Ranks what the bank must be able to keep running.",
        },
        mvb_declared: {
          title: "{{mvbTermShort}} flagged",
          feeds:
            "Feeds {{mvbTermShort}} weighting in cascade / fragility / war-room / board.",
        },
        mvb_signoff: {
          title: "{{mvbTermShort}} declaration approved",
          feeds: "The board-signed minimum-viable-bank scope.",
        },
        /* ROUND 13 H -- his half of the ceremony, before hers.
           Declaring a tolerance and having it signed are two acts by
           two hands, and the collapsed line could not say which hand
           the work was waiting on -- the distinction round 12 drew
           when it split the admission ceremony into four stations. */
        tolerance_declared: {
          title:
            "{{toleranceTermPlural, capitalize}} declared by the service owner (admitted {{serviceTermShortPlural}})",
          feeds: "The figure the CRO is asked to sign, and the breach clock reads.",
        },
        impact_tolerances: {
          /* B1 -- "set" is what the portfolio counts (a number in the
             column, 12 of 12); this station counts SIGNED tolerances
             among ADMITTED IBSes, which is a smaller population
             answering a different question. Reading "1 of 5" beside
             "12 of 12" looked like a contradiction because neither
             said which it was counting. */
          title:
            "{{toleranceTermPlural, capitalize}} signed off (admitted {{serviceTermShortPlural}})",
          feeds: "Feeds war-room verdicts + the breach clock.",
        },
        risk_appetite: {
          title: "Risk-appetite statements",
          feeds: "The declared appetite the posture is measured against.",
        },
        obligation_map: {
          title: "Obligations mapped",
          feeds: "Feeds obligation traceability + assurance answers.",
        },
        assessment_cycle: {
          title: "Assessment cycle open",
          feeds: "Feeds maturity, recommendations + the compliance spine.",
        },
        caps: {
          title: "Corrective action plans",
          feeds: "The remediation workflow closing the gaps.",
        },
        telemetry: {
          title: "Telemetry flowing",
          feeds: "Feeds the live service-health shadow into resilience.",
        },
        resilience: {
          title: "Resilience derived",
          feeds: "The sealed R/A/G posture the cascade + board report.",
        },
        open_findings: {
          title: "Findings triaged",
          feeds: "Drives the live posture + the recommendation engine.",
        },
        incidents: {
          title: "Raise an incident",
          feeds:
            "Judged against tolerance in the war room; the regulator report drafts itself from it.",
        },
        evidence_out: {
          title: "Evidence out (board / dossier / assurance)",
          feeds: "What the board reads + the examiner verifies offline.",
        },
      },
    },
    board: {
      // Round 18 Q4 -- the words on this page, defined.
      glossary: "What these terms mean",
      /* Round 12 B1 -- the board chip and card printed the raw RAG
         value, so a fourth state would read "not_assessed" at a board
         meeting. And the fourth state is the point of the workstream:
         a bank that has assessed nothing is not green. */
      ragWord: {
        red: "Red",
        amber: "Amber",
        green: "Green",
        not_assessed: "Not yet assessed",
      },
      heading: "Board snapshot",
      // C4 -- this printed the column name and a raw ISO timestamp
      // with microseconds at a board reader.
      asOf: "As at {{value}}",
      // Round 9 B1. This sentence was written when only the sealed
      // resilience counts replayed, and it named impacted services,
      // revenue at risk and the MVB intersection as the figures that do
      // NOT move. Round 8 made the cascade's findings replay, so those
      // three now move -- and the sentence kept denying it, directly
      // above the three figures that had just changed.
      //
      // The list below is MEASURED, by composing the board with and
      // without an as_of and diffing every field, not reasoned about.
      // Impact tolerance is the one headline that genuinely holds,
      // because it reads live telemetry, and the old sentence did not
      // mention it at all.
      asOfScope:
        "Rewound: impacted services, products and revenue at risk, the {{mvbTermShort}} intersection, the sealed resilience counts and the hot spots all read that date. {{toleranceTerm, capitalize}} and the groups are measured from live state, so they show today's position whatever date you pick.",
      // C4 -- the download finished in silence.
      downloadDone: "Board snapshot downloaded.",
      downloadFailed: "The board snapshot could not be downloaded.",
      loading: "Loading board snapshot...",
      /* Round 13 B2 -- the audience widened, so the sentence naming it
         had to. It named two seats and the Head of Operational
         Resilience, who owns the readiness score this page reports, was
         not one of them. */
      forbidden:
        "This view is for the approver and reviewer seats, the Head of Operational Resilience, and admins. Ask your tenant admin to grant one of those in Keycloak.",
      // Round 14 B4 -- the board reads for more seats than may issue it.
      pdfIsApprovers: "Issuing the signed PDF is an approver's act.",
      downloadButton: "Download PDF",
      downloadInProgress: "Rendering PDF...",
      kpi: {
        // Round 14 G -- the set the revenue and MVB figures are priced over.
        // Round 20 P19 -- the population named: the register.
        ibsImpacted:
          "Admitted {{serviceTermShortPlural}} impacted, including downstream / total",
        productsAtRisk: "Products at risk",
        revenueAnnual: "Revenue at risk / yr",
        revenueDaily: "Revenue at risk / day",
      },
      headline: {
        weightedSubline: "by revenue: {{weighted}}",
        // C4 -- the RED chip was inert; this opens the finding behind it.
        // C3 -- somewhere honest to go when no single finding is named.
        openRows: "See the impacted services",
        openWorst: "See what makes it that",
        /* Round 10 F3 -- three IBS totals inside four lines, none of
           which said what it counted: this one counts the minimum
           viable bank, the one below counts the IBSes that have a
           declared tolerance, and the cascade counts every entity a
           finding reaches. Each denominator names its own population
           now. */
        // Round 20 P19 -- the flagged admitted set, then the declaration's.
        mvbIntersection:
          "{{mvbTermShort}}: {{impacted}} of the {{total}} admitted minimum-viable services are impacted ({{pct}}%)",
        mvbBreached: "{{breached}} of them breached",
        mvbFrozenSet:
          "the declaration's frozen set holds {{scoped}}, {{admitted}} of them admitted",
        /* Round 17 P1 -- the register's population, named: admitted
           services, as home and the posture count them. */
        toleranceBreached:
          "{{toleranceTerm, capitalize}}: {{breached}} of the {{assessed}} admitted services with a declared tolerance are breached ({{mvb}} of them minimum-viable)",

        toleranceOverrun: "worst overrun {{hours}}h ({{ibs}})",

        toleranceHeadroom: "closest to breaching {{ibs}}, {{hours}}h left",

        toleranceCif:
          "Critical Internal Functions in the register, counted apart: {{breached}} of {{assessed}} breached",
        // Round 17 P1 -- a breach on a candidate is said, not mixed in.
        // Round 18 P5 -- the figure is those carrying a tolerance; the
        // posture puts more under assessment, and the line says which.
        toleranceCandidates:
          "under assessment, not in the register: {{breached}} of the {{assessed}} with a declared tolerance breached",
        toleranceCandidatesOf:
          "under assessment, not in the register: {{breached}} of the {{assessed}} with a declared tolerance breached ({{total}} under assessment)",
        // P429.6 -- beside the IBS count, never inside it.
        cifCounted:
          "Critical Internal Functions, counted apart: {{impacted}} of {{total}} impacted, including downstream",
        // Round 20 P19 -- functions by population, each named.
        cifAdmitted:
          "Critical Internal Functions, counted apart: {{total}} admitted, {{impacted}} impacted",
        cifAdmittedBreached: "{{breached}} breached",
        cifUnderAssessment:
          "{{count}} under assessment, {{breach}} with a breach finding",
        /* P428.3 -- the group grain. A group reads its worst member's
           colour; the ungrouped count is printed so the list is never
           taken for complete. */
        groupsHeading: "Groups",
        groupLine: "{{breached}} of {{members}} in breach",
        groupTolerance: "tolerance {{hours}}h, from {{from}}",
        groupsUngrouped_one: "{{count}} admitted service is in no group",
        groupsUngrouped_other: "{{count}} admitted services are in no group",
        groupsOpen: "Open the groups",
      },
      outcomeRag: {
        heading: "Outcome RAG",
        // Round 14 G -- direct findings only; the RAG is judged from them.
        impactedRatio: "{{impacted}} of {{total}} services with a finding of their own",
        // P429.6 -- they colour the quadrant, and are counted apart.
        cifRatio:
          "and {{impacted}} of {{total}} Critical Internal Functions with a finding of their own",
        // Round 18 P5 -- a row that holds anything under assessment says
        // how many are admitted, beside a headline that counts the
        // register only.
        impactedRatioCut:
          "{{impacted}} of {{total}} services with a finding of their own ({{admitted}} admitted, {{assessing}} under assessment)",
        // Round 20 P19 -- an outcome holding only functions, said first.
        cifRatioOnly:
          "{{impacted}} of {{total}} Critical Internal Functions with a finding of their own",
        cifRatioCutOnly:
          "{{impacted}} of {{total}} Critical Internal Functions with a finding of their own ({{admitted}} admitted, {{assessing}} under assessment)",
        noMember: "No member",
        noMemberBody:
          "No service or function, admitted or being assessed, sits in this outcome.",
        findingOnCandidateFunction: "on a function under assessment",
        findingOnCandidateService: "on a service under assessment",
        findingOnAdmittedFunction: "on an admitted function",
        cifRatioCut:
          "and {{impacted}} of {{total}} Critical Internal Functions with a finding of their own ({{admitted}} admitted, {{assessing}} under assessment)",
        revenueShare: "{{pct}}% of revenue at risk",
      },
      compare: {
        /* Round 11 F4 -- the same ISO field the as-of control uses,
           because a native date input follows the BROWSER's locale and
           ignores the element's `lang`. Stated, not implied. */
        format: "YYYY-MM-DD",
        formatHint: "Type the date as YYYY-MM-DD, e.g. 2026-08-30.",
        apply: "Compare",
        label: "Compared with",
        clear: "Clear",
        red: "{{serviceTermShortPlural}} sealed red",
        amber: "{{serviceTermShortPlural}} sealed amber",
        nothingSealed:
          "No sealed board state before {{when}} -- there is nothing to compare against.",
        note: "Sealed resilience only. Findings and revenue are current-state and do not move with the date.",
      },
      hotSpots: {
        heading: "Top hot spots",
        empty: "All clear -- no impacted {{serviceTermShortPlural}} right now.",
        // Round 9 B1: "right now" is false under a rewound banner.
        emptyAsOf: "All clear -- no impacted {{serviceTermShortPlural}} at that date.",
        // Round 14 B2: no hot spot on an estate nobody has assessed is an
        // absence, not a clean bill of health.
        emptyNotAssessed:
          "No hot spots, but nothing has been assessed yet -- this is not a clean bill of health.",
        col: {
          // Round 16 L3 -- a hot spot can be a Critical Internal
          // Function; the column read "IBS" over Treasury settlement.
          ibs: "{{serviceTermShort}} or function",
          outcome: "Outcome",
          severity: "Severity",
          open: "Open",
          revenue: "Revenue / yr",
          journeys: "Journeys",
        },
      },
      mvb: {
        empty: "No active {{mvbTermShort}} declaration.",
        readiness: "Readiness: {{value}}",
        scoped_one: "{{count}} {{serviceTermShort}} scoped",
        scoped_other: "{{count}} {{serviceTermShortPlural}} scoped",
        // Round 17 P1 -- the frozen set, and how many of it are admitted:
        // the tile read "6 scoped" beside home's "3 minimum-viable".
        scopedAdmitted:
          "{{count}} in the declaration's frozen set, {{admitted}} of them admitted {{serviceTermShortPlural}}",
        reviewDue_one: "Review due in {{count}} day",
        reviewDue_other: "Review due in {{count}} days",
        // Round 20 -- a declaration past its review read "Review due in
        // -40 days". Overdue is said as overdue.
        reviewDueToday: "Review due today",
        reviewOverdue_one: "Review overdue by {{count}} day",
        reviewOverdue_other: "Review overdue by {{count}} days",
        reviewDueUnknown: "Review date not set",
        // Round 20 G43
        reviewBeforeEffective:
          "The review date is before the declaration came into force.",
        signoffComplete: "Dual signoff complete",
        signoffIncomplete: "Signoff incomplete",
      },
      framework: {
        heading: "Framework assessments",
        empty: "No assessments yet.",
        cyclesOpen_one: "{{count}} cycle open",
        cyclesOpen_other: "{{count}} cycles open",
        approvedRatio: "{{approved}} of {{total}} approved ({{pct}}%)",
        inReview_one: "{{count}} in review",
        inReview_other: "{{count}} in review",
        draft_one: "{{count}} draft",
        draft_other: "{{count}} draft",
      },
      card: {
        heading: "Board snapshot",
        loading: "Loading board posture...",
        error: "Couldn't load the board snapshot.",
        summary: "{{impacted}} of {{total}} {{serviceTermShortPlural}} impacted",
        revenue: "{{annual}} annual at risk",
        open: "Open board →",
      },
    },
    foundation: {
      /* P439.3 -- importing the bank's architecture model (ArchiMate
         exchange file), reviewed before anything is written. */
      eaImport: {
        heading: "Architecture model import",
        standfirst:
          "Upload the architecture model your EA tool exports in the ArchiMate exchange format. Each element is matched to a record MARSAD holds, each relationship becomes a suggested change, and nothing is written until you commit. Your decisions are kept, so the next export of the same model does not ask again.",
        upload: "Choose an exchange file (.xml)",
        empty: "No model has been imported yet.",
        summary:
          "{{model}}: {{elements}} elements, {{relationships}} relationships. File SHA-256 {{sha}}...",
        runStatus: {
          staged: "in review",
          committed: "committed",
          discarded: "discarded",
        },
        identities: {
          heading: "What the elements are",
          settled_one: "{{count}} element matched a record by its code.",
          settled_other: "{{count}} elements matched records by their codes.",
          unmapped_one: "{{count}} element is of a type MARSAD does not model.",
          unmapped_other: "{{count}} elements are of types MARSAD does not model.",
          byName: "matched by name; confirm it is the same thing",
          ambiguous_one:
            "{{count}} record has this name; name it in the register first",
          ambiguous_other:
            "{{count}} records have this name; tell them apart in the register first",
          canCreate: "not in MARSAD; can be created from the model",
          raiseIbs:
            "not in MARSAD; {{serviceTermShortA}} is raised through its admission, not from a model",
          fromCmdb: "not in MARSAD; services come from the CMDB",
        },
        kind: {
          capability: "Capability",
          ibs_service: "{{serviceTermShort}}",
          service: "Service",
          configuration_item: "Configuration item",
          none: "Not modelled",
        },
        edges: {
          heading: "What the model would change",
          present_one: "{{count}} edge already matches the register.",
          present_other: "{{count}} edges already match the register.",
          showPresent: "Show edges already in the register",
          needsIdentity: "waits for a name match to be confirmed",
          cmdbOwned_one:
            "{{count}} relationship between services is the CMDB's to state and is not written from the model.",
          cmdbOwned_other:
            "{{count}} relationships between services are the CMDB's to state and are not written from the model.",
        },
        edgeKind: {
          capability_parent: "Capability tree",
          capability_dependency: "Capability dependencies",
          capability_ibs: "Capabilities behind each {{serviceTermShort}}",
          ibs_service: "Services behind each {{serviceTermShort}}",
        },
        edgeSentence: {
          capability_parent: "{{left}} sits under {{right}}",
          capability_dependency: "{{left}} depends on {{right}}",
          capability_ibs: "{{left}} supports {{right}}",
          ibs_service: "{{left}} rests on {{right}}",
        },
        status: {
          new: "new",
          present: "in the register",
          rejected_before: "rejected before",
          absent: "no longer in the model",
          cmdb_owned: "the CMDB's",
          unresolved: "an end is not in MARSAD",
        },
        decision: {
          confirmed: "confirmed",
          rejected: "rejected",
          create: "to be created",
          created: "created",
          accepted: "accepted",
          pending: "undecided",
        },
        confirm: "Same thing",
        notThis: "Not this",
        create: "Create it",
        accept: "Accept",
        reject: "Reject",
        markRemove: "Remove it",
        keep: "Keep it",
        acceptAll_one: "Accept {{count}}",
        acceptAll_other: "Accept all {{count}}",
        preview: {
          heading: "Before you commit",
          write_one: "{{count}} accepted change will be written.",
          write_other: "{{count}} accepted changes will be written.",
          create_one: "{{count}} capability will be created.",
          create_other: "{{count}} capabilities will be created.",
          remove_one:
            "Also remove the {{count}} edge you marked, which an earlier import wrote and the model no longer holds. Edges made by hand or by a connector are never removed.",
          remove_other:
            "Also remove the {{count}} edges you marked, which an earlier import wrote and the model no longer holds. Edges made by hand or by a connector are never removed.",
        },
        commit: "Commit",
        discard: "Discard this import",
        committed:
          "Committed: {{written}} written, {{waiting}} waiting for a name match to be confirmed. Blast radius and resilience are being worked out again.",
        closed: {
          committed: "This import has been committed.",
          discarded: "This import was discarded.",
        },
      },
      floorList: {
        added: "“{{label}}” added to the floor list.",
        /* Round 16 O6 -- taking an item or an answer back. */
        clear: "Clear the answer",
        remove: "Remove",
        cancel: "Cancel",
        removeAnswered: "An answered item cannot be removed; clear its answer first.",
        confirmClear:
          "Clear the answer to “{{label}}”? The item stays on the list, unanswered, and the clearing is recorded.",
        confirmRemove:
          "Remove “{{label}}” from the floor list? The removal is recorded.",
        cleared: "The answer to “{{label}}” is cleared.",
        removed: "“{{label}}” is removed from the floor list.",
        heading: "Regulator floor list",
        standfirst:
          "Items your regulator pre-admits, and the bank's answer to each: admitted through a service on the register, or opted out with a reason the CRO approves. The list starts empty -- it is entered from the regulator's own letters, not supplied by the product.",
        door: {
          open: "{{regime}} admits Critical Internal Functions beside customer and State services.",
          closed:
            "{{regime}} does not present Critical Internal Functions as identified services; they stay on the bank's own register.",
          unconfirmed:
            "Whether {{regime}} admits Critical Internal Functions is not confirmed, so they are not counted as identified services.",
        },
        empty: "No floor list is recorded for this regulator.",
        class: {
          customer: "Customer service",
          state: "State service",
          internal: "Critical Internal Function",
        },
        state: {
          answered: "answered",
          pending: "not yet in force",
          unanswered: "unanswered",
        },
        admittedThrough: "Admitted through {{service}}.",
        notYetAdmitted: "That service is not yet admitted.",
        optedOut: "Opted out: {{reason}}",
        approvedBy: "Approved by {{who}}.",
        awaitingApproval: "Awaiting the CRO's approval.",
        answerLabel: "Answer",
        choice: {
          admitted: "Admit through a service",
          opted_out: "Opt out",
        },
        serviceLabel: "Service that answers the item",
        pickService: "Choose a service of the same class",
        reasonLabel: "Why the bank opts out",
        save: "Record answer",
        approve: "Approve opt-out",
        addHeading: "Add an item from the regulator's list",
        field: {
          label: "Item",
          key: "Short key",
          class: "Class",
          citation: "Where the regulator says so",
        },
        add: "Add item",
      },
      // P128: module-home copy routed through i18n so it translates
      // (the inline tile strings were bypassing the catalogue).
      home: {
        rubric: "Framing · Foundation",
        heading: "Foundation",
        positioning:
          "Your bank's resilience model. The {{serviceTermShort}} catalogue, services, " +
          "capabilities, and supporting registers -- the data the Twin " +
          "reads from and the Framework references when drafting CAPs.",
        signals: {
          loading: "Reading catalogue counts...",
          // Stitching review 5.4 -- the posture's register: admitted.
          // Round 18 P11 -- said as what it counts: admitted.
          ibses: "{{serviceTermShortPlural}} admitted",
          notYours:
            "Register counts are shown to the estate owner. The catalogue itself is on the pages below.",
          mvbFlagged: "{{mvbTermShort}} admitted",
          services: "services",
          vendors: "vendors",
        },
        sections: {
          register: "Register",
          catalogue: "Catalogue",
          workspace: "Workspace",
        },
        tiles: {
          adminConsole: {
            name: "Admin console",
            desc:
              "{{serviceTermShortPlural}}, services, capabilities, vendors, products, CIs, " +
              "channels, journeys -- all catalogue CRUD lives in the " +
              "admin console now",
          },
          capabilityMap: {
            name: "Capability map",
            desc: "Service x capability grid",
          },
          landscape: {
            name: "BIAN landscape",
            desc: "Industry-standard taxonomy overlay",
          },
          capmap: {
            name: "Capability map",
            desc: "Bank-wide capability map with live resilience overlay",
          },
          mvb: {
            name: "{{mvbTermShort}} declarations",
            desc: "{{mvbTermTitle}} scope + dual-signoff workflow",
          },
          dataAssets: {
            name: "Data assets",
            desc: "What data the bank holds, where it lives, which services touch it",
          },
          ibsGroups: {
            name: "{{serviceTermShort}} groups",
            /* Round 19 G30 -- the picker offers candidates as well. */
            desc: "Services, admitted or under assessment, consolidated into the few groups a board governs",
          },
          technologyLifecycle: {
            name: "Technology lifecycle register",
            desc: "Each product's support dates and the maker's notice they come from",
          },
          eaImport: {
            name: "Architecture model import",
            desc: "Bring in the model your EA tool exports and review what it would change",
          },
          floorList: {
            name: "Regulator floor list",
            desc: "Items the regulator pre-admits, and the bank's answer to each",
          },
          fundingResources: {
            name: "Funding resources",
            desc: "Settlement accounts, facilities, buffers and market access the services draw on",
          },
          regulatoryCommitments: {
            name: "Regulatory commitments",
            desc: "Named returns, notifications, limits and licence conditions",
          },
          riskAppetite: {
            name: "Risk appetite",
            desc: "CRO-curated tolerances by SAMA-aligned category",
          },
          outsourcingContracts: {
            name: "Outsourcing contracts",
            desc: "Contract register: criticality, SAMA no-objection, exit strategy, fourth parties",
          },
        },
      },
      hello: {
        heading: "Foundation",
        body:
          "The bank's resilience model - {{serviceTermShort}} catalogue, services, " +
          "capabilities, {{mvbTermShort}} declarations, vendor register. The data " +
          "the Twin reads from and the Framework references when " +
          "drafting CAPs.",
        status: "Admin UI shipping in P15",
        statusBody:
          "Manage every foundation entity -- {{serviceTermShortPlural}}, services, " +
          "capabilities, configuration items, products, channels, and " +
          "vendors -- from the admin browser, with every write " +
          "audit-logged. {{mvbTermShort}} declarations have a dedicated workflow surface.",
        surfaceLinks: {
          heading: "Admin surfaces",
        },
      },
      admin: {
        // Round 18 G26 -- a field set at create and never changed.
        fixedOnceCreated: "Set when it was created; it cannot be changed.",
        /* Round 13 C4 -- the count of the estate beside the count on the
           screen. The chip read "100 CIs" against a database holding
           20,000, and nothing qualified it. */
        searching: "Searching...",
        rowCountOfTotal: "{{shown}} shown of {{total}} in the register",
        /* Round 12 A6 -- one label for the route out of every empty
         register, landing on the importer with the entity already
         chosen rather than on a picker the reader must search. */
        importCta: "Import them from a CSV template",
        vendors: {
          list: {
            heading: "Vendors",
            subheading:
              "Third parties the bank depends on. Material flag drives " +
              "the cascade vendor chip.",
            createButton: "New vendor",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} vendor",
            rowCount_other: "{{count}} vendors",
            loading: "Loading...",
            dataLoading: "Loading vendors...",
            empty: "No vendors match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No vendors are recorded yet. They arrive with the service catalogue -- by CSV import, or derived from a connected CMDB when a configuration item names its supplier.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New vendor",
            editTitleFallback: "Edit vendor",
            closeLabel: "Close",
            loading: "Loading vendor...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            // E2 -- deleting finished in silence.
            deletedToast: "Deleted the scenario {{name}}.",
            deleteFailed: "That scenario could not be deleted.",
            deleteConfirm: "Click again to confirm",
          },
        },
        ibses: {
          list: {
            heading: "{{serviceTermPluralTitle}}",
            subheading:
              "SAMA-shaped {{serviceTermShort}} catalogue. {{mvbTermShort}} + sovereign flags drive " +
              "the resilience + blast derivers + the BIAN landscape overlay.",
            createButton: "New {{serviceTermShort}}",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} {{serviceTermShort}}",
            rowCount_other: "{{count}} {{serviceTermShortPlural}}",
            loading: "Loading...",
            dataLoading: "Loading {{serviceTermShortPlural}}...",
            empty: "No {{serviceTermShortPlural}} match.",
            /* A1 -- her own desk's next step sends her here, and it
               said "No IBSes match" to someone who had not searched.
               An IBS exists only once a service is promoted, so the
               honest empty state names the step before this one. */
            emptyUnfiltered:
              "No {{serviceTermPlural}} yet. They are raised by promoting a service, then challenged and signed off.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New {{serviceTermShort}}",
            editTitleFallback: "Edit {{serviceTermShort}}",
            closeLabel: "Close",
            loading: "Loading {{serviceTermShort}}...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
          channelLinks: {
            heading: "Linked channels",
            empty: "No channels linked yet.",
            pickerPlaceholder: "Add a channel...",
            add: "Link",
            remove: "Unlink",
            // P429.6 -- a function the bank performs for itself.
            function:
              "A Critical Internal Function has no delivery channel: the bank performs it for itself.",
          },
          fundingLinks: {
            heading: "Funds itself from",
            empty:
              "No funding resources mapped. The funding layer reads as a gap until one is.",
            pickerPlaceholder: "Add a funding resource...",
            add: "Link",
            remove: "Unlink",
          },
          fundedByLinks: {
            heading: "Funded by (Critical Internal Functions)",
            empty: "No internal function is recorded as funding this service.",
            pickerPlaceholder: "Add a Critical Internal Function...",
            add: "Link",
            remove: "Unlink",
          },
          regulatoryLinks: {
            heading: "Regulatory commitments",
            empty:
              "No regulatory commitments mapped. The regulatory layer reads as a gap until one is.",
            pickerPlaceholder: "Add a commitment...",
            extraPlaceholder: "How it relates",
            add: "Link",
            remove: "Unlink",
            relation: {
              bound_by: "Bound by",
              generates: "Produces",
            },
          },
        },
        services: {
          list: {
            heading: "Services",
            subheading:
              "The bank's service catalogue. Classification drives the " +
              "blast walker; parent_service_id supports two-level trees.",
            createButton: "New service",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} service",
            rowCount_other: "{{count}} services",
            loading: "Loading...",
            dataLoading: "Loading services...",
            empty: "No services match.",
            emptyUnfiltered:
              "No services are loaded yet. The data steward loads them from the service catalogue -- by CSV import, or from a connected CMDB.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New service",
            editTitleFallback: "Edit service",
            closeLabel: "Close",
            loading: "Loading service...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        capabilities: {
          /* Round 13 D4 -- an edge has no page of its own; the place
             it belongs is the drawer of the entity it hangs off. */
          dependencyLinks: {
            heading: "Depends on",
            empty: "No upstream capabilities linked yet.",
            pickerPlaceholder: "Add an upstream capability...",
            add: "Link",
            remove: "Unlink",
          },
          list: {
            heading: "Capabilities",
            subheading:
              "3-tier capability stack, from customer outcomes through bank " +
              "services to platform building blocks. Drives the capability map.",
            createButton: "New capability",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} capability",
            rowCount_other: "{{count}} capabilities",
            loading: "Loading...",
            dataLoading: "Loading capabilities...",
            empty: "No capabilities match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No capabilities are recorded yet. The capability catalogue is the bank's own -- import it from a CSV template, or add the first one by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New capability",
            editTitleFallback: "Edit capability",
            closeLabel: "Close",
            loading: "Loading capability...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        products: {
          list: {
            heading: "Products",
            subheading:
              "Customer-facing product catalogue. Annual revenue feeds " +
              "the cascade banner's SAR-at-risk chip.",
            createButton: "New product",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} product",
            rowCount_other: "{{count}} products",
            loading: "Loading...",
            dataLoading: "Loading products...",
            empty: "No products match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No products are recorded yet. They carry the revenue the cascade prices, and arrive by CSV import or by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New product",
            editTitleFallback: "Edit product",
            closeLabel: "Close",
            loading: "Loading product...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
          ibsLinks: {
            heading: "Linked {{serviceTermShortPlural}}",
            empty: "No {{serviceTermShortPlural}} linked yet.",
            pickerPlaceholder: "Add {{serviceTermShortA}}...",
            add: "Link",
            remove: "Unlink",
          },
          capabilityLinks: {
            heading: "Linked capabilities",
            empty: "No capabilities linked yet.",
            pickerPlaceholder: "Add a capability...",
            add: "Link",
            remove: "Unlink",
          },
        },
        configurationItems: {
          list: {
            heading: "Configuration items",
            subheading:
              "CMDB-shaped CI register. Linked service powers the " +
              "cascade's CI-finding reroute.",
            createButton: "New CI",
            searchPlaceholder: "Search name + class...",
            rowCount_one: "{{count}} CI",
            rowCount_other: "{{count}} CIs",
            loading: "Loading...",
            dataLoading: "Loading CIs...",
            empty: "No CIs match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No configuration items are recorded yet. They normally arrive from a connected CMDB; a CSV import is the alternative where there is none.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New CI",
            editTitleFallback: "Edit CI",
            closeLabel: "Close",
            loading: "Loading CI...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        /* Round 13 D9 -- P371 modelled the team that restores a
           service and the site it runs in, the MVB recovery sequence
           has been bounded by their capacity ever since, and neither
           had a screen. A CSV import of either ended at "Committed"
           with nowhere to go. */
        recoveryTeams: {
          /* A NULL capacity means the scheduler treats the team as
             unbounded -- a different claim from zero, and a blank cell
             reads as a zero. */
          unbounded: "unbounded",
          list: {
            heading: "Recovery teams",
            subheading:
              "Who restores a service, and how much they can restore at " +
              "once. The capacity figure bounds the {{mvbTermShort}} " +
              "recovery sequence.",
            createButton: "New recovery team",
            searchPlaceholder: "Search code, name, contact...",
            rowCount_one: "{{count}} recovery team",
            rowCount_other: "{{count}} recovery teams",
            loading: "Loading...",
            dataLoading: "Loading recovery teams...",
            empty: "No recovery teams match.",
            emptyUnfiltered:
              "No recovery teams are recorded yet. A recovery team is the group that brings a service back, and how many restorations it can run at once is what bounds the recovery plan. They arrive by CSV import or by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New recovery team",
            editTitleFallback: "Edit recovery team",
            closeLabel: "Close",
            loading: "Loading recovery team...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        facilities: {
          isRecoverySite: "Recovery site",
          /* The closed vocabulary the database CHECK enforces. */
          type: {
            data_centre: "Data centre",
            dr_site: "DR site",
            office: "Office",
            branch: "Branch",
          },
          list: {
            heading: "Facilities",
            subheading:
              "The sites services run in, and which of them are recovery " +
              "sites. Concentration here is what the architecture " +
              "standards read.",
            createButton: "New facility",
            searchPlaceholder: "Search code, name, location...",
            rowCount_one: "{{count}} facility",
            rowCount_other: "{{count}} facilities",
            loading: "Loading...",
            dataLoading: "Loading facilities...",
            empty: "No facilities match.",
            emptyUnfiltered:
              "No facilities are recorded yet. A facility is a site a service runs in -- a data centre, a DR site, an office, a branch -- and marking which are recovery sites is what lets the product tell you when everything runs and recovers in one place. They arrive by CSV import or by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New facility",
            editTitleFallback: "Edit facility",
            closeLabel: "Close",
            loading: "Loading facility...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        channels: {
          list: {
            heading: "Channels",
            subheading:
              "Delivery channels. Hand-picked sort order + colour drive " +
              "the heatmap column headers.",
            createButton: "New channel",
            searchPlaceholder: "Search code, name, description...",
            rowCount_one: "{{count}} channel",
            rowCount_other: "{{count}} channels",
            loading: "Loading...",
            dataLoading: "Loading channels...",
            empty: "No channels match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No channels are recorded yet. A channel is how a customer reaches a service -- branch, ATM, mobile, web -- and they arrive by CSV import or by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New channel",
            editTitleFallback: "Edit channel",
            closeLabel: "Close",
            loading: "Loading channel...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        journeys: {
          list: {
            heading: "Customer journeys",
            subheading:
              "Sequential step-by-step customer flows per ({{serviceTermShort}}, channel). " +
              "Drives the cascade overlay -- when {{serviceTermShortA}} impact card opens, " +
              "the journey breadcrumb shows the customer path.",
            createButton: "New journey",
            searchPlaceholder: "Search description...",
            rowCount_one: "{{count}} journey",
            rowCount_other: "{{count}} journeys",
            loading: "Loading...",
            dataLoading: "Loading journeys...",
            empty: "No journeys match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No journeys are recorded yet. A journey is one {{serviceTermShort}} reached through one channel, so both must exist before the first can be drawn.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New journey",
            editTitleFallback: "Edit journey",
            closeLabel: "Close",
            loading: "Loading journey...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
          steps: {
            heading: "Steps",
            empty: "No steps yet. Add the first step below.",
            namePlaceholder: "Step name",
            descriptionPlaceholder: "Optional narrative...",
            add: "Add",
            save: "Save",
            saving: "Saving...",
            delete: "Delete",
            tagPlaceholder: {
              service: "(no service)",
              capability: "(no capability)",
              vendor: "(no vendor)",
            },
          },
        },
        dataAssets: {
          list: {
            heading: "Data assets",
            subheading:
              "What data the bank holds, where it lives and which " +
              "services touch it. A classification on a service is " +
              "prose; this is the register residency and data impact " +
              "can be answered from.",
            createButton: "New data asset",
            searchPlaceholder: "Search name, code, classification, owner...",
            rowCount_one: "{{count}} data asset",
            rowCount_other: "{{count}} data assets",
            loading: "Loading...",
            dataLoading: "Loading data assets...",
            empty: "No data assets match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No data assets are recorded yet. They are what a service stores or processes, and they carry the residency question -- import them, or add the first by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New data asset",
            editTitleFallback: "Edit data asset",
            closeLabel: "Close",
            loading: "Loading data asset...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        ibsGroups: {
          list: {
            heading: "{{serviceTermShort}} groups",
            subheading:
              "Services, admitted or under assessment, consolidated into the few groups a board can govern. A function is never grouped. A service sits in one group; a group's tolerance is derived from its tightest member, never typed. The Indirect Services group is the methodology's own.",
            createButton: "New group",
            searchPlaceholder: "Search code, name, owner, member...",
            rowCount_one: "{{count}} group",
            rowCount_other: "{{count}} groups",
            loading: "Loading...",
            dataLoading: "Loading groups...",
            empty: "No groups match.",
            emptyUnfiltered:
              "Only the reserved Indirect Services group exists yet. The customer-exposed groups are the bank's judgement -- create the first, or import them.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New group",
            editTitleFallback: "Edit group",
            closeLabel: "Close",
            loading: "Loading group...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
          members: {
            heading: "Services in this group",
            empty: "No services in this group yet.",
            pickerPlaceholder: "Add a service...",
            extraPlaceholder: "Why it belongs here",
            // Round 16 O7 -- a label, a reason Add is grey, a removal
            // that asks first, and a word when each is done.
            extraLabel: "Why it belongs here (required)",
            addHint: "Pick a service and say why it belongs here to add it.",
            add: "Add",
            remove: "Remove",
            cancel: "Cancel",
            confirmRemove: "Remove it from the group?",
            added: "{{name}} added to the group.",
            removed: "{{name}} removed from the group.",
          },
          created: "Group {{name}} created.",
          noTolerance: "none declared by a member",
          derivedTolerance: "{{hours}}h, from {{from}}",
        },
        fundingResources: {
          list: {
            heading: "Funding resources",
            subheading:
              "What money moves through or rests in: settlement accounts, facilities, buffers, market access and counterparties. A service is mapped to these on its own drawer, and they are the funding layer of its dependency map.",
            createButton: "New funding resource",
            searchPlaceholder: "Search code, name, kind, owner...",
            rowCount_one: "{{count}} funding resource",
            rowCount_other: "{{count}} funding resources",
            loading: "Loading...",
            dataLoading: "Loading funding resources...",
            empty: "No funding resources match.",
            emptyUnfiltered:
              "No funding resources are recorded yet. They are what a service needs money to move through or rest in -- import them, or add the first by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New funding resource",
            editTitleFallback: "Edit funding resource",
            closeLabel: "Close",
            loading: "Loading funding resource...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        regulatoryCommitments: {
          list: {
            heading: "Regulatory commitments",
            subheading:
              "The named returns, notifications, limits and licence conditions the bank must meet. A service is bound by or produces these, mapped on its own drawer, and they are the regulatory layer of its dependency map.",
            createButton: "New commitment",
            searchPlaceholder: "Search code, name, kind, when due...",
            rowCount_one: "{{count}} commitment",
            rowCount_other: "{{count}} commitments",
            loading: "Loading...",
            dataLoading: "Loading commitments...",
            empty: "No commitments match.",
            emptyUnfiltered:
              "No regulatory commitments are recorded yet. They are the returns and conditions a service is bound by or produces -- import them, or add the first by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New commitment",
            editTitleFallback: "Edit commitment",
            closeLabel: "Close",
            loading: "Loading commitment...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        technologyLifecycle: {
          list: {
            heading: "Technology lifecycle register",
            subheading:
              "Each product's mainstream and extended support dates, and the maker's notice they were taken from. Configuration items are matched to a row by manufacturer and model; a row matching no item is usually a spelling the CMDB does not use.",
            createButton: "New product",
            searchPlaceholder: "Search manufacturer, model, source...",
            rowCount_one: "{{count}} product",
            rowCount_other: "{{count}} products",
            loading: "Loading...",
            dataLoading: "Loading the technology lifecycle register...",
            empty: "No products match.",
            emptyUnfiltered:
              "No products yet. Until a model is here with its support dates, technology currency cannot judge the items built on it.",
            loadMore: "Load more",
            auditHint: "Every create, update or delete is recorded in the audit log.",
            forbidden:
              "You need the admin role to change this register. Ask your platform administrator -- they can grant it, and the Users and roles page shows who holds what today.",
          },
          import: {
            heading: "Import from a file",
            body: "A CSV with one product per row. Choosing a file checks it first; nothing is written until you import, and only when no row has an error. A product already in the register is updated, keeping its spelling.",
            template: "Download a template",
            summary:
              "{{file}}: {{inserted}} to add, {{updated}} to update, {{errors}} with errors.",
            ignored: "Columns not read: {{columns}}.",
            rowError: "Row {{row}} ({{product}}): {{error}}",
            fixFirst: "Correct the errors to import",
            commit_one: "Import {{count}} product",
            commit_other: "Import {{count}} products",
            done: "Imported: {{inserted}} added, {{updated}} updated.",
          },
          drawer: {
            createTitle: "New product",
            editTitleFallback: "Edit product",
            closeLabel: "Close",
            loading: "Loading product...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        riskAppetite: {
          list: {
            heading: "Risk appetite statements",
            subheading:
              "CRO-curated tolerances by SAMA-aligned category. " +
              "Summary is the regulator-facing disclosure; structured " +
              "fields are optional fuller detail.",
            createButton: "New statement",
            /* Round 19 Q6 -- whose register it is. */
            ownerNote:
              "The register is the CRO's. As a business owner you may add a statement; the CRO sees it here.",
            searchPlaceholder: "Search category, title, summary, owner...",
            rowCount_one: "{{count}} statement",
            rowCount_other: "{{count}} statements",
            loading: "Loading...",
            dataLoading: "Loading risk appetite statements...",
            empty: "No risk appetite statements match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No risk appetite statements yet. These are written here rather than imported: each one is the board's own words about what it will tolerate.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          drawer: {
            createTitle: "New risk appetite statement",
            editTitleFallback: "Edit risk appetite statement",
            closeLabel: "Close",
            loading: "Loading statement...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
        outsourcingContracts: {
          list: {
            heading: "Outsourcing contracts",
            subheading:
              "The contract register a field examination reads: " +
              "criticality must discriminate; Material outsourcing " +
              "carries SAMA no-objection evidence (section J).",
            createButton: "New contract",
            searchPlaceholder: "Search ref, title, criticality, location...",
            rowCount_one: "{{count}} contract",
            rowCount_other: "{{count}} contracts",
            loading: "Loading...",
            dataLoading: "Loading outsourcing contracts...",
            empty: "No outsourcing contracts match.",
            /* Round 12 A6 -- "No X match." told a first-day
               administrator his search had failed, when what was true
               is that his bank is new. `emptyUnfiltered` is what an
               unfiltered register says instead. */
            emptyUnfiltered:
              "No outsourcing contracts are recorded yet. The register holds the material arrangements a supervisor asks about -- import them, or add the first by hand.",
            loadMore: "Load more",
            auditHint:
              // Round 16 H25 -- the log, not its API path.
              "Every create, update or delete is recorded in the audit log.",
            // Round 9 C2. A refusal that names a product to configure
            // asks the reader to solve an identity-management problem.
            // The person who can actually grant it is the platform
            // administrator, and saying so is the difference between a
            // dead end and a next step. (The registers that were
            // refusing their own owners now admit them; this is the
            // wording for the refusals that legitimately remain.)
            forbidden:
              "You need the admin role to open this register. Ask your " +
              "platform administrator -- they can grant it, and the " +
              "Users and roles page shows who holds what today.",
          },
          serviceLinks: {
            heading: "Services this contract underpins",
            empty: "No services linked yet.",
            pickerPlaceholder: "Add a service...",
            add: "Link",
            remove: "Unlink",
          },
          subcontractorLinks: {
            heading: "Fourth parties behind this arrangement",
            empty:
              "No subcontractors modelled. Any named in the narrative field above are invisible to concentration analysis until modelled here.",
            pickerPlaceholder: "Add a subcontractor...",
            extraPlaceholder: "What they do",
            add: "Link",
            remove: "Unlink",
          },
          drawer: {
            createTitle: "New outsourcing contract",
            editTitleFallback: "Edit outsourcing contract",
            closeLabel: "Close",
            loading: "Loading contract...",
            save: "Save",
            saving: "Saving...",
            cancel: "Cancel",
            delete: "Delete",
            deleteConfirm: "Click again to confirm",
          },
        },
      },
    },
    twin: {
      // P421 -- the change register.
      changes: {
        kicker: "Operational twin",
        heading: "Changes",
        standfirst:
          "A new product, an acquisition, a new market, a technology system, a process, a vendor, a facility or a regulatory change, raised over what it affects and assessed in words. Assessing a significant change brings the review of every admitted service it reaches forward, raises a finding on each, and counts a scenario test from before it as out of date. A routine change is recorded and does none of that.",
        loading: "Reading the register\u2026",
        empty: "No change has been recorded.",
        raise: "Raise a change",
        raiseSubmit: "Raise the change",
        /* Round 19 G37 / G39 -- no default significance, and a way out. */
        raiseCancel: "Cancel",
        significanceNeeded: "Choose whether the change is significant or routine.",
        raised: "Change raised.",
        /* Round 19 N12 -- a significant change's watched rule told only
           the approvers and administrators already signed in here; the
           rest of the seat is told at their own first sign-in. */
        toldNow_one:
          "1 person told now; approvers and administrators not yet signed in are told when they first sign in.",
        toldNow_other:
          "{{count}} people told now; approvers and administrators not yet signed in are told when they first sign in.",
        toldNobodyYet:
          "Nobody in the approver or administrator seat has signed in yet; each is told when they first sign in.",
        raisedOn: "Raised {{date}} by {{who}}",
        someone: "someone",
        kindLabel: "What changed",
        titleLabel: "Title",
        descriptionLabel: "Description",
        effectiveLabel: "Takes effect (optional)",
        effectiveOn: "Takes effect {{date}}.",
        significanceLabel: "Significance",
        significanceHelp: {
          significant:
            "Significant -- when assessed, brings reviews forward and raises findings on what it reaches.",
          routine: "Routine -- recorded, with no consequence for the estate.",
        },
        significance: {
          significant: "Significant",
          routine: "Routine",
        },
        status: {
          open: "Open",
          assessed: "Assessed",
          closed: "Closed",
        },
        affected: "Affects",
        affectedKindLabel: "Kind of thing affected",
        search: "Search by name",
        remove: "Remove {{name}}",
        affectedNone:
          "Nothing named yet. Name what this change affects before assessing it, or it reaches no service.",
        gone: "no longer in the estate",
        editAffected: "Change what it affects",
        saveAffected: "Save",
        affectedKind: {
          ibs_service: "{{serviceTerm}}",
          service: "service",
          vendor: "vendor",
          configuration_item: "configuration item",
          facility: "facility",
          recovery_team: "recovery team",
          regime: "regulator",
        },
        routineNote:
          "A routine change: assessing it records the assessment and changes nothing else.",
        previewReach_one: "Assessing this reaches 1 admitted service.",
        previewReach_other: "Assessing this reaches {{count}} admitted services.",
        previewReviews_one: "1 review comes forward to within {{days}} days.",
        previewReviews_other: "{{count}} reviews come forward to within {{days}} days.",
        previewReviewsNone: "No review moves: each is already due sooner.",
        previewFindings_one: "1 finding is raised.",
        previewFindings_other: "{{count}} findings are raised.",
        previewFindingsNone:
          "No new finding: each already carries one for this change.",
        previewNone:
          "As it stands this reaches no admitted service, so assessing it changes nothing in the estate.",
        previewVendors_one: "It also raises a finding on the vendor it names.",
        previewVendors_other:
          "It also raises a finding on each of the {{count}} vendors it names.",
        reviewMoves: "review moves from {{from}} to {{to}}",
        reviewStays: "review already due {{date}}, stays",
        /* Round 19 G39 -- "already due" over a date already past. */
        reviewOverdue: "overdue since {{date}}; stays",
        notScheduled: "not scheduled",
        assessmentLabel: "Assessment -- what this change does to the risk",
        assess: "Assess",
        assessed: "Assessed. Its consequences are on the record.",
        /* Round 19 G39 -- a routine change, by the page's own words,
           has no consequences. */
        assessedRoutine: "Assessed. A routine change changes nothing else.",
        assessedBy: "Assessed by {{who}} on {{date}}.",
        reached_one: "Reached 1 admitted service.",
        reached_other: "Reached {{count}} admitted services.",
        close: "Close",
        /* Round 19 G39 -- Close was one press, and cannot be undone. */
        closeConfirm: "Close this change? It cannot be reopened.",
        closeConfirmYes: "Close it",
        closeConfirmNo: "Keep it open",
        closedBy: "Closed by {{who}} on {{date}}.",
        closedOn: "Closed on {{date}}.",
        reachedServices: "Reached:",
        showInternal_one: "Show {{count}} internal tool",
        showInternal_other: "Show {{count}} internal tools",
        hideInternal: "Hide the internal tools",
        // P421.2 -- from the regulatory change page.
        fromRegulatory: "Raise as a change",
        fromRegulatoryDone: "Raised as a change. Name what it affects, then assess it.",
        /* Round 19 G39 -- the second press opened the change it raised
           the first time, and said "Raised as a change." */
        fromRegulatoryExisting: "This change was raised on {{date}}; opened it.",
        // P421 -- the IBS page's chip.
        chipLabel: "Change",
        /* Round 19 G40 -- the chip says when nothing has been raised. */
        chipNone: "No change raised on this service",
        chipNote: "raised {{date}}",
        chipNoteMore_one: "raised {{date}}, and 1 more",
        chipNoteMore_other: "raised {{date}}, and {{count}} more",
        /* Round 19 G40 -- the register filtered from a service's chip. */
        filteredTo: "Changes touching {{name}}.",
        filteredMine: "Changes touching services you own.",
        filterMine: "Touching services you own",
        filterAll: "Show every change",
        /* Round 19 G40 -- who a significant raise tells. */
        tellsApprovers:
          "Raising this significant {{kind}} tells the approvers and administrators.",
        tellsNobodySignificant:
          "Raising this significant change notifies no one; assessing it tells the owners of the services it reaches.",
        tellsNobody: "No one is notified.",

        // P421 -- why a coverage cell is stale.
        staleAfterChange:
          "stale: a significant change on {{date}} came after this test",
        staleChangedShort: "changed {{date}}",
        /* Round 19 G40 -- the kind inside a sentence. */
        kindWord: {
          new_product: "new product",
          acquisition: "acquisition",
          new_market: "new market",
          technology_system: "technology change",
          process_change: "process change",
          vendor_change: "vendor change",
          facility_change: "facility change",
          regulatory_rebase: "regulatory change",
          other: "change",
        },
        kind: {
          new_product: "New product or service",
          acquisition: "Acquisition",
          new_market: "New market",
          technology_system: "Technology system",
          process_change: "Business process",
          vendor_change: "Vendor",
          facility_change: "Facility",
          regulatory_rebase: "Regulatory change",
          other: "Other",
        },
      },
      // P128: module-home copy routed through i18n.
      home: {
        rubric: "Framing · Operational Twin",
        heading: "Operational Twin",
        positioning:
          "Live state of the bank in motion. Real-time resilience, blast " +
          "radius, and cascade investigation across the {{serviceTermShort}} register.",
        signals: {
          loading: "Reading live cascade...",
          /* Round 12 B1 -- "All clear" in emerald on the Twin's front
             page over a bank with nothing in it. Zero impacted because
             nothing is wrong and zero impacted because there is nothing
             to impact are the same zero. */
          notAssessed:
            "Not yet assessed -- nothing admitted, no tolerance declared, no telemetry sampled.",
          allClear: "All clear",
          impacted: "{{count}} entities impacted",
          critical: "{{count}} critical",
          high: "{{count}} high",
          products: "{{count}} products at risk",
          mvb: "{{mvbTermShort}}: {{impacted}}/{{total}} ({{pct}}%)",
        },
        sections: {
          state: "State",
          analysis: "Analysis",
          governance: "Governance",
        },
        tiles: {
          recommendations: {
            name: "What to do about it",
            desc: "The ranked remediation the estate's own state argues for, with the evidence behind each one.",
          },
          toleranceMetrics: {
            name: "{{toleranceTerm, capitalize}} dimensions",
            desc: "Every declared tolerance metric, evaluated live, each naming its source",
          },
          continuityPlans: {
            name: "Continuity plans",
            desc: "BCP and ITDR plans, generated from the recovery sequence and run as playbooks",
          },
          changes: {
            name: "Significant change",
            desc: "A change raised over what it affects; assessing it brings reviews forward and asks for a new test",
          },
          architectureStandards: {
            name: "Architecture standards",
            desc: "Conformance against structural standards, derived and naming every breaching row",
          },
          serviceDisposition: {
            name: "Service disposition",
            desc: "Derived invest / migrate / tolerate / eliminate proposal per service, with its evidence",
          },
          cascade: {
            name: "Cascade",
            desc: "Live impact view -- entities, products, {{mvbTermShort}} framing",
          },
          channels: {
            name: "Channel heatmap",
            desc: "Per-({{serviceTermShort}}, channel) resilience state",
          },
          telemetry: {
            name: "Live telemetry",
            desc: "Live service-health feed -- up / degraded / down, beyond findings",
          },
          mvbTargetState: {
            name: "{{mvbTermShort}} target state",
            desc: "What closing the {{mvbTermShort}}'s gaps would cost, and in what order",
          },
          technologyCurrency: {
            name: "Technology currency",
            desc: "Whether the technology each service rests on is still supported by its maker",
          },
          scenarios: {
            name: "Scenarios",
            desc: "Counterfactual cascade for tabletop runs",
          },
          journeys: {
            name: "Customer journeys",
            desc: "Per-{{serviceTermShort}} step breadcrumbs + failing-step overlay",
          },
          /* Round 11 F4 -- offered from the Twin, where the idea
             lives. It was on the admin console and nowhere else. */
          edgeDelays: {
            name: "Edge propagation delays",
            desc: "How long a failure takes to cross each edge, and which edges have no declared delay",
          },
          capabilityMap: {
            name: "Capability map",
            desc: "Service x capability grid with overlays",
          },
          landscape: {
            name: "BIAN landscape",
            desc: "Overlay view -- confidence + cascade + {{mvbTermShort}}",
          },
          landscapeClassic: {
            name: "BIAN classic",
            desc: "Tile grid with detail popovers",
          },
          capmap: {
            name: "Capability map",
            desc: "Bank-wide capability map with live resilience overlay",
          },
          constellation: {
            name: "Constellation",
            desc:
              "Live constellation of channels, {{serviceTermShortPlural}}, services + vendors. " +
              "3 layouts; click any node to trace its blast radius + time travel.",
          },
          board: {
            name: "Board snapshot",
            desc: "One-page exec view (admin / approver)",
          },
          ibsAdmission: {
            name: "{{serviceTermShort}} admission",
            /* Round 19 H55 -- it named the Engine over rows reading "Scored
               by the resilience desk". */
            desc: "Scored by the resilience desk, with the Engine or by hand; challenged, then signed by the CRO and the business owner",
          },
          cifs: {
            name: "Critical internal functions",
            desc: "The second door: functions the bank performs for itself, signed by the CRO and the CIO",
          },
          mvbCandidates: {
            name: "{{mvbTermShort}} candidates",
            desc: "Score, tier + CRO / AE sign-off of {{mvbTermTitle}} candidates",
          },
          ibsPortfolio: {
            name: "{{serviceTermShort}} portfolio",
            desc: "Every {{serviceTermShort}} on one screen: admission, resilience, standing tolerance posture",
          },
          ibsGroups: {
            name: "{{serviceTermShort}} groups",
            desc: "The few groups a board governs, each as bad as its worst member",
          },
          raiseFinding: {
            name: "Raise a finding",
            desc: "Manual intake for observations no connector carries",
          },
          readiness: {
            name: "Resilience readiness",
            desc:
              "Your MARSAD Resilience Score, the maturity assessment behind it, " +
              "and the evidence each pillar rests on.",
          },
          fieldExam: {
            name: "Field-exam readiness",
            desc: "The SAMA field-examiner's questions answered live: classification discrimination, cross-layer coherence, per-component test coverage",
          },
          drills: {
            name: "Resilience drills",
            desc: "Schedule + run BCP drills; a failure raises a finding on the {{serviceTermShort}}",
          },
          warRoom: {
            name: "Resilience war-room",
            desc: "Stress a scenario against {{toleranceTermPlural}}; see what breaches",
          },
          fragility: {
            name: "Fragility radar",
            desc: "Always-on concentration-risk watchlist -- your single points of failure",
          },
        },
      },
      hello: {
        heading: "Operational Twin",
        body:
          "Live state of the bank - real-time resilience, blast " +
          "radius, cascade view, telemetry connectors, incidents. " +
          "Time-travel through any historical state via the chain " +
          "audit.",
        status: "Live",
        statusBody:
          "Cascade view + capability map + 5 entity detail pages + " +
          "time-travel via ?as_of= + DIFF + TREND + 2 telemetry " +
          "connectors (ServiceNow + Tenable) all shipped. Pick a " +
          "surface from the links below.",
        surfaceLinks: {
          heading: "Live surfaces",
          cascade: "Live cascade -- impacted services + downstream products",
          capabilityMap: "Capability map -- tier-column grid + cell drawer",
          landscape:
            "BIAN landscape -- reference tile grid with {{mvbTermShort}} highlight + cascade overlay",
          landscapeClassic:
            "BIAN landscape (classic) -- tile grid with detail popovers",
          channels:
            "Channel resilience heatmap -- {{serviceTermShort}} x channel cross-tab with pillar overlay",
          journeys:
            "Customer journeys -- step-by-step flows per ({{serviceTermShort}}, channel); cascade-overlay friendly",
          scenarios: "Scenario library -- severe-but-plausible counterfactual cascade",
          chains: "Chain time-travel + DIFF + TREND -- via entity detail pages",
        },
      },
      /* P422 -- incident reporting per regime. A deadline is printed
         only where the regulator states hours; otherwise the basis the
         regulator does state. */
      incidentReporting: {
        heading: "Reporting to {{regulator}}",
        recipient: "Reported to {{recipient}}.",
        ruleHours: "Initial notice within {{hours}} hours of detection.",
        ruleBasis: {
          immediately:
            "Initial notice immediately; the regulator states no number of hours.",
          set_by_rts:
            "Time limits are set by technical standards this deployment does not hold, so no deadline is shown.",
          hours: "Initial notice within a stated number of hours.",
        },
        unrecorded:
          "{{regulator}}'s incident-reporting rule is not recorded in this deployment.",
        defaultRegime:
          "No regulator is recorded for this bank, so the Saudi Central Bank's rule is shown.",
        none: "No report has been opened for this incident.",
        stage: { initial: "Initial notice", update: "Update", final: "Final report" },
        status: { draft: "Not yet submitted", submitted: "Submitted", late: "Late" },
        dueAt: "due {{at}}",
        basis: {
          immediately: "due immediately (no hours stated)",
          set_by_rts: "time limit set by technical standards not held here",
          unrecorded: "no rule recorded",
          as_available: "as new information becomes available",
          on_closure: "after the incident is closed",
          on_resumption: "after operations resume",
          hours: "due within the stated hours",
          days: "due within the stated days",
        },
        submitted: "Submitted {{at}}, reference {{reference}} ({{kind}}).",
        submittedLate:
          "Submitted {{at}}, after the deadline, reference {{reference}} ({{kind}}).",
        reference: "Your reference for what you sent",
        /* Round 20 U3 -- the reference was whatever was typed, and read
           as if the regulator had issued it. */
        referenceIssued: "The regulator issued this reference",
        referenceBankRecord: "the bank's own record",
        referenceRegulatorIssued: "issued by the regulator",
        noticeHeading: "What the notice says",
        noticeDraftedFrom:
          "Drafted from the finding and the services it reaches. Check every line before you send it; MARSAD does not send it.",
        noticeField: {
          what_happened: "What happened",
          services_affected: "Services affected",
          customers_affected: "Customers affected",
          classification: "Classification",
          contact: "Contact for the regulator",
        },
        noticeSave: "Save the notice",
        noticeSaved: "Notice saved.",
        noticeMissing: "Fill these before recording it as sent: {{fields}}.",
        record: "Record submission",
        recorded: "Submission recorded.",
        open: {
          initial: "Open initial notice",
          update: "Add an update",
          final: "Open final report",
        },
        // Round 20 H59
        openUnreached:
          "The notice was not opened: the request did not reach the server. Nothing was recorded. Try again.",
      },
      finding: {
        // E4 -- the connector payload is an operator's artefact; for
        // an owner it is a wall of somebody else's JSON.
        /* D7 -- one string on two disclosures rendered "Technical
           detail (for the operations team)" twice on one page, and
           neither said what was inside. They hold different things. */
        /* D7 -- who the estate records as accountable for the thing
           this finding is about. The page named nobody at all. */
        accountable: "Accountable for the affected service: {{who}}",
        // Round 9 C4. Naming the accountable person and nobody else
        // tells him he is responsible and nothing about who is
        // responding. The recovery team has been modelled since P371
        // and was never shown here.
        whoIsOnIt: "Who is on it: {{team}}",
        whoIsOnItMany: "Who is on it: {{teams}}",
        /* Round 20 U2 -- asked "who knows?", the product had no answer. */
        toldHeading: "Who has been told",
        toldRow: "{{name}}, {{at}}{{how}}",
        toldByBell: " (bell)",
        toldByBellAndEmail: " (bell and e-mail)",
        toldAcknowledged: "acknowledged {{at}}",
        toldNotAcknowledged: "not yet acknowledged",
        toldHeld_one:
          "Held for {{count}} person who has not signed in yet; they are told at first sign-in.",
        toldHeld_other:
          "Held for {{count}} people who have not signed in yet; they are told at first sign-in.",
        toldNobodyCritical: "No one has been told of this finding.",
        toldNobodyBySeverity:
          "No one is told of a {{severity}} finding by rule. Tell the desk directly if it needs action.",
        whatYouDo: "What you do: confirm the customer impact and the workaround here.",
        // Round 16 L4 -- a function has no customers; what it funds does.
        whatYouDoInternal:
          "What you do: confirm what has stopped downstream and the workaround here.",
        commentPlaceholder: "What are your customers seeing?",
        commentPlaceholderInternal: "What has stopped, and what is the workaround?",
        commentSubmit: "Add to the incident record",
        /* Round 19 G35 -- a finding a planned change raised. */
        whatYouDoChange:
          "What you do: say what the change did to this service, then map it again, review its tolerance and test it again.",
        commentPlaceholderChange: "What did the change do to this service?",
        commentSubmitChange: "Add a note",
        raisedByChange: "Raised by a significant change: open it",
        commentAdded: "Added to the incident record.",
        /* Round 10 C1 -- "added to the record" says the record took it
           and nothing about whether a person will read it. */
        commentSent_one: "Sent to the resilience desk -- {{count}} person notified.",
        commentSent_other: "Sent to the resilience desk -- {{count}} people notified.",
        commentSentNobody:
          "Recorded. Nobody on the resilience desk has signed in yet, so no one has been alerted -- tell them directly.",
        commentAudience_one:
          "What you write here goes to the resilience desk: {{count}} person will be told.",
        commentAudience_other:
          "What you write here goes to the resilience desk: {{count}} people will be told.",
        /* Round 20 U2 -- "1 person will be told" was the reader's own seat. */
        commentAudienceNamed:
          "What you write here goes to the resilience desk: {{names}} will be told.",
        commentAudienceNobody:
          "This is recorded against the incident, but nobody on the resilience desk has signed in yet, so writing here will not alert anyone.",
        commentsHeading: "Incident record",
        commentsEmpty: "Nothing recorded yet.",
        operatorDetailMeta: "Where this came from (for the operations team)",
        operatorDetailPayload: "Raw record from the source system",
        loading: "Loading finding...",
        error: "Could not load finding. Try again.",
        notFoundHeading: "Finding not found",
        notFoundBody:
          "We couldn't find a finding with id {{id}}. It may have been " +
          "closed and pruned, or the id is wrong.",
        raisedAt: "Raised at",
        closedAt: "Closed at",
        impactedEntity: "Impacted entity",
        raisedBy: "Raised by",
        raisedVia: "(through {{source}})",
        /* D6 -- the incident as the accountable owner needs it. */
        owner: {
          headline:
            "{{name}} has had a {{severity}} incident since {{since}} ({{elapsed}}).",
          /* ROUND 13 G2 -- the monitor raises this finding BECAUSE the
             live projection already exceeds the declared tolerance, and
             the strip printed a countdown to a breach two hours in the
             future above a title saying the breach had happened. A
             projection is not a deadline. The monitor's own sentence,
             which until now reached none of the owner's screens, says
             the thing exactly. */
          toleranceBreached:
            "Breaching now: live conditions project {{projected}}h to recover against your declared {{tolerance}}h {{toleranceTerm}}.",
          toleranceBreachedVia: "Driven by {{via}}.",
          toleranceBreachedWhen:
            "This is a projection from current conditions, not a countdown -- it was judged at {{when}} and is re-judged as conditions change.",
          tolerancePending:
            "Tolerance: {{hours}}h declared and awaiting signature -- nothing is in force yet, so no breach time can be given.",
          toleranceNone:
            "No {{toleranceTerm}} is in force, so there is no breach time to give. Declaring one is how that changes.",
          /* ROUND 13 G7, ninth round -- `signed` and `notSigned` were
             a third and a fourth wording for a state two other keys
             already said. The state comes from `toleranceSentence`;
             what this strip adds is the time. */
          breachAt: "Breach at {{breach}}.",
          // E4 -- "who is on it" was on no surface the owner could reach.
          beingHandled: "Being handled under {{title}}, now with the {{role}}.",
          // B6 -- the page told him everything and asked him nothing.
          ask: "Nobody has picked this up.",
          askAction: "Ask for a remediation plan",
          nobodyOnIt:
            "Nobody has picked this up yet. Raising a corrective action plan is how it gets an owner and a date.",
          openIbs: "Open {{name}}",
        },
        raisedByHand: "Raised by hand in MARSAD",
        /* Round 19 A45 -- the references MARSAD mints, in words. */
        ref: {
          demoSeed: "Seeded with the demonstration estate",
          telemetry: "Raised by MARSAD from live telemetry",
          toleranceBreach: "Raised by MARSAD when an {{toleranceTerm}} was breached",
          change: "Raised by MARSAD when a significant change was assessed",
        },
        telemetryRaised: "Telemetry auto-raised",
        draftCap: {
          action: "Draft CAP from this finding",
          working: "Drafting...",
          done: "CAP drafted and linked to this finding.",
        },
        incidentReport: {
          download: "Regulator report (signed pack)",
          downloading: "Composing the signed report...",
          done: "Incident report downloaded: {{filename}}",
        },
        viewSource: "Source",
        viewSourceLink: "View in source",
        // Round 19 H53 -- the cell holds a link into the source system.
        sourceLinkLabel: "Link in the source system",
        descriptionHeading: "Description",
        linkedCaps: {
          heading: "Linked CAPs",
          loading: "Loading linked CAPs...",
          empty: "No CAPs reference this finding yet.",
          needsFramework: "Linked CAPs require framework access.",
          // D6 -- an owner will never have framework access, so telling
          // him he lacks it is the product describing its own licensing
          // where he needed to know where remediation is kept.
          keptInFramework:
            "Any corrective action plan for this is kept in the compliance module.",
        },
      },
      // D6 -- what is happening to this service right now, on the page
      // the owner actually starts from. The finding page had the clock;
      // his own service's page said nothing about it at all.
      ibsIncident: {
        headline_one:
          "{{name}} has 1 open finding, the worst of them {{severity}}, " +
          "running for {{elapsed}}.",
        headline_other:
          "{{name}} has {{count}} open findings, the worst of them " +
          "{{severity}}, running for {{elapsed}}.",
        tolerance: "Its {{toleranceTerm}} is {{hours}}h -- breach at {{breach}}.",
        /* Round 16 G10 -- a live breach is the present tense, and a
           projected time already behind us is not "breach at". */
        breachingNow:
          "Breaching now: live conditions project {{projected}}h against your {{hours}}h.",
        toleranceBreached:
          "Its {{toleranceTerm}} is {{hours}}h -- passed at {{breach}}.",
        toleranceNone:
          "No {{toleranceTerm}} is in force, so there is no breach clock on this.",
        open: "Open the incident: {{title}}",
      },
      evidence: {
        loading: "Loading evidence artefact...",
        error: "Could not load evidence artefact. Try again.",
        notFoundHeading: "Evidence artefact not found",
        notFoundBody:
          "We couldn't find an evidence artefact with id {{id}}. It may " +
          "have been detached and pruned, or the id is wrong.",
        folder: "Folder",
        folderTop: "Top of the library",
        untitled: "Evidence artefact",
        size: "Size",
        uploader: "Uploaded by",
        retrievedAt: "Retrieved at",
        /* Round 20 Y4 -- the upload date, said as such. */
        uploadedInstead: "not recorded; uploaded {{when}}",
        viewSource: "Source",
        viewSourceLink: "Open source",
        notesHeading: "Notes",
        linkedAssessments: "Linked assessments ({{count}})",
        linkedFindings: "Linked findings ({{count}})",
        noAssessments: "No assessments reference this artefact.",
        noFindings: "No findings reference this artefact.",
      },
      cascade: {
        heading: "Live cascade",
        subheading:
          "Open critical and high findings, the entities they impact, " +
          "and the downstream products at risk. Refreshes every 15 " +
          "seconds.",
        loading: "Loading cascade view...",
        errorTitle: "Could not load cascade",
        // C3 -- a reader arriving from a figure needs the page to say
        // that these rows are it.
        strip: {
          // D1 -- this counted `impacted`, which holds every entity
          // kind a finding lands on -- services, IBSes, capabilities,
          // vendors, CIs -- and called them business services. The
          // banner beside it counts IBSes downstream and the board
          // counts the MVB set: four honest answers to four different
          // questions, and a reader was left reconciling them as
          // contradictions because none said what it counted.
          all_one: "The {{count}} entity a finding reaches.",
          all_other: "All {{count}} entities a finding reaches.",
          filtered_one: "{{count}} entity a finding reaches at {{severity}} severity.",
          filtered_other:
            "{{count}} entities a finding reaches at {{severity}} severity.",
          /* D2 -- arriving from the board's "10 / 16 IBSes impacted".
             That figure counts IBSes and this page counts every entity
             a finding reaches; filtered to the kind, the figure and the
             rows are one set. */
          kind_one: "The {{count}} {{kind}} a finding reaches.",
          kind_other: "All {{count}} {{kind}} a finding reaches.",
          /* Round 10 F3 -- and what the figures ABOVE are counting
             while this list is filtered. The banner describes the
             whole cascade whatever the list is showing, so a filtered
             list of thirteen sat under "12 products impacted across 13
             entities / 45 nodes / 14 IBSes downstream" and the reader
             was left to work out that the two were different
             questions. */
          summaryUnfiltered: "The summary above counts the whole cascade.",
        },
        emptyTitle: "All clear",
        /* Round 12 B1 -- "All clear. No open critical or high findings
           impact any service right now" was rendered over a tenant with
           nothing in it. Arithmetically true and institutionally false:
           a zero is not a claim and All clear is. */
        notAssessedTitle: "Not yet assessed",
        notAssessedBody:
          "Nothing has been admitted, no tolerance is declared and no telemetry has been sampled, so there is nothing for a finding to impact. This is the absence of a verdict, not a clean one.",
        emptyBody:
          "No open critical or high findings impact any service " +
          "right now. The next telemetry connector run may change " +
          "this -- the view auto-refreshes every 15 seconds.",
        banner: {
          productsImpacted_one: "{{count}} product impacted",
          productsImpacted_other: "{{count}} products impacted",
          entitiesImpacted_one: "across {{count}} entity",
          entitiesImpacted_other: "across {{count}} entities",
          severityCritical: "{{count}} critical",
          severityHigh: "{{count}} high",
          cascadeStats:
            "{{nodes}} nodes / {{ibses}} {{serviceTermShortPlural}} downstream",
          revenueAtRiskDaily: "{{amount}} daily at risk",
          revenueAtRiskAnnual: "{{amount}} annually at risk",
          revenueMeasured: "{{measured}} of {{total}} products measured",
          revenueMeasuredAll: "all {{total}} products measured",
          mvbIntersection:
            "{{impacted}} of {{total}} {{mvbTermShort}} {{serviceTermShortPlural}} impacted ({{pct}}%)",
          splitHintExplicit: "(split-aware)",
          splitHintUniform: "(uniform-split)",
          resilienceRed_one: "{{count}} {{serviceTermShort}} red",
          resilienceRed_other: "{{count}} {{serviceTermShortPlural}} red",
          resilienceAmber_one: "{{count}} {{serviceTermShort}} amber",
          resilienceAmber_other: "{{count}} {{serviceTermShortPlural}} amber",
          resilienceLink: "Inspect on the operations wall",
        },
        impact: {
          /* Round 20 U7 -- a new breach on a service already red. */
          breachingSince: "breaching since {{since}}",
          openFindings_one: "{{count}} open finding",
          openFindings_other: "{{count}} open findings",
          /* Round 20 P22 -- the cascade counts critical and high only. */
          openSerious_one: "{{count}} open critical or high finding",
          openSerious_other: "{{count}} open critical or high findings",
          /* Round 20 P20 -- and through what it reaches them. */
          reachesThrough_one: "{{reach}}, through {{count}} dependent service",
          reachesThrough_other: "{{reach}}, through {{count}} dependent services",
          headlineSince: "Raised {{when}}",
          alsoBreached: "Also open on it:",
          /* Round 14 G -- a scenario's cascade is what WOULD happen.
             Its cards looked like live findings and linked to one; the
             id was a scenario impact, and the link was a 404. */
          wouldRaise_one: "Would raise {{count}} finding",
          wouldRaise_other: "Would raise {{count}} findings",
          wouldRaiseTitle: "Would raise: {{title}}",
          modelled: "Modelled by this scenario, not raised",
          cascadeStatsCounted: "{{nodes}} · {{ibses}}",
          nodesInBlast_one: "{{count}} node in blast",
          nodesInBlast_other: "{{count}} nodes in blast",
          reachesIbses_one: "reaches {{count}} {{serviceTermShort}}",
          reachesIbses_other: "reaches {{count}} {{serviceTermShortPlural}}",
          /* Round 17 P2 -- the war room beside the card also judges
             candidates and functions; the footer names that cut rather
             than reading 5 beside a war room judging 10 and 4. */
          reachesAdmitted_one: "reaches {{count}} admitted {{serviceTermShort}}",
          reachesAdmitted_other:
            "reaches {{count}} admitted {{serviceTermShortPlural}}",
          reachesWithCandidates: "{{count}} with candidates",
          reachesFunctions_one: "{{count}} critical internal function",
          reachesFunctions_other: "{{count}} critical internal functions",
          reachesCut: "{{admitted}} ({{rest}})",
          /* B4 -- for an IBS, which nothing depends on. "Reaches 0
             IBSes" was true and told a reader nothing, beneath a
             banner counting twelve. */
          restsOn_one: "Rests on {{count}} service; nothing downstream depends on it",
          restsOn_other:
            "Rests on {{count}} services; nothing downstream depends on it",
          /* Round 16 M1 -- a Critical Internal Function is not a leaf:
             the payments it funds stop when it stops. */
          restsOnAndFunds_one:
            "Rests on {{count}} service; funds {{names}}, which stop when it stops",
          restsOnAndFunds_other:
            "Rests on {{count}} services; funds {{names}}, which stop when it stops",
          externalLink: "View in source",
          deleted: "(deleted)",
          journeysHeading: "Customer journeys",
          journeySteps_one: "{{count}} step",
          journeySteps_other: "{{count}} steps",
          failingHere: "Failing here",
          degradedHere: "Degraded here",
        },
        chip: {
          resilience: "Resilience",
          blastIbsCount: "{{serviceTermShortPlural}}",
          // Round 18 D25 -- the chip beside "(10 with candidates)".
          blastIbsAdmitted: "Admitted {{serviceTermShortPlural}}",
          processes: "Processes",
          obligations: "Obligations",
          notApplicable: "n/a",
          /* Round 11 F2 -- a zero here is a mapping gap, not a
             quantity, and the digit read as a measurement beside a
             sentence naming three services. */
          noneMapped: "none mapped",
          noneMappedWhy:
            "No customer journey is mapped to this entity, so nothing " +
            "here can say which processes a disruption would interrupt.",
          /* ROUND 13 G2 -- the sealed chain said green while the card
             above it listed an open critical. The seal is written by a
             scheduled pass, so it lags the incident; this chip now
             reads the findings on the card and says where the number
             came from, because the chain page will still say green. */
          // Round 18 B17 -- the seal, uncoloured, over nothing admitted.
          resilienceUnadmitted:
            "This reaches no admitted service, so its seal is shown without colour: nothing the bank has admitted has been judged here.",
          resilienceFloored:
            "Read from the open findings on this card. The resilience " +
            "chain still reads {{sealed}} -- it is sealed by a scheduled " +
            "pass and has not run since these findings were raised.",
        },
        products: {
          heading: "Products impacted",
          viaIbs_one: "via {{count}} {{serviceTermShort}}",
          viaIbs_other: "via {{count}} {{serviceTermShortPlural}}",
          revenueLine: "{{annual}} annual / {{daily}} daily",
          revenueUnmeasured: "Revenue not measured",
        },
        homeCard: {
          heading: "Live cascade",
          loading: "Checking live state...",
          error: "Couldn't load live cascade.",
          /* B1 -- the same zero, on the home card: green over a bank
             with nothing in it. */
          notAssessed: "Not yet assessed",
          notAssessedPill: "nothing to judge",
          allClear: "All clear -- no live impact",
          clearPill: "clear",
          ibsesImpacted_one: "{{count}} {{serviceTermShort}} impacted",
          ibsesImpacted_other: "{{count}} {{serviceTermShortPlural}} impacted",
          openSummary_one: "across {{count}} entity",
          openSummary_other: "across {{count}} entities",
          link: "Open cascade",
        },
      },
      channels: {
        drawer: {
          title: "Per-cell resilience",
          channelLabel: "Channel: {{name}} ({{code}})",
          closeLabel: "Close",
          currentHeading: "Current state",
          sourceChannel: "explicit channel-level override",
          sourceIbs: "inherited from the {{serviceTermShort}} row",
          newStateLabel: "New state",
          reasonLabel: "Reason (optional)",
          reasonPlaceholder: "Why is this cell being set?",
          save: "Save",
          saving: "Saving...",
          cancel: "Cancel",
          auditHint:
            "Writes to the resilience chain. Every set fires an audit event " +
            "(admin.twin.ibs_channel.resilience.set).",
          readOnlyHint:
            "Admin role required to write per-cell state. Ask your tenant " +
            "admin to grant the admin role.",
        },
      },
      journeys: {
        heading: "Customer journeys",
        subheading:
          "Step-by-step customer flows per ({{serviceTermShort}}, channel). When the " +
          "cascade view surfaces an impacted {{serviceTermShort}} that has a journey, " +
          "the impact card shows the journey breadcrumb so you can see " +
          "exactly which step the customer hits the wall on.",
        searchPlaceholder: "Search {{serviceTermShort}} or channel...",
        loading: "Loading journeys...",
        empty: "No journeys match.",
        steps_one: "{{count}} step",
        steps_other: "{{count}} steps",
        detail: {
          // Round 15 I7 -- named by order, not by a side that flips.
          prompt: "Select a journey from the list to view its steps.",
          loading: "Loading journey...",
          noSteps: "No steps defined yet. Add some via the Foundation admin.",
        },
      },
      /* The 3D mode shared by every Operational Twin tab. The
         constellation keeps its own `constellation3d` namespace below:
         those strings belong to that VIEW, not to the mode, and moving
         them would churn a shipped surface for nothing. */
      twin3d: {
        title: "Operational Twin",
        /* ROUND 13 G6 -- the exported frame carried its title, the
           bank, the date, the counts, the legend and its source
           sentence, and named NO ENTITY -- so the picture left the
           product saying what kind of thing it showed and never which
           things, and the "yours" panel, which is the whole of the
           previous round's fix for this reader, went with it. */
        frame: {
          named: "In this picture:",
          yours: "Yours:",
          andMore: "and {{count}} more",
        },
        viewsLabel: "3D view",
        view: {
          constellation: "Constellation",
          cascade: "Cascade",
          journey: "Journey",
          fragility: "Fragility",
          city: "Capability city",
        },
        /* Where depth means something, what it means -- in the tier
           vocabulary the scene's own legend already uses, not the
           six-layer dependency spine it was being confused with. */
        depthAxis: {
          constellation:
            "Depth is the tier: channels nearest, then business services, services, vendors",
          cascade:
            "Depth is the tier the impairment travels through: vendors, services, business services, channels",
        },
        /* An estate-shaped statement about the bank's own dependency
           graph, which fired twice per render into a console no banker
           will open. */
        irregularEdges_one:
          "{{count}} dependency skips a tier or stays inside one -- drawn faint",
        irregularEdges_other:
          "{{count}} dependencies skip a tier or stay inside one -- drawn faint",
        subtitle: {
          constellation: "How the estate stands up, tier by tier",
          cascade: "Impact propagating upstream from an impairment",
          journey: "One customer outcome, end to end",
          fragility: "Where fragility clusters across the estate",
          /* Round 10 A5: printed cut off mid-phrase -- "carry the
             most" of WHAT was never said, and what the towers are
             actually sized by is the thing the reader needs. */
          city: "Which capabilities carry the most business services",
        },
        hint: {
          constellation:
            "Drag to rotate, scroll to zoom, click a node to trace its chain. Escape clears.",
          cascade:
            "Press play or drag the clock. Pick another origin, or hover any node for its hit time.",
          journey: "Press play to ride the chain.",
          fragility: "Hover a peak to name it.",
          city: "Click a tower to see what it carries.",
        },
        /* Round 14 B5 -- what each scene says when there is nothing in
           it to act on, in place of the invitation to click it. */
        empty: {
          constellation:
            "No {{serviceTerm}} or service is loaded yet, so there is no chain to trace.",
          city: "No capability carries a business service yet, so there are no towers to open.",
          fragility: "No service is loaded yet, so there are no peaks to name.",
          journey:
            "No customer journey is recorded yet. Journeys are added in the Foundation admin.",
        },
        journey: {
          pick: "Journey",
          broken: "· broken",
          scrub: "Ride position",
          orbit: "Orbit",
          legend_one: "{{count}} step on this journey",
          legend_other: "{{count}} steps on this journey",
          /* A journey with fewer than two mapped nodes is a mapping gap,
             not an empty picture. Say which. */
          noPath:
            "This journey's steps carry no entity tags, so there is no path to trace. Tag the steps in the Foundation admin.",
          unmodelled_one: "{{count}} step is not modelled as a dependency -- reconcile",
          unmodelled_other:
            "{{count}} steps are not modelled as dependencies -- reconcile",
          untagged_one: "{{count}} step carries no entity tag",
          untagged_other: "{{count}} steps carry no entity tag",
          tipStep: "Step {{n}} of {{total}}",
          tipOffPath: "Not on this journey",
        },
        /* Round 10 A5 -- the figures that shape a scene, named on the
           scene. The labels say what the number DOES, not what the
           column is called: "weight_sole_supplier_x100" is a column
           name, and a reader deciding whether to trust a picture needs
           the sentence. */
        figures: {
          heading: "Figures shaping this view, set by this bank",
          whose:
            "No regulator sets these. They are the bank's own tuning, and moving one moves the picture.",
          /* Round 11 C2 -- the disclosure the settings keys sit behind,
             so a board pack does not carry
             twin3d.fragility.weight_sole_vendor_impaired_x100 across
             its frame. */
          howSet: "Where these are set",
          open: "Open in the settings audit",
          changed: "changed by {{who}}, {{when}}",
          untouched: "MARSAD default {{value}}, never changed by this bank",
          label: {
            weightIbs: "Per business service standing on a service",
            weightCrit: "Extra, per critical business service",
            weightSoleSupplier: "Penalty: one supplier or none",
            weightImpaired: "Penalty: the service is itself impaired",
            weightSoleVendorImpaired: "Penalty: its sole vendor is impaired",
            hopDelay: "Minutes to cross one dependency hop",
            unknown: "Unrecognised figure",
          },
        },
        city: {
          metricLabel: "Tower height",
          filterLabel: "Show",
          metric: {
            reliance: "{{serviceTermShort}} reliance",
            vendors: "Vendors",
          },
          filter: {
            all: "All",
            critical: "Critical only",
            impaired: "Impaired chains",
          },
          ibsRelying: "{{serviceTermShort}} rely",
          services: "Services",
          vendors: "Vendors",
          /* Named for what it IS. Nothing in the twin declares which
             services REALISE a capability -- these are the ones
             supporting the business services that rely on it, and
             calling them "realised by" would invent a design
             statement nobody made. */
          viaServices: "Via its business services",
          vendorsBeneath: "Vendors beneath",
          // A red tower is red because of these two numbers. Naming
          // them separately matters on an estate where most services
          // are degraded through a dependency rather than down.
          chainState: "Chain: {{down}} down, {{degraded}} degraded",
          tip: "{{ibs}} business services · {{services}} services",
          unmappedTip: "nothing mapped to it yet",
          unmappedNote:
            "No business service is linked to this capability, so nothing can be said about what it carries. A gap in the map, not a healthy capability.",
          sharing_one: "{{count}} other capability stands on the same services",
          sharing_other: "{{count}} other capabilities stand on the same services",
          legend: {
            /* Round 10 A5. The colour key named the STATE and never the
               RULE, so with fifteen of eighteen towers red nothing on
               the scene said what had turned them. These say the rule,
               in the terms that actually drive it. */
            /* Round 12 E5 -- reported cut off mid-clause for a second
               round, at "...or that service's only vendor is". The
               cause was not a layout clip and no wrapping fix would
               have touched it: the sentence ENDS there in the
               catalogue, one word short, in the block that explains the
               only colour on the frame. */
            impaired:
              "Red: a service under one of its business services is degraded or down, or that service's only vendor is impaired",
            critical:
              "Gold: one of the business services standing on it is in the {{mvbTerm}}",
            steel: "Steel: neither -- nothing beneath it is impaired",
            unmapped_one: "{{count}} capability with nothing mapped to it",
            unmapped_other: "{{count}} capabilities with nothing mapped to them",
          },
          /* Said plainly because the walkthrough looked for a threshold
             here and there is none. The colours come from live state
             and from a signed declaration, so the honest answer is to
             name both and point at them rather than print a settings
             panel that would be a lie. */
          noThreshold:
            "No configurable threshold colours this scene. The two inputs are live service state and the approved minimum-viable-bank declaration.",
          heightIs_one:
            "Height is {{metric}}: {{low}} to {{high}} across {{count}} capability.",
          heightIs_other:
            "Height is {{metric}}: {{low}} to {{high}} across {{count}} capabilities.",
          /* A spread this narrow is a fact about the estate's capability
             map, not a broken picture, and saying so beats leaving a
             reader to conclude the render failed. */
          heightFlat:
            "Every mapped capability carries the same number, so the towers stand level -- a thin capability map, not a rendering fault.",
        },
        fragility: {
          metricLabel: "Height metric",
          metric: {
            fragility: "Fragility",
            reliance: "{{serviceTermShort}} reliance",
            vendor: "Vendor exposure",
          },
          legend_one: "Height is {{metric}}, across {{count}} service",
          legend_other: "Height is {{metric}}, across {{count}} services",
          /* Round 11 C3. The scene named a service and gave it a
             number and then the algebra that produced the number, with
             nothing saying whether the number was good or bad. */
          meaning: "Higher means {{higher}}.",
          /* ROUND 13 I1 -- the clause that gives the number its
             meaning. It was a string literal in
             `twin/twin3d/fragilityModel.ts`, interpolated into the
             sentence above, so a fully commissioned Arabic build
             would have shown this frame in Arabic and its meaning in
             English. It is the sentence this programme asked for
             twice, and it could have been catalogued in an
             afternoon. */
          higher: {
            reliance: "more {{serviceTermPlural}} stand on this one service",
            vendor: "fewer suppliers behind it, or one of them is already impaired",
            fragility:
              "more of the bank leans on this one service, with fewer ways round it",
          },
          /* I1, one line down -- the same charge. The operators and
             the variable names are an expression and stay; the
             CONNECTIVES were English prose in the same literal. */
          formula: {
            reliance: "ibs + 0.5 x critical",
            vendor:
              "(1 vendor: 3, none: 4, otherwise 1) + 3 if a vendor is impaired + 0.2 x ibs",
            fragility:
              "1 + {{ibs}} x ibs + {{crit}} x critical + {{soleSupplier}} if one supplier or none + {{impaired}} if impaired + {{soleVendorImpaired}} if its sole supplier is impaired",
          },
          /* Against this estate's own highest, NAMED -- not "out of
             20". The expression grows with the number of business
             services standing on a service, so there is no ceiling to
             be out of, and printing one would invent a scale. */
          highest: "The highest here is {{score}}, {{name}}.",
          howWorkedOut: "How this is worked out",
          tip: "{{metric}} {{score}} · {{ibs}} business services rely on it · {{vendors}} vendors",
        },
        /* Round 11 C3 -- whose screen this is. */
        yours: {
          heading: "Yours in this view",
          tag: "Yours",
          /* Round 12 E7 -- the walk read the gold tags on three of the
             reader's entities and not on the one he had come for, and
             concluded the panel named him while the picture pointed
             elsewhere. The MARK is the ring, drawn from the projection,
             which survives a collision that takes a name; the tag rides
             the label and does not. */
          mark: "Each one is ringed in gold in the scene. Click a name to fly to it.",
          reachedAt: "· reached {{clock}}",
          breachAt: "· declared tolerance exceeded {{clock}}",
          more_one: "and {{count}} more of yours.",
          more_other: "and {{count}} more of yours.",
          notHere_one: "{{count}} more of yours is not drawn in this view.",
          notHere_other: "{{count}} more of yours are not drawn in this view.",
          state: {
            origin: "The impairment starts here.",
            reached: "The impairment reaches it.",
            untouched: "This cascade never reaches it.",
            impaired: "Impaired now.",
            degraded: "Degraded now.",
            ok: "No open incident.",
          },
        },
        cascade: {
          play: "Play",
          pause: "Pause",
          scrub: "Cascade clock",
          speed: "Speed",
          speedX: "{{speed}}x",
          origin: "Origin",
          // Round 18 L12 -- a name two nodes share, told apart.
          originWithKind: "{{name}} ({{kind}})",
          kindFunction: "critical internal function",
          kindIbs: "{{serviceTermShort}}",
          kindService: "service",
          kindVendor: "vendor",
          laneChannels: "channels",
          laneIbs: "business services · ◆ tolerance breach",
          // Round 17 I16 -- each count its own plural ("1 business services").
          sentence: "{{ibs}} and {{channels}} impacted",
          sentenceFunctions: "{{ibs}}, {{functions}} and {{channels}} impacted",
          sentenceIbs_one: "{{count}} business service",
          sentenceIbs_other: "{{count}} business services",
          sentenceFunctionsPart_one: "{{count}} critical internal function",
          sentenceFunctionsPart_other: "{{count}} critical internal functions",
          sentenceChannels_one: "{{count}} channel",
          sentenceChannels_other: "{{count}} channels",
          /* Round 10 A1 -- said as a PROJECTION, because that is what
             it is: what this propagation would breach by the clock
             position, not what is breached now. The scene printed the
             bare number and read as a contradiction of the war room. */
          // Round 17 M6 -- the funding edge, said as the 2D card says it.
          // Round 18 M8 -- its own line now, so a sentence of its own.
          funded_one: "Funds {{names}}, which stops when it stops.",
          funded_other: "Funds {{names}}, which stop when it stops.",
          breached_one: "· {{count}} tolerance breached by then",
          breached_other: "· {{count}} tolerances breached by then",
          liveBreached_one: "War room now: {{count}} breached",
          liveBreached_other: "War room now: {{count}} breached",
          source: {
            live: "Live",
            scenario: "Scenario",
          },
          originsAre: "Propagating from: {{names}}",
          originsMore_one: "and {{count}} more.",
          originsMore_other: "and {{count}} more.",
          /* Round 11 C3 -- this said "from the war room" and it was not
             true. The origins are every entity an open critical or high
             finding is impacting, read from /v1/cascade; the war room
             is a different surface answering a different question, and
             its own figure is printed beside the clock with its own
             name on it. A service owner read the wrong attribution and
             concluded, reasonably, that the scene was showing somebody
             else's scenario while his own outage was open. */
          liveOrigins_one:
            "1 impairment, from the critical and high findings open right now.",
          liveOrigins_other:
            "{{count}} impairments, from the critical and high findings open right now.",
          /* Not "all clear". No open finding and a cascade with no
             origin are different claims, and an empty scene explains
             neither. */
          noLiveOrigin:
            "No critical or high finding is impacting anything right now, so there is nothing to propagate. Switch to Scenario to choose an origin.",
          /* Round 12 F4 -- the sentence above was printed for twelve
             seconds during a service owner's open critical incident, on
             the scene his own alert routed him to, over a canvas
             reading "0 nodes · 0 dependencies". The graph was empty
             because nothing had arrived. An empty state asserted where
             a loading state belongs is the worst place this product can
             be wrong. */
          stillReading:
            "Still reading the estate. Nothing is being claimed about what is or is not impaired until it has answered.",
          legend: {
            breached: "Tolerance breached",
            reached: "Impact reached",
            untouched_one: "{{count}} node the impairment never reaches",
            untouched_other: "{{count}} nodes the impairment never reaches",
          },
          tip: {
            notInCascade: "Not in the cascade",
            origin: "Origin of the impairment",
            hops_one: "{{count}} hop upstream",
            hops_other: "{{count}} hops upstream",
            impactedAt: "impacted at {{clock}}",
            tolerance: "tolerance {{hours}}h, breach at {{clock}}",
            /* Silence here would read as "within tolerance", which is a
               different claim from having no tolerance to be within. */
            noTolerance: "no {{toleranceTerm}} declared",
          },
        },
      },
      constellation3d: {
        /* ROUND 15 K8 -- a chunk the new build no longer serves. */
        couldNotLoad: "The 3D view could not load; reload the page.",
        reload: "Reload the page",
        /* The 3D inspector's Close button referenced this and it was
           never defined, so the button read "modules.twin.
           constellation3d.close". */
        close: "Close",
        mode2d: "2D",
        mode3d: "3D",
        modeLabel: "View mode",
        /* Round 12 E3 -- the scene's code arrives as its own chunk, and
           while it does the stage was a black rectangle with nothing in
           it. Two of eight drives caught that state: no canvas, no
           fallback, no unsupported message, nothing said. */
        sceneLoading: "Building the scene...",
        unsupported:
          "3D needs WebGL, which this browser or device does not provide. Showing the 2D constellation instead.",
        tier: {
          channel: "Channels",
          ibs: "{{serviceTermPlural, capitalize}}",
          service: "Services",
          vendor: "Vendors",
        },
        lens: {
          all: "All",
          critical: "Critical {{serviceTermShort}}",
          blast: "Blast radius",
          concentration: "Vendor concentration",
        },
        layout: {
          tiers: "Tiers",
          orbits: "Orbits",
          free: "Free",
        },
        labels: {
          key: "Key",
          all: "All",
          off: "Off",
        },
        reset: "Reset view",
        capture: "PNG",
        panelsTuck: "Tuck panels",
        panelsShow: "Show panels",
        panelsTab: "Legend and controls",
        present: "Present",
        presentStop: "End presentation",
        orbitFree: "Free orbit",
        orbitFreeHint:
          "Roll the estate any way you like. Reset puts it the right way up again.",
        links_one: "{{count}} link",
        links_other: "{{count}} links",
        whatIf: {
          // Round 17 M7 -- what the what-if reaches, funding included.
          reaches_one: "Reaches {{count}} node",
          reaches_other: "Reaches {{count}} nodes",
          /* Round 18 M10 -- said through the function that funds
             them: the bare "; funds 3 services" read as if the
             impaired node did. */
          fundsThrough_one: "; through {{funder}}, which funds {{names}}",
          fundsThrough_other:
            "; through {{funder}}, which funds {{count}} services: {{names}}",
          open: "What if...",
          label: "What if... is impaired",
          search: "Find a node",
          noMatch: "No match",
          remove: "Remove from scenario",
          scenario: "Scenario",
          /* Round 11 C3 -- the same wrong attribution as the cascade's
             origin note, on the same status set. It is /v1/cascade. */
          live: "Live from open findings",
          backToLive: "Back to live",
          save: "Save as scenario",
          // Round 16 M5 -- the scenario is named, not composed.
          nameLabel: "Scenario name",
          defaultName: "What if {{names}} fails",
          saved_one: "Saved with {{count}} impact",
          saved_other: "Saved with {{count}} impacts",
          notSaved:
            "-- not saved: {{names}} (channels and vendors are not scenario targets)",
        },
        status: {
          degraded: "Degraded",
          impaired: "Impaired",
        },
        critical: "Critical",
        upstream: "Upstream",
        downstream: "Downstream",
        direct: "Direct",
        openRecord: "Open record",
        /* Round 10 A1: the same words the flat view uses for the same
           overlay, because it is the same overlay. */
        howWeGotHere: "How we got here",
        // Round 18 M10 -- the panel's count under a scenario.
        scenarioReachedCount_one:
          "{{count}} reached beyond the origin by this scenario",
        scenarioReachedCount_other:
          "{{count}} reached beyond the origin by this scenario",
        impairedCount_one: "{{count}} impaired or degraded",
        impairedCount_other: "{{count}} impaired or degraded",
        /* Round 19 B30 -- over an estate with nothing admitted, the count
           is of nodes with open findings, in a neutral colour. */
        withFindingsCount_one: "{{count}} with open findings",
        withFindingsCount_other: "{{count}} with open findings",
        depthHint: "Depth is the dependency layer",
        /* Round 10 A1 -- this read "... . depth is the dependency
           layer" on ALL FOUR scenes, in the release whose headline
           change is that a business service rests on six NAMED layers.
           Two things wrong in one clause: depth is not the six-layer
           spine, and on three of the four scenes depth is not a
           dependency axis at all. The claim moved to the scenes where
           it is true, and says what it actually is. */
        /* Round 13 B4 -- the seven "nodes" on an empty tenant were the
           shipped channel catalogue. A reference catalogue counted as
           the bank's estate makes an empty tenant look partly
           populated. */
        countsShippedOnly_one:
          "1 shipped channel \u00b7 none of the bank's own entities loaded yet",
        countsShippedOnly_other:
          "{{count}} shipped channels \u00b7 none of the bank's own entities loaded yet",
        /* Round 13 C1 -- the scene draws the most important N and says
           so. A round trip into the animating scene cost 3,076 ms at
           20,000 items against a 200 ms criterion; a scene silently
           drawing a subset is a scene lying about the estate. */
        countsCapped_one:
          "{{shown}} of the most affected nodes \u00b7 {{edges}} dependencies \u00b7 1 more not drawn",
        countsCapped_other:
          "{{shown}} of the most affected nodes \u00b7 {{edges}} dependencies \u00b7 {{count}} more not drawn",
        counts: "{{nodes}} nodes \u00b7 {{edges}} dependencies",
        /* ROUND 15 E7 -- each scene states its own count. The city
           headed "400 of the most affected nodes" over 200 capabilities. */
        countsCity_one:
          "{{count}} capability across {{plates}} districts \u00b7 {{unmapped}} with no business service",
        countsCity_other:
          "{{count}} capabilities across {{plates}} districts \u00b7 {{unmapped}} with no business service",
        countsCascade_one:
          "{{count}} of {{drawn}} drawn nodes reached \u00b7 {{graph}}",
        countsCascade_other:
          "{{count}} of {{drawn}} drawn nodes reached \u00b7 {{graph}}",
        /* Round 19 P18 -- with an origin, the count leaves it out, as
           the panel's does, and says so. */
        countsCascadeBeyond_one:
          "{{count}} of {{drawn}} drawn nodes reached beyond the origin \u00b7 {{graph}}",
        countsCascadeBeyond_other:
          "{{count}} of {{drawn}} drawn nodes reached beyond the origin \u00b7 {{graph}}",
        /* Round 16 E13 -- fragility and an unchosen journey, by their
           own count. */
        countsFragility_one: "{{count}} service by fragility",
        countsFragility_other: "{{count}} services by fragility",
        countsJourneysRecorded_one: "1 customer journey recorded",
        countsJourneysRecorded_other: "{{count}} customer journeys recorded",
        countsJourney_one: "{{name}} \u00b7 {{count}} step",
        countsJourney_other: "{{name}} \u00b7 {{count}} steps",
        /* And the legend counts what is DRAWN: "Channels · 0" under
           "channels nearest" read as an estate with none. */
        tierDrawn: "{{caption}} \u00b7 {{drawn}} of {{total}} drawn",
        // P429.6 -- on the IBS ring, in their own colour, counted apart.
        cifLegend: "Critical Internal Functions \u00b7 {{count}}",
      },
      constellation: {
        /* Round 9 F2 -- the rows behind a highlighted figure. */
        metricList: "See the rows this counts",
        kicker: "OPERATIONAL TWIN -- LIVE CONSTELLATION",
        heading: "Click any node to trace its blast radius.",
        loading: "Loading constellation...",
        error: "Could not load constellation topology",
        /* Round 12 A6 -- 492 characters of five zero tiles and one
           sentence about a node she cannot select. It named a place
           ("Foundation admin") without going there, and named it to a
           reader who may not be admitted. */
        empty:
          "Nothing is in the estate yet. The constellation draws the channels, {{serviceTermShortPlural}}, services and vendors the bank has loaded -- from a CSV template, or from a connected CMDB -- and fills in as they land.",
        emptyCta: "Load the estate",
        blastToggle: "Show blast radius",
        /* Round 12 E4 -- the walk read 78, 80, 81 and 83 nodes across
           five loads of the same estate and found nothing on the page
           saying where the figure came from. Driven again it is stable
           over five loads and over sixteen seconds on one, so what is
           left is the sentence: the estate is live, two readings an
           hour apart can legitimately differ, and a figure that can
           move without saying it may is one a reader is right to
           distrust. */
        drawnFrom:
          "Drawn from the live twin graph: {{nodes}} entities and {{edges}} dependencies as recorded now.",
        /* Labels that would collide are dropped rather than
           overprinted. On the live estate that is seventeen of
           seventy-eight names, and the walk counted twenty and wrote it
           down as a defect -- reasonably, because a scene that draws a
           node and declines to name it, saying nothing, reads as a
           scene that does not know what it is drawing. */
        /* Round 13 C6 -- at 3,031 nodes a click selected a neighbour 174
           times, because the orbs stack rather than sit adjacent. The
           layout answer for three thousand orbs in one viewport is not
           to draw three thousand orbs. */
        nodesCapped_one:
          "Showing the {{shown}} that carry the most -- 1 more is not drawn. Search for it by name.",
        nodesCapped_other:
          "Showing the {{shown}} that carry the most -- {{count}} more are not drawn. Search for one by name.",
        labelsHidden_one:
          "1 name is not drawn where it would print over another; hover or click the node to read it.",
        labelsHidden_other:
          "{{count}} names are not drawn where they would print over another; hover or click a node to read it.",
        search: {
          placeholder: "Find a node...",
          count_one: "{{count}} match",
          count_other: "{{count}} matches",
          // Round 14 E1 -- more matches than the drawing brings in.
          countCapped:
            "{{count}} matches, the first {{drawn}} drawn -- type more to narrow it",
          clear: "Clear search",
        },
        modes: {
          observatory: "Observatory",
          flow: "Dependency Flow",
          orbit: "Resilience Orbit",
          ring: "Service Ring",
        },
        trace: {
          downstream_one: "{{count}} downstream -- affected if this fails",
          downstream_other: "{{count}} downstream -- affected if this fails",
          upstream_one: "{{count}} upstream -- must recover first",
          upstream_other: "{{count}} upstream -- must recover first",
          cycle_one: "{{count}} on a cycle -- recovers together with this",
          cycle_other: "{{count}} on a cycle -- recover together with this",
          skipHint: "Click anywhere to skip",
        },
        ring: {
          showIsolated: "Show unconnected",
          isolatedHint:
            "Services with no modelled dependency sit off the ring by default. Showing them makes the gap visible: it is what the CMDB states, not a missing part of the picture.",
        },
        lens: {
          concentration: "Concentration",
          exposure: "Exposure",
          live: "Live health",
          mvb: "{{mvbTermShort}}",
          mvbCount: "{{count}} node(s) in the {{mvbTermShort}} scope",
          liveCount: "{{down}} down · {{degraded}} degraded",
          hint:
            "Fragility lens. Concentration: upstream single points of failure " +
            "(big blast x {{mvbTermShort}}-downstream x stress). Exposure: thinly-protected " +
            "{{serviceTermShortPlural}}. The fragile nodes glow largest.",
        },
        metrics: {
          /* ROUND 15 E7 -- "1 IBSes impacted" on the flat rail. */
          ibsImpacted_one: "{{serviceTermShort}} impacted",
          ibsImpacted_other: "{{serviceTermShortPlural}} impacted",
          criticalFindings: "Critical findings",
          revenueDaily: "Revenue at risk / day",
          mvbIntersection: "{{mvbTermShort}} intersection",
          channelsDegraded: "Channels degraded",
        },
        inspector: {
          close: "Close inspector",
          // Round 18 B16 -- no finding on a node nothing has judged.
          notAssessed: "Not assessed",
          notAssessedTitle:
            "Nothing has judged this yet: no admission with a declared tolerance, no observation and no fresh telemetry. No open finding is the absence of a verdict, not a clean one.",
          stateTrace: "Trace how this state was derived",
          /* Round 19 B30 -- what "Not assessed" means, under the pill. */
          notAssessedWhy:
            "No judgement yet: no admission with a declared tolerance, no observation and no fresh telemetry.",
          /* Round 19 B25 -- the node's word, from its latest seal. */
          seal: {
            down: "Down",
            atRisk: "At risk",
            stable: "Stable",
            notAssessed: "Not assessed",
            notSealed: "Not sealed",
          },
          notSealedTitle:
            "The resilience chain does not seal this kind of node on its own; its word is its findings' count.",
          openFindings: "open",
          revenueAtRisk: "Revenue at risk",
          sovereign: "Sovereign-mandated",
          inMvb: "In {{mvbTermShort}}",
          // P429.6 -- a function the bank performs for itself.
          registerClass: "Register class",
          cif: "Critical Internal Function (no delivery channel)",
          stateService: "State service (no delivery channel)",
          bizCrit: "Business criticality",
          materiality: "Materiality",
          depsCount: "Direct dependencies",
          reach: "Downstream reach",
          dependsOn: "Depends on",
          blastTitle: "Blast radius -- if this fails",
          blastLoading: "Computing blast...",
          blastNone: "Nothing downstream depends on this node.",
          blastSummary:
            "Failure here reaches {{count}} downstream node(s). Up to {{sar}}/yr of revenue exposed across the business services it reaches.",
          deriveButton: "Show how this was derived",
          // Round 19 H57 -- a channel has no sealed blast radius.
          deriveNoneChannel:
            "Channels carry no blast-radius seal. Select a service this channel delivers to see how its reach was derived.",
        },
        scrubber: {
          now: "Now",
          empty: "No sealed snapshots for this node yet.",
          sealed: "sealed · {{hash}}",
          live: "live state",
        },
        provenance: {
          kicker: "PROVENANCE · HOW WE GOT HERE",
          close: "Close trace",
          loading: "Loading trace...",
          errorTitle: "Could not load trace",
          formula: "Derivation",
          lineage: "Lineage",
          emptyInputs:
            "No inputs recorded yet -- the deriver hasn't been wired against this chain. The formula above is the canonical rule.",
          sealedOutput: "Sealed output",
          verifyAction: "Verify chain",
          verifyRunning: "Verifying...",
          verifyAdminOnly: "Admin-only action.",
          // C4 -- name the chain. "Chain intact" said nothing about WHICH
          // chain, which is how a stale verdict passed for a fresh one.
          verifyOk: "{{chain}} chain intact: {{examined}} snapshot(s) checked.",
          verifyBroken:
            "Break at snapshot {{at}} of {{examined}}. Tell the head of " +
            "operational resilience and the CRO, and do not issue a dossier.",
          // Round 16 C15 -- not tamper; a governance fault.
          verifyDefects_one:
            "{{count}} approval is sealed twice. The chain is intact; the record holds one decision approved twice.",
          verifyDefects_other:
            "{{count}} approvals are sealed twice. The chain is intact; the record holds decisions approved twice.",
        },
      },
      /* P429.1 -- the Critical Internal Function register (Stream C). */
      cifs: {
        // Round 17 L8 -- "Stream C" named the methodology's own stream to
        // a reader who had never met the other two.
        kicker: "Critical internal functions",
        heading: "Critical internal functions",
        standfirst:
          "Functions the bank performs for itself -- funding, settlement, ledger close, screening -- whose failure would impair the bank before any customer or the State saw it. The test for customer-facing services can never admit them; this is the second, narrower door. The CRO and the CIO sign each one, and the Board notes the register.",
        size: "{{admitted}} admitted. Expected ceiling {{ceiling}}: above it, raise the thresholds rather than accept the register.",
        forbidden: "This register is read by those who can be asked to sign it.",
        loading: "Loading the register...",
        loadFailed: "The register could not be loaded.",
        empty:
          "No Critical Internal Functions yet. Raise one over the services it rests on.",
        noServices: "no supporting services mapped",
        open: "Open",
        hide: "Hide",
        // Round 16 N5 -- each row opens the function's own page.
        page: "Function page",
        pageOf: "{{name}}: its page",
        k2Flag: "K2 check",
        k2Note:
          "The services that depend on this function would breach their tolerance ({{hours}}h) before it could impair the bank. It is probably a mapped resource of those services, not a Critical Internal Function. This is for the challenger, not a refusal.",
        excludedBecause: "Not admitted: {{reason}}.",
        triggers: "Admitted on: {{triggers}}.",
        // Round 16 N2 -- until the signatures are in, it is assessed.
        triggersAssessed: "Assessed on: {{triggers}}.",
        noTrigger: "Qualified, but no trigger fired: deferred.",
        approved:
          "Admitted. The CRO and the CIO have signed; the Board notes the register.",
        awaitingChallenge: "Waiting on the second-line challenge.",
        challenge: "Record the challenge",
        signed: "Signature recorded.",
        actionFailed: "That was not recorded. {{detail}}",
        awaiting: {
          cro: "Challenged. Waiting on the CRO.",
          cio: "The CRO has signed. Waiting on the CIO.",
          /* Round 16 N2 -- who, by name, and who comes next. */
          croNamed:
            "Challenged by {{challenger}}, {{when}}. Waiting on {{cro}}, then {{cio}}.",
          cioNamed: "Signed by {{signer}}, {{when}}. Waiting on {{cio}}.",
          named: "{{name}} ({{role}})",
          seat: {
            cro: "the CRO",
            cio: "the CIO",
          },
          secondLine: "the second line",
        },
        sign: {
          cro: "Sign as CRO",
          cio: "Sign as CIO",
        },
        status: {
          candidate: "Candidate",
          admitted: "Admitted",
          removed: "Removed",
        },
        decision: {
          none: "Not assessed",
          included: "Included",
          excluded: "Excluded",
          deferred: "Deferred",
          /* Round 16 -- "Included" beside "0 admitted" read as a
             contradiction: the assessment had concluded, the dual
             signature had not. Until it has, the pill says both. */
          pending: "Assessed: {{decision}} · awaiting sign-off",
          // Round 16 N2 -- before the challenge, it awaits the challenge.
          pendingChallenge: "Assessed: {{decision}} · awaiting the challenge",
        },
        trigger: {
          viability: "viability",
          time_critical: "time critical",
          licence: "licence",
          contagion: "contagion",
          composite: "composite",
        },
        kind: {
          funding_settlement: "Funding and settlement",
          liquidity: "Liquidity",
          collateral: "Collateral",
          positions: "Positions",
          ledger: "Ledger",
          screening: "Screening",
          prudential_reporting: "Prudential reporting",
          other: "Other",
        },
        contagion: {
          heading: "F4 contagion: {{band}} of 4, computed, never entered",
          dependents:
            "Admitted services that rest on what this function rests on: {{names}}.",
          /* Round 16 M4 -- read from the declared edges, the list is
             what the function funds or shares a regulatory commitment
             with, and the sentence said it was what shares its supports. */
          dependentsDeclared:
            "Admitted services it funds, or shares a regulatory commitment with: {{names}}.",
          noneDeclared:
            "No admitted service is funded by this function or shares a regulatory commitment with it.",
          none: "No admitted service rests on what this function rests on.",
          lowerBound:
            "A lower bound: no funding or regulatory edge is mapped to this function yet, so this counts services sharing its supporting services.",
          declared:
            "Read from the funding and regulatory edges declared for this function.",
        },
        /* Round 17 N9 -- the recorded answers, read-only. */
        answers: {
          score: "{{value}} of 4",
        },
        assess: {
          heading: "Critical internal function assessment",
          f4Note: "F4 contagion is computed from the graph and is never entered.",
          // Round 18 L13 -- recorded, waiting on its challenge.
          replaces:
            "Recording again replaces this assessment and restarts the challenge.",
          recordAgain: "Record the assessment again",
          inRoute2:
            "This assessment is in Route 2. Recording another would replace it and restart the challenge and both signatures, so it is not offered until the ceremony ends.",
          choose: "Choose",
          yes: "Yes",
          no: "No",
          why: "Why, and from what source",
          submit: "Record the assessment",
          done: "Assessment recorded. It now waits on the second-line challenge.",
          criteria: {
            k1_firm_continuity: "K1 firm continuity",
            k2_precedence: "K2 precedence",
            k3_non_substitutable: "K3 not substitutable",
            time_to_impairment: "F1 time to impairment (0-4)",
            firm_severity: "F2 severity of firm impact (0-4)",
            regulatory_consequence: "F3 regulatory and licence consequence (0-4)",
          },
        },
        raise: {
          open: "Raise a function",
          close: "Close",
          name: "Function name",
          kind: "What it does for the bank",
          services: "The services it rests on",
          rationale: "Why these services",
          submit: "Raise the function",
          done: "Function raised as a candidate.",
        },
      },
      ibsAdmission: {
        // E2 -- these four finished without a word, so the operator
        // pressed again or went to another page to find out.
        derivedToast: "Scored {{name}}. Read the rubric before challenging it.",
        engineOff:
          "The Marsad Engine is not configured. Candidates can still be scored by hand on any row.",

        removalToast:
          "Removal requested for {{name}}. It needs the same two signatures.",
        challengedToast: "Challenge recorded. It is now with the {{role}}.",
        signedToast: "Signed. It is now with the {{role}}.",
        // Round 18 N11 -- the person, where the product knows one.
        challengedToastNamed: "Challenge recorded. It is now with {{name}} ({{role}}).",
        signedToastNamed: "Signed. It is now with {{name}} ({{role}}).",
        approvedToast: "Approved. {{name}} is admitted to the register.",
        kicker: "OPERATIONAL TWIN -- {{serviceTermShort}} IDENTIFICATION",
        heading: "{{serviceTermShort}} admission register",
        nbsGate: "NBS gate (evidence)",
        q1Outcome: "Q1 -- direct customer outcome",
        q2Material: "Q2 -- material financial impact",
        /* ROUND 15 A16 -- Q2 read "Yes" four lines above the answer's
           own "workarounds exist". Said where it happens. */
        gateReasoningSaysWorkaround:
          "The answer's own reasoning says workarounds exist. One of the two is wrong; read the reasoning before you challenge.",
        breadthScore: "reach score {{score}}",
        gateBasis: "{{basis}} lost over a {{hours}}h outage vs threshold {{threshold}}",
        gateWorkaroundYes: "evidence: every supporting service has a proven workaround",
        /* Round 14 A9 -- what the REGISTER records, not a finding about
           the world. It read "no credible workaround" beside a rationale
           naming workarounds; the register may simply not record them. */
        gateWorkaroundNo:
          "evidence: the register records no workaround posture for {{names}}",
        gateWorkaroundUnknown:
          "no supporting service records a continuity posture, so this cannot be judged",
        gateMagnitude:
          "Impact magnitude (evidence, not a gate test): {{basis}} over {{hours}}h vs {{threshold}}",
        gateBasisUnknown: "no basis -- tolerance not declared",
        gateUngrounded:
          "Q2 cannot be grounded: this {{serviceTermShort}} has no declared {{toleranceTerm}}, " +
          "so the financial impact of an outage is unknown. Declare a " +
          "tolerance to evidence the gate.",
        gateDisagree:
          "Engine and evidence disagree on Q2 -- the financial basis is on the other side of the materiality threshold. Second-line challenge should reconcile.",
        // Round 20 G45 -- the challenge's answer to it, or none.
        gateReconciled: "Reconciled at challenge by {{by}}: {{text}}",
        gateNotReconciled:
          "Not reconciled: the challenge was recorded without answering this disagreement.",
        // Round 17 H34 -- who scores: the resilience desk, with the
        // Engine or by hand. The standfirst said only the Engine, above
        // rows reading "Scored by the resilience desk".
        intro:
          "The resilience desk scores each {{serviceTermShort}} candidate against the methodology, with the Marsad Engine or by hand; a second-line reviewer challenges the score and two signatures settle it. The scores are a judgement; the rules decide.",
        loading: "Loading candidates...",
        error: "Could not load {{serviceTermShort}} candidates.",
        empty:
          "No {{serviceTermShort}} candidates yet. Promote a service to get started.",
        review: {
          heading: "Reviews due",
          intro:
            "Admitted {{serviceTermShortPlural}} carry a tier-driven review cadence (Tier 0/1 quarterly, else annually). These are due or overdue for re-derivation + re-sign-off.",
          overdue: "{{count}} overdue",
          dueSoon: "{{count}} due soon",
          allCurrent:
            "All admitted {{serviceTermShortPlural}} are within their review cadence.",
          // ROUND 15 B8 -- with none admitted there is nothing to review.
          nothingAdmittedYet:
            "No {{serviceTermShort}} is admitted yet, so there is nothing to review.",
          colIbs: "{{serviceTermShort}}",
          colTier: "Tier",
          colDue: "Next review due",
          colStatus: "Status",
          colDrift: "Drift",
          status: {
            overdue: "Overdue",
            due_soon: "Due soon",
            current: "Current",
            unscheduled: "Unscheduled",
          },
          drift: {
            promoted: "Promoted",
            demoted: "Demoted",
            stable: "Stable",
            new: "New",
          },
        },
        promote: {
          done: "{{name}} raised as {{serviceTermShortA}} candidate. Derive its admission scores, then it needs a second-line challenge and two signatures.",
          failed: "The candidate was not raised",
          theService: "The service",
          toggle: "Promote a service to {{serviceTermShort}} candidate",
          heading: "Promote a service",
          serviceLabel: "Service",
          // F -- the list mixed customer services with the canteen and
          // the wiki in one flat alphabetical run. Each row says which
          // it is.
          serviceOption: "{{name}} ({{code}}) -- {{classification}}",
          servicePlaceholder: "Select a service...",
          /* ROUND 15 H7 -- the canteen booking system and the internal
             wiki were offered for promotion beside the bank's customer
             services. Held behind a toggle rather than hidden. */
          showInternal_one:
            "Show {{count}} internal tool (non-critical or out of scope)",
          showInternal_other:
            "Show {{count}} internal tools (non-critical or out of scope)",
          hideInternal: "Hide the internal tools",
          /* A1 -- an empty picker must name the reason, the seat and
             the route.

             It said "No services available to promote." and stopped,
             while the rest of the form rendered live and fillable: the
             Resilience Lead was shown five constructs she had never
             met, invited to fill three of them, and refused at the one
             field that mattered, silently. She reached zero admitted
             services. Going looking told her no more -- the register
             says "0 services. No services match" over a line naming
             its own refusal and not who can lift it, and the only
             place in the product that named the missing seat was one
             line on the Journey spine.

             Three different empty states, because they need three
             different moves. */
          noServicesLoaded:
            "No services are loaded yet. Services are loaded by the data steward from the service catalogue; 0 are loaded.",
          noServicesLoadedCta: "Load services",
          /* ROUND 13 G4 -- and then the route refused her. The panel
             already knows her roles; offering a seat without the admin
             role a door that closes in its face is worse than offering
             none, because she spends the click finding out. */
          loadIsNotYourSeat:
            // Round 14 F4 -- it named the Users and roles page, which her
            // seat is refused. The link below goes somewhere she can read.
            "Loading the register is the data steward's step, and it needs the admin role -- yours does not have it. Ask your platform administrator to load it.",
          loadSeeTheStep: "See the step and what it waits on",
          allPromoted:
            "Every service in the register is already promoted or classified out of scope ({{promoted}} promoted, {{outOfScope}} out of scope, {{total}} in total).",
          /* A refusal is not an empty estate, and until now they
             rendered identically: the picker showed "No services
             available to promote" whether the register was empty, the
             request failed, or the seat was not allowed to ask. */
          servicesUnavailable:
            "The service list could not be loaded, so nothing can be offered here yet.",
          /* Stated on the panel, because two loaded services were
             silently excluded during the walk and nothing accounted
             for it. */
          eligibilityRule:
            "Eligible: any service the bank has not already promoted and has not classified out of scope.",
          outcomeLabel: "Outcome category",
          rationaleLabel: "Why is this {{serviceTermShortA}}?",
          rationalePlaceholder:
            "What customer outcome would a disruption harm, and why does it cross the threshold?",
          sovereignLabel: "Sovereign-mandated (SMS pathway)",
          streamLabel: "Provenance stream",
          streamHint:
            "Where this candidate came from -- shapes the evidence the gate reads. Sovereign candidates are auto-tagged regulator-named.",
          needService: "Choose the service you are proposing.",
          needRationale: "Say why this is {{serviceTermShortA}}.",
          submit: "Raise candidate",
          submitting: "Raising...",
          cancel: "Cancel",
          outcome: {
            minimize_customer_harm: "Minimise customer harm",
            retain_customer_confidence: "Retain customer confidence",
            regulatory_engagement: "Regulatory engagement",
            protect_safety_and_soundness: "Protect safety + soundness",
          },
          stream: {
            catalogue: "Service catalogue",
            nbs: "NBS nomination",
            regulator_named: "Regulator-named",
          },
        },
        stream: {
          catalogue: "Catalogue",
          nbs: "NBS",
          regulator_named: "Regulator-named",
        },
        /* Round 11 F3 -- what these four words mean. They are the
           methodology's intake provenance (runbook section 3) and were
           printed as uppercase chips with no explanation anywhere in
           the product, on the register where an IBS is admitted. */
        /* ROUND 15 H10 -- the tooltip was the only explanation; a
           sighted reader never found it. Said under the chip. */
        streamExplained: "{{stream}}: {{why}}",
        streamWhy: {
          catalogue:
            "Raised from the bank's own service catalogue: somebody proposed " +
            "this service for assessment.",
          nbs:
            "Nominated through the National Business Services screen -- the " +
            "customer-outcome and material-financial gate.",
          regulator_named:
            "Named by the regulator, so it enters on the sovereign pathway " +
            "rather than through the standard gate.",
        },
        sovereignWhy:
          "Sovereign or regulator-mandated: assessed on the SMS pathway, " +
          "where a single mandated criterion can admit it on its own.",
        removal: {
          open: "Request removal",
          heading: "Remove this {{serviceTermShort}}",
          intro:
            "Mints an excluded decision; the second-line challenge + CRO/business-owner sign-off then governs the removal (the {{serviceTermShort}} leaves the register on the 2nd sign-off).",
          placeholder:
            "Why is this {{serviceTermShort}} no longer in scope? (e.g. service decommissioned)",
          submit: "Raise removal",
          submitting: "Raising...",
          cancel: "Cancel",
        },
        sovereign: "Sovereign",
        derive: "Derive (Engine)",
        deriving: "Deriving...",
        deriveFailed: "Derivation failed.",
        /* A4 -- with the Engine off there was no way to score a candidate
         at all: Derive answered with an environment variable, which is
         an instruction for somebody with shell access shown to somebody
         who has none. */
        manual: {
          engineOff:
            "Score candidates by hand on any row — the rules, the decision and the tier are derived exactly as they are from an Engine proposal.",
          open: "Score manually",
          hide: "Hide manual scoring",
          explainer:
            "Score each criterion {{lo}} to {{hi}}; each score's band is beside it, as the rules read it. The decision and criticality tier are derived from these — you are proposing the scores, not the verdict.",
          // Round 20 W10 -- which form this is, and why, before scoring.
          pathway: {
            ces_one:
              "Customer-exposed service pathway: {{count}} criterion, after the two gate questions.",
            ces_other:
              "Customer-exposed service pathway: {{count}} criteria, after the two gate questions.",
            ids_one:
              "Indirect (sovereign) service pathway: {{count}} criterion, after the three qualification questions.",
            ids_other:
              "Indirect (sovereign) service pathway: {{count}} criteria, after the three qualification questions.",
            critical_operation_one:
              "Critical operation pathway (E-21): {{count}} criterion, after the two gate questions.",
            critical_operation_other:
              "Critical operation pathway (E-21): {{count}} criteria, after the two gate questions.",
          },
          pathwayReason: {
            sovereign: "On this pathway because the register marks it sovereign.",
            rail_name:
              "On this pathway because its code or name matches a sovereign rail; the three qualification questions decide whether it stays.",
            customer:
              "On this pathway because the register does not mark it sovereign and its name matches no sovereign rail.",
            single_pathway:
              "Every candidate is assessed on this pathway under this regime: there is no external-customer test.",
          },
          unscored: "—",
          why: "Why these scores? (recorded with the decision)",
          submit: "Save scores",
          scoring: "Saving…",
          scored: "Scored. The decision and tier were derived from it.",
          failed: "Those scores were not recorded.",
        },
        criterion: {
          q1_external_customer: "Serves an external customer",
          q2_client_impact: "Failure impacts clients",
          severity: "Severity",
          breadth: "Breadth",
          time_to_impact: "Time to impact",
          systemic_risk: "Systemic risk",
          firm_safety: "Firm safety and soundness",
          c1_regulatory_instrument: "Named in a regulatory instrument",
          c2_kingdom_specific: "Kingdom-specific",
          c3_transactional: "Transactional",
          time_to_breach: "Time to breach",
          penalty_severity: "Penalty severity",
          reversibility: "Reversibility",
          // P416 -- the OSFI E-21 critical-operation rubric.
          g1_institution_relies: "The institution delivers or relies on it",
          g2_no_sustained_workaround: "No sustained workaround absorbs a disruption",
          continued_operations: "Continued operations",
          safety_soundness: "Safety and soundness",
          interconnectedness: "Interconnectedness",
          customer_impact: "Customer impact",
        },
        // P416 -- which regime's rubric decided a row, and its edition.
        decidedUnder: "Decided under the {{regime}} rubric, edition {{version}}.",
        decidedBeforeRubric:
          "Decided under the {{regime}} methodology before rubrics were data files.",
        engineUnconfigured:
          "Marsad Engine is not configured (MARSAD_AI_PROVIDER). Configure it in admin settings to derive.",
        noDecision: "Not yet derived",
        scores: "Criterion scores",
        because: "Reasoning: {{why}}",
        thresholds: "Threshold placeholders",
        maxOutage: "Max outage",
        // Round 20 G44 -- nothing edits these, and signed rows kept
        // "edit before sign-off" beside a signed tolerance.
        placeholderNote:
          "The Engine's placeholders, not a tolerance: the service's tolerance is declared and signed on its own.",
        placeholderSuperseded:
          "The Engine's placeholders, frozen at sign-off: superseded by the signed tolerance of {{hours}}h ({{signedBy}}).",
        placeholderUnsuperseded:
          "The Engine's placeholders, frozen at sign-off: no tolerance has been signed for this service, so none is in force.",
        nextSteps: {
          declareTolerance: "Declare {{toleranceTerm}} →",
          scoreMvb: "Score for {{mvbTermShort}} →",
        },
        col: {
          ibs: "{{serviceTermShort}}",
          status: "Admission",
          decision: "Decision",
          actions: "Actions",
        },
        status: {
          candidate: "Candidate",
          admitted: "Admitted",
          removed: "Removed",
        },
        decision: {
          included: "Included",
          excluded: "Excluded",
          deferred: "Deferred",
        },
        // C5 -- a placeholder decision minted at promotion is not a
        // deferral anybody decided.
        notYetDerived: "Not yet derived",
        signoff: {
          title: "Governance",
          /* Round 10 F2 -- what the CEREMONY has reached. Never a word
             the admission outcome also uses: "approved" beside a
             deferral is what made this heading read as a
             contradiction for eight rounds. */
          ceremonyState: {
            draft: "not yet challenged",
            challenged: "challenged, awaiting both signatures",
            cro_signed: "awaiting the business owner",
            business_owner_signed: "awaiting the CRO",
            approved: "both signatures recorded",
          },
          waiting: "Waiting on the CRO and the business owner. They sign from My Work.",
          // E2 -- the fixed sentence named both seats however far the
          // ceremony had got, so a CRO who had just signed could not
          // tell whether it had landed.
          waitingOn: "Waiting on the {{roles}}. They sign from My Work.",
          /* ROUND 15 G2 -- the person, where the record names one. */
          waitingOnNamed: "Waiting on {{who}}. They sign from My Work.",
          // Round 20 W3 -- the register names the owner and holds no address.
          ownerNotSeat:
            "{{name}} (the owner: named, not yet a seat -- record the address they sign in with in the register to bind them)",
          // Round 20 N15 -- the owner's leg is the owner's alone.
          ownerNamed: "{{name}} (the service's recorded owner)",
          ownerHoldsNoSeat:
            "{{name}}, the recorded owner, who holds no business-owner seat",
          ownerNone: "the service's owner, and none is recorded",
          approved:
            "Approved -- both signatures recorded; {{serviceTermShort}} status updated.",
          // C3 -- what the ceremony CONCLUDED, not merely that it
          // concluded. A signed deferral is not an approval.
          completed: {
            included: "Signed off -- admitted as {{serviceTermShortA}}.",
            excluded: "Signed off -- excluded; this is not {{serviceTermShortA}}.",
            deferred: "Signed off -- deferred; no admission decision yet.",
          },
          completedOther: "Signed off -- both signatures recorded.",
          cro: "CRO",
          businessOwner: "Business owner",
          name: "Signatory name",
          email: "Signatory email",
          sign: "Sign off",
          // A5 -- the signature is the session's; the name is shown,
          // not asked for.
          signingAs: "Signing as {{name}}",
          failed: "Sign-off failed (already signed?).",
        },
        /* A4 -- what the owner and the reviewer see where the scoring
           verbs used to be. Scoring is the resilience desk's work, and
           the endpoints behind those buttons refuse everyone else. */
        readOnlyForYou: "Scored by the resilience desk",
        challenge: {
          // Round 20 V1 -- challenged and signed by one person.
          notIndependent:
            "Challenged and signed by the same person: {{name}} recorded the challenge and signed as {{role}}. The second-line challenge was not independent.",
          // Round 20 A46
          backdated:
            "Dated before its own record: {{legs}} carry a time earlier than this row was written ({{created}}). A ceremony stamps the moment it is given, so these times were written in.",
          backdatedChallenge: "the challenge",
          required: "Second-line Risk challenge (required before sign-off)",
          /* A2 -- what everyone who is not the reviewer sees here. The
             form used to render for all of them, and the first line
             recorded the second line's opinion under her own name. */
          awaitingReviewer:
            "Waiting on the reviewer to record the second-line challenge. Nothing for you to do here.",
          awaitingReviewerNamed:
            "Waiting on {{who}} (Reviewer) to record the second-line challenge. Nothing for you to do here.",
          /* Round 16 G12 -- who to chase, and nothing to chase before
             the candidate is scored. */
          awaitingNamed:
            "Waiting on {{name}} (second line) since {{since}} to record the challenge. Nothing for you to do here.",
          awaitingUnheld:
            "Waiting on the second-line challenge -- nobody signed in holds the reviewer seat yet.",
          awaitingScores:
            "Not scored yet, so there is nothing for the second line to challenge.",
          by: "Challenged by {{name}}",
          recordingAs: "Recording this challenge as {{name}}",
          reviewerName: "Risk reviewer name",
          reviewerEmail: "Risk reviewer email",
          note: "Challenge note (optional)",
          // Round 20 G45
          reconcileLabel:
            "The Engine and the evidence disagree on Q2. Say which answer stands, and why (required):",
          submit: "Record challenge",
          failed: "Challenge failed.",
        },
      },
      mvbCandidates: {
        /* ROUND 13 H, eighth round -- the register did not contain
           the reader's own service and did not say why. It holds what
           the intake FOUND, by three rules, and named none of them. */
        /* Round 14 G -- "12 admitted" where five were. The intake
           takes EVERY business service on the register, admitted or
           not (the admission page decides admission, not this one),
           and the services it adds are those a service in the
           minimum viable bank rests on. The words now say what the
           rules do, rather than the rules being changed to fit them:
           the rules decide what is scored, and moving them moves every
           MVB figure downstream. */
        provenance: {
          ibs_register: "on the business-service register",
          // Round 17 L6 -- a Critical Internal Function, named as one.
          function_register: "on the internal-function register",
          notYetAdmitted: " · not yet admitted",
          supporting_service: "supports one",
          regulator_mandated: "regulator-named",
        },
        provenanceWhy: {
          ibs_register:
            "In the register because it is on the {{serviceTermShort}} register. Every one there is scored, whether or not it has been admitted -- admission is decided on the admission page, not here.",
          supporting_service:
            "In the register because {{serviceTermShortA}} in the {{mvbTerm}} rests on it, or because it is a material supplier.",
          regulator_mandated:
            "In the register because the regulator named it, whatever the rubric would otherwise say.",
        },
        intakeSummary:
          "{{total}} candidates, found by three rules: {{ibses}} {{serviceTermShortPlural}} and {{functions}} critical internal functions on the registers, admitted or not; {{services}} services and material suppliers they rest on; {{vendors}} regulator-named.",
        /* Round 18 P7 -- Set up lists the register; this one scores a
           scan of it. Rows added since the last scan are said, so the
           two counts are not read as one population. */
        intakeUnscanned:
          "{{parts}} on the registers since the last scan {{verb}} not scored here yet; Derive all scores them.",
        intakeUnscannedServices_one: "{{count}} {{serviceTermShort}}",
        intakeUnscannedServices_other: "{{count}} {{serviceTermShortPlural}}",
        intakeUnscannedFunctions_one: "{{count}} critical internal function",
        intakeUnscannedFunctions_other: "{{count}} critical internal functions",
        intakeUnscannedVerb_one: "is",
        intakeUnscannedVerb_other: "are",
        intakeExcludes:
          "A service reaches this register through {{serviceTermShortA}} in the {{mvbTerm}}; one that rests only on {{serviceTermShortPlural}} outside it is not scored here.",
        // F -- the score sat beside its band separated only by a
        // margin, so "1" and "10k-100k" read as "110k-100k".
        scoreOfThree: "{{value}} of 3",
        kicker: "OPERATIONAL TWIN -- {{mvbTermShort}} CANDIDATE SELECTION",
        heading: "{{mvbTermShort}} candidates register",
        intro:
          "Score each candidate against the eight-criterion rubric, apply the two gates, and tier it (Core {{mvbTermShort}} / {{mvbTermShort}} / Watchlist / Out). Second-line Risk challenges, then the CRO + Accountable Executive sign off -- which seals the tamper-evident record and sets the bank's {{mvbTermShort}} membership.",
        loading: "Loading {{mvbTermShort}} candidates...",
        error: "Could not load {{mvbTermShort}} candidates.",
        empty:
          "No {{mvbTermShort}} candidates yet. Run 'Derive all' to score the estate.",
        /* A4 -- the same sentence on an empty tenant was an instruction
           that could not work: there was no estate to score, and
           pressing it succeeded meaninglessly and then told her to
           press it again. */
        emptyNoEstate:
          "Nothing to score yet. Candidates are derived from the {{serviceTermPlural}} and the services beneath them, and none are loaded.",
        nothingToScoreTitle:
          "Nothing to score: no services and no {{serviceTermShortPlural}} are loaded.",
        derivedNothing: "Nothing to score",
        derivedNothingDetail:
          "No services or {{serviceTermShortPlural}} are loaded, so no candidates were created.",
        forbidden:
          "{{mvbTermShort}} candidate selection is a governance ceremony. It is open to the roles it can wait on, to a reviewer recording the second-line challenge, and to administrators.",
        deriveAll: "Derive all",
        declareFromTierSet: "Draft formal declaration",
        // Round 11 D1 -- the register's own sentence, carried by the
        // control that could otherwise walk round it. Same rule, same
        // words, wherever the reader meets it.
        // Round 18 G22 -- what drafting does while one is in force.
        declareSupersedes: "A new declaration supersedes {{name}} once both sign.",
        declareBlocked:
          "One declaration at a time. Finish or delete \u201c{{name}}\u201d before starting another.",
        declared:
          "Draft declaration created with {{count}} member(s) -- run the ceremony to approve it.",
        // C1 -- pressing twice made a second identically named draft.
        declareReused:
          "You already have a draft from the approved tier set. Opened that one rather than making a second.",
        /* B3 -- a declaration already in ceremony is not a draft to hand
           back; it is a signature to chase. Three accumulated on the
           register, one of them already CRO-signed and indistinguishable
           from the two nobody had touched. */
        declareInCeremony:
          "That declaration is already submitted and waiting on a signature. Opened it rather than starting a second.",
        declareFailed: "Could not draft the declaration.",
        convergenceToggle: "Convergence review",
        col: {
          candidate: "Candidate",
          /* B2 -- the register SCORES candidates and recommends a tier
             for the next declaration. Membership comes from the
             approved declaration, and a column headed "Tier" above
             badges reading "MVB" and "Out of scope" was read as the
             membership itself -- so an IBS that is in the declaration
             appeared here as out of scope, and its own page agreed
             with the register against every other screen. */
          tier: "Recommended tier",
          composite: "Composite",
          recommendation: "Recommendation",
        },
        // P269: ceremony + convergence panel labels.
        ceremony: {
          challengeLabel: "Second-line Risk challenge (required before sign-off)",
          // A5 -- the reviewer is the session; the name is shown, not
          // asked. reviewerName/reviewerEmail are kept for the labels
          // that report who DID challenge.
          recordingAs: "Recording this challenge as {{name}}",
          signingAs: "Signing as {{name}}",
          reviewerName: "Reviewer name",
          reviewerEmail: "Reviewer email",
          challengeNote: "Challenge note (optional)",
          recordChallenge: "Record challenge",
          dualSignoff: "Dual sign-off",
          waitingOnSigners:
            "Waiting on the CRO and the accountable executive. They sign from My Work.",
          signatoryName: "Signatory name",
          signatoryEmail: "Signatory email",
          signAs: "Sign as {{role}}",
        },
        convergence: {
          loading: "Loading convergence...",
          error: "Could not load convergence.",
          proposedSet: "Proposed {{mvbTermShort}} set:",
          reconciliation: "Declaration reconciliation:",
          declarationsLink: "Formal {{mvbTermShort}} declarations →",
          /* P439: the architecture model moved under the set. */
          architectureChanged:
            "The architecture model changed on {{date}} (from {{file}}). The dependency closure and journey coverage below may have moved: review the set again.",
          architectureReviewed:
            "Reviewed since the architecture model changed on {{date}}.",
          markReviewed: "I have reviewed it",
        },
      },
      // P132 (#482): resilience drills / BCP tests.
      drills: {
        rubric: "Framing · Resilience drills",
        heading: "Resilience drills",
        intro:
          "Schedule business-continuity drills against {{serviceTermShortA}}, run them, and " +
          "record RTO/RPO target-vs-actual. A failed drill raises a finding " +
          "on the {{serviceTermShort}} that feeds the cascade.",
        loading: "Loading drills...",
        empty: "No drills scheduled yet.",
        schedule: "Schedule a drill",
        scheduled: "Drill scheduled.",
        create: "Schedule",
        cancel: "Cancel",
        ibs: "{{serviceTermShort}} or critical internal function",
        functionsGroup: "Critical internal functions",
        selectIbs: "Select a service or function...",
        // Round 17 L7 -- the services under their own heading too.
        servicesGroup: "{{serviceTermPlural, capitalize}}",
        type: "Type",
        namePlaceholder: "e.g. Q2 core-banking failover drill",
        scheduledFor: "Scheduled for",
        targetRto: "Target RTO (min)",
        targetRpo: "Target RPO (min)",
        start: "Start",
        pass: "Pass",
        fail: "Fail",
        cancelDrill: "Cancel",
        confirmFail: "Confirm fail",
        failNotesPlaceholder: "Outcome notes (what failed)",
        /* Round 10 F2 -- the severity picker on the fail form had no
           name at all, spoken or written. */
        failSeverityLabel: "How severe the failure was",
        // Round 20 V3 -- the figures on the row, and a pass they contradict.
        rowFigures: "RTO target {{target}} → actual {{actual}} min",
        rowTolerance: "tolerance {{minutes}} min",
        /* Round 20 Y7 -- a target the tolerance does not allow. */
        targetAboveTolerance:
          "The target sits {{minutes}} min above the signed tolerance, so meeting it can still breach.",
        againstBoth:
          "Against its target: {{target}} · against the tolerance: {{tolerance}}",
        againstWord: {
          met: "met",
          missed: "missed",
          within: "within",
          over: "over",
        },
        contradiction: {
          not_measured:
            "Recorded Passed with no actual recovery time: not measured, so not counted as a pass.",
          missed_target:
            "Recorded Passed; missed its target by {{minutes}} minutes, so not counted as a pass.",
          over_tolerance:
            "Recorded Passed; over the service's tolerance by {{minutes}} minutes, so not counted as a pass.",
        },
        rto: "RTO target → actual",
        rpo: "RPO target → actual",
        minutes: "min",
        outcome: "Outcome",
        viewFinding: "View raised finding →",
        types: {
          bcdr: "BCDR",
          tabletop: "Tabletop",
          failover: "Failover",
          chaos: "Chaos",
          scenario: "Scenario",
        },
        status: {
          planned: "Planned",
          in_progress: "In progress",
          passed: "Passed",
          failed: "Failed",
          cancelled: "Cancelled",
        },
        acted: {
          start: "Drill started.",
          pass: "Drill passed.",
          fail: "Drill failed -- finding raised.",
          cancel: "Drill cancelled.",
        },
      },
      testRuns: {
        // Round 20 V4 -- the scenario changed after its test.
        /* Round 20 Y6 -- a run, opened. */
        detail: {
          booked: "Booked for {{date}}; not yet run.",
          ran: "Run on {{date}}.",
          facilitator: "Facilitator: {{who}}.",
          participants: "Participants: {{who}}.",
          participant: "{{name}} ({{kind}})",
          notRecorded: "not recorded",
          noneRecorded: "none recorded",
          revisedAfter: "The scenario was revised after this run, on {{date}}.",
          unchanged: "The scenario has not changed since this run.",
        },
        staleSinceRevision: "stale since the scenario changed on {{date}}",
        rubric: "Framing -- testing that counts",
        heading: "Scenario test runs",
        body: "A scenario that merely exists is not a scenario anybody has tested. A run records what was actually done: by what method, with whom, when, and with what verdict -- and it freezes that verdict, because the estate moves and a result that silently re-derives is not a result.",
        recordHeading: "Record a test",
        recordNote:
          "Recorded as planned. Executing it evaluates the scenario against the estate as it stands and freezes the answer onto the run. A second test is a second run, linked to this one.",
        recorded: "Test recorded. Execute it when it has happened.",
        recordFailed: "That test could not be recorded.",
        executed: "Evaluated, and the verdict is frozen onto the run.",
        scenario: "Scenario",
        pickScenario: "Pick a scenario",
        method: "Method",
        intensity: "Intensity",
        facilitator: "Facilitator email",
        vendorParticipant: "Third-party participant",
        record: "Record",
        execute: "Execute",
        runsHeading: "Runs",
        noRuns: "No scenario test has been recorded.",
        // ROUND 15 D17 -- a failed read is a failure, not "nothing recorded".
        runsFailed: "Could not load the scenario test runs.",
        notExecuted: "not executed",
        when: "When",
        verdict: "Verdict",
        methods: {
          tabletop: "Table top",
          simulation: "Simulation",
          live_systems: "Live systems",
        },
        intensities: {
          light: "Light",
          standard: "Standard",
          severe: "Severe",
        },
        coverageHeading: "Critical operations tested",
        coverageHeadline:
          "{{pct}}% of operation-class pairs tested; {{complete}} of {{total}} operations complete across every required class",
        deadline: "({{days}} days to {{date}})",
        cifExcluded_one:
          "{{count}} admitted Critical Internal Function left out: this regulator's definition does not admit it, or that is not confirmed",
        cifExcluded_other:
          "{{count}} admitted Critical Internal Functions left out: this regulator's definition does not admit them, or that is not confirmed",
        coverageFailed:
          "The coverage could not be read. This says nothing about what has been tested; try again, and if it persists the request is failing.",
        noCoverage:
          "No admitted critical operation, or the regime requires no scenario class, so there is nothing to count.",
        operation: "Critical operation",
        regime: "Regulator",
        regimes: {
          sama: "SAMA",
          osfi: "OSFI",
        },
        coverageNote:
          "Tested means an executed run within the regulator's recency window whose frozen report reached this operation. Planned is a booking, not a test. Stale is a test nobody has repeated, which is not evidence of a current capability. Before this, all four read the same.",
        states: {
          tested: "tested",
          stale: "stale",
          planned: "planned",
          untested: "not tested",
        },
      },
      /* Round 20 U9 -- "Is it safe right now?" answered with a list. */
      openFindings: {
        heading: "Open incidents, newest first",
        intro:
          "Open critical and high findings, and every incident somebody has raised, with what each is on. The newest is at the top.",
        loading: "Loading open findings...",
        error: "Could not load the open findings.",
        empty: "Nothing critical or high is open, and no raised incident is open.",
        // Round 20 Q7 -- reached from a service's "See them".
        serviceHeading: "Open findings reaching one service",
        serviceIntro:
          "Every open finding raised on this service or on a configuration item it runs on, whatever its severity, newest first.",
        serviceEmpty: "No open finding reaches this service.",
        backToService: "Back to the service",
        showAll: "Show every open incident",
        incident: "raised incident",
        on: "on {{name}}",
        severity: {
          critical: "critical",
          high: "high",
          medium: "medium",
          low: "low",
        },
      },
      /* Round 20 U7 -- on an estate already in alarm the aggregate
         cannot carry news; this list can. */
      whatsNew: {
        heading: "Since you last looked ({{since}})",
        summary_one: "{{count}} new critical or high finding.",
        summary_other: "{{count}} new critical or high findings.",
        breach: "tolerance breach",
        more_one: "and {{count}} more",
        more_other: "and {{count}} more",
      },
      warRoom: {
        crumb: "War-room",
        kicker: "OPERATIONAL TWIN -- RESILIENCE WAR-ROOM",
        heading: "Are we within tolerance?",
        intro:
          "Pick a severe-but-plausible scenario. We run it through the live " +
          "topology and judge each impacted {{serviceTermShort}} or critical internal function against its declared " +
          "{{toleranceTerm}} (falling back to the tightest supporting-service " +
          "RTO " +
          "where none is declared). Recovery is modelled from the slowest " +
          "supporting dependency.",
        pickPrompt: "Choose a scenario to stress-test.",
        /* Round 20 U6 -- what is live leads; the what-if comes second. */
        liveNow: {
          heading: "Live now",
          summary:
            "Under current live conditions: {{breached}} breaching, {{atRisk}} at risk.",
          via: "via {{via}}",
          projected: "projected {{projected}}h against {{tolerance}}h",
          since: "breaching since {{since}}",
          finding: "Open the breach finding",
          rehearse: "Or rehearse a scenario",
        },
        /* ROUND 15 D12 -- a member scenario, marked in the picker. */
        partOf: "part of {{names}}",
        /* Round 12 A6 -- "Choose a scenario to stress-test" rendered
           whether or not one existed, so on a new bank it asked her to
           pick from nothing, with no picker and no route to the library
           that holds them. */
        emptyNoScenarios:
          "No scenarios are in the library yet. The war-room stresses a severe-but-plausible scenario against the live topology, so there is nothing to run until one is written.",
        emptyNoScenariosCta: "Open the scenario library",
        actions: {
          declareFor: "Declare a tolerance for {{ibs}}",
          recommendations: "View recommendations →",
          governTolerances: "Declare / govern {{toleranceTermPlural}} →",
        },
        loading: "Running the scenario...",
        error: "Could not run the tolerance assessment.",
        // Round 16 L3 -- functions counted apart from the IBS headline.
        functionsHeadline_one:
          "Critical internal functions: {{breached}} of {{count}} breached",
        functionsHeadline_other:
          "Critical internal functions: {{breached}} of {{count}} breached",
        breachedHeadline:
          "{{breached}} of {{assessed}} impacted {{serviceTermShortPlural}} breach tolerance",
        mvbBreached: "{{count}} {{mvbTermShort}}",
        /* Round 17 P1 -- a scenario reaches candidates too, and the war
           room judges them; its figures said "6 of 11" and "5 MVB" beside
           home's 5 in the register and 3 minimum-viable without saying
           so. The headline keeps its count and names the cut. */
        mvbBreachedAdmitted:
          "{{count}} {{mvbTermShort}} ({{admitted}} in the register)",
        /* Round 18 D25 -- "1 of these are under assessment". */
        candidatesHeadline_one:
          "{{count}} of these is under assessment, not yet in the register ({{breached}} breached)",
        candidatesHeadline_other:
          "{{count}} of these are under assessment, not yet in the register ({{breached}} breached)",
        soonestBreach: "first breach at T+{{n}}h",
        /* ROUND 13 G1 -- the headline divides by what this scenario
           REACHED, and read as a statement about the estate. A service
           owner whose business service was never touched by the
           propagation read "7 of 13 breached" as covering it. Say how
           many declared a tolerance, and name the ones left out:
           round 10 settled that a bare count leaves the reader holding
           a number they cannot act on. */
        // Round 14 D1 -- a target that reached nothing, named.
        emptyFacility:
          "Nothing is recorded at {{name}}, so this scenario reaches no {{serviceTermShort}} there. Site the services that run at {{name}} to test it.",
        emptyChannel:
          "No {{serviceTermShort}} is delivered through {{name}}, so this scenario reaches nothing through it.",
        emptyTarget:
          "{{name}} supports no {{serviceTermShort}} on this estate, so this scenario reaches nothing through it.",
        notReachedHeading:
          "Not judged by this scenario: {{count}} of {{declared}} {{serviceTermShortPlural}} with a declared {{toleranceTerm}}",
        notReachedNames: "{{names}}",
        notReachedMore: "{{names}} and {{count}} more",
        notReachedWhy:
          "Nothing in this scenario reaches them, so they carry no verdict here -- in either direction.",
        notReachedLive: "See what is happening to them now →",
        totalServiceHoursLost: "{{n}}h service lost",
        propagatedVia: "via {{name}}",
        // Round 16 M2 -- reached through a funding edge.
        propagatedViaFunding: "via {{name}}, which funds it",
        liveToggle: "Include current live conditions ('if it hits now')",
        /* C5 -- this chip counts live conditions FOLDED INTO the
           verdict; the A/B delta below says whether they CHANGED it.
           Both were true and read as a contradiction ("1 from live
           conditions" beside "live conditions add nothing"), because
           neither said which question it was answering. */
        liveImpacted_one: "{{count}} live condition included",
        liveImpacted_other: "{{count}} live conditions included",
        downloadPack: "Download signed pack",
        packDownloading: "Building pack...",
        packDownloaded: "Signed war-room pack downloaded",
        packFailed: "Could not download the war-room pack",
        colIbs: "{{serviceTermShort}}",
        /* Round 19 L15 -- a run that reaches a function. */
        colIbsOrFunction: "{{serviceTermShort}} or function",
        colProjected: "Projected outage",
        colTolerance: "{{toleranceTerm, capitalize}}",
        colClock: "Clock",
        colRecovery: "Recovery",
        colWeakest: "Weakest link",
        colVerdict: "Verdict",
        recovery: {
          step: "Step",
          linear: "Ramp",
          lost: "{{n}}h lost",
          /* Round 16 H17 -- the two words stood bare for three rounds. */
          stepTip:
            "Step recovery: one supporting service, so nothing comes back until all of it does.",
          linearTip:
            "Ramp recovery: two or more supporting services, so capacity returns in stages and half the service-hours are lost.",
        },
        /* Round 16 H17 -- between the headline's chips, in the text. */
        chipSeparator: " \u00b7 ",
        hours: "{{n}}h",
        noTolerance: "not rated",
        toleranceDeclared: "declared",
        toleranceModelled: "modelled",
        toleranceSigned: "✓ signed",
        upstreamBound: "≥ upstream {{n}}h",
        upstreamBoundTip:
          "Recovery is bounded by the failed upstream service's RTO -- this {{serviceTermShort}} can't recover before its failed dependency does.",
        statedDuration: "scenario states {{n}}h",
        statedRange: "scenario states {{from}} to {{to}}h",
        statedUntilRecovered: "scenario runs until recovery completes",
        // Round 14 D9 -- why, in words.
        statedWhyFloored:
          "The scenario states {{n}}h, longer than recovery on its own, so the outage is raised to {{n}}h: nothing recovers while the event is still running.",
        statedWhyFlooredOffset:
          "Raised to {{raised}}h: the member starts {{onset}}h in and {{source}} states {{n}}h. Nothing recovers while the event is still running.",
        statedWhyFlooredRange:
          "The scenario states {{from}} to {{to}}h, judged on the upper bound, longer than recovery on its own, so the outage is raised to {{to}}h: nothing recovers while the event is still running.",
        theScenario: "the scenario",
        stillWorking:
          "Lowered to high: still delivered through {{channels}}, which this scenario leaves working.",
        soonestUnsigned: "(against a tolerance in force, not signed)",
        // Round 17 D23 -- a figure the bank set, not the scenario's.
        statedWhyFlooredBank:
          "Raised to {{n}}h, the figure {{who}} set ({{source}} states {{figure}}): nothing recovers while the event is still running.",
        // Round 17 D24 -- the member's start, as the floored variant says it.
        statedWhyOutlastsOffset:
          "Recovery takes longer than the {{n}}h {{source}} states for this member, which starts {{onset}}h in: service is still down when the event ends.",
        statedWhyOutlasts:
          "Recovery takes longer than the {{n}}h the scenario states: service is still down when the event ends.",
        statedDurationTip:
          "How long the scenario itself says the disruption lasts. Where that is longer than the modelled recovery, the projected outage is raised to meet it: nothing recovers while the event is still running. A stated range is judged on its upper bound.",
        capacityLoss: "{{pct}}% of this {{serviceTermShort}} lost",
        // Round 14 D8 -- each stated figure under its own target.
        figure: "{{target}}: {{duration}}",
        figureWithCapacity: "{{target}}: {{duration}}, {{pct}}% lost",
        figureWithStaffLoss:
          "{{target}}: {{duration}}, {{pct}}% of staff unable to work there",
        figureHours: "{{n}}h stated",
        figureRange: "{{from}} to {{to}}h stated",
        // Round 18 D25 -- the bank's own figure, not the source's.
        figureHoursSet: "{{n}}h set by the bank",
        figureRangeSet: "{{from}} to {{to}}h set by the bank",
        figureUntilRecovered: "until recovered",
        figureNoDuration: "no duration stated",
        activeWindow: "{{label}} window, tightened from {{base}}h",
        activeWindowTip:
          "A declared window tightens this tolerance while it is in force. A window can only tighten: one declaring a longer tolerance is ignored, because a window exists to say where there is less room.",
        limitLine: "limit {{n}}h",
        limitTip:
          "The operational risk limit the bank declared and signed for this {{serviceTermShort}}. Where one exists, at risk means crossing THIS rather than a fraction of the tolerance chosen for every service at once.",
        nextWindow: "next tighter window: {{label}} from {{date}}",
        ab: {
          scenarioOnly: "Scenario alone",
          ifNow: "If it hits now (live)",
          // Round 20 P24 -- the population named: services.
          breached:
            "{{breached}} of the {{assessed}} services breached ({{mvb}} {{mvbTermShort}})",
          functionsApart_one:
            "{{count}} critical internal function judged apart{{status}}, {{breached}} breached",
          functionsApart_other:
            "{{count}} critical internal functions judged apart{{status}}, {{breached}} breached",
          deltaWorse:
            "Current live conditions pull in +{{ibs}} more breaching {{serviceTermShortPlural}} (+{{mvb}} {{mvbTermShort}}) on top of the scenario.",
          deltaSame:
            "Those live conditions push no further {{serviceTermShort}} into breach beyond the scenario alone.",
          /* Round 10 F3 -- and what the sentence above is not about.
             An IBS with no declared tolerance has nothing to breach,
             so it sits outside both figures. */
          unjudged_one:
            "{{count}} more could not be judged: no tolerance is declared for it.",
          unjudged_other:
            "{{count}} more could not be judged: no tolerance is declared for them.",
          /* Round 11 F2 -- and WHICH ones. A reader told two were left
             out of the reassurance still could not tell whether the
             IBS they were watching was one of the two. */
          unjudgedNamed_one:
            "{{count}} more could not be judged -- no tolerance is declared for {{names}}.",
          unjudgedNamed_other:
            "{{count}} more could not be judged -- no tolerance is declared for {{names}}.",
          /* Round 14 G -- a declaration waiting on its second signature
             is not "no tolerance declared". Said to the person who had
             just declared 2h, that sentence was false about her own
             record. The state words come from toleranceSentence, so
             this reads as every other surface does. */
          /* ROUND 15 D15 -- the sentence claimed a declaration is in
             force once signed, three rows above verdicts judged against
             figures nobody had signed. A projection is judged against
             the figure in force, signed or not, and each row says
             which; a declaration still awaiting a signature is the one
             thing not judged. */
          unjudgedPending_one:
            "{{count}} more could not be judged yet -- {{rows}}. A declaration still awaiting a signature is not judged; a figure already in force is, signed or not, and its row says which.",
          unjudgedPending_other:
            "{{count}} more could not be judged yet -- {{rows}}. A declaration still awaiting a signature is not judged; a figure already in force is, signed or not, and its row says which.",
        },
        fixAb: {
          heading: "Remediation under live conditions",
          plan: "Harden {{services}} services → clears {{cleared}} of {{breached}} breaches{{fnCut}}",
          deltaWorse:
            "Live conditions need +{{services}} more services hardened (+{{breaches}} more breaches to clear).",
          deltaSame: "Same remediation plan whether or not it hits now.",
        },
        packWhoSigns: "The CRO signs this pack -- ask an approver to download it.",
        /* ROUND 15 G2 -- by name where the record names one. */
        packWhoSignsNamed:
          "{{who}} (Chief Risk Officer) signs this pack -- ask an approver to download it.",
        // Round 18 G23 -- the seat is empty, and said so.
        packWhoSignsUnheld:
          "The Chief Risk Officer signs this pack -- nobody signed in holds that seat yet.",
        // A3 -- the chip named a gap and offered no route to close it.
        governedChipHint: "Sign the outstanding tolerances from My Work",
        /* Round 17 P3 -- it read "2 of 6 governed" beside home's "1 of
           5 tolerances signed" over admitted services; it counts the
           services that breach, and says so. */
        governedChip: "{{governed}} of the {{total}} that breach governed",
        breachesIn: "breaches in {{n}}h",
        recoversAt: "· recovers +{{n}}h",
        headroom: "{{n}}h headroom",
        verdict: {
          within: "Within",
          at_risk: "At risk",
          breached: "Breached",
          unknown: "Unrated",
        },
        cheapestFix: {
          heading: "Highest-impact fix",
          recommend:
            "Harden {{service}} -- cut RTO {{current}}h → {{target}}h to " +
            "clear {{cleared}} breached {{serviceTermShortPlural}}",
          recommendMvb: "({{count}} {{mvbTermShort}})",
          none:
            "No single-service hardening clears a breach -- these breaches " +
            "have multiple slow dependencies.",
          colService: "Harden service",
          colChange: "RTO change",
          colCleared: "{{serviceTermShortPlural}} cleared",
          colMvb: "{{mvbTermShort}} cleared",
          colCost: "Cost (SAR)",
          rtoChange: "{{current}}h → {{target}}h",
        },
        fixPlan: {
          heading: "Full remediation plan",
          headline:
            "Harden {{services}} services → clears {{cleared}} of " +
            "{{breached}} breaches{{fnCut}} ({{mvbCut}}), +{{hours}}h RTO capacity",
          /* Round 19 P16 -- the plan's MVB figure names its cut: the
             run's summary counts MVB services and this counted the
             function among them, "3 MVB" above "4 MVB" on one page. */
          mvbCut: "{{mvb}} {{mvbTermShort}}",
          mvbCutFunctions_one:
            "{{services}} {{mvbTermShort}} services, {{count}} {{mvbTermShort}} function",
          mvbCutFunctions_other:
            "{{services}} {{mvbTermShort}} services, {{count}} {{mvbTermShort}} functions",
          /* Round 17 P3 -- "clears 6 of 5 breaches": the plan cleared a
             function the denominator left out. Both sides count it now,
             and the sentence says how many are functions. */
          fnCut_one: ", {{count}} of them a critical internal function",
          fnCut_other: ", {{count}} of them critical internal functions",
          totalCost: "Total hardening cost: {{cost}}",
          cumulative: "+{{count}} cleared",
        },
        budget: {
          basis: {
            hours: "By RTO hours",
            sar: "By SAR cost",
          },
          label: "Within an RTO budget (hours):",
          labelSar: "Within a SAR budget:",
          result:
            "Best spend: harden {{services}} services ({{spend}}h) → clears " +
            "{{cleared}} breaches ({{mvb}} {{mvbTermShort}}).",
          resultSar:
            "Best spend: harden {{services}} services ({{spend}}) → clears " +
            "{{cleared}} breaches ({{mvb}} {{mvbTermShort}}).",
          unaffordable: "{{count}} can't be cleared within budget.",
        },
      },
      fragility: {
        kicker: "OPERATIONAL TWIN -- FRAGILITY RADAR",
        heading: "Where are we fragile?",
        intro:
          "An always-on, scenario-free scan of the topology. We rank the " +
          "upstream nodes -- services and vendors -- by how much depends on " +
          "them (blast reach), how many {{mvbTermShort}} {{serviceTermShortPlural}} that includes, and whether " +
          "they're already showing stress. Your concentration risks, before " +
          "an incident forces the question.",
        loading: "Scanning the topology...",
        error: "Could not run the fragility scan.",
        /* Round 12 A6 -- on an empty estate the page drew a five-column
           table header over nothing. A header with no rows is a claim
           that the scan ran and found nothing, which is the opposite of
           what is true. */
        empty:
          "Nothing has been scanned yet. The radar ranks the services and vendors the bank has loaded by how much depends on them, so it has nothing to rank until the estate arrives.",
        emptyCta: "Load the estate",
        headline:
          "{{assessed}} upstream nodes assessed -- {{degraded}} already showing stress",
        colNode: "Node",
        colBlast: "Blast reach",
        colMvb: "{{mvbTermShort}} downstream",
        colStress: "Current stress",
        colScore: "Fragility",
        findingsPill: "{{count}} open ({{severity}})",
        // B7 -- each header counts its own rows.
        exposedStress_one: "{{count}} carrying an open finding",
        exposedStress_other: "{{count}} carrying open findings",
        changes: {
          heading: "What changed",
          why: "Each movement is split between the inputs a reading records: blast reach, {{mvbTermShort}} reach, open findings and worst severity. Whatever those do not account for is said, not spread across them.",
          days_one: "{{count}} day",
          days_other: "{{count}} days",
          tooFew:
            "Fewer than two readings are recorded, so nothing can have moved yet.",
          none_one: "No score moved in the last day.",
          none_other: "No score moved in the last {{count}} days.",
          rose: "rose from {{from}} to {{to}}",
          fell: "fell from {{from}} to {{to}}",
          part: "{{input}} {{from}} to {{to}} ({{points}})",
          noneValue: "none",
          input: {
            blast_reach: "blast reach",
            mvb_downstream: "{{mvbTermShort}} reach",
            open_findings: "open findings",
            max_severity: "worst severity",
          },
          unexplained: {
            weighting:
              "{{points}} not explained by these inputs: the score weights changed between readings",
            unstored_input:
              "{{points}} from redundancy, tolerance or {{mvbTermShort}} membership, which a reading does not record",
          },
          truncated: "Showing the {{shown}} largest of {{total}} movements.",
        },
        // Round 16 L3 -- the list scores Critical Internal Functions too.
        exposedHeading:
          "Most exposed services ({{serviceTermShortPlural}} and critical internal functions)",
        colIbs: "{{serviceTermShort}}",
        colRedundancy: "Redundancy",
        /* B1 -- these are two different numbers and the column that
           said "Tolerance (RTO)" was printing the second one. */
        colBindingRto: "Binding RTO",
        colTolerance: "{{toleranceTerm, capitalize}}",
        colExposure: "Exposure",
        singleSupplier: "single supplier",
        supporting: "{{count}} services",
        rtoHours: "{{n}}h",
      },
      telemetry: {
        kicker: "OPERATIONAL TWIN -- LIVE TELEMETRY",
        heading: "Is it up right now?",
        intro:
          "The twin's live leg -- a continuous service-health feed (up, " +
          "degraded, down) sampled from operational telemetry. Distinct from " +
          "findings: a service can be telemetry-down with no open finding, " +
          "and that degrades its dependent {{serviceTermShortPlural}} in the cascade. Synthetic " +
          "feed today; a real push connector is the production swap-in.",
        loading: "Loading live health...",
        error: "Could not load telemetry.",
        /* Round 12 A6 -- this told the Resilience Lead to "sample now"
           on a page with no such control: the button is admin-gated,
           because so is the endpoint. The sentence now says what the
           feed is and who fills it, and the control sits with it for
           the seat that holds it. */
        empty:
          "No telemetry has been sampled yet. The feed arrives either from an external monitor pushing to MARSAD, or from a sample taken here.",
        emptyAskAdmin:
          "Sampling is an administrator's; the feed fills in as readings land.",
        sampleNow: "Sample now",
        sampling: "Sampling...",
        sampled: "Sampled {{sampled}} services -- {{down}} down, {{degraded}} degraded",
        downCount: "{{n}} down",
        degradedCount: "{{n}} degraded",
        /* ROUND 13 H, eighth round -- the word above the sentence.
           Twelve rows read "healthy" while naming, in the same row,
           the business service on them carrying an open critical.
           The reading stays (a breach on a business service is
           derived FROM live conditions; marking its supports
           degraded would invert cause and effect). What it loses is
           the claim to be a verdict on anything but itself. */
        /* ROUND 15 H2 -- the programme's decision: lead with the
           incident. "healthy (own telemetry)" above an open critical
           incident read as a verdict; the reading is still its own. */
        statusOwnTelemetry: "incident open \u00b7 {{status}} (own telemetry)",
        healthyButIncident:
          "This service's own telemetry is clean. It is named here because {{names}} rests on it and carries an open critical incident.",
        healthyCount: "{{n}} healthy",
        // Not plural: both forms read identically, and being plural
        // was the defect -- i18next selects a variant from `count`,
        // the call passes `n`, so the bare key was looked up, did not
        // exist, and the strip printed the key itself.
        noDataCount: "{{n}} never monitored",
        /* Round 10 F6 -- and why. The walk created three services and
           then read "3 never monitored" with nothing on screen saying
           that a service is watched only once a pass has sampled it. */
        noDataWhy:
          "A service is watched from the first pass that samples it. One added since the last pass has no reading yet, and stays honestly unmeasured rather than being given one nothing measured.",
        staleCount: "{{n}} stale",
        staleNote: "stale -- last seen {{ago}} ago",
        /* Round 10 F6 -- how current the reading is, on every row. */
        observedNote: "{{ago}} ago",
        /* Round 11 F2 -- why a true reading looks false. The incident
           is on the business service this one supports, not on this
           one, and until now the page said only the second half. */
        /* Round 20 U8 -- who is told when a supporting service stays down. */
        outageTold:
          "When a service a business service rests on has been down for {{minutes}} minutes, the {{seat}} ({{holders}} holding the seat) and administrators ({{admins}}) are notified once and asked to acknowledge.",
        outageNobody:
          "Nobody is told when a service a business service rests on stays down for {{minutes}} minutes: no one holds the {{seat}} seat and there is no administrator.",
        outageRaise: "Raise an incident to tell someone.",
        /* Round 20 U5 -- an alert on the service's own host was not on
           its row at all; it comes first, before what it supports. */
        ownAlertHost: "Open {{severity}} alert on its host since {{since}}",
        ownAlert: "Open {{severity}} alert on this service since {{since}}",
        supportsIncident_one: "Supports {{names}}, which has an open critical incident",
        supportsIncident_other:
          "Supports {{names}}, which have open critical incidents",
        colService: "Service",
        colStatus: "Status",
        colAvailability: "Availability",
        colLatency: "Latency",
        colErrorRate: "Error rate",
        colTrend: "Availability (recent)",
        live: "live",
        liveSource: "Pushed by an external monitor",
        history: {
          title: "Telemetry history",
          loading: "Loading history...",
          error: "Could not load history.",
          empty: "Not enough history yet.",
          availability: "Availability",
          latency: "Latency",
          errorRate: "Error rate",
          points: "{{n}} samples",
        },
        status: {
          healthy: "healthy",
          degraded: "degraded",
          down: "down",
          unknown: "unknown",
          /* C5 -- never monitored. "unknown" means a reading existed
             and went stale; this means there has never been one. */
          no_data: "no data yet",
        },
      },
      capmap: {
        crumb: "Operational Twin",
        heading: "KSA Capability Map",
        loading: "Loading capability map...",
        error: "Could not load capability map",
        /* Round 12 A6. */
        empty:
          "No capabilities are loaded yet. The map places each capability in the zone its `zone_code` names, so the grid fills in as the catalogue arrives -- by CSV import, or by hand in the capability register.",
        emptyCta: "Load capabilities",
        /* A6 -- the walk met "2 CAPABILITIES MAPPED" printed over twelve
           zeroes. Both capabilities existed; neither carried a zone, so
           both sat in Unzoned and every zone on the map was honestly
           empty. The map was not broken and said nothing about why. */
        unzoned_one:
          "{{count}} capability has no zone set, so it sits under Unzoned rather than on the map. Set its zone_code to place it.",
        unzoned_other:
          "{{count}} capabilities have no zone set, so they sit under Unzoned rather than on the map. Set zone_code on each to place them.",
        unzonedCta: "Open the capability register",
        cascadeToggle: "Live cascade",
        lenses: {
          map: "Capability Map",
          mvb: "{{mvbTermShort}} Lens",
          live: "Live health",
        },
        metrics: {
          capsTotal: "Capabilities mapped",
          mvbCount: "{{mvbTermTitle}}",
          mvbCountSub: "caps · {{ibsTotal}} {{serviceTermShort}}",
          impacted: "Capabilities impacted",
          mvbIntersection: "{{mvbTermShort}} intersection",
          ibsAffected: "{{serviceTermShortPlural}} affected",
        },
        incidentBanner: {
          title: "Live incident — cascade in progress",
          /* Round 11 C6 -- this read "as_of 09:48", a clock time no
             reading in the estate supports: the scrubber steps a
             DEMONSTRATION of how a cascade spreads and every one of
             its ticks carries a null timestamp. A fabricated time on
             a supervisory picture is worse than a raw key. */
          asOf: "walk-through step {{tick}}",
        },
        scrubber: {
          sealed: "sealed",
          /* Round 14 B5 -- the walk-through is a demonstration of how a
             cascade spreads, not a reading of this estate. */
          demonstration: "Demonstration, not this estate:",
        },
        demoStep: {
          baseline: "1 -- baseline",
          firstDegradation: "2 -- first degradation",
          servicesFailing: "3 -- services failing",
          coreImpacted: "4 -- core impacted",
          channelsDegraded: "5 -- channels degraded",
        },
        /* Round 11 C5 -- what shapes this picture, said on the picture.
           Second round for this item, and the answer the 3D capability
           city gave in round 11 turned out to be better than the ask:
           it said there was NO configurable threshold rather than
           inventing one to name. This map rests on the same two inputs,
           plus telemetry under its live lens, and it said nothing at
           all -- measured, zero occurrences of the word "threshold" and
           zero links to a setting.

           The two MVB claims are stated SEPARATELY and deliberately.
           The capability flag and the approved declaration are two
           different declarations (0058 says so in its own words: "the
           bank declares both"), the rail prints them side by side as
           "8 caps · 6 IBS", and on this estate they already disagree on
           two capabilities. Naming them as one thing would be the
           conflation this block exists to prevent. */
        inputs: {
          /* ROUND 13 H -- the summary did not say it answers anything.
             "What shapes this view" is a heading, not a question, so a
             reader with the supervisor's question in mind -- can the
             bank move its own goalposts under this picture? -- had no
             reason to open it, and the 3D city one toggle away paints
             the same sentence with no click at all. The answer goes IN
             the summary; opening it gets the working. */
          heading: "No threshold colours this map -- what it reads instead",
          noThreshold:
            "No configurable threshold colours this map. What follows is what it reads.",
          findings:
            "Colour: open findings at critical or high severity, through the business services standing on each capability. The severity set is fixed in MARSAD, not tuned by this bank.",
          atRisk:
            "Amber edge: the capability carries an open finding of any severity that is not in today's impact.",
          mvbFlag:
            "{{mvbTermShort}} lens and the {{mvbTermShort}} tiles: the per-capability minimum-viable-bank flag, set on the capability record by an administrator.",
          mvbDistinct:
            "That is not the same claim as the approved {{mvbTermShort}} declaration, which is what the {{serviceTermShort}} count beside it reads and what the capability city is coloured by. A capability can carry one and not the other.",
          live: "Live health lens: the latest service-health reading, which reads 'unknown' once it is older than the staleness window.",
          whose:
            "No regulator sets these. They are the bank's own model, and moving one moves the picture.",
          openSettings: "Open the settings audit",
          openMvb: "Open the {{mvbTermShort}} declarations",
        },
        popover: {
          close: "Close popover",
          unzoned: "Unzoned",
          incidentSource: "incident source",
          stateTrace: "Trace how this state was derived",
          linkedIbs: "Linked {{serviceTermShort}}",
          inMvb: "In {{mvbTermShort}}",
          openFindings: "Open findings",
          dependencies: "Dependencies",
          /* Round 13 D4 -- named, not counted. */
          noDependencies: "None recorded",
          andMoreDependencies_one: "1 more",
          andMoreDependencies_other: "{{count}} more",
          tier: "Tier",
          // A6 -- the cell leads to the capability it names.
          openCapability: "Open this capability \u2192",
          deriveButton: "Show how this was derived",
        },
      },
      capabilityMap: {
        heading: "Capability map",
        subheading:
          "Bank capabilities laid out by tier, coloured by live " +
          "resilience state. Click a cell to drill into impacted " +
          "services + open findings.",
        loading: "Loading capability map...",
        errorTitle: "Could not load capability map",
        emptyTitle: "No capabilities defined yet",
        emptyBody:
          "Run scripts/seed_foundation_demo.py to populate a demo " +
          "capability catalogue, or add capabilities via the " +
          "foundation admin facade.",
        tier: {
          label: "Tier {{tier}}",
          1: "Customer outcomes",
          2: "Customer services",
          3: "Business processes",
          4: "Operational platforms",
          5: "Foundational platforms",
          empty: "No capabilities at this tier",
        },
        cell: {
          findingBadge_one: "{{count}} open",
          findingBadge_other: "{{count}} open",
          noFindings: "Clean",
        },
        drawer: {
          closeLabel: "Close drawer",
          tierLabel: "Tier {{tier}}",
          chipsHeading: "Live state",
          findingsHeading: "Open findings",
          noFindings: "No open findings on this capability.",
          findingsLoading: "Loading findings...",
          findingsError: "Could not load findings.",
          findingHeadlinePrefix: "{{severity}}:",
          findingsCount_one: "{{count}} finding",
          findingsCount_other: "{{count}} findings",
          servicesHeading: "Impacted services",
          servicesEmpty:
            "Service linkage not yet populated -- this list appears " +
            "once the capability-service link table lands (backlog #430).",
        },
      },
      resilienceTrend: {
        heading: "Resilience -- 30-day trend",
        loading: "Loading trend...",
        errorTitle: "Could not load trend",
        emptyTitle: "No resilience data yet",
        emptyBody:
          "Once the resilience deriver writes snapshots, this card " +
          "shows the daily R/A/G tally across all entities.",
        legend: {
          red: "Red",
          amber: "Amber",
          green: "Green",
          /* Round 14 B1 -- the deriver seals unknown when nothing watched
             the entity. That is "nobody has assessed it", not an error. */
          unknown: "Not assessed",
        },
      },
      asOfPicker: {
        liveLabel: "Viewing live state",
        activeLabel: "Viewing as of {{when}}",
        inputLabel: "Pick a point in time",
        /* Round 10 D3 -- the order is STATED, because a native date
           control reads it from the browser and ignores the page. */
        format: "YYYY-MM-DD HH:MM",
        formatHint:
          "Type a date and time as YYYY-MM-DD HH:MM, for example 2026-09-15 14:30.",
        applyButton: "Apply",
        returnToLiveButton: "Return to live",
      },
      chainDiff: {
        toggleClosed: "Compare states...",
        toggleOpen: "Hide comparison",
        chainLabel: "Chain",
        chain: {
          "resilience-state": "Resilience",
          "blast-radius": "Blast",
          process: "Process",
          obligation: "Obligation",
        },
        fromLabel: "From",
        toLabel: "To",
        applyButton: "Compare",
        unavailable: "No chains apply to this entity kind.",
        emptyPrompt: "Pick a From + To date to see what changed between them.",
        loading: "Loading diff...",
        errorTitle: "Could not load diff",
        derivationBumped:
          "Deriver formula bumped from v{{before}} to v{{after}} between these anchors.",
        zeroDelta: "No measurable change between these two anchors.",
        scalarSection: "Field changes",
        countSection: "Count moves",
        listSectionAdded: "Added entries",
        listSectionRemoved: "Removed entries",
        listEmpty: "(none)",
      },
      layers: {
        heading: "What this service rests on",
        headingInternal: "What this function rests on",
        /* Round 16 -- the regime maps a Critical Internal Function
           across the same layers, and the standfirst said "every
           important business service" above a treasury function. */
        blurbInternal:
          "The layers the regime asks every critical internal function " +
          "to be mapped across. Where a layer is not mapped it says so, " +
          "rather than showing nothing and reading like a clean bill of " +
          "health.",
        blurb:
          "The eight layers every {{serviceTerm}} is mapped across: " +
          "the regime's six, with funding and regulatory obligation. " +
          "Where a layer is not mapped it " +
          "says so, rather than showing nothing and reading like a " +
          "clean bill of health.",
        /* Round 10 C2 -- why a reader should care that a layer is
           mapped at all. Asked for by the walk in these words. */
        whyLayers: "Each layer is a place a control can be tested.",
        /* Round 10 D2 -- the six derivation paths were English prose
           with ASCII arrows, composed on the server and rendered
           identically in both locales: thirteen of them on every
           business-service page. The hops are catalogue keys now, and
           the separator between them is the mirroring Arrow. */
        hop: {
          ibs: "Business service",
          service: "Service",
          configuration_item: "Configuration item",
          journey: "Customer journey",
          journey_step: "Step",
          recovery_team: "Recovery team",
          facility: "Facility",
          vendor_or_contract: "Vendor or outsourcing contract",
          data_asset: "Data asset",
          location: "Location",
          funding_resource: "Funding resource or function",
          regulatory_commitment: "Regulatory commitment",
          obligation: "Obligation",
        },
        /* A2 -- "accounted for", not "resolve". A layer the bank has
           DECLARED it does not have is an answer; counting it as a
           shortfall invited exactly the question the standfirst above
           was written to prevent. The trailing space is load-bearing:
           this sentence is followed by the gaps sentence, and the two
           ran together as "...layers resolve.Unmapped: Process" for
           anyone reading the text rather than the margin. */
        summary:
          "{{accounted}} of {{total}} layers accounted for: " +
          "{{mapped}} mapped, {{byDesign}} none by design.",
        gaps: "Unmapped: {{layers}}.",
        /* G5 -- and the layer count's own clock, said out loud. It is
           computed on read, so it moves the moment a dependency is
           named; without a stamp of its own the nearest one belonged
           to something else. */
        liveNow: "Read live -- this count moves as soon as you name a dependency.",
        show_one: "Show {{count}}",
        show_other: "Show all {{count}}",
        truncated: "Showing {{shown}} of {{total}}.",
        /* A2 -- the owner's half. The block is addressed to the data
           steward who will do the mapping and rendered on the page of
           the man who cannot: five of the six layers are recorded by
           other teams, and "not mapped" told him there was a hole and
           nothing about whose it is. */
        howReached: "How this was reached",
        ownerLine:
          "Of these eight, Technology is yours -- the services this rests on are the ones you name above. The rest are recorded by the teams named beside each gap.",
        ownerGap: {
          technology:
            "Technology: no supporting service is named for this. Add one in the panel above.",
          process:
            "Process: no customer journey is mapped to this service. Your channel owner can add one.",
          people:
            "People: no recovery team is named for the systems this rests on. Your operations lead can add one.",
          facilities:
            "Facilities: no site is recorded for the systems this rests on. Your operations lead can add one.",
          third_parties:
            "Third parties: no supplier is linked to the systems this rests on. Procurement holds the contract register.",
          data: "Data: no data asset is recorded against the systems this rests on. Your data steward can register one.",
          funding:
            "Funding: no funding resource or funding function is mapped. Treasury holds the funding map.",
          regulatory:
            "Regulatory: no return, limit or licence condition is mapped. Compliance holds the regulatory commitments.",
        },
        showRest_one: "Show the remaining {{count}}",
        showRest_other: "Show the remaining {{count}}",
        /* The kinds in the reader's words. These were rendering as
           our own column values -- "data_asset", "configuration_item"
           -- beside the name, so the best copy in the product
           degraded into schema the moment a row was expanded. */
        kind: {
          service: "a supporting service",
          configuration_item: "a configuration item",
          journey: "a customer journey",
          recovery_team: "a recovery team",
          facility: "a site",
          vendor: "a supplier",
          outsourcing_contract: "an outsourcing contract",
          data_asset: "a data asset",
          data_classification: "a data classification",
          funding_resource: "a funding resource",
          critical_internal_function: "a funding function",
          regulatory_commitment: "a regulatory commitment",
        },
        name: {
          technology: "Technology",
          process: "Process",
          people: "People",
          facilities: "Facilities",
          third_parties: "Third parties",
          data: "Data",
          funding: "Funding",
          regulatory: "Regulatory obligation",
        },
        reach: {
          // Named for what they MEAN, because "absent" and
          // "unreachable" look alike in a list and are opposite claims.
          direct: "mapped directly",
          transitive: "via its services",
          absent: "not mapped",
          unreachable: "cannot be reached",
          not_applicable: "none by design",
        },
      },
      /* Round 16 O2 -- every report the bank has opened, in one list. */
      incidentRegister: {
        heading: "Incident reports",
        body: "Every report the bank has opened for a regulator, newest incident first. Each opens the finding it is about, where the report is recorded and its signed pack is taken.",
        error: "Could not load the incident reports.",
        empty:
          "No incident report has been opened. A report is opened from the finding it is about.",
        all: "All",
        regulator: "Regulator",
        stageLabel: "Stage",
        statusLabel: "Status",
        detected: "Detected {{when}} · {{severity}}",
        late: "submitted after its deadline",
        noClock: "no stated deadline",
        cols: { incident: "Incident", due: "Due", reference: "Regulator's reference" },
        stage: { initial: "Initial", update: "Update", final: "Final" },
        status: { draft: "Draft", submitted: "Submitted", late: "Late" },
      },
      /* Round 16 O5 -- what a group means, on the owner's own page. */
      ibsGroupNote: {
        inGroup:
          "{{group}}: the board sees {{together}}; its tolerance is {{hours}}h, from {{from}}.",
        inGroupNoTolerance:
          "{{group}}: the board sees {{together}}; no member declares a tolerance yet.",
        withOthers: "this, {{others}} as one line",
        alone: "this as a line of its own",
        ungrouped: "Not in a group: only admitted services are grouped.",
        ungroupedAdmitted:
          "Not in a group yet: an admitted service is placed in one on the group register.",
        open: "Open the group",
      },
      ibsGroups: {
        // Round 17 Q1 -- the way into the group editor.
        newOrEdit: "New or edit groups",
        heading: "{{serviceTermShort}} groups",
        // Round 17 O8 -- a group may hold candidates; the list below is
        // of admitted services in no group.
        body: "The groups a board governs. A group reads no better than its worst member and its tolerance is its tightest member's. A group may hold candidates, marked where they sit; the admitted services in no group are listed below.",
        error: "Could not load the groups.",
        reserved: "reserved by the methodology",
        // Round 18 O9 -- the group a link arrived for.
        arrivedFor: "The group you followed a link to",
        reservedWhy:
          "The methodology keeps this group for services that are not customer-facing, such as State and regulator-named services, so that none goes ungoverned.",
        breachedWhy:
          "Breached now: a member's live projected outage is longer than its declared tolerance.",
        owner: "Accountable: {{owner}}",
        noOwner: "No accountable owner named",
        /* Round 19 O10 -- the record held a role ("Head of Retail
           Payments") and the page read it as a person. */
        ownerNotAPerson:
          "No person named as accountable: the record holds “{{owner}}”, with no address or account behind it.",
        tolerance: "Tolerance {{hours}}h, set by {{from}}",
        toleranceUnsigned:
          "Tolerance {{hours}}h, set by {{from}} -- not signed: no approved declaration stands behind it",
        notAdmitted: "{{status}}, not admitted",
        status: {
          candidate: "Candidate",
          removed: "Removed",
        },
        noTolerance: "No member declares a tolerance",
        breached: "breached now",
        breachedCount_one: "{{count}} member breached now",
        breachedCount_other: "{{count}} members breached now",
        empty: "No services in this group yet.",
        ungroupedHeading_one: "{{count}} admitted service in no group",
        ungroupedHeading_other: "{{count}} admitted services in no group",
        allGrouped: "Every admitted service sits in a group.",
        placeThem: "Place them on the group register",
      },
      thread: {
        // Stitching review 5.2 -- one matter's thread, in five stages,
        // always in this order and with these words (docs/the-story.md).
        heading: "The thread",
        stage: {
          expected: "Expected",
          have: "Have",
          judged: "Judged",
          decided: "Decided",
          proved: "Proved",
        },
        question: {
          expected: "What the regulator asks of this",
          have: "What the bank holds",
          judged: "What the product found",
          decided: "What a named person decided",
          proved: "What can be shown and verified",
        },
        status: {
          done: "Done",
          attention: "Needs attention",
          missing: "Not started",
        },
        summary: {
          expectedNone: "No obligations are mapped to this service yet.",
          expected: "{{gaps}} gaps across {{controls}} controls; {{assessed}} graded.",
          haveNone: "No supporting services are mapped yet.",
          have_one:
            "{{register}}; {{count}} supporting service, {{unmapped}} layers unmapped.",
          have_other:
            "{{register}}; {{count}} supporting services, {{unmapped}} layers unmapped.",
          judgedNone: "No resilience state has been sealed yet.",
          /* Round 19 B23 -- sealed, but nothing has judged it. */
          judgedUnknown: "Sealed on {{date}}, state not yet known.",
          judged_one: "Resilience {{resilience}}; {{count}} open finding.",
          judged_other: "Resilience {{resilience}}; {{count}} open findings.",
          decidedNone: "No admission decision has been taken yet.",
          decided: "Admission {{admission}}; tolerance {{tolerance}}.",
          /* Round 17 R1 -- a deferral is not an approval. */
          /* Round 18 R9 -- a deferral, said as the decision it is. */
          decidedDeferred: "Deferred: the resilience desk may re-propose it.",
          decidedAwaiting:
            "Admission signed by the CRO; awaiting the {{role}}. Tolerance {{tolerance}}.",
          decidedExcluded: "Excluded from the register. Tolerance {{tolerance}}.",
          // Round 20 V1 -- challenged and signed by one person.
          decidedNotIndependent:
            "Challenged and signed by the same person ({{name}}, also the {{role}}): the second-line challenge was not independent.",
          /* Round 20 A46 */
          decidedBackdated:
            "Dated before its own record: {{legs}} carry a time earlier than the row, written on {{created}}.",
          decidedBackdatedChallenge: "the challenge",
          /* Round 19 R20 -- the tolerance's open leg, named. */
          decidedToleranceAwaiting: "Tolerance awaiting {{who}}.",
          provedNone: "Nothing about this service has been sealed yet.",
          provedNoneFunction: "Nothing about this function has been sealed yet.",
          proved: "Admission sealed on {{date}}.",
          provedDeferral: "Deferral sealed on {{date}}.",
          provedExclusion: "Exclusion sealed on {{date}}.",
        },
        rag: {
          red: "red",
          amber: "amber",
          green: "green",
          unknown: "unknown",
          not_assessed: "not assessed",
        },
        tolerance: {
          signed: "signed",
          set_unsigned: "set, not signed",
          pending: "awaiting signature",
          undeclared: "not declared",
        },
        heldBy: "Held by {{names}} ({{role}})",
        heldBySeat: "Held by the {{role}}",
        /* Round 18 R7 -- a decision taken, by whom and when. */
        approvedBy: "Approved by {{names}} on {{date}}",
        /* Round 18 R9 -- two seats, each named in theirs. */
        signerInSeat: "{{name}} ({{role}})",
        /* Round 20 Y8 -- the seal, inspectable. */
        sealHash: "Seal {{hash}}...",
        sealHowToVerify: "how to verify a seal",
        admittedBy: "Admitted by {{signers}} on {{date}}",
        deferredBy: "Deferred by {{signers}} on {{date}}",
        excludedBy: "Excluded by {{signers}} on {{date}}",
        /* Round 19 R19 -- a sealed record, held by those who signed it. */
        sealedAfter: "Sealed on {{date}} after {{signers}} signed",
        /* Round 17 R2 -- several hold the seat and this record names none. */
        heldBySeatMany: "Held by the {{role}} seat ({{count}} people hold it)",
        /* Round 17 R3 -- a step the reader's seat cannot take says who takes it. */
        notYours: "Taken from the {{role}} seat; your access does not open it.",
        /* Round 19 R22 -- and when the record names who takes it, who to ask. */
        notYoursNamed:
          "{{names}} ({{role}}) takes this step; your access does not open it, so ask them.",
        /* Round 17 R6 -- the strip waits for its record rather than vanish. */
        loading: "Reading the thread...",
        next: {
          assess_controls: "Assess the controls",
          map_dependencies: "Map what it rests on",
          open_cascade: "Open the cascade",
          open_my_work: "Open My Work",
          open_admission: "Open the admission",
          open_tolerance: "Open the tolerance",
          complete_actions: "Complete the plan's actions",
          open_board: "Open the board",
          record_report: "Record the regulator report",
          /* Round 19 G35 -- a change's finding steps to the change. */
          open_change: "Open the change",
          open_entity: "Open where it lands",
          open_cap: "Open the plan",
          link_cap: "Link a corrective action plan",
          record_observations: "Record the letter's observations",
          link_controls: "Link the controls the plan answers",
          answer_observations: "Answer the remaining observations",
          raise_cap: "Create a plan from this letter",
          cite_regime: "Cite the regulator's ask",
          add_targets: "Add what it strikes",
          open_war_room: "Open the war room",
          record_basis: "Record the plausibility basis",
          record_test_run: "Record a test run",
        },
        // The same five stages, read from a scenario.
        scenario: {
          expectedRequired: "{{regime}} requires this class of scenario.",
          expectedCited_one: "Cited to {{count}} regulator's text.",
          expectedCited_other: "Cited to {{count}} regulators' texts.",
          expectedNone: "No regime asks for this scenario; it is the bank's own.",
          haveNone: "It strikes nothing yet.",
          have_one: "Strikes {{count}} target.",
          have_other: "Strikes {{count}} targets.",
          judgedNone: "No service is in its reach, so nothing is judged.",
          /* Round 18 R10 -- the functions beside the services, plural-
             aware; the breached counted across both. */
          judgedServices_one: "{{count}} service",
          judgedServices_other: "{{count}} services",
          judgedFunctions_one: "{{count}} critical internal function",
          judgedFunctions_other: "{{count}} critical internal functions",
          judgedAnd: "{{services}} and {{functions}}",
          judgedCounted: "{{who}} judged; {{breached}} breached, {{at_risk}} at risk.",
          /* Round 20 P24 -- one population per figure: the services,
             then the functions judged apart, each with its status. It
             read "5 breached" as 4 services and 1 candidate function. */
          judgedServicesOf_one:
            "{{breached}} of the {{count}} service breached, {{at_risk}} at risk{{cand}}.",
          judgedServicesOf_other:
            "{{breached}} of the {{count}} services breached, {{at_risk}} at risk{{cand}}.",
          judgedServicesCandidates: "; {{n}} of them under assessment",
          judgedFunctionsApart_one:
            " {{count}} critical internal function judged apart{{status}}: {{breached}} breached.",
          judgedFunctionsApart_other:
            " {{count}} critical internal functions judged apart{{status}}: {{breached}} breached.",
          judgedFnUnderAssessment: " (under assessment)",
          judgedFnAdmitted: " (admitted)",
          judgedFnSome: " ({{n}} under assessment)",
          decidedNone: "No severe-but-plausible basis is recorded.",
          decidedUnapproved: "A basis is recorded; nobody is named as approving it.",
          decidedApproved: "Basis approved by {{name}}.",
          decidedOverdue: "The basis is past its review date.",
          provedNone: "It has never been tested.",
          provedPlanned_one: "{{count}} test run planned; none executed.",
          provedPlanned_other: "{{count}} test runs planned; none executed.",
          proved_one: "{{count}} test run executed, last on {{date}}.",
          proved_other: "{{count}} test runs executed, last on {{date}}.",
        },
        // The same five stages, read from a regulator's letter.
        letter: {
          expectedNone: "No observations have been recorded from this letter.",
          expected_one: "{{count}} observation; {{serious}} critical or high.",
          expected_other: "{{count}} observations; {{serious}} critical or high.",
          haveNone: "No plan answers this letter yet.",
          have: "Controls behind the plans: {{controls}}; findings: {{findings}}.",
          judgedNone: "Nothing to answer until the observations are recorded.",
          judged_one: "{{answered}} of {{count}} observation has an action against it.",
          judged_other:
            "{{answered}} of {{count}} observations have an action against them.",
          decidedNone: "No corrective action plan has been created.",
          decided_one: "{{count}} plan; {{approved}} approved.",
          decided_other: "{{count}} plans; {{approved}} approved.",
          proved_one:
            "{{count}} plan sealed; {{complete}} of {{actions}} actions complete.",
          proved_other:
            "{{count}} plans sealed; {{complete}} of {{actions}} actions complete.",
        },
        // The same five stages, read from an incident.
        finding: {
          expectedNone: "No report to the regulator has been recorded.",
          /* Round 19 G35 -- a finding a significant change raised. */
          expectedChange:
            "A significant change reached it: map the service again, review its tolerance, and test it again.",
          expected_one:
            "{{count}} report stage recorded; {{submitted}} submitted, {{late}} late.",
          expected_other:
            "{{count}} report stages recorded; {{submitted}} submitted, {{late}} late.",
          haveNone: "It is not bound to anything in the estate.",
          have_one:
            "Lands on {{name}}; reaches {{count}} service, {{mvb}} minimum-viable.",
          have_other:
            "Lands on {{name}}; reaches {{count}} services, {{mvb}} minimum-viable.",
          /* Round 18 P8 -- the reach names its cut: admitted services,
             with the candidates beside them, and how it reaches. */
          haveCut_one:
            "Lands on {{name}}; reaches {{count}} admitted service ({{all}} with candidates){{via}}, {{mvb}} admitted minimum-viable ({{mvbAll}} with candidates).",
          haveCut_other:
            "Lands on {{name}}; reaches {{count}} admitted services ({{all}} with candidates){{via}}, {{mvb}} admitted minimum-viable ({{mvbAll}} with candidates).",
          haveVia: " through what it funds",
          /* Round 20 P20 -- the reach by its cut: supported directly,
             and reached through the services that depend on it. */
          haveThrough_one:
            " {{direct}} of them directly; the others through {{count}} service that depends on it.",
          haveThrough_other:
            " {{direct}} of them directly; the others through {{count}} services that depend on it.",
          haveThroughNone: " {{direct}} of them directly; no service depends on it.",
          judged:
            "{{severity}}, {{status}}; {{breached}} breached, {{at_risk}} at risk.",
          /* Round 17 R4 -- a function's breach, counted apart as the pack does. */
          judgedFunctions_one: "{{count}} function breached.",
          judgedFunctions_other: "{{count}} functions breached.",
          /* Round 19 R21 -- a service under assessment it breaches. */
          judgedCandidates_one: "{{count}} service under assessment breached.",
          judgedCandidates_other: "{{count}} services under assessment breached.",
          decidedNone: "No corrective action plan answers it yet.",
          decidedClosed: "Closed without a plan; nothing is owed.",
          decided_one: "{{count}} plan answers it; {{approved}} approved.",
          decided_other: "{{count}} plans answer it; {{approved}} approved.",
          proved_one: "{{count}} plan sealed; {{reports}} reports submitted.",
          proved_other: "{{count}} plans sealed; {{reports}} reports submitted.",
        },
      },
      ibsHub: {
        /* Round 16 I14 -- the admission chip's words. */
        status: {
          candidate: "candidate",
          admitted: "admitted",
          removed: "removed",
        },
        tier: {
          tier_0: "tier 0",
          tier_1: "tier 1",
          tier_2: "tier 2",
          tier_3: "tier 3",
        },
        // P428.2 -- the group a service is governed in.
        group: "Group",
        ungrouped: "not in a group",
        /* B2 -- membership and recommendation are different questions,
         and the chip was answering the second where a reader looks for
         the first. */
        inDeclaration: "In the approved declaration",
        /* A4 -- what to say when the flag is set and no approved
           declaration exists. Imported, or left behind by a superseded
           one; either way it is a working list, not a governed
           decision, and the page said the opposite. */
        markedNotDeclared: "Marked minimum-viable -- no approved declaration yet",
        recommends: "register recommends: {{tier}}",
        heading: "This {{serviceTermShort}}'s story",
        headingInternal: "This function's story",
        funds: {
          lead: "What stops when this stops:",
          notAdmitted: "(not yet admitted)",
          none: "What stops when this stops: no service is mapped as funded by this function yet.",
        },
        admission: "Admission",
        notAdmitted: "not admitted",
        tolerance: "Tolerance",
        /* Round 12 H1 -- the fifth wording for one state, on the IBS
           hub, three lines from a badge saying "awaiting the CRO".
           Aligned to the vocabulary the badge uses. */
        pending: "{{hours}}h declared, awaiting signature",
        notSet: "not set",
        hours: "{{hours}}h",
        signed: "signed",
        // Round 16 D21 -- the same words the badge and the war room use.
        notSigned: "in force, not signed",
        mvb: "{{mvbTermShort}}",
        flagged: "flagged",
        notMvb: "not {{mvbTermShort}}",
        resilience: "Resilience",
        rag: {
          red: "red",
          amber: "amber",
          green: "green",
          unknown: "unknown",
          /* Round 12 B1 -- an IBS page read "RESILIENCE green" on an
             estate with no telemetry and nothing admitted. The seal is
             right; the word claimed health where all that is true is
             that nothing has reported a problem. */
          not_assessed: "not yet assessed",
        },
        obligations: "Obligations",
        scenarios: "Scenarios",
      },
      entity: {
        loading: "Loading entity...",
        notFoundTitle: "Entity not found",
        notFoundBody: "Check the URL -- this id may have been deleted.",
        errorTitle: "Could not load entity",
        chipsHeading: "Live state",
        // Round 9 C1. "No open findings on this entity" was true of the
        // query and false about the estate: the connector feeds raise
        // findings on configuration items, and the page asked only for
        // those raised directly on the service.
        reach: {
          noneDirect_one:
            "No findings are raised directly on this service; {{count}} reaches it through the configuration items it runs on.",
          noneDirect_other:
            "No findings are raised directly on this service; {{count}} reach it through the configuration items it runs on.",
          andVia_one:
            "{{count}} more reaches this service through the configuration items it runs on.",
          andVia_other:
            "{{count}} more reach this service through the configuration items it runs on.",
          seeThem: "See them",
          /* Round 10 C4 -- the OPEN FINDINGS block printed "No open
             findings on this entity" eight lines under the sentence
             above. True of that query, false about the estate, and the
             reader has no reason to doubt it. */
          noneDirectHere_one:
            "None raised directly on this service. {{count}} reaches it through the configuration items it runs on -- listed above.",
          noneDirectHere_other:
            "None raised directly on this service. {{count}} reach it through the configuration items it runs on -- listed above.",
          ibsesHeading: "{{serviceTermPlural, capitalize}} it supports directly",
          /* Round 20 P20 -- the page named 2, the thread 9, the cascade
             43 nodes; each now says which. */
          cut_one:
            "Supports {{direct}} admitted directly; reaches {{all}} admitted in all, through {{count}} service that depends on it.",
          cut_other:
            "Supports {{direct}} admitted directly; reaches {{all}} admitted in all, through {{count}} services that depend on it.",
          cutNone: "Supports {{direct}} admitted directly; no service depends on it.",
          /* Round 20 P22 -- the cut the cascade card counts, named. */
          serious_one: "{{count}} of them critical or high, which the cascade counts.",
          serious_other:
            "{{count}} of them critical or high, which the cascade counts.",
          ibsesNone: "This service supports no {{serviceTerm}}.",
        },
        findingsHeading: "Open findings",
        findingsNone: "No open findings on this entity.",
        /* Round 10 F2 -- why the resilience chip is not green when
           nothing here is open. Eighth round of an unexplained amber. */
        resilienceWhy: {
          /* ROUND 15 H1 -- a bare amber chip above a list of findings
             still did not say the two were the same fact. */
          ownFindings_one:
            "It reads {{state}} because of the open finding listed below.",
          ownFindings_other:
            "It reads {{state}} because of the {{count}} open findings listed below.",
          dependency:
            "It reads {{state}} because something it depends on is {{upstreamState}}:",
          telemetry:
            "It reads amber on live telemetry rather than on a finding: this service is currently reporting {{status}}.",
          unnamedUpstream: "a dependency this page cannot name",
          /* Round 11 F1 -- the commonest reason of the three, and the
             one round 10 left unexplained: the state was sealed from
             open findings that are not in the list beneath it, because
             they landed on a technology asset this service runs on and
             were rerouted onto the service, or because they were
             closed after the seal was taken. */
          sealedOnFindings_one:
            "It reads {{state}} on a finding that is not in the list above -- " +
            "findings raised on the technology this rests on are counted " +
            "against the service:",
          sealedOnFindings_other:
            "It reads {{state}} on {{count}} findings that are not in the list " +
            "above -- findings raised on the technology this rests on are " +
            "counted against the service:",
          /* And where the seal records no reason at all, the one thing
             that can be said without guessing: when it was taken. The
             chip is older than the list beneath it. */
          sealedEarlier:
            "It reads {{state}} from the snapshot sealed on {{when}}. Nothing " +
            "is open against it now, so the chip is older than the list above.",
        },
        findingsLoading: "Loading findings...",
        obligations: {
          universalForCapability:
            "Every capability carries the whole catalogue: obligations are narrowed per business service and per service, and a capability inherits the set rather than a subset of it.",
          notDerivedForKind:
            "Regulatory obligations are derived for {{serviceTermShortPlural}}, services and " +
            "capabilities. A vendor carries none of its own -- the " +
            "obligations sit on the services that rest on it.",
          heading: "Regulatory obligations",
          loading: "Loading obligations...",
          none: "No regulatory obligations derived for this entity yet.",
          count_one: "{{count}} obligation",
          count_other: "{{count}} obligations",
          met: "Met",
          open: "Open",
          showOthers: "Show {{count}} obligations from other regimes ({{regimes}})",
          /* D7 -- how many, and from where, before the wall of text. */
          clauseCount_one: "{{count}} clause",
          clauseCount_other: "{{count}} clauses",
          hideOthers: "Hide obligations from other regimes",
          /* ROUND 13 G5 -- this stamp belongs to the SEALED obligation
             snapshot, which a scheduled pass writes. It sat beneath a
             six-layer summary that moves live, so a reader who had
             just named a dependency and watched the count go from 1 of
             6 to 5 of 6 read a footer stamped two minutes before he
             arrived and concluded the page was stale. Two clocks, one
             footer. Naming what it stamps is the whole fix -- the time
             itself was right. */
          asOf: "Obligations last derived {{when}}",
          trace: {
            loading: "Loading controls...",
            error: "Could not load control mapping.",
            forbidden: "Control mapping requires framework access.",
            summary: "{{compliant}} of {{count}} controls compliant",
            unassessed_one: "{{count}} not assessed",
            unassessed_other: "{{count}} not assessed",
            status: {
              compliant: "Compliant",
              partial: "Partial",
              non_compliant: "Gap",
              no_evidence: "No evidence",
              unassessed: "Not assessed",
            },
            capsHeading: "Remediating CAPs",
            capsEmpty: "No CAPs linked to these controls yet.",
            capsInFlight_one: "{{count}} CAP in flight",
            capsInFlight_other: "{{count}} CAPs in flight",
          },
          equivalents: {
            find: "Find cross-framework equivalents",
            finding: "Asking the Engine...",
            none: "No equivalent obligations in other frameworks.",
            engineOff: "Marsad Engine not configured.",
            error: "Could not check for equivalents.",
            confirm: "Confirm",
            confirmedBadge: "✓ confirmed",
            confirmedHeading: "Confirmed equivalents",
            unconfirm: "Remove this confirmed equivalence",
            viewControls: "View its controls",
            hideControls: "Hide its controls",
          },
        },
        kind: {
          ibs_service: "{{serviceTermShort}}",
          /* Round 16 -- the class, not the table: a CIF was headed "IBS". */
          ibs_service_internal: "Critical internal function",
          service: "Service",
          capability: "Capability",
          vendor: "Vendor",
          configuration_item: "Configuration item",
        },
        /* Round 9 E7 -- the words a SENTENCE needs, as opposed to the
           words a header needs.
           "All 10 ibs services a finding reaches" was the enum with
           its underscore swapped for a space: it reads as a word and
           is not one. The block above already names these kinds, but
           in title case for a header eyebrow, and it carries no
           plural.
           Casing lives here rather than in the sentence. Lowercasing
           at the call site is what turns "IBS" into "ibs" -- an
           acronym is not a word you may downcase, and a rule that
           cannot tell the two apart will get one of them wrong every
           time. */
        kindInSentence: {
          ibs_service: "{{serviceTermShort}}",
          service: "service",
          capability: "capability",
          vendor: "vendor",
          configuration_item: "configuration item",
        },
        kindInSentencePlural: {
          ibs_service: "{{serviceTermShortPlural}}",
          service: "services",
          capability: "capabilities",
          vendor: "vendors",
          configuration_item: "configuration items",
        },
      },
    },
    framework: {
      // P222: Home assessment-cycle coverage card.
      cycles: {
        micro: "Assessment cycles",
        open: "Open assessments",
        overdue: "{{days}}d overdue",
        dueIn: "due in {{days}}d",
        signoff: "✓ {{count}} signed off · {{pct}}%",
      },
      // P128: module-home copy routed through i18n.
      home: {
        rubric: "Framing · Framework + CAP",
        heading: "Framework + CAP",
        positioning:
          "Regulator-facing compliance. From letter intake to CAP closure " +
          "-- in one round-trip. The Marsad Engine drafts grounded in your " +
          "live operational data; you ship the regulator-ready evidence pack.",
        signals: {
          loading: "Reading work-in-flight...",
          openCaps_one: "open CAP",
          openCaps_other: "open CAPs",
          lettersOnFile_one: "letter on file",
          lettersOnFile_other: "letters on file",
        },
        resume: {
          prefix: "Resume where you left off",
          queue: "queue",
        },
        sections: {
          yourWork: "Your work",
          assessments: "Assessments",
          letters: "Letters",
        },
        tiles: {
          recommendations: {
            name: "What needs attention",
            desc: "Prioritised remediation across assessment gaps, {{serviceTermShort}} risk + scenarios",
          },
          assessmentsHub: {
            name: "Frameworks hub",
            desc: "Browse SAMA + ISO catalogues + open / close cycles",
          },
          letterNew: {
            name: "Intake new letter",
            desc: "Upload a regulator PDF; Marsad Engine extracts observations",
          },
          attestations: {
            name: "Attestation cycles",
            // F -- DRRA is an initialism nobody outside the drafting
            // team reads. Say what the thing is.
            desc: "Quarterly sign-off that each {{serviceTerm}} is still resilient",
          },
          regChange: {
            name: "Regulatory-change autopilot",
            desc: "Paste a circular; the Engine finds the obligations it affects",
          },
          outboundFeed: {
            name: "Outbound feed",
            desc: "What your GRC platform can pull out of MARSAD, at what grain, and how every pull is recorded.",
          },
          documentGap: {
            name: "Document gap analysis",
            desc: "What each framework expects vs the documents you have on file",
          },
        },
      },
      hello: {
        heading: "Framework + CAP",
        body:
          "Regulator-facing compliance: letter intake, " +
          "Marsad-Engine-assisted CAP authoring, three-position " +
          "review workflow with tamper-evident hash chain. The " +
          "commercial moat.",
        recentLetters: "Recent letters",
        noRecentLetters: "No letters yet. Intake your first to get started.",
        observationsCount_one: "{{count}} observation",
        observationsCount_other: "{{count}} observations",
        queueSummary: "Your queues",
        viewAllLetters: "View all letters",
        assessmentsLink:
          "Framework assessments -- per-control SAMA ITGF / CSF / BCM judgements with evidence chain + Marsad Engine analyser",
      },
      // P131 (#483): attestation programme surface.
      attestations: {
        rubric: "Framing · Attestations",
        heading: "Attestation cycles",
        intro:
          "The quarterly self-assessment. Open a cycle, attest each " +
          "{{serviceTerm}} and critical internal function, " +
          "and sign it off. The snapshot is frozen at submit as the " +
          "regulator-audited evidence.",
        loading: "Loading cycles...",
        create: "Create",
        cancel: "Cancel",
        forbidden:
          "You do not have access to the attestation programme. Ask an administrator for the Framework module.",
        loadFailed: "The attestation programme could not be loaded.",
        noCycles: "No attestation cycles yet.",
        noAttestations: "No attestations in this cycle yet.",
        newCycle: "New attestation cycle",
        cycleCreated: "Cycle created.",
        cycleNamePlaceholder: "e.g. 2026 Q2 {{serviceTermShort}} Self-Assessment",
        periodStart: "Period start",
        periodEnd: "Period end",
        dueDate: "Due date",
        due: "due",
        approvedShort: "approved",
        openCycle: "Open cycle",
        closeCycle: "Close cycle",
        cycle_opened: "Cycle opened.",
        cycle_closed: "Cycle closed.",
        ibs: "{{serviceTermShort}} or critical internal function",
        // Round 16 G7 -- maker and checker are different people.
        ownSubmission: "You submitted this. Another approver must sign it.",
        functionsGroup: "Critical internal functions",
        selectIbs: "Select a service or function...",
        // Round 17 L7 -- the services under their own heading too.
        servicesGroup: "{{serviceTermPlural, capitalize}}",
        statementPlaceholder:
          "Attestation statement (optional now; required to submit)",
        raise: "Raise",
        raised: "Attestation raised.",
        submit: "Submit",
        approve: "Approve",
        reject: "Reject",
        reopen: "Reopen",
        confirmReject: "Confirm reject",
        rejectPlaceholder: "Rejection comments (required)",
        rejectionComments: "Rejection comments",
        snapshotLabel: "Snapshot",
        snapshotPending: "Snapshot frozen at submit.",
        currentLabel: "Attesting to (as it stands now)",
        statementLabel: "Your attestation statement",
        saveStatement: "Save statement",
        statementSaved: "Statement saved.",
        needsStatement:
          "Add a statement before submitting -- an attestation is the " +
          "statement, and it cannot be signed off empty.",
        submittedBy: "Submitted by",
        actionedBy: "Actioned by",
        // Round 14 G, sixth round.
        inMvb: "in the {{mvbTerm}}",
        notInMvb: "not in the {{mvbTerm}}",
        // Round 18 T10 -- the letter and the plan, from the attestation.
        plansOnService: "Plans answering a finding on this service",
        plansOnServiceNone: "No plan answers a finding on this service.",
        fromLetter: "from",
        answersFinding: "answers: {{finding}}",
        signatures: "Signatures",
        signedAttested: "Attested by {{who}} on {{when}}",
        signedApproved: "Approved by {{who}} on {{when}}",
        signedRejected: "Rejected by {{who}} on {{when}}",
        awaitingApprover: "Awaiting the approver's signature",
        cycleStatus: {
          draft: "Draft",
          open: "Open",
          closed: "Closed",
        },
        status: {
          draft: "Draft",
          submitted: "Submitted",
          approved: "Approved",
          rejected: "Rejected",
        },
        acted: {
          submit: "Attestation submitted.",
          approve: "Attestation approved.",
          reject: "Attestation rejected.",
          reopen: "Attestation reopened.",
        },
      },
      regChange: {
        /* Round 19 A45 -- the shortlist's width, said. */
        chosenFrom_one: "Chosen from {{count}} obligation.",
        chosenFrom_other: "Chosen from {{count}} obligations.",
        findCandidates: "Find candidate obligations",
        finding: "Searching...",
        candidatesIntro:
          "{{count}} obligations are closest to this text. Tick the ones it applies to -- this is a shortlist by wording, not a judgement.",
        candidateCounts: "{{controls}} controls · {{gaps}} not compliant",
        logManual: "Log against {{count}} selected",
        rubric: "Framing · Regulatory change",
        heading: "Regulatory-change autopilot",
        intro:
          "Paste a new SAMA circular or rule change. The Marsad Engine " +
          "identifies which of your existing obligations it affects and " +
          "their current compliance exposure -- grounded in your real " +
          "obligation catalogue.",
        placeholder: "Paste the regulatory change text here...",
        assess: "Assess impact",
        assessing: "Assessing impact",
        engineOff: "Marsad Engine not configured.",
        error: "Could not assess the change.",
        none: "No existing obligations appear affected by this change.",
        headline:
          "{{affected}} obligation(s) affected, {{gaps}} with open control gaps",
        impact: {
          new_requirement: "New requirement",
          tightened: "Tightened",
          clarified: "Clarified",
        },
        controls: "{{count}} controls",
        gaps: "{{count}} gaps",
        viewTrace: "Trace",
        draftCap: {
          button: "Draft remediation CAP",
          drafting: "Drafting CAP...",
          // Round 18 A33 -- the plan answers the gaps, and says so.
          hint:
            "Creates a draft CAP for this circular, pre-linked to the " +
            "affected obligations' implementing controls that have a gap. " +
            "Controls graded compliant are left off.",
          /* Round 19 N13 -- the approver who assessed it was told the
             seat, not who holds it, with no way to hand the draft on. */
          locked: "Drafting a plan is the preparer's.",
          lockedNamed: "Drafting a plan is the preparer's: {{names}} can draft it.",
          lockedNobody:
            "Drafting a plan is the preparer's, and nobody here holds the preparer seat yet.",
          others_one: "{{count}} other",
          others_other: "{{count}} others",
          sendToPreparers: "Send it to them",
          sending: "Sending...",
          sent_one:
            "Sent to {{count}} preparer. Anyone in the seat not yet signed in is told when they first sign in.",
          sent_other:
            "Sent to {{count}} preparers. Anyone in the seat not yet signed in is told when they first sign in.",
          sentNobodyYet:
            "No preparer has signed in here yet; each is told when they first sign in.",
          fallback: "regulatory change",
          success: "Remediation CAP drafted -- {{count}} controls linked.",
          compliantLeftOff_one: "{{count}} control graded compliant was not linked.",
          compliantLeftOff_other:
            "{{count}} controls graded compliant were not linked.",
          error: "Could not draft the remediation CAP.",
        },
        lineage: {
          /* Round 19 A42 -- every assessment is on the record when made,
             not only the ones a plan was drafted from. */
          heading: "Regulatory changes assessed",
          noPlan: "Assessed; no plan drafted",
          /* Round 19 N13 -- a preparer handed the assessment drafts from
             its row. */
          draftHere: "Draft the plan",
          handedToYou: "Sent to the preparers to draft",
          obligations_one: "{{count}} obligation",
          obligations_other: "{{count}} obligations",
        },
      },
      controlTesting: {
        // Round 17 A30 -- the picker said nothing when it cut its list.
        filterPlaceholder: "Filter by file name or title",
        moreOnGapPage: "Showing the first {{count}}. Filter by name, or",
        gapPageLink: "see every candidate on the gap page",
        heading: "Control testing (ToD / ToI / ToE)",
        intro:
          "Three structured tests behind this control. The assessment " +
          "status is the worst-of rollup across the tests you record.",
        save: "Save",
        saved: "Test result saved.",
        runToD: "Test design with Engine",
        todBusy: "Reading your policies",
        todDone_one: "Test of Design complete (1 document read).",
        todDone_other: "Test of Design complete ({{count}} documents read).",
        // Round 9 B6. The sentence sent her to another screen while the
        // verb that satisfies it -- "Map a policy document" -- stands
        // directly beside it and now works on every control. Pointing
        // away from the button next to you is how a dead end survives
        // being fixed.
        /* Round 13 A2 -- the Engine proposes and a person records. One
           press used to move the bank's compliance position on this
           control, and create it when none existed. These are the words
           on that gate. */
        /* Round 13 A5 -- a recorded position with nothing behind it. */
        // ROUND 15 A18 -- not a withdrawal; nothing was ever recorded.
        noTestRecordedYet:
          "No test has been recorded on this control yet. Its position, {{status}}, is a hand grade.",
        statusUnsupported:
          "This control is recorded as {{status}}, and no test now supports " +
          "that. Withdrawing a test does not withdraw what it wrote. Re-run " +
          "a test, or change the status on the assessment itself.",
        todProposesTitle: "The Engine proposes a status",
        todWouldCreate:
          "Accepting would record this control as {{status}}. Nothing is " +
          "recorded on it yet, so this would be the bank's first stated " +
          "position on this control.",
        todWouldChange: "Accepting would move this control from {{from}} to {{to}}.",
        /* Round 14 A1 -- the proposal is held apart from the result
           until someone accepts or declines it. */
        todProposedResult: "Proposed result: {{result}}.",
        /* Round 14 A9 -- the maturity from the same answer, shown and
           not applied: a design test judges whether the control is
           written to work, and a maturity level is a claim about the
           whole control. */
        todSuggestedMl:
          "The Engine also suggested maturity {{ml}}. It is not applied by this test; set maturity on the assessment if you agree.",
        todWouldKeep:
          "Accepting would leave this control at {{status}}, and the " +
          "Engine's result and reasoning would become the recorded ones.",
        todNotYetRecorded:
          "Nothing has been recorded. Until you accept it, this result counts " +
          "for nothing: not in the status, and not in any later test.",
        todAccept: "Accept and record",
        todDiscard: "Decline",
        todAccepted: "Recorded.",
        /* Round 14 A2 -- found by reading the text, not by asking the
           model: the model that was talked round is the one that stays
           quiet about it. */
        todDirectiveHeading_one:
          "One passage in the covering documents is addressed to an automated reader, not to a person. The Engine may have followed it. Read it before you decide.",
        todDirectiveHeading_other:
          "{{count}} passages in the covering documents are addressed to an automated reader, not to a person. The Engine may have followed them. Read them before you decide.",
        /* Round 14 A3 -- a document the test read has been withdrawn. */
        resultRestsOnWithdrawn_one:
          "This result rests on {{names}}, which no longer covers this control. It stands until you change it: re-run the test or record your own result.",
        resultRestsOnWithdrawn_other:
          "This result rests on {{count}} documents that no longer cover this control ({{names}}). It stands until you change it: re-run the test or record your own result.",
        // Round 16 A24.
        todReasoningDisagrees:
          "The answer's own reasoning says the requirement is not evidenced. Read it before accepting.",
        todReadWithdrawn_one:
          "The Engine read {{names}}, which has since been withdrawn from this control. Re-run the test before you accept.",
        todReadWithdrawn_other:
          "The Engine read {{count}} documents that have since been withdrawn from this control ({{names}}). Re-run the test before you accept.",
        /* Round 14 A6 -- whose result this is, on the record. */
        recordEngineAccepted:
          "The Marsad Engine proposed this result; {{who}} accepted it on {{when}}.",
        recordEnteredBy: "Recorded by {{who}}.",
        recordSomeone: "a person",
        /* Round 18 A35 -- the reviewer reads the passages, and that the
           preparer turned the Engine down. */
        recordDirectivesShow: "Show the passages",
        recordDeclined: "{{who}} declined an Engine proposal ({{result}}) on {{when}}.",
        recordDeclinedNoResult: "{{who}} declined an Engine proposal on {{when}}.",
        recordProposalWaiting:
          "An Engine proposal is waiting for a person to accept or decline it. It is not part of this result.",
        todDeclined: "Declined. The decline is on the record.",
        todActionFailed: "That did not go through. Nothing was changed.",
        /* Round 13 A3 -- what the judgement rested on, beside the
           judgement. The run payload carried seven keys and none was the
           evidence. */
        todExcerptSummary_one: "Read the excerpt this judgement rested on (1 document)",
        todExcerptSummary_other:
          "Read the excerpt this judgement rested on ({{count}} documents)",
        /* Round 13 A1 -- the document tried to address the Engine. It is
           removed before the Engine reads it; the attempt is not hidden. */
        todDocumentAddressedEngine_one:
          "A mapped document contained text addressed to an automated reader. It was removed before the Engine read it. Check what this document is.",
        todDocumentAddressedEngine_other:
          "Mapped documents contained {{count}} pieces of text addressed to an automated reader. They were removed before the Engine read them. Check what these documents are.",
        todNoDocs:
          "No confirmed policy documents are mapped to this control, so " +
          "there is nothing for the Engine to read. Map one below, then " +
          "run the test again.",
        // One brief toast alongside the sentence that stays. Round 8
        // removed four copies of this and left silence, so a preparer
        // who pressed the button and looked away had nothing telling
        // her it had not run.
        todDidNotRun: "Test of Design did not run -- see the reason on the control.",
        engineOff: "Marsad Engine not configured.",
        // B1 -- naming a prerequisite is not the same as giving
        // somebody a way to satisfy it.
        mapDocument: "Map a policy document",
        // D9 -- says which of the two situations it is. The catalogue
        // names 209 deliverables against six frameworks, so for most
        // controls it cites none at all, and a reader could not tell
        // that from "no documents resemble them".
        noCandidates:
          "Nothing here yet -- either the catalogue names no deliverable for this control, or no held document resembles one.",
        noCandidatesRoute: "Map a document to a deliverable",
        /* B3 -- the catalogue names 57 distinct control references for
           a 240-control framework, so for most controls there is
           nothing to map TO. Confirming records the expectation. */
        recordsDeliverable: "records a deliverable for this control",
        mapped: "Mapped. Run Test of Design again.",
        /* Round 19 A45 -- the mapping stood before the press. */
        alreadyCovered: "Already covered: {{file}} is mapped to {{deliverable}}.",
        todError: "Could not run Test of Design.",
        type: {
          design: "Test of Design",
          implementation: "Test of Implementation",
          effectiveness: "Test of Effectiveness",
        },
        result: {
          effective: "Effective",
          partially_effective: "Partially effective",
          ineffective: "Ineffective",
          not_tested: "Not tested",
        },
      },
      kpi: {
        heading: "Effectiveness KPIs",
        empty:
          "No KPIs yet. Add an auto KPI (fed from the twin) or a manual one, " +
          "then derive Test of Effectiveness.",
        addKpi: "+ Add KPI",
        deriveToE: "Derive ToE from KPIs",
        toeDerived: "Test of Effectiveness derived: {{result}}.",
        noKpis: "Add an effectiveness KPI before deriving ToE.",
        deriveError: "Could not derive Test of Effectiveness.",
        noReading: "no reading",
        /* Round 11 D5 -- why it says that. An auto KPI a preparer adds
           herself reads "no reading" for ever while sampling is off,
           and she has no verb anywhere on her route that would change
           it. The sentence names who does, because the alternative is
           a reader waiting for a number that is never coming.
           `samplingOff` already said the first half and rendered only
           beside a reading, which is the one case it was not for. */
        noReadingBecause:
          "Automatic sampling is off, so this will not read until an administrator turns it on or samples once.",
        /* Said on the add form too, before she commits to a metric she
           cannot read. Shorter, because she is mid-task. */
        addAutoSamplingOff:
          "Automatic sampling is off: an auto metric will show no reading until an administrator turns it on.",
        sampleNow: "Sample now",
        sampling: "Sampling…",
        /* Estate-wide, because that is what the endpoint does -- the
           same pass the scheduler runs on cadence. "Sampled this KPI"
           would be a narrower claim than the act. */
        sampled:
          "Sampled {{kpis}} auto KPIs across the estate: {{readings}} took a reading.",
        sampledNothing:
          "Nothing read: each metric had no data to count, or its source is not reporting.",
        sampleFailed: "Could not sample the auto KPIs.",
        measuredAt: "Measured {{when}}",
        nextSample: "next sample at {{when}}",
        samplingOff: "automatic sampling is off",
        estateWide: "counted across the whole estate",
        counted: "What it counted:",
        andMore: "and {{n}} more",
        target: "target {{target}} ± {{tolerance}}",
        readingPlaceholder: "value",
        record: "Record",
        remove: "Remove",
        sourceAuto: "Auto (from the twin)",
        added: "KPI {{name}} added. Derive Test of Effectiveness again to fold it in.",
        removed: "KPI {{name}} removed. Test of Effectiveness will no longer read it.",
        sourceManual: "Manual",
        pickMetric: "Pick a metric…",
        namePlaceholder: "KPI name",
        targetPlaceholder: "target",
        tolerancePlaceholder: "± tol",
        higherBetter: "Higher is better",
        lowerBetter: "Lower is better",
        add: "Add",
        trend: "Trend",
        hideTrend: "Hide trend",
        noTrend: "Not enough readings for a trend yet.",
      },
      probe: {
        heading: "Implementation probes",
        empty:
          "No probes yet. Add a probe (a live implementation signal from " +
          "the twin), then derive Test of Implementation.",
        addProbe: "+ Add probe",
        pickProbe: "Pick a probe…",
        add: "Add",
        remove: "Remove",
        deriveToI: "Derive ToI from probes",
        toiDerived: "Test of Implementation derived: {{result}}.",
        noProbes: "Add an implementation probe before deriving ToI.",
        deriveError: "Could not derive Test of Implementation.",
        status: {
          present: "Present",
          partial: "Partial",
          absent: "Absent",
          unknown: "Unknown",
        },
      },
      documentGap: {
        rubric: "Framing · Document gap analysis",
        heading: "Document gap analysis",
        intro:
          "What each SAMA framework expects you to maintain, against the " +
          "documents you have on file. Confirmed mappings count as present; " +
          "Engine suggestions await your review.",
        allFrameworks: "All frameworks",
        coverage: "{{present}} of {{total}} present · {{pct}}%",
        mandatoryMissing_one: "{{count}} mandatory document missing",
        mandatoryMissing_other: "{{count}} mandatory documents missing",
        empty:
          "No expected deliverables yet. Seed the catalogue from the admin " +
          "console, then run the document mapping pass.",
        error: "Could not load the gap report.",
        status: {
          present: "Present",
          candidate: "Candidate",
          missing: "Missing",
        },
        col: {
          deliverable: "Expected deliverable",
          type: "Type",
          status: "Status",
          covering: "Covering document",
        },
        mandatory: "Mandatory",
        /* Round 13 A4 -- "This covers it" was one click with no way back,
           and a confirmed-mapped document is refused deletion by design,
           so this is the only containment a bank has once it discovers a
           bad document. */
        rejectCovers: "Withdraw from this deliverable",
        rejectCoversAsk:
          "Withdraw {{name}} as evidence for this deliverable? It can be mapped again later.",
        rejectCoversConfirm: "Withdraw",
        rejected: "{{name}} no longer covers this deliverable.",
        awaitingReview: "awaiting review",
        noDocument: "--",
        // Round 17 A30 -- a covered row may take another document.
        addAnother: "Add another document",
        findCandidates: "Find candidate documents",
        candidatesFor: "Candidate documents for {{name}}",
        // B1 -- the candidate list had no verb.
        confirmCovers: "This covers it",
        confirmed:
          "{{name}} confirmed as covering this deliverable. Test of Design can read it now.",
        // Round 18 A35 -- the candidates' percentage, said.
        similarity: "{{pct}}% name match",
        withdrawn: "withdrawn from this deliverable",
        similarityWhy:
          "How closely the document's name and title match this deliverable's name. It is a hint for finding the document, not a measure of how much of the deliverable it covers.",
        candidatesNone: "No held documents look similar.",
        candidatesError: "Could not load candidate documents.",
      },
    },
  },
  home: {
    /* Round 16 H24 -- the accountable executive's seat, said. */
    ae: {
      heading: "You are the Accountable Executive",
      body: "You give the last signature on the bank's {{mvbTerm}}. Each {{mvbTermShort}} pack and each declaration of the set comes to you after the Chief Risk Officer has signed, and none of it is in force until you sign too.",
      link: "Your signatures waiting in My Work",
    },
    // Navigation re-cut, option A -- the five question cards.
    questions: {
      heading: "The five questions",
      answer: {
        // Round 17 H40 -- plural-aware parts ("1 functions").
        matters: "{{services}} and {{functions}} admitted; {{mvb}} minimum-viable.",
        mattersServices_one: "{{count}} service",
        mattersServices_other: "{{count}} services",
        mattersFunctions_one: "{{count}} function",
        mattersFunctions_other: "{{count}} functions",
        /* Round 18 P9 -- nothing admitted yet, something under
           assessment: the empty estate read "0 services and 0 functions
           in the register" beside a portfolio's "1 under assessment". */
        mattersNothingAdmitted:
          "0 admitted, {{under}} under assessment; {{mvb}} minimum-viable.",
        safe: "{{count}} of {{of}} services impacted.",
        owe: "{{governed}} of {{of}} tolerances signed; {{tested}} of {{cells}} {{regime}} test cells done.",
        do_one: "{{count}} item waits on you.",
        do_other: "{{count}} items wait on you.",
        doNone: "Nothing waits on you.",
        /* Round 20 Y1 -- the internal audit seat reads; it has no queue. */
        doReadsOnly: "Nothing waits on you: this seat reads.",
        prove: "Resilience Score {{score}}, tier {{tier}}.",
        proveNone: "No Resilience Score yet.",
        /* Round 17 B10 -- it read "Resilience Score 6.0, tier E" on the
           empty estate and 63.5 over two unassessed candidates. */
        proveNotScored: "Not yet scored: nothing admitted.",
      },
    },
    /**
     * P79.3 (brand handoff): Home leads with the category claim, not
     * "Welcome to MARSAD". The claim is the brand voice -- measured,
     * specific, the thing that distinguishes MARSAD from
     * "yet-another-dashboard".
     */
    // P124.3 (brand P1.6): rubric above the claim, matching the
    // module-home "Framing - {module}" rhythm.
    rubric: "Live posture",
    heading: "MARSAD is the operational twin of bank resilience.",
    body:
      "Operational resilience and regulatory compliance, observed. " +
      "Your live posture across Foundation, the Operational Twin, " +
      "and Framework + CAP.",
    // P214.4 (Observatory): the night-sky hero copy. The heading + lede
    // lead-sentence carry the brand voice; the count + posture come from
    // live board/cascade data.
    hero: {
      kickerDegraded: "LIVE POSTURE · DEGRADED",
      /* Round 12 B1 -- read, and the answer is that nothing has been
         assessed. Different from kickerUnknown, which is "could not
         read at all", and very different from the green this showed. */
      kickerNotAssessed: "LIVE POSTURE \u00b7 NOT YET ASSESSED",
      kickerUnknown: "LIVE POSTURE · NOT AVAILABLE TO YOU",
      // D10 -- it told a reviewer the board is somebody else's, and the
      // board has admitted her since the gate widened. Where the number
      // comes from is the useful half; who may open a different page is
      // not this line's business.
      fromCascade: "From the live cascade · the same estate the board snapshot reads",
      kickerElevated: "LIVE POSTURE · ELEVATED",
      kickerNormal: "LIVE POSTURE · NORMAL",
      asOf: "as of {{stamp}}",
      // F -- this counts `ibs_impacted`, and called them services. The
      // gauge two inches below said "IBSes impacted" off the SAME
      // number, so the page contradicted itself about what it was
      // counting -- and "service" is the wider word, so the headline
      // read as the larger claim.
      headImpacted_one: "{{count}} {{serviceTerm}}",
      headImpacted_other: "{{count}} {{serviceTermPlural}}",
      headTail: "impacted across the estate.",
      headSteady: "All {{serviceTermPlural}} steady across the estate.",
      ledeLead: "MARSAD is the operational twin of bank resilience.",
      ledeRest:
        "Your live posture across Foundation, the Operational Twin, and " +
        "Framework + CAP, observed continuously and assembled into one " +
        "regulator-ready picture.",
      // F -- "IBSes" is ours. The headline above it says the words.
      gaugeImpacted: "Business services impacted",
      gaugeAtRisk: "/yr at risk",
      gaugeRated: "Board snapshot · rated {{rag}}",
      // Stitching review -- it printed the impacted SERVICES under
      // "Entities", the word the cascade uses for every card.
      horizonEntities: "Services impacted",
      horizonNodes: "Cascade nodes",
      horizonMvb: "{{mvbTermShort}} intersection",
      horizonResilience: "Resilience today",
    },
    search: {
      placeholder: "Search the catalogue...",
      ariaLabel: "Search the catalogue",
      submitLabel: "Run search",
      viewHeading: "Catalogue search",
      searchedFor: "Showing results for “{{query}}”",
      emptyQuery:
        "Type a query into the search box at the top of the page to " +
        "find {{serviceTermShortPlural}}, services, capabilities, vendors, products, " +
        "configuration items, channels, journeys, and risk-appetite " +
        "statements.",
      loading: "Searching the catalogue...",
      error: "Search failed. Try again, or check the topnav search box " + "for typos.",
      noResults: "No results match. Try a partial code or a synonym.",
      withheld:
        "Some results are hidden: your account cannot open {{modules}} records. Ask an administrator if you need them.",
      truncated:
        "More results matched than fit in this view. Refine your " +
        "query or open a kind directly.",
      typeaheadLoading: "Searching...",
      typeaheadError: "Couldn't search. Press Enter to retry on the full page.",
      typeaheadNoHits: "No matches. Try a partial code.",
      typeaheadListboxLabel: "Search suggestions",
      typeaheadOpenAll: "Open all results for “{{query}}” →",
      recentSearchesHeading: "Recent searches",
      recentSearchesClear: "Clear recent searches",
    },
  },
  letters: {
    /* Round 16 H25 -- the INTERNAL letter, in words. */
    internalSource: "Raised internally",
    register: {
      kicker: "FRAMEWORK + CAP · REGULATOR LETTERS",
      heading: "Regulator letters",
      intake: "Intake new letter",
      loading: "Loading letters...",
      error: "Could not load the letters register.",
      empty: "No regulator letters on file yet. Intake one to begin a CAP.",
      colLetter: "Letter",
      colReceived: "Received",
      colObservations: "Observations",
      colClosure: "Remediation",
      noCaps: "No CAP yet",
      capRollup: "{{approved}}/{{total}} CAPs approved",
    },
    intake: {
      heading: "Intake a regulator letter",
      // D4 -- the Framework tile promised PDF extraction and the page
      // offered paste-text only.
      pdfHeading: "Have the letter as a PDF?",
      pdfBody:
        "Upload it and the Marsad Engine reads the observations out of it. The original text is kept, so a reviewer can always go back to it.",
      pdfSubmit: "Upload and extract",
      pdfWorking: "Reading the letter...",
      // B8 -- extraction lands here for review; nothing is recorded
      // until Create.
      reviewLead_one:
        "The Engine read {{count}} observation. Edit anything it got wrong -- nothing is recorded yet.",
      reviewLead_other:
        "The Engine read {{count}} observations. Edit anything it got wrong -- nothing is recorded yet.",
      reviewThenCreate:
        // D11 -- one verb. The review step said "Press Create letter"
        // and the button said "Intake letter", so the sentence told the
        // operator to press something that was not on the screen.
        "The title and the date below are the letter's. Press Record the letter when they are right.",
      obsTitle: "Observation title",
      obsBody: "Observation text",
      obsDrop: "Not an observation -- drop it",
      pdfEngineOff:
        "The Marsad Engine is not configured, so a PDF cannot be read. Paste the text below instead.",
      subheading:
        "Paste the full text of a regulator letter to intake it and begin " +
        "a corrective action plan.",
      preparerOnly:
        "Only users with the Preparer position can intake letters. " +
        "Speak to an admin if this is the wrong gate for your role.",
      field: {
        regulator_code: "Regulator",
        regulator_code_help: "e.g. SAMA, DIFC, FCA",
        title: "Letter title",
        title_help: "The header line on the letter.",
        received_at: "Received at",
        received_at_help: "When the letter arrived at the bank.",
        /* Round 16 A26 -- the date the letter prints, read into the field. */
        received_at_from_letter:
          "Read from the date the letter states. Change it if the letter arrived later.",
        source_text: "Letter body",
        source_text_help: "Paste the full text - copy-paste from PDF is fine.",
      },
      submit: "Record the letter",
      submitting: "Submitting...",
      // F -- "intaken" is not a word.
      /* ROUND 15 G5 -- "(id 5798cc1d-...)" named the row, not the letter. */
      success: "Letter recorded: {{title}}. The library is updated.",
      reset: "Intake another letter",
      newLink: "Intake a regulator letter",
      viewLetter: "Open the letter",
    },
    detail: {
      back: "Back to Framework",
      received: "Received",
      observations: "Observations",
      addObservation: "Add an observation",
      obsTitlePlaceholder: "What this requirement is",
      obsBodyPlaceholder: "The requirement in the letter's own words",
      obsAdd: "Add",
      obsAdded: "Observation added.",
      obsRemove: "Remove",
      obsRemoved: "Observation removed.",
      noObservations: "No observations on this letter.",
      // F -- the plan was named for the operator, every time, and a
      // letter that already had one was offered a bare "Create CAP".
      capTitleLabel: "Name this corrective action plan",
      createAnotherCap: "Raise another CAP",
      alreadyHasCaps_one: "This letter already has 1 plan against it.",
      alreadyHasCaps_other: "This letter already has {{count}} plans against it.",
      createCap: "Create CAP from this letter",
      creating: "Creating…",
      created: "Remediation CAP created",
      notFound: "Letter not found.",
      capsHeading: "Corrective action plans",
      noCaps: "No CAP has been raised for this letter yet.",
      capRollup: "{{approved}} of {{total}} CAPs approved",
      // Round 18 T10.
      attestationsHeading: "Attestations on the services its plans reach",
      /* Round 19 G41 -- said rather than silent. */
      attestationsNone: "No attestation is on a service its plans' findings reach.",
      attestationIn: "-- {{cycle}}, {{status}}",
    },
  },
  caps: {
    detail: {
      /* Round 12 D5, fifth round -- "No owner or date yet" on all five
         drafted actions, with no separator, and the prompt deliberately
         and correctly saying "Do NOT assign an owner. Accountability is
         the bank's to decide", which the screen never said. A blank
         that reads as an omission is a blank somebody goes looking for
         a bug behind. */
      // Round 16 H26 -- a full stop before the reason that follows.
      entryUnowned: "No owner or date yet.",
      entryUnownedWhy:
        "Who is answerable is the bank's to decide -- the Engine is told not to name one.",
      entrySetOwner: "Set an owner and a date",
      entrySetOwnerAfterPickUp: "Press Pick up and edit, then set an owner and a date.",
      entryOwnerPlaceholder: "Who is answerable",
      entrySaved: "Action item updated.",
      backToMyWork: "Back to My Work",
      backToQueue: "Back to {{position}} queue",
      letter: "Letter",
      /* Round 19 A45 -- "Letter: Raised internally" headed a plan raised
         from a control. */
      raisedFromControl: "Raised from a control: {{control}}",
      state: "State",
      currentPosition: "Current position",
      entries: "Action items",
      noEntries: "No action items yet.",
      newEntry: {
        title: "Add an action item",
        observation: "Against observation",
        description: "What needs doing",
        descriptionPlaceholder: "e.g. Establish the IT Steering Committee charter",
        ownerRole: "Owner role (optional)",
        dueDate: "Due date (optional)",
        add: "Add action item",
        adding: "Adding…",
        added: "Action item added",
        noObservations: "This CAP's letter has no observations to attach an item to.",
      },
      draftActions: {
        /* Round 12 F5 -- one press took a three-item plan to eight
           items, with nothing saying a draft already existed.
           Appending is right: a drafted action somebody has since
           edited must not be silently destroyed. The sentence is what
           was missing, and it has to come before the press. */
        appends_one:
          "This plan already has 1 action. Drafting ADDS to it rather than replacing it.",
        appends_other:
          "This plan already has {{count}} actions. Drafting ADDS to them rather than replacing them.",
        /* C4 -- the Engine drafts FROM the linked controls, so the
           button was live before there were any and refused by toast:
           the reader learned the prerequisite by being told no. */
        // Round 17 A31 -- while the controls are being read.
        readingControls: "Reading the controls this CAP remediates...",
        needsControl:
          "Link a control first -- the Engine drafts from the controls this CAP remediates.",
        button: "Draft actions with Engine",
        drafting: "Drafting actions",
        /* Round 16 A22 -- a plan raised with the Engine's drafting
           refused said "drafted" in a toast and "No action items yet."
           on the plan. The plan now says what happened, where it is. */
        createdRefused:
          "Remediation CAP created. The Engine could not draft actions this time; add them by hand.",
        success: "Drafted {{count}} remediation actions.",
        /* Round 14 A9 -- why none. */
        noneGrounded_one:
          "No actions were added: the Engine proposed one, and it did not cite a control linked to this CAP, so it was not kept.",
        noneGrounded_other:
          "No actions were added: the Engine proposed {{count}}, and none cited a control linked to this CAP, so none were kept.",
        noneProposed:
          "No actions were added: the Engine proposed none for the controls linked to this CAP.",
        /* ROUND 15 A16 -- an empty draft offered nothing next. */
        noneProposedNext: "Link a control that fits the letter, then draft again.",
        engineOff: "Marsad Engine not configured.",
        error: "Could not draft actions.",
      },
      entryStatus: {
        open: "Open",
        in_progress: "In progress",
        complete: "Complete",
      },
      actions: {
        heading: "Actions available to you",
        // F -- "the queue holder" is our word for a person. The reader
        // wants to know WHO, and the state says which seat.
        none:
          "Nothing for you to do here at the moment -- this record is " +
          "with somebody else. The state above says which seat holds it.",
        submit: "Submit for review",
        approve: "Approve + forward",
        send_back: "Send back",
        withdraw: "Withdraw",
        approveCommits:
          "Approving accepts this plan on the bank's behalf: its action items, their owners and their dates become the commitments the regulator is answered with, and the transition is sealed into the workflow chain.",
        // Round 16 G14 -- what a forwarding approval commits to.
        approveForwardCommits:
          "Approving records your review and sends the plan to the {{position}}. It becomes the bank's commitment to the regulator only when the {{position}} approves it; this transition is sealed into the workflow chain.",
        commentLabel: "Reason / comment",
        commentRequired: "A comment is required when sending back.",
        commentOptional: "(optional)",
      },
      state_labels: {
        draft: "Draft",
        in_review: "In review",
        approved: "Approved",
        sent_back: "Sent back",
      },
      controls: {
        heading: "Remediated controls",
        empty: "This CAP isn't linked to any framework controls yet.",
        framework: "Framework",
        control: "Control",
        add: "Link control",
        added: "Control linked.",
        remove: "Unlink",
        suggest: "Suggest with Engine",
        suggesting: "Asking the Engine...",
        // D3 -- an empty result rendered nothing at all, so the button
        // read as broken rather than as answered.
        suggestionsNone:
          "The Engine found no controls to suggest for this CAP. Link one by hand, or widen the framework filter.",
        /* Round 14 A9 -- nothing resembled the plan closely enough to put
           to the Engine, so it was not asked. */
        suggestionsNotAsked:
          "No control's wording is close enough to this CAP's to put to the Engine, so it was not asked. Link a control by hand, or widen the framework filter.",
        suggestionsHeading: "Engine suggestions -- review + link",
        link: "Link",
        engineOff: "Marsad Engine not configured",
        status: {
          compliant: "Compliant",
          partial: "Partial",
          non_compliant: "Gap",
          no_evidence: "No evidence",
          unassessed: "Not assessed",
        },
      },
      /* D1 -- keyed on the ACTION taken, not the state landed in. A
         preparer pressing Submit was told "Approved and sent on",
         because the state it moved to happened to be named for the
         reviewer's act rather than his. The person who pressed the
         button knows what they did; the confirmation has to agree
         with them, then say who has it now. */
      done: {
        submit: "Sent for review. It is now in the {{position}} queue.",
        approve: "Approved and sent on. It is now with the {{position}}.",
        // Round 18 N11 -- the person, where one holds the seat.
        approveNamed: "Approved and sent on. It is now with {{name}} ({{position}}).",
        /* Round 19 N14 -- four held the seat and the toast read "It is
           now with the Approver." */
        approveSeveral_one:
          "Approved and sent on. With the {{position}} seat ({{holders}}); {{name}} and {{count}} other can act on it.",
        approveSeveral_other:
          "Approved and sent on. With the {{position}} seat ({{holders}}); {{name}} and {{count}} others can act on it.",
        approveNobody:
          "Approved and sent on to the {{position}} seat, which nobody here holds yet.",
        approveFinal:
          "Approved and closed. Everyone who was waiting on it has been told.",
        send_back: "Sent back with your comment. You will be told when it comes back.",
        reopen: "Reopened. It is with the preparer, with your comment.",
        withdraw: "Withdrawn. It is back with you for editing.",
        // F -- the button said "Pick up and edit" and the toast said
        // "Withdrawn": two words for one act, in the same second.
        pickUp: "Picked up. It is back with you for editing.",
        resubmit: "Resubmitted for review. It is now in the {{position}} queue.",
        movedGeneric: "Done. It is now with the {{position}}.",
      },
      history: {
        heading: "History",
        loading: "Reading the history...",
        empty: "Nothing has happened to this CAP yet.",
        challengeOpen:
          "{{who}} sent this back and asked for the following before it goes further:",
        challengePast: "{{who}} sent this back earlier and asked for:",
        /* D1 -- the ACT, not the state it landed in. A preparer taking
           a CAP back off the queue read "started this CAP"; a
           reviewer's approval read "sent it for review". */
        did: {
          created: "raised this CAP",
          submitted: "sent it for review",
          resubmitted: "sent it back for review",
          approved_forward: "approved it and sent it to the {{position}}",
          approved_closed: "approved and closed it",
          sent_back: "sent it back",
          picked_up: "took it back to edit",
        },
      },
      verbs: {
        submit: "Submit for review",
        resubmit: "Resubmit for review",
        /* ROUND 15 G4 -- "Approve and send to the CRO" toasted "now with
           the approver". The verb names the seat the plan goes to, which
           is what the toast says. */
        approveForward: "Approve and send to the {{position}}",
        approveClose: "Approve and close",
        sendBack: "Send back to the preparer",
        // F -- from approved this is a reopen. The CAP is closed and
        // nobody is waiting on it, so there is nowhere to send it back
        // to.
        reopen: "Reopen and send to the preparer",
        pickUp: "Pick up and edit",
        withdraw: "Take it back off the queue",
      },
      findings: {
        heading: "Linked findings",
        searchFailed: "Couldn't search findings. Try again in a moment.",
        searchNoHits: "No open findings match. Try a host name or a CVE.",
        empty: "This CAP isn't linked to any telemetry findings yet.",
        searchPlaceholder: "Search findings to link...",
        added: "Finding linked.",
        remove: "Unlink",
      },
      entryControls: {
        empty: "No controls linked",
        add: "Link control",
        link: "Link",
        added: "Control linked.",
        cancel: "Cancel",
      },
    },
    queue: {
      heading: "{{position}} queue",
      /* Round 16 H24, tenth round -- what each seat does here. */
      standfirst: {
        preparer:
          "Plans and assessments you draft. Submitting one sends it to the reviewer; anything sent back to you returns here with the reason.",
        reviewer:
          "What preparers have submitted, for your second-line challenge. You forward an item to the approver, or send it back to the preparer with a reason; you do not edit it.",
        approver:
          "What reviewers have forwarded. Your approval is the final sign-off and is sealed on the record; sending an item back returns it to the preparer with a reason.",
      },
      empty:
        "No CAPs in this queue. New letters recorded by the preparer will " +
        "appear here after they're submitted.",
      // B2 -- the queue lists assessments too now; a preparer who
      // submitted one could not follow it anywhere before.
      assessmentsHeading_one: "{{count}} assessment waiting on this seat",
      assessmentsHeading_other: "{{count}} assessments waiting on this seat",
      column: {
        title: "Title",
        letter: "Letter",
        state: "State",
        entries: "Items",
        updated: "Updated",
        control: "Control",
        cycle: "Cycle",
      },
    },
  },
  // Round 20 H58 -- a person: seats and what they signed.
  people: {
    heading: "A person",
    loading: "Loading...",
    error: "Could not load this person.",
    notFound: "Nobody called {{name}} has an account here or a signature on record.",
    seats: "Signs in as {{username}}, and holds: {{seats}}.",
    noSeats: "Signs in as {{username}}, and holds no seat.",
    noAccount:
      "Known only from signatures: no account carries this name, so the name is what was written on each record.",
    signedHeading: "What they signed",
    signedNone: "Nothing signed under this name.",
    challenge: "Second-line challenge",
    openRecord: "Open the record",
    kind: {
      admission: "Admission",
      tolerance: "{{toleranceTerm}}",
      mvb_declaration: "{{mvbTermShort}} declaration",
      mvb_pack: "{{mvbTermShort}} pack",
      maturity: "Resilience maturity assessment",
      classification: "Service classification",
    },
  },
  queue: {
    // Round 20 N16 -- the chips count corrective plans and assessments,
    // not every item in My Work: "13 items wait on you" sat beside
    // "Reviewer 0".
    label: "Plan and assessment queues",
    preparer: "Preparer",
    reviewer: "Reviewer",
    approver: "Approver",
    /** Pluralised count tooltip - shown on hover. */
    countTooltip_one: "{{count}} plan or assessment awaiting action",
    countTooltip_other: "{{count}} plans and assessments awaiting action",
    /** Adornment when this position is not the user's. */
    notMine: "not yours",
  },
  // ── G1 screens ──
  //
  // Text routed out of .tsx files so it can translate. One key per
  // string, namespaced by the screen it belongs to.
  /* ROUND 13 E4 -- the refusals a person reaches, in the catalogue.
     "One of positions ['preparer'] required; user has ['reviewer']"
     was shown to a second-line risk officer: Python repr, composed in
     the backend where no catalogue could reach it, scoring zero on
     translation readiness. The backend still writes a sentence -- an
     API consumer and a log line read it -- and these are what a SCREEN
     says instead. */
  // ROUND 15 I7 -- the supervisory checks' sentences, which were
  // composed in English on the server and reachable by no catalogue.
  // Generated from the backend templates (`say(...)` in
  // backend/twin/supervisory_checks.py and regime_checks.py), so the
  // two cannot drift; a test holds them to each other.
  /* Round 15 A17 -- what a document contains that a reader should be
     told, wherever it is uploaded, mapped or read. */
  documents: {
    reading: {
      document: {
        directed_one:
          "This document contains 1 passage addressed to an automated reader.",
        directed_other:
          "This document contains {{count}} passages addressed to an automated reader.",
        hidden_one: "1 passage in it was not visible in the original.",
        hidden_other: "{{count}} passages in it were not visible in the original.",

        /* Round 17 A32 -- said alone, the sentence names its subject;
           "in it" had no antecedent when nothing came before it. */
        hiddenAlone_one:
          "This document contains 1 passage that was not visible in the original.",
        hiddenAlone_other:
          "This document contains {{count}} passages that were not visible in the original.",
      },
      documents: {
        directed_one:
          "The documents this test reads contain 1 passage addressed to an automated reader.",
        directed_other:
          "The documents this test reads contain {{count}} passages addressed to an automated reader.",
        hidden_one: "1 passage in them was not visible in the original.",
        hidden_other: "{{count}} passages in them were not visible in the original.",

        hiddenAlone_one:
          "The documents this test reads contain 1 passage that was not visible in the original.",
        hiddenAlone_other:
          "The documents this test reads contain {{count}} passages that were not visible in the original.",
      },
      notVisible: "not visible in the original",
      directedHeading: "Addressed to an automated reader",
    },
  },
  supervisory: {
    join: {
      dash: " -- ",
      slash: " / ",
      colon: ": ",
    },
    topClass: {
      critical: "critical",
      material: "material",
    },
    say: {
      allInTopClass:
        "all {{total}} classified {{topClass}} -- no differentiation (the '46 of 46 critical contracts' failure)",
      basisCurrent:
        "{{current}} of {{total}} scenarios carry a basis with no lapsed review",
      channelsMapped:
        "{{mapped}} of {{total}} customer-class {{serviceTermShort}}(es) on the register mapped ({{admitted}} of the {{total}} admitted)",
      cifNone: "no Critical Internal Function admitted yet",
      cifToleranceNone: "no admitted Critical Internal Function to judge",
      groupedNone: "no admitted service to group yet",
      incidentProfileRecorded:
        "the regulator's incident-reporting rule is recorded, with its citation",
      incidentProfileMissing:
        "the regulator's incident-reporting rule is not recorded in this deployment",
      incidentNoDeadline:
        "no incident report in the last year carries a deadline the regulator states",
      incidentOnTime: "{{met}} of {{total}} report(s) with a stated deadline met it",
      floorNone: "no floor list is recorded for this regulator",
      floorAnswered:
        "{{answered}} of {{total}} floor item(s) answered by an admitted service or an approved opt-out",
      // P421 -- the change register's two checks.
      changeNone: "no significant change has been recorded",
      changeUnassessed:
        "{{late}} of {{total}} significant change(s) open for more than {{days}} days without an assessment",
      changeNoneAssessed:
        "no significant change has been assessed, so nothing is due a test",
      changeRetest:
        "{{overdue}} of {{total}} service(s) reached by a significant change not tested within {{days}} days of it",
      groupedCount: "{{grouped}} of {{total}} admitted service(s) sit in a group",
      cifToleranceDeclared:
        "{{declared}} of {{total}} admitted Critical Internal Function(s) declare a time tolerance and a further measure",
      cifAdmitted:
        "{{admitted}} Critical Internal Function(s) admitted against {{ibs}} admitted {{serviceTermShort}}(es)",
      cifAboveCeiling:
        "above the expected ceiling of {{ceiling}} -- raise the thresholds rather than accept the register",
      cifAboveIbs:
        "more than the {{serviceTermShort}} register -- the second door should be the narrower one",
      compositeRows:
        "{{count}} composite membership row(s); a composite is how a scenario carries concurrent events on different targets",
      connectorsFailing: "failing: {{kinds}}",
      connectorsNeverSucceeded: "never succeeded: {{kinds}}",
      connectorsUnconfigured: "credentials missing: {{kinds}}",
      connectorsVerified: "{{verified}} of {{enabled}} enabled connector(s) verified",
      crisisPlansComplete:
        "{{complete}} of {{total}} approved crisis plans state both invocation criteria and a communication protocol",
      daysPast: "{{days}} days PAST {{date}}",
      daysRemaining: "{{days}} days remaining until {{date}}",
      declassified:
        "rolled-up footprint criticality is High but the service is classified {{classification}} -- the examiner reads this as inconsistent classification and technical linkage",
      // Round 20 V8.
      lowBehindMvb:
        "graded Low, yet {{services}} rest on it -- its grade is below the {{mvbTerm}} it carries",
      discriminating: "{{count}} of {{total}} {{topClass}} -- discriminating",
      dispositionCount: "{{count}} {{disposition}}",
      exampleDoesNot: "e.g. {{example}} does not",
      outsideCustomerClass:
        "{{state}} State service(s) and {{functions}} Critical Internal Function(s) carry no channel by class",
      failoverTested:
        "{{tested}} of {{total}} critical technology assets have a failover drill within {{months}} months ({{stale}} stale)",
      familiesDeclared:
        "{{declared}} of {{total}} critical operations declare all {{required}} required families",
      layersUnmapped:
        "{{count}} of {{total}} {{serviceTermPlural}} on the register ({{admitted}} admitted) leave a dependency layer unmapped -- e.g. {{example}}: {{layers}}",
      lessonsOverdue:
        "{{overdue}} of {{accepted}} accepted lessons have sat more than {{days}} days without being incorporated ({{total}} lessons on the register)",
      libraryEmpty: "The scenario library is empty.",
      longestStated:
        "the longest stated disruption in the library is {{longest}}h against a {{bar}}h bar",
      mvbMedian: "{{mvbTermShort}} median {{hours}}h",
      mvbNoTolerance:
        "{{count}} {{mvbTermShort}} {{serviceTermShort}}(es) with NO declared tolerance",
      mvbSupporting: "{{count}} {{mvbTermShort}}-supporting service(s)",
      noCompletionDate: "This regime sets no completion date in the texts held here.",
      noConnector: "NO connector is enabled -- the twin has no live feed",
      noCrisisPlan: "No approved crisis management plan exists.",
      noCriticalOperationOrClass:
        "No admitted critical operation, or no required scenario class, so there is nothing to count.",
      noCriticalOperationOrFamily:
        "No admitted critical operation, or no required family.",
      noCriticalService:
        "no linked service is classified ibs_supporting -- the business-critical layer has no critical technical layer beneath it",
      noDeliveryChannel: "{{count}} with NO delivery channel",
      // Round 18 B19 -- no pass over an unadmitted population.
      nothingAdmittedNotAssessed:
        "not assessed: nothing on the register is admitted ({{candidates}} candidate(s) under assessment)",
      noFootprint:
        "classified ibs_supporting but linked to no configuration items -- its criticality has no technical anchor",
      noLessons:
        "No lesson has been recorded from any exercise or incident, so there is nothing to incorporate.",
      noMvbTolerances: "no {{mvbTermShort}} tolerances declared",
      noObjectionMissing:
        "material_outsourcing is set but neither sama_no_objection_ref nor sama_no_objection_date is recorded (SAMA Outsourcing section J rule 23)",
      noPassport:
        "vendor {{vendor}} holds no unexpired resilience passport (third-party assurance is mandatory under the SAMA 2026 supervisory arc; ingest the vendor's signed passport or record why not)",
      noPosture: "{{count}} with NO posture recorded",
      noRecentTest: "no scenario test has been executed in {{months}} months",
      noStatedDuration:
        "No scenario in the library states a duration at all, so severity cannot be judged from the library.",
      nonMvbMedian: "non-{{mvbTermShort}} median {{hours}}h",
      noneClassified: "0 of {{total}} carry any classification -- no judgment",
      noneInTopClass:
        "0 of {{total}} classified {{topClass}} -- the classification does not discriminate (the '0 of 313 core services' failure)",
      noneValued: "0 of {{total}} carry a value -- no judgment",
      nothingToFailOver:
        "No configuration item is graded critical beneath an admitted critical operation, so there is nothing to fail over.",
      nothingToTest: "No admitted critical operation, so there is nothing to test.",
      oneInMinority:
        "{{count}} of {{total}} {{topClass}} -- one row in the minority class is not enough to judge",
      pairsTested:
        "{{tested}} of {{total}} operation-class pairs tested within {{months}} months; {{complete}} of {{operations}} critical operations complete across every required class",
      planTested:
        "{{tested}} of {{total}} critical operations rest on an approved continuity plan tested within {{months}} months",
      postureAllSufficient: "all postured and sufficient",
      postureInsufficient: "{{count}} insufficient for their tolerance",
      postureUnproven: "{{count}} unproven/stale",
      registerEmpty:
        "register empty -- nothing to check (population is checked elsewhere)",
      standardBreached:
        "{{count}} of {{assessed}} breach this standard -- e.g. {{example}}: {{exampleDetail}}",
      standardBreachedLead:
        "{{count}} of {{assessed}} breach this standard -- e.g. {{example}}",
      archStandsBehind: "stands behind {{n}} {{mvbTermShort}}-supporting services",
      archHosts: "hosts {{n}} {{mvbTermShort}}-supporting services",
      archSingleSupplier: "rests on a single recorded supplier",
      archDependsOn: "depends on {{upstream}} ({{upClass}})",
      archPosture: "posture is {{posture}}",
      archPostureNone: "posture is not recorded",
      archNoService: "linked to no service",
      archSubcontracts: "subcontracts on {{n}} arrangements",
      // Round 20 V6.
      archNoExitPlan: "no exit plan; behind {{services}}",
      archContractUnsupplied: "names {{services}}, which {{vendor}} is not linked to",
      archOutOfSupport: "{{manufacturer}} {{model}}, out of support since {{date}}",
      archSubcontractorsNarrative:
        "names subcontractors in narrative but has no modelled links",
      archSlaLonger:
        "supplier commits to {{sla}}h recovery for {{service}}, which is rated {{rto}}h",
      archNoTransferBasis:
        "{{classification}} data held in {{country}} as {{role}} with no transfer basis recorded",
      archNoDataAsset: "declares data {{classification}} but links no data asset",
      tierCount: "{{count}} {{tierWord}}",
      tighterThanNonMvb: "tighter than non-{{mvbTermShort}}",
      uniformGrade:
        "all {{valued}} valued rows carry one identical value -- a uniform grade is a non-grade",
      valuedAcross: "{{valued}} of {{total}} valued across {{distinct}} grades",
      vendorParticipant:
        "{{count}} of {{total}} tests in the last {{months}} months recorded a vendor participant",
      weakEvidence:
        "classified ibs_supporting but the rolled-up footprint criticality is {{grade}} -- classification and technical evidence disagree",
    },
    label: {
      bcp_tested: "Every critical operation has a tested continuity plan",
      ci_asset_criticality: "Technology-asset criticality carries judgment",
      concurrent_scenario_tested: "The library carries a concurrent-event scenario",
      connectors_verified: "Enabled connectors have delivered data",
      contracts_criticality: "Outsourcing-contract criticality discriminates",
      crisis_plan_present: "An approved crisis plan, with criteria and a protocol",
      incident_profile_present: "The regulator's incident-reporting rule is recorded",
      // Round 19 I22 -- the P421 change checks, catalogued as they ship.
      significant_change_unassessed: "Every significant change is assessed",
      post_change_retest_overdue:
        "A service a significant change reached is tested again",
      incident_reports_on_time: "Incident reports met the regulator's deadline",
      floor_items_answered: "Every item on the regulator's floor list is answered",
      critical_op_coverage_by_class:
        "Critical operations tested across every required class",
      critical_service_no_footprint: "Critical service without a technical footprint",
      critical_service_weak_evidence:
        "Critical classification contradicted by its footprint",
      criticality_tier_discriminates:
        "{{serviceTermShort}} criticality tier discriminates",
      customer_facing_ibs_has_channel:
        "Customer-class {{serviceTermShortPlural}} reach a delivery channel",
      disposition_discriminates: "Derived service disposition discriminates",
      dr_failover_tested: "Failover tested for every critical technology asset",
      highcrit_service_declassified:
        "High-criticality footprint under a non-critical classification",
      // Round 20 V8.
      ci_low_behind_mvb: "Configuration item graded below the services that rest on it",
      ibs_dependency_layers: "Business services with an unmapped dependency layer",
      ibs_without_critical_service:
        "{{serviceTerm, capitalize}} resting on no critical service",
      lessons_incorporated: "Accepted lessons incorporated into the plans",
      long_duration_scenario_tested: "At least one scenario states a long disruption",
      material_contract_no_objection:
        "Material outsourcing without SAMA no-objection evidence",
      material_contract_no_passport:
        "Material outsourcing without a current vendor resilience passport",
      mvb_continuity_posture:
        "{{mvbTermShort}} continuity posture recorded and sufficient",
      mvb_tolerance_discriminates: "{{mvbTermShort}} tolerances discriminate",
      scenario_basis_current:
        "Every scenario states a current severe-but-plausible basis",
      service_cyber_criticality: "Service cyber-criticality carries judgment",
      services_classification: "Service classification discriminates",
      testing_deadline_countdown: "Testing complete by the regulator's date",
      third_party_participated: "A third party took part in a recent test",
      tolerance_families_declared: "Every required tolerance family declared",
      vendor_materiality: "Third-party materiality differentiates",
      /* Round 16 I9 -- the last labels that fell back to the server's
         English: the new register's three and the architecture
         standards (keyed "architecture:<key>" on the wire; ":" is
         i18next's namespace separator, so "__" here). */
      cif_register_size:
        "The Critical Internal Function register stays narrower than the {{serviceTermShort}} register",
      cif_tolerance_declared:
        "Every admitted Critical Internal Function declares more than a time tolerance",
      ibs_grouped: "Every admitted service is governed in a group",
      architecture__mvb_supplier_concentration:
        "No single supplier concentrated across the {{mvbTermShort}}",
      architecture__mvb_site_concentration:
        "The {{mvbTermShort}} is not concentrated in one site",
      architecture__mvb_single_supplier:
        "Every {{mvbTermShort}} service has an alternative supplier",
      architecture__no_tier_inversion:
        "No critical service depends on a non-critical one",
      architecture__critical_service_real_posture:
        "Every {{serviceTermShort}}-supporting service has a real continuity posture",
      architecture__no_orphan_configuration_items:
        "Every configuration item belongs to a service",
      architecture__no_fourth_party_concentration:
        "No fourth party is concentrated across arrangements",
      architecture__subcontractors_are_modelled:
        "Named subcontractors are modelled, not just described",
      architecture__supplier_sla_meets_service:
        "Supplier recovery commitments meet the services they underpin",
      architecture__data_residency_declared:
        "Sensitive data leaving the Kingdom states why",
      architecture__data_modelled_for_classified_services:
        "Services declaring sensitive data model it",
      // Round 20 V6.
      architecture__material_supplier_exit_plan:
        "A material supplier behind a critical service has an exit plan",
      architecture__contract_services_supplied:
        "A contract names only services its supplier supports",
      architecture__mvb_technology_supported:
        "No {{mvbTermShort}}-supporting technology is out of support",
    },
  },
  /* Round 16 I10 -- the settings screen's descriptions, written for a
     bank's administrator and catalogued. Keyed by the variable or the
     setting; a setting's dots are "__" (i18next reads a dot as
     nesting). A ratchet holds these to the server's words. */
  settingsCatalogue: {
    envGroup: {
      deployment: "Deployment",
      marsadEngine: "Marsad Engine",
      telemetry: "Telemetry",
      connectors: "Connectors",
      dossier: "Dossier",
      branding: "Branding",
      notifications: "Notifications",
      governance: "Governance",
      benchmark: "Benchmark",
      recommendations: "Recommendations",
      security: "Security",
      performance: "Performance",
      licensing: "Licensing",
    },
    env: {
      MARSAD_API_DOCS_ENABLED:
        "Serve the API explorer (/docs, /redoc) and the schema (/openapi.json) from this process. Unset or 1 = yes, the development default; 0 = the routes do not exist, which is how a hosted deployment runs (its edge answers 404 for them as well). Read when the process starts.",
      MARSAD_ANALYSER_QUEUED_STALE_SECONDS:
        "A queued analyser run that has not started within this many seconds is marked failed with a reason (default 120; 0 disables). A restart can orphan a queued run, and an orphan otherwise holds the analyser for every seat on the cycle until somebody clears it.",
      MARSAD_ANALYSER_RUNNING_STALE_SECONDS:
        "A running analyser run older than this many seconds is marked failed with a reason (default 900; 0 disables).",
      MARSAD_AI_PROVIDER:
        "Which LLM provider serves the analyser surfaces (none / openai_compatible / anthropic / bedrock).",
      MARSAD_AI_MODEL: "Provider-specific model identifier.",
      MARSAD_AI_BASE_URL:
        "OpenAI-compatible base URL (e.g. http://host.docker.internal:11434/v1 for Ollama).",
      MARSAD_AI_API_KEY: "Provider API key. Value masked; presence shown.",
      MARSAD_AI_BEDROCK_REGION: "AWS region when provider=bedrock.",
      MARSAD_AI_TIMEOUT_S: "Per-call LLM timeout in seconds (default 60).",
      MARSAD_ENGINE_GROUNDING_FLOOR:
        "Grounding coverage floor 0..1 (default 0.5); below it an Engine result is flagged low-confidence. WHAT IT MEASURES: the fraction of the model's citations that were on the shortlist it was given -- an identifier check, not a check that the statement is correct. APPLIES TO: cross-framework equivalents, regulatory-change assessment and CAP control suggestion. NOT the mock-exam narration, which returns one note per question, so the ratio there can only be 0 or 1 and a floor between them cannot discriminate.",
      MARSAD_ENGINE_HARD_REFUSAL:
        "When set, a retrieve-then-judge surface below the grounding floor returns NOTHING (refuse-and-stop) instead of flagged weak content. Default off. Applies to the same three surfaces as the floor above, and to no others.",
      MARSAD_AUTO_DISPATCH_DEBOUNCE_SECONDS:
        "Window during which repeat auto-dispatches on the same control collapse to one run (default 30; <=0 disables).",
      MARSAD_TELEMETRY_STALE_SECONDS:
        "How old a service-health reading may be before it reads 'unknown' rather than its last value (default 900). A stale reading stops driving the resilience chain, so raising this keeps a silent service looking healthy for longer.",
      MARSAD_TELEMETRY_RETENTION_DAYS:
        "TTL on the service_health_samples history the sparklines and the telemetry-driven calibration read (default 30; <=0 disables retention).",
      MARSAD_CREDENTIAL_ENCRYPTION_KEY:
        "Fernet key that encrypts stored connector credentials. UNSET = the credential store is inert: writes are rejected and every connector falls back to its env vars. Env-only, never in the database. Value masked; presence shown.",
      MARSAD_CONNECTOR_SCHEDULER_ENABLED:
        "1 = the scheduler fires due connectors every 60s; 0 = manual-only (test posture).",
      MARSAD_CONNECTOR_PAYLOAD_RETENTION_DAYS:
        "TTL on the connector_run_payloads table (default 90; <=0 disables retention).",
      MARSAD_SHAREPOINT_CLIENT_SECRET:
        "Client secret for the SharePoint OAuth app registration consumed by the sharepoint_evidence connector. Value masked; presence shown.",
      MARSAD_CONFLUENCE_API_TOKEN:
        "Atlassian API token consumed by the confluence_evidence connector. Pairs with the email in the connector config for HTTP Basic auth. Value masked; presence shown.",
      MARSAD_OAUTH_CLIENT_SECRET:
        "OAuth 2.0 client secret consumed by the oauth_evidence connector, which reaches many evidence sources through configuration alone. Pairs with client_id + token_url in the connector config. A credential stored on the connector takes precedence over this variable. Value masked; presence shown.",
      MARSAD_ARCHITECTURE_TOKEN:
        "Optional bearer token for the architecture_edges connector. Sent only when set; the modelling SoR endpoint in connector.config.url may be unauthenticated. Value masked; presence shown.",
      ARCHER_BASE_URL:
        "Archer site URL (the host alone) consumed by the archer_findings connector. Pairs with ARCHER_INSTANCE, ARCHER_USERNAME and ARCHER_PASSWORD.",
      ARCHER_PASSWORD:
        "Password of the Archer API user consumed by the archer_findings connector. A refused login is never retried. Value masked; presence shown.",
      QUALYS_USERNAME:
        "Qualys VMDR API username (HTTP Basic) consumed by the qualys_vmdr connector. Pairs with QUALYS_PASSWORD + QUALYS_BASE_URL.",
      QUALYS_PASSWORD:
        "Qualys VMDR API password (HTTP Basic) consumed by the qualys_vmdr connector. Value masked; presence shown.",
      OPENVAS_PASSWORD:
        "Gvmd password consumed by the openvas_results connector over GMP. Pairs with OPENVAS_USERNAME + OPENVAS_HOST. GMP authenticates with a command on the socket, not a bearer token. Value masked; presence shown.",
      FALCON_CLIENT_ID:
        "CrowdStrike Falcon OAuth client id consumed by the crowdstrike_falcon connector. Pairs with FALCON_CLIENT_SECRET + FALCON_BASE_URL.",
      FALCON_CLIENT_SECRET:
        "CrowdStrike Falcon OAuth client secret consumed by the crowdstrike_falcon connector. Value masked; presence shown.",
      DEFENDER_CLIENT_ID:
        "Microsoft Defender (Azure AD) OAuth client id consumed by the defender_alerts connector. Pairs with DEFENDER_TENANT_ID + DEFENDER_CLIENT_SECRET.",
      DEFENDER_CLIENT_SECRET:
        "Microsoft Defender (Azure AD) OAuth client secret consumed by the defender_alerts connector. Value masked; presence shown.",
      SPLUNK_TOKEN:
        "Splunk Enterprise Security bearer token consumed by the splunk_es connector. Pairs with SPLUNK_BASE_URL. Value masked; presence shown.",
      QRADAR_SEC_TOKEN:
        "IBM QRadar SEC-header token consumed by the qradar_offenses connector. Pairs with QRADAR_BASE_URL. Value masked; presence shown.",
      MARSAD_DOSSIER_SIGNING_PRIVATE_KEY:
        "Ed25519 private key (base64 of the 32-byte seed) for third-party-verifiable dossier manifest signing. When set, the public key travels in the manifest. Value masked; presence shown.",
      MARSAD_DOSSIER_SIGNING_KEY:
        "HMAC-SHA256 key for the audit dossier zip manifest (fallback when no Ed25519 key is set). Value masked; presence shown.",
      MARSAD_DOSSIER_SIGNING_KEY_ID:
        "Identifier embedded in the signed manifest so the verifier knows which key to check against.",
      MARSAD_BANK_TIMEZONE:
        "The bank's IANA time zone (for example Asia/Riyadh), so a board page gives its local time beside UTC.",
      MARSAD_DEMONSTRATION_ESTATE:
        "Set to 1 on a deployment running a demonstration estate: the board page then says, in one line, that the bank and its figures are synthetic.",
      MARSAD_DOSSIER_SIGNED_BY:
        "Who holds the dossier signing key, as the deployment states it (the bank, or the hosting on the bank's behalf). Printed on the cover and signed into the manifest; the key id is only a label.",
      MARSAD_CHAIN_ANCHOR_ENABLED:
        "Sign and store every chain's heads (row count + latest hash per entity) on a schedule, so a chain later rewritten consistently or cut short shows as a mismatch. On unless set to 0.",
      MARSAD_CHAIN_ANCHOR_INTERVAL_SECONDS:
        "How often a chain anchor is written. Default 86400 (daily); minimum 60.",
      MARSAD_PROOF_REQUIRE_ED25519:
        "When truthy, signing RAISES instead of falling back to the HMAC sentinel -- prod sets this so no sentinel-signed artefact can ship silently.",
      MARSAD_PROOF_RETIRED_PUBLIC_KEYS:
        "JSON list of {key_id, public_key} for rotated-out Ed25519 keys, published alongside the active key at /v1/proof/public-keys so old artefacts still cross-check.",
      MARSAD_BANK_NAME: "Bank display name surfaced in headers / dossier cover.",
      MARSAD_EMAIL_DRAIN_ENABLED:
        "1 = the email worker drains pending notification rows + sends digests on the scheduler tick; 0 = in-app only (default).",
      NOTIF_SMTP_HOST:
        "SMTP relay host (dev: mailpit). In production, the bank's own relay inside the Kingdom, so notification email stays in-country.",
      NOTIF_SMTP_PORT: "SMTP relay port (dev: 1025 for mailpit; prod typically 587).",
      NOTIF_SMTP_FROM:
        "From address on outbound notification email (default noreply@marsad.local).",
      NOTIF_SMTP_FROM_NAME: "From display name (default MARSAD).",
      NOTIF_SMTP_USE_TLS:
        "1 = STARTTLS the SMTP connection before sending (prod relay); 0 = plain (mailpit dev). Default 0.",
      NOTIF_SMTP_USER:
        "SMTP auth username (prod relay). Empty = no auth (mailpit dev).",
      NOTIF_SMTP_PASSWORD:
        "SMTP auth password (prod relay). Value masked; presence shown.",
      MARSAD_CLASSIFICATION_POSITION_GATE:
        "1 (default) = service-classification signoffs require the cro / service_owner realm role and admin does NOT bypass; 0 = admin-only.",
      MARSAD_IMPACT_TOLERANCE_POSITION_GATE:
        "1 (default) = impact-tolerance declaration signoffs require the cro / business_owner realm role; 0 = admin-only.",
      MARSAD_MATURITY_SIGNOFF_POSITION_GATE:
        "1 (default) = resilience maturity assessment signoffs require the cro / head_of_opsres realm role; 0 = admin-only.",
      MARSAD_IBS_ADMISSION_SIGNOFF_POSITION_GATE:
        "1 (default) = {{serviceTermShort}} admission dual sign-off requires the cro / business_owner realm role; 0 = admin-only.",
      MARSAD_MVB_PACK_SIGNOFF_POSITION_GATE:
        "1 (default) = {{mvbTermShort}} evidence-pack dual sign-off requires the cro / ae realm role; 0 = admin-only.",
      MARSAD_MVB_DECLARATION_SIGNOFF_POSITION_GATE:
        "1 (default) = {{mvbTermShort}} formal-declaration dual sign-off requires the cro / ae realm role; 0 = admin-only.",
      MARSAD_SMS_PROVIDER_URL:
        "Generic SMS provider endpoint: POST {to, message}. An in-Kingdom gateway keeps the sovereignty posture; unset = sms rows stay queued.",
      MARSAD_SMS_PROVIDER_TOKEN: "Bearer token for the SMS provider. Masked.",
      MARSAD_SMS_DRAIN_ENABLED:
        "1 = the scheduler drains pending sms notifications through the provider seam. 0 (default) = off.",
      MARSAD_NOTIF_CHASE_ENABLED:
        "1 = unacknowledged ack-required notifications get ONE reminder across every channel after the chase window. 0 (default).",
      MARSAD_NOTIF_CHASE_MINUTES: "Chase window in minutes (default 30).",
      MARSAD_WEBHOOK_DRAIN_ENABLED:
        "1 = the scheduler POSTs pending webhook deliveries to registered endpoints (Teams / Slack / generic). 0 (default) = off.",
      MARSAD_TRIGGER_RULES_ENABLED:
        "1 = the scheduler evaluates user-defined trigger rules (metric + comparator + threshold; edge-triggered notify). 0 (default).",
      MARSAD_BENCHMARK_AGGREGATOR_URL:
        "Base URL of the sector-benchmark aggregator. Unset (default) = air-gapped; the benchmark shows the labelled synthetic baseline only.",
      MARSAD_BENCHMARK_CONTRIBUTOR_ID:
        "This deployment's contributor id at the aggregator (onboarded out of band). Required for submit, not for fetch.",
      MARSAD_INCIDENT_REPORT_NOTIFY_ENABLED:
        "1 = offer the signed regulator incident-report draft to approvers when a qualifying critical finding lands. 0 (default).",
      MARSAD_TOLERANCE_MONITOR_ENABLED:
        "1 = the scheduler continuously evaluates live conditions against declared {{toleranceTermPlural}}; a breach auto-raises a finding + notifies admins. 0 (default) = war-room on-demand only.",
      MARSAD_CONTRACT_REVIEW_SCAN_ENABLED:
        "1 = the scheduler notifies admins of expired / review-overdue outsourcing contracts; 0 (default) = off.",
      MARSAD_RECOMMENDATIONS_REVENUE_ENABLED:
        "Entitlement for the SAR revenue-at-risk dimension on recommendations. 1 (default) = on (figure + priority weighting); 0/false/no/off = off (recommendations rank without revenue).",
      MARSAD_RATE_LIMIT_ENABLED:
        "Per-IP request rate limiting. Unset (default) = ON; 0 = off. Off is for a drive or a load test, not for a deployment.",
      MARSAD_RATE_LIMIT_MAX:
        "Requests allowed per window per source IP. Unset = the code's default of 300. Raise it where a bank's staff share one egress address, which is the ordinary case behind a corporate NAT.",
      MARSAD_RATE_LIMIT_WINDOW_SECONDS:
        "The window the limit above is counted over. Unset = 60.",
      MARSAD_RATE_LIMIT_REDIS_URL:
        "Share the limiter's counter across backends. Unset = an in-process window, which counts per container rather than per deployment. Fails OPEN if Redis is unreachable: the limiter never takes the API down.",
      MARSAD_DEPLOYMENT_REGION:
        "Where this deployment runs, in the hosting provider's own words for its region. Unset = not stated, and the surfaces say so rather than assuming. Residency is a claim a bank makes to its regulator, so the product repeats what it was told and never infers it.",
      MARSAD_DEPLOYMENT_HOSTING:
        "How this deployment is hosted, in one line (e.g. 'Single VM, hardened compose, single tenant by construction'). Unset = not stated.",
      MARSAD_DEPLOYMENT_BACKUP:
        "How this deployment's data is backed up, in one line (e.g. 'Nightly pg_dump to encrypted object storage in-region, 30-day retention'). Unset = not stated, and the surfaces say so.",
      MARSAD_DEPLOYMENT_RESTORE_TESTED:
        "When the restore was last EXERCISED, in the operator's own words (e.g. '2026-08-14, full restore to a scratch host, 42 minutes' or 'never'). Unset = not stated. A backup nobody has restored from is a backup nobody has, so 'never' is a real answer and a better one than silence.",
      MARSAD_DEPLOYMENT_DOC_URL:
        "Where the deployment's own document lives, if it is published (e.g. an intranet URL). Unset = not stated, and the surfaces name the variables without linking anything. The repository copy is not a link a supervisor can follow.",
      MARSAD_DEMO_SEED:
        "Gate for demonstration data embedded in migrations. Unset (default) = a bare `alembic upgrade head` is reference-only; 1/true/yes/on = include the demonstration seed (e.g. the capability map's demonstration incident).",
      MARSAD_FRAGILITY_CACHE_TTL_SECONDS:
        "In-process TTL (seconds) for the fragility radar's full ranking (the scoring is super-linear + limit-independent). Unset (default) = 60s; <=0 disables the cache (always re-score). The radar is live-but-current, so a short TTL trades sub-minute staleness for a large read-cost win.",
      MARSAD_SHARED_READ_TTL_SECONDS:
        "Seconds the board, posture, readiness and setup ladder keep one shared answer for every seat; identical requests in flight share one computation, and a write in the process clears it. Unset (default) = 10s; <=0 disables.",
      MARSAD_FRAGILITY_OBSERVE_ENABLED:
        "Append one fragility observation per scored node on the scheduler cadence -- the series a forecast needs. The hash chains are change-triggered by construction (a row means the truth changed), so they record events, not readings, and can never supply one. Opt-in; default off.",
      MARSAD_FRAGILITY_OBSERVE_INTERVAL_SECONDS:
        "How often a fragility reading is taken. Default 86400 (daily) -- the scheduler scans every ~60s, so without this gate it would record on every scan (~18M rows a year instead of ~21k) and re-run the whole-estate scoring BFS every couple of minutes.",
      MARSAD_FRAGILITY_RETENTION_DAYS:
        "How long fragility observations are kept before the retention purge hard-deletes them. Default 730 -- deliberately long, because the point is a series that outlives any single incident.",
      MARSAD_LICENSE_GATE_ENABLED:
        "Master switch for the deployment license gate. 0 (default) = off, the estate is fully open. 1 = enforce (missing/invalid/revoked refused; expired/grace block writes; module claims gate modules).",
      MARSAD_LICENSE_SERVER_URL:
        "Opt-in management-plane base URL for online licence sync. Unset (default) = air-gapped, licences are uploaded. Set = a background poll refreshes a renewed licence and applies the revocation list.",
      MARSAD_LICENSE_SYNC_INTERVAL_SECONDS:
        "How often the license-sync poll runs when a management-plane URL is set. Unset (default) = 3600s (hourly); clamped to >=60s.",
      MARSAD_LICENSE_DEPLOYMENT_ID:
        "Optional deployment identity for online sync. Lets a FRESH deployment (no license yet) bootstrap its first license from the management plane -- the refresh polls /license/{this-id}. Ignored once a license carrying its own deployment_id/customer is installed.",
      MARSAD_DOC_MAP_AUTOCONFIRM_THRESHOLD:
        "Opt-in. Confidence in (0, 1] at or above which an Engine document-to-deliverable mapping is AUTO-CONFIRMED (counts as PRESENT in the gap report without operator review; source 'engine_autoconfirmed'). Unset = every suggestion is staged for a person to confirm.",
    },
    app: {
      // Round 20 J52 -- the score reseal can be paused under a hold.
      score__reseal_paused:
        "Pause the hourly Resilience Score reseal. While paused the score is neither composed nor sealed, and each pass says so. Set while a restored estate must keep the score it was taken with; leave it off otherwise.",
      // P421 -- the change windows, catalogued (round 19: found missing).
      telemetry__outage_notify_minutes:
        "Minutes a service some business service rests on may read down before the head of operational resilience and administrators are notified.",
      change__review_days:
        "Days after a significant change by which every admitted service it reaches is reviewed again: the review date is pulled forward to this, never pushed back. A bank setting -- no regulator text held here states a number.",
      change__assess_within_days:
        "Days a significant change may stay unassessed before the supervisory check flags it. A bank setting.",
      bank__lifecycle_stage:
        "Onboarding lifecycle: 'fresh' (Getting Started visible), 'active' (hidden -- set up or all steps done), 'seasoned' (admin-pinned hidden).",
      fragility__exposure__mvb:
        "Points for an {{mvbTermShort}}-critical {{serviceTermShort}}.",
      fragility__exposure__redundancy_single:
        "Penalty for a single supporting service (a hard dependency).",
      fragility__exposure__redundancy_two:
        "Penalty for exactly two supporting services (brittle redundancy).",
      fragility__exposure__tolerance_tight:
        "Penalty for a <=4h binding RTO (still tight recovery).",
      fragility__exposure__tolerance_zero:
        "Penalty for a zero-RTO binding service (tolerates no downtime).",
      fragility__weight__blast: "Points per downstream node reached (blast reach).",
      fragility__weight__mvb_downstream:
        "Points per downstream {{mvbTermShort}} {{serviceTermShort}} (regulator-weighted concentration).",
      fragility__weight__open_findings:
        "Points per open finding on the node itself (current stress).",
      ibs__threshold__osfi__co_composite_min:
        "Critical operation (E-21) composite trigger: minimum C1 + C4 + C5 sum. Proposed default; the bank calibrates it.",
      ibs__threshold__osfi__co_interconnectedness_min:
        "Critical operation (E-21) interconnectedness trigger: minimum C3 score. Proposed default; the bank calibrates it.",
      ibs__threshold__osfi__co_soundness_min:
        "Critical operation (E-21) soundness trigger: minimum C2 safety-and-soundness score. Proposed default; the bank calibrates it.",
      ibs__threshold__osfi__co_viability_min:
        "Critical operation (E-21) viability trigger: minimum C1 continued-operations score. Proposed default; the bank calibrates it.",
      ibs__threshold__sama__ces_composite_min:
        "Standard trigger B: minimum severity + breadth + time-to-impact sum.",
      ibs__threshold__sama__ces_containment_min:
        "Q4 systemic/market risk bar -- the Containment trigger. A candidate scoring at or above this is {{serviceTermShortA}}.",
      ibs__threshold__sama__ces_firm_safety_min:
        "Standard trigger D: minimum financial-stability (Q5) score.",
      ibs__threshold__sama__ces_severity_trigger_breadth_min:
        "Standard trigger A: minimum breadth (Q3b) score (with severity).",
      ibs__threshold__sama__ces_severity_trigger_min:
        "Standard trigger A: minimum severity (Q3a) score.",
      ibs__threshold__sama__ces_severity_trigger_time_min:
        "Standard trigger A: minimum time-to-impact (Q3c) score (alternative to breadth).",
      ibs__threshold__cif_composite_min:
        "Stream C composite trigger: F1+F2+F3+F4 at or above this admits.",
      ibs__threshold__cif_contagion_min:
        "Stream C contagion trigger: F4 (computed from the graph) at or above this (with F2).",
      ibs__threshold__cif_contagion_severity_min:
        "Stream C contagion trigger: F2 at or above this (with F4).",
      ibs__threshold__cif_licence_min:
        "Stream C licence trigger: F3 regulatory and licence consequence at or above this admits.",
      ibs__threshold__cif_register_size_max:
        "Expected ceiling on the Critical Internal Function register. Above it, raise the thresholds rather than accept the register (paper \u00a7 7.4).",
      ibs__threshold__cif_time_critical_severity_min:
        "Stream C time-critical trigger: F2 at or above this (with F1).",
      ibs__threshold__cif_time_critical_time_min:
        "Stream C time-critical trigger: F1 time to impairment at or above this (with F2).",
      ibs__threshold__cif_viability_severity_min:
        "Stream C viability trigger: F2 severity of firm impact at or above this admits.",
      ibs__threshold__sama__ids_composite_min:
        "SMS trigger iii: minimum R1 + R2 + R3 sum.",
      ibs__threshold__sama__ids_gate_time_to_breach_min:
        "SMS mandatory gate: minimum time-to-breach (R1) score.",
      ibs__threshold__sama__ids_severity_irreversibility_penalty_min:
        "SMS trigger ii: minimum penalty-severity (R2) score.",
      ibs__threshold__sama__ids_severity_irreversibility_reversibility_min:
        "SMS trigger ii: minimum reversibility (R3) score.",
      ibs__threshold__material_financial_sar:
        "NBS gate Q2 materiality bar (SAR). The financial impact of a tolerance-length outage at or above this is treated as material when flagging Engine-vs-evidence disagreement. Informational only -- the admission decision uses the Engine's own answer, not this bar. Set to your bank's materiality line; SAMA measures materiality relative to bank size, so a smaller bank should lower it and a larger bank raise it.",
      mvb__challenge__include_vendors:
        "Queue a vendor's {{mvbTermShort}} evidence pack for second-line challenge in My Work. Off by default: a supplier scored outside the {{mvbTerm}} does not need a mandatory review to say so. The candidate register still offers the challenge either way.",
      mvb__rubric__continuity_threshold_hours:
        "Gate 1: a candidate is continuity-critical if its declared {{toleranceTerm}} is no longer than this many hours.",
      mvb__rubric__customer_reach_threshold:
        "Gate 2: externally observable at or above this many customers affected per 24-hour outage.",
      mvb__rubric__financial_throughput_threshold_sar:
        "Gate 2: externally observable at or above this SAR gross daily value passing through the service.",
      mvb__rubric__rto_breach_share:
        "Gate 1: continuity-critical if actual RTO breached declared tolerance in at least this share of the last incidents.",
      recommendations__revenue__band1_bonus:
        "Recommendations: priority bonus for SAR < threshold1.",
      recommendations__revenue__band2_bonus:
        "Recommendations: priority bonus for SAR < threshold2.",
      recommendations__revenue__band3_bonus:
        "Recommendations: priority bonus for SAR < threshold3.",
      recommendations__revenue__band4_bonus:
        "Recommendations: priority bonus for SAR >= threshold3.",
      recommendations__revenue__threshold1_sar:
        "Recommendations: revenue band-1 SAR cut-off.",
      recommendations__revenue__threshold2_sar:
        "Recommendations: revenue band-2 SAR cut-off.",
      recommendations__revenue__threshold3_sar:
        "Recommendations: revenue band-3 SAR cut-off.",
      recovery__restore_threshold:
        "Recovery curve: availability fraction counted as partial restore.",
      recovery__severity_factor__critical: "Recovery: critical-severity RTO factor.",
      recovery__severity_factor__high: "Recovery: high-severity RTO factor.",
      recovery__severity_factor__low: "Recovery: low-severity RTO factor.",
      recovery__severity_factor__medium: "Recovery: medium-severity RTO factor.",
      roadmap__thresholds__applications_min:
        "Bank's minimum threshold for applications_min.",
      roadmap__thresholds__bian_pct_min: "Bank's minimum threshold for bian_pct_min.",
      roadmap__thresholds__dimensions_pct_min:
        "Bank's minimum threshold for dimensions_pct_min.",
      roadmap__thresholds__ibs_min: "Bank's minimum threshold for ibs_min.",
      roadmap__thresholds__ml4_attestation_completion_min:
        "Bank's minimum threshold for ml4_attestation_completion_min.",
      roadmap__thresholds__ml4_evidence_cadence_quarters:
        "Bank's minimum threshold for ml4_evidence_cadence_quarters.",
      roadmap__thresholds__ml4_findings_closure_pct_min:
        "Bank's minimum threshold for ml4_findings_closure_pct_min.",
      roadmap__thresholds__ml4_test_pass_rate_min:
        "Bank's minimum threshold for ml4_test_pass_rate_min.",
      roadmap__thresholds__services_min: "Bank's minimum threshold for services_min.",
      roadmap__thresholds__vendors_min: "Bank's minimum threshold for vendors_min.",
      tolerance__default_at_risk_ratio:
        "Fraction of a tolerance at which {{serviceTermShortA}} reads at_risk, where no operational risk limit is declared for it. E-21 expects limits to sit below tolerances; a declared limit overrides this.",
      twin3d__cascade__hop_delay_minutes:
        "3D cascade: minutes the impairment takes to travel one dependency hop.",
      twin3d__fragility__weight_crit_x100:
        "3D fragility: weight per CRITICAL upstream business service, x100.",
      twin3d__fragility__weight_ibs_x100:
        "3D fragility: weight per upstream business service, x100 (60 = 0.6).",
      twin3d__fragility__weight_impaired_x100:
        "3D fragility: penalty when the service itself is impaired, x100.",
      twin3d__fragility__weight_sole_supplier_x100:
        "3D fragility: penalty when a service has one supplier or none, x100.",
      twin3d__fragility__weight_sole_vendor_impaired_x100:
        "3D fragility: penalty when a service's SOLE vendor is impaired, x100.",
    },
  },
  /* Round 16 I14 -- a dependency layer's note, where the server sends
     its key beside the English. */
  dependencyLayers: {
    note: {
      processByDesign:
        "A State service or a Critical Internal Function, so it has no customer journey by design.",
      processNone: "No customer journey is declared.",
      // Round 18 I19 -- the stack's other notes, from the catalogue.
      noService:
        "No service is linked to this {{serviceTermShort}}, so nothing downstream resolves.",
      unreachable: "Unreachable: this {{serviceTermShort}} is linked to no service.",
      noRecoveryTeam: "No supporting service names a recovery team.",
      noFacility: "No supporting service names a facility.",
      noVendor: "No supporting service records a vendor or contract.",
      dataFreeText:
        "A free-text classification on the service, not a modelled asset, so it cannot be aggregated or asked about residency.",
      noDataAsset: "No data asset is linked to any supporting service.",
      dataNotModelled:
        "Data is not modelled on this estate yet, so residency and data impact cannot be answered.",
      noFunding: "No funding resource or funding function is mapped.",
      noRegulatory: "No return, limit or licence condition is mapped.",
    },
  },
  /* Round 16 I14 -- the connector register's times and states, which
     a helper composed in English. */
  connectorTimes: {
    manual: "manual only",
    everySeconds_one: "every {{count}} second",
    everySeconds_other: "every {{count}} seconds",
    everyMinutes_one: "every {{count}} min",
    everyMinutes_other: "every {{count}} min",
    everyHours_one: "every {{count}} hour",
    everyHours_other: "every {{count}} hours",
    never: "never",
    justNow: "just now",
    minutesAgo_one: "{{count}} min ago",
    minutesAgo_other: "{{count}} min ago",
    hoursAgo_one: "{{count}}h ago",
    hoursAgo_other: "{{count}}h ago",
    daysAgo_one: "{{count}}d ago",
    daysAgo_other: "{{count}}d ago",
    reason: {
      notEnabled: "Not enabled -- this system is not being read.",
      failing_one: "Failing: {{count}} run in a row has errored.",
      failing_other: "Failing: {{count}} runs in a row have errored.",
      neverSucceeded: "Enabled, and no run has ever succeeded.",
      neverRun:
        "Enabled, but it has never run. If you expected it to run on a schedule, check that a cadence is set and the connector scheduler is switched on (MARSAD_CONNECTOR_SCHEDULER_ENABLED).",
      overdue: "Overdue: the last success is older than twice its cadence.",
    },
  },
  /* Round 16 I14 -- labels the server composed or a component held in
     English: the data baseline's pillars and rungs, the board's four
     outcomes and the recovery horizons. */
  /* Round 19 I23 -- the data baseline's pillar sentences, composed in
     the backend, catalogued here word for word. */
  dataBaseline: {
    pillarDetail: {
      linkage:
        "graph fields the twin derives over -- an unlinked component is invisible to blast, cascade and the war-room",
      completeness:
        "mean mandated-field coverage across {{count}} populated entity registers (the asset-register floor an examiner checks)",
      discrimination:
        "{{ok}} of {{assessed}} assessed distribution checks passing -- classifications must carry judgment, not a uniform grade",
      discriminationSome:
        "{{ok}} of {{assessed}} assessed distribution checks passing, {{notAssessed}} not assessed -- classifications must carry judgment, not a uniform grade",
      discriminationNone:
        "not assessed: none of {{notAssessed}} distribution checks had a register to judge",
    },
  },
  enumWords: {
    baselinePillar: {
      structural_linkage: "Structural linkage",
      sama_completeness: "{{regulatorShort}}-mandated field completeness",
      discrimination: "Classification discrimination",
    },
    baselineRatio: {
      ci_service_link: "Technology components linked to a service",
      ibs_service_link: "{{serviceTermPlural, capitalize}} with supporting services",
      service_vendor_link: "Services with third-party (vendor) visibility",
      ibs_channel_link: "Customer-facing business services mapped to delivery channels",
    },
    outcome: {
      minimize_customer_harm: "Customer harm",
      retain_customer_confidence: "Customer confidence",
      regulatory_engagement: "Regulatory",
      protect_safety_and_soundness: "Safety + soundness",
    },
    incidentRecipient: {
      sama: "{{regulatorShort}} IT Risk Supervision",
      osfi: "OSFI Technology Risk Division and the Lead Supervisor",
      dora: "The relevant competent authority (Article 46)",
    },
    horizon: {
      t_plus_4h: "T+4h",
      t_plus_24h: "T+24h",
      t_plus_72h: "T+72h",
      t_plus_1w: "T+1w",
    },
  },
  refusals: {
    /* Round 20 U3 -- an initial notice says what it says before it is
       recorded as sent, and what was sent is not rewritten. */
    incidentNoticeIncomplete:
      "The notice cannot be recorded as sent while it is missing: {{fields}}.",
    incidentNoticeAlreadySent:
      "This notice is recorded as sent; what was sent is not rewritten.",
    // Round 18 I18 -- an examiner without a grant, in the catalogue.
    examinerNeverGranted:
      "This account has never held an examination grant. Ask the bank to mint one.",
    examinerGrantRevoked:
      "Your examination grant was revoked on {{date}}. Ask the bank to mint a new one if the examination is continuing.",
    examinerGrantRevokedBy:
      "Your examination grant was revoked on {{date}}. It was granted by {{by}}. Ask the bank to mint a new one if the examination is continuing.",
    examinerGrantExpired:
      "Your examination grant expired on {{date}}. Ask the bank to renew it.",
    examinerGrantExpiredBy:
      "Your examination grant expired on {{date}}. It was granted by {{by}}. Ask the bank to renew it.",
    // Round 18 A33 -- somebody else's draft, named, with the way out.
    controlOnDraftPlan:
      "{{control}} is already on \u201c{{plan}}\u201d, drafted by {{drafter}}. Open it, or raise a plan for this control.",
    declarationCroFirst:
      "The CRO signs this declaration first. It comes to the Accountable Executive once the CRO has signed.",
    // Round 17 G15 -- the owner confirms his own service.
    attestationOwnerConfirms:
      "{{owner}} owns this service, so the confirmation is theirs to give.",
    /* Round 16 I8 -- the new registers' refusals, coded, so they render
       in the reader's language with their names isolated. Each mirrors
       the server's sentence. */
    cifNotFound: "No Critical Internal Function with that id.",
    cifFunctionKind: "A function's kind is one of: {{kinds}}.",
    serviceNotFound: "That service was not found.",
    ibsNotFound: "That {{serviceTermShort}} was not found.",
    registerCodeTaken: "The code {{code}} is already on the register.",
    cifAlreadyInternal: "'{{name}}' is already a Critical Internal Function.",
    cifHasChannels:
      "'{{name}}' is delivered through {{channels}}. A Critical Internal Function has no delivery channel or customer journey, so remove those first.",
    cifScoreRange: "{{criterion}} is scored {{min}} to {{max}}.",
    cifYesNo: "{{question}} is a yes or no answer: 1 or 0.",
    groupReserved:
      "The Indirect Services group is the methodology's own and cannot be deleted; it exists before anything qualifies into it.",
    groupNotEmpty_one:
      "The group still holds {{count}} service; move it first, so no service leaves a group without anybody deciding where it goes.",
    groupNotEmpty_other:
      "The group still holds {{count}} services; move them first, so no service leaves a group without anybody deciding where they go.",
    groupAlreadyMember:
      "{{name}} is already in a group. Remove it there first: a service sits in one group, and moving it is a deliberate act.",
    groupHandoverPredates: "A handover cannot predate the current owner's start.",
    groupHoldsNoFunction:
      "{{name}} is a Critical Internal Function; functions sit on their own register, not in {{serviceTermShortA}} group.",
    groupNotMember: "That service is not in this group.",
    incidentReportSeat:
      "Recording a regulator report needs the approver seat or the operational resilience role.",
    incidentDetectionReason:
      "A detection time other than when the finding was raised needs a reason: the deadline runs from it.",
    incidentStageExists_initial: "This finding already has an initial report.",
    incidentStageExists_final: "This finding already has a final report.",
    incidentStageExists: "This finding already has a report at that stage.",
    findingNotFound: "That finding was not found.",
    incidentReportNotFound: "That report was not found.",
    incidentAlreadySubmitted: "This report is already recorded as submitted.",
    incidentSubmittedFuture: "A submission cannot be recorded in the future.",
    incidentSubmittedBeforeDetection:
      "A submission cannot precede the incident's detection.",
    regimeUnknown: "That regulator is not on the register.",
    floorItemKeyTaken:
      "This regulator's floor list already carries an item with that key.",
    floorItemNotFound: "That floor item was not found.",
    floorItemAnswered:
      "The bank has answered this item. Clear the answer before removing it.",
    floorItemNoAnswer: "This item has no answer.",
    floorAdmitNamesService: "Admitting a floor item names the service that answers it.",
    floorServiceRemoved:
      "{{name}} has been removed from the register; it cannot answer a floor item.",
    floorClassMismatch:
      "The item is for a {{itemClass}} and {{name}} is a {{serviceClass}}.",
    floorOptOutReason: "An opt-out states why.",
    floorApproveCro: "Approving an opt-out is the CRO's.",
    floorNoOptOut: "There is no opt-out on this item to approve.",
    floorAlreadyApproved: "This opt-out is already approved.",
    floorRecorderCannotApprove:
      "The person who recorded an opt-out cannot also approve it.",
    // ROUND 15 A10 -- the Engine's refusals, and the gateway's. A
    // sentence on the control or the plan; never an address, an
    // exception class or a web server's page.
    engineUnavailable: "The Engine could not answer this time. Nothing was recorded.",
    // ROUND 15 E9 -- the row-limit refusal, with its figures grouped.
    importTooManyRows: "CSV has {{rows}} rows; the limit is {{limit}}.",
    // Round 16 G14 -- names the place, and was missing from the catalogue.
    toleranceNoOwner:
      "{{name}} has no recorded owner, and only its owner can countersign a tolerance, so this declaration could never complete. Record the owner in the {{serviceTermShort}} register first.",
    // Round 20 V1 -- the second-line challenger cannot also sign.
    admissionChallengerSigns:
      "{{who}} recorded this decision's second-line challenge, so cannot also sign it: the challenge would no longer be independent. Another holder of the seat signs.",
    // Round 20 W3 -- an owner named in the register, bound to no seat.
    toleranceOwnerNotSeat:
      "Owner named, not yet a seat: {{owner}} owns {{name}}, and only the owner can countersign a tolerance, so this declaration could never complete. Record the address they sign in with in the {{serviceTermShort}} register first.",
    engineUnreadable:
      "The Engine answered in a shape the product could not read. Nothing was recorded.",
    gatewayUnavailable: "The server did not answer this time. Nothing was recorded.",
    gatewayTimeout:
      "The server took too long to answer. What you asked for may still have happened; check before trying again.",
    // Round 18 F10 -- one problem with the file, said once.
    importNoKeyColumn:
      "The file has no '{{column}}' column. Every {{entity}} is matched on it.",
    // Round 18 A36 -- a plan acted on from a seat it is not with.
    notYourPosition: "This plan is with the {{seat}}. It is not yours to act on.",
    notYourPositionHeld:
      "This plan is with the {{seat}} ({{holder}}). It is not yours to act on.",
    // Round 19 N14 -- four held the seat and the refusal named nobody.
    notYourPositionSeveral:
      "This plan is with the {{seat}}; {{holders}} people hold it. It is not yours to act on.",
    seatRequired: "This needs the {{needed}} seat. You hold {{held}}.",
    // Round 16 I12 -- the sentences when there is nothing to list.
    seatRequired_none:
      "This needs the {{needed}} seat. You hold none of the workflow seats.",
    seatRequiredForAction:
      "To {{action}} you need the {{needed}} seat. You hold {{held}}.",
    seatRequiredForAction_none:
      "To {{action}} you need the {{needed}} seat. You hold none of the workflow seats.",
    seatAdministrator: "Administrator",
    noLegalTransition:
      "This plan is {{state}}, so you cannot {{action}} again. From here the {{seat}} can {{allowed}}.",
    noLegalTransition_nothing:
      "This plan is {{state}}, so you cannot {{action}} again. There is nothing the {{seat}} can do from here.",
    // Round 16 I12 -- the workflow's verbs, as a refusal says them.
    action: {
      submit: "submit it",
      approve: "approve it",
      reject: "reject it",
      send_back: "send it back",
      close: "close it",
      reopen: "reopen it",
      withdraw: "withdraw it",
    },
    planEntriesDraftOnly:
      "'{{name}}' is {{state}}, so its action items are fixed. They are edited only while a plan is a draft.",
    // Round 15 I7 -- these printed the plan's UUID and a Python repr.
    planNotEditable:
      "'{{name}}' is {{state}}, so its title is fixed. Only a draft is edited; send it back to draft first.",
    planActionsDraftOnly:
      "'{{name}}' is {{state}}. Actions are drafted only while a plan is a draft.",
    // Round 14 C6 -- the declaration's refusals. `context` picks the
    // sentence, so no English fragment is interpolated into one.
    declarationSlotHeld_draft:
      "'{{name}}' is already in progress as a draft. One declaration is taken at a time: complete or delete it before starting another.",
    declarationSlotHeld_draft_named:
      "'{{name}}' is already in progress as a draft, started by {{holder}}. One declaration is taken at a time: complete or delete it before starting another.",
    declarationSlotHeld_open:
      "'{{name}}' is already in progress and open for signature. One declaration is taken at a time: complete or delete it before starting another.",
    declarationSlotHeld_open_named:
      "'{{name}}' is already in progress and open for signature, submitted by {{holder}}. One declaration is taken at a time: complete or delete it before starting another.",
    declarationSlotTaken:
      "Another declaration was started at the same moment and is already in progress. Open the {{mvbTermShort}} register to finish or delete it before starting another.",
    declarationNotOpenToSign:
      "This declaration is {{state}}. A signature can be given once it has been submitted, and not after it is in force.",
    declarationLegSigned_named:
      "This leg was already signed by {{holder}} on {{at}}. A signature is not given twice; to change it, the signer withdraws it first.",
    declarationLegSigned_unnamed:
      "This leg was already signed on {{at}}. A signature is not given twice; to change it, the signer withdraws it first.",
    declarationSetMoved_cro:
      "The set has changed since the CRO signed. They signed for {{signedIbs}} {{serviceTermPlural}}, {{signedServices}} services and {{signedVendors}} vendors; this would put {{nowIbs}} {{serviceTermPlural}}, {{nowServices}} services and {{nowVendors}} vendors in force. Withdraw their signature and take the ceremony again over one set.",
    declarationSetMoved_ae:
      "The set has changed since the Accountable Executive signed. They signed for {{signedIbs}} {{serviceTermPlural}}, {{signedServices}} services and {{signedVendors}} vendors; this would put {{nowIbs}} {{serviceTermPlural}}, {{nowServices}} services and {{nowVendors}} vendors in force. Withdraw their signature and take the ceremony again over one set.",
    declarationSameSigner:
      "You have already signed this declaration for the other role. It needs two different people: the CRO and the Accountable Executive.",
    declarationNotOpenToWithdraw:
      "This declaration is {{state}}. A signature can be withdrawn while the declaration is still open; once it is in force it is superseded, not edited.",
    declarationNothingToWithdraw: "That leg carries no signature to withdraw.",
    declarationWithdrawNotSigner_named:
      "Only {{signer}} can withdraw this signature, or an administrator on their behalf.",
    declarationWithdrawNotSigner_unnamed:
      "Only the person who signed can withdraw this signature, or an administrator on their behalf.",
    declarationNotDeletable:
      "This declaration is {{state}}. A declaration that has been in force is superseded, not deleted -- deleting it would destroy the record of a decision the bank took.",
    declarationFieldsFixed:
      "This declaration is {{state}}, so its fields are fixed. Supersede it and declare a replacement.",
    declarationNotDraft:
      "This declaration is {{state}}. Only a draft can be submitted for signature.",
    declarationNotInForce:
      "This declaration is {{state}}. Only one that is in force can be superseded.",
    declarationReplacementNotInForce:
      "The replacement declaration is {{state}}. It has to be in force before it can take over.",
    // Round 15 C8 and C10 -- the MVB pack's refusals, given the words
    // the declaration's already have.
    packLegSigned_named:
      "This leg was already signed by {{holder}} on {{at}}. A signature is not given twice.",
    packLegSigned_unnamed:
      "This leg was already signed on {{at}}. A signature is not given twice.",
    packAlreadyChallenged_named:
      "This pack is not awaiting a challenge: the second line ({{holder}}) recorded one on {{at}}. A challenge is given once; what it said stands beside the CRO's signature.",
    // Round 15 G1 -- the environment's answer, not an authority one.
    ceremonySigningOff:
      "Signing is switched off for this ceremony in this environment, so only an administrator can record it. Ask your administrator to enable role-based signing or to sign the {{role}} leg on your behalf.",
    packAlreadyChallenged_unnamed:
      "This pack is not awaiting a challenge: the second line recorded one on {{at}}. A challenge is given once; what it said stands beside the CRO's signature.",
    // Round 16 C13 -- the admission ceremony (an IBS, or a Critical
    // Internal Function on Route 2), given the pack's words.
    admissionLegSigned_named:
      "This leg was already signed by {{holder}} on {{at}}. A signature is not given twice.",
    admissionLegSigned_unnamed:
      "This leg was already signed on {{at}}. A signature is not given twice.",
    admissionAlreadyChallenged_named:
      "This admission is not awaiting a challenge: the second line ({{holder}}) recorded one on {{at}}. A challenge is given once; what it said stands beside the signatures.",
    // Round 16 A25 -- a derive that failed, said as what happened.
    deriveFailed:
      "The Engine could not score this now. Nothing was saved. Score it by hand, or try again.",
    // Round 16 C17 -- one shape for "already signed", on every
    // ceremony: who signed the leg, and when.
    legSigned_named:
      "This leg was already signed by {{holder}} on {{at}}. A signature is not given twice.",
    legSigned_unnamed:
      "This leg was already signed on {{at}}. A signature is not given twice.",
    alreadyApproved_dated:
      "This was already approved on {{at}}. A signature is not given twice; a change starts a new decision.",
    alreadyApproved_undated:
      "This was already approved. A signature is not given twice; a change starts a new decision.",
    // Round 16 G7.
    attestationOwnSubmission:
      "You submitted this attestation, so you cannot also approve it. Another approver must sign it.",
    admissionAlreadyChallenged_unnamed:
      "This admission is not awaiting a challenge: the second line recorded one on {{at}}. A challenge is given once; what it said stands beside the signatures.",
  },
  screens: {
    /* Round 20 Y3 -- the Score's published method, in the product. */
    scoreMethodology: {
      loading: "Loading the method...",
      error: "Could not load the method.",
    },
    engineRunsConsole: {
      engineBatchRuns: "Engine batch runs",
    },
    metricsRail: {
      highlightTheImpactedNodesOn: "Highlight the impacted nodes on the map",
    },
    obligationEngineView: {
      obligationEnginePasses: "Obligation Engine passes",
    },
    twinDeriveView: {
      chainDerivers: "Chain derivers",
    },
    mvbDeclarationDetail: {
      approved: "approved",
    },
    adminListView: {
      asc: "asc",
    },
    documentGapView: {
      suggested: "suggested",
    },
    kpiPanel: {
      auto: "auto",
      manual: "manual",
    },
    controlTestingSection: {
      design: "design",
    },
    telemetryView: {
      external: "external",
    },
    architectureStandardsView: {
      breach: "breach",
    },
    serviceOwnerHome: {
      admitted: "admitted",
    },
    homeHero: {
      unknown: "unknown",
    },
    drillsView: {
      planned: "planned",
      inProgress: "in_progress",
    },
    continuityPlansView: {
      retired: "retired",
    },
    constellation3D: {
      ok: "ok",
    },
    inspector: {
      ibs: "ibs",
      mvb: "{{mvbTermShort}}",
      vendor: "vendor",
      material: "Material",
      service: "service",
    },
    constellationView: {
      ring: "ring",
      mvb: "mvb",
      live: "live",
    },
    iBSChannelStateDrawer: {
      none: "none",
      channel: "channel",
    },
    cascadeImpactCard: {
      red: "red",
    },
    cascadeBanner: {
      split: "split",
    },
    capabilityPopover: {
      mvb: "· {{mvbTermShort}}",
      yes: "Yes",
      no: "No",
    },
    myWorkView: {
      overdue: "overdue",
      due: "due",
    },
    notificationBell: {
      unread: "unread",
    },
    fullscreenToggle: {
      exitFullScreen: "Exit full screen",
      fullScreen: "Full screen",
    },
    appShell: {
      unknownError: "Unknown error",
    },
    journeyView: {
      blocked: "blocked",
      done: "done",
    },
    setUpThisBank: {
      done: "done",
    },
    twinViz: {
      observatory: "observatory",
      orbit: "orbit",
      ring: "ring",
      flow: "flow",
      both: "both",
      service: "service",
      ibs: "ibs",
      vendor: "vendor",
      isolatedCount_one: "{{count}} with no modelled dependency",
      isolatedCount_other: "{{count}} with no modelled dependency",
      canvasTitle: "Constellation -- mode={{mode}}, {{count}} nodes",
    },
    auditEventsView: {
      // Round 17 H35 -- the filter's kinds, in words.
      kind: {
        service: "Service",
        ibs_service: "Business service or function",
        mvb_declaration: "{{mvbTermShort}} declaration",
        connector: "Connector",
        twin_analyser_run: "Service analyser run",
        framework_assessment: "Control assessment",
        framework_analyser_run: "Control analyser run",
        framework_cycle: "Assessment cycle",
        chain: "Hash chain (verification)",
        service_account: "Service account",
        app_setting: "Setting",
        regulator_letter: "Regulator letter",
        cap: "Corrective action plan",
        attestation: "Attestation",
        deployment: "Deployment (restore)",
        // Round 19 I24 -- kinds the list showed as codes.
        ibs_group: "{{serviceTermShort}} group",
        change_event: "Change",
        regulatory_change_assessment: "Regulatory change assessment",
        outsourcing_contract: "Outsourcing contract",
        vendor: "Vendor",
      },
      /* Round 10 F4 -- a service account is not a user and must not
         read like one in the actor column. */
      serviceAccount: "(service account)",
      eGAdminFoundationOr: "e.g. admin.foundation. or system.cmdb.",
      // C4 -- an example taken from the log rather than guessed, and a
      // way to see the types it holds.
      prefixExample: "e.g. {{example}}",
      prefixPick_one: "Pick from {{count}} recorded type",
      prefixPick_other: "Pick from {{count}} recorded types",
      any: "(any)",
      unknown: "unknown",
      none: "(none)",
      loading: "Loading...",
      rangeOf: "of",
      auditEvents: "Audit events",
      everyAdminActionSystemDerivedWriteThe:
        "Every admin action + system-derived write the bank performed in the audit window. Click a row to see the full payload; the “View source” button jumps to the entity surface where one exists.",
      filteredByEntity: "Filtered by entity:",
      clear: "Clear",
      // ROUND 15 E9 -- the importer writes one audit event per batch;
      // the record's own import is read from the batch's rows.
      importTouchInsert:
        "Created by the import of {{filename}}, row {{row}}, {{when}}.",
      importTouchUpdate:
        "Changed by the import of {{filename}}, row {{row}}, {{when}}.",
      importTouchUndone: "That import has since been undone.",
      unnamedFile: "an unnamed file",
      openTheBatch: "Open the batch",
      eventTypePrefix: "Event-type prefix",
      entityType: "Entity type",
      pageSize: "Page size",
      // D2 -- the format hint is a figure inside a translated label,
      // so it is interpolated rather than baked into the sentence.
      // Interpolated values are bidi-isolated (shared/i18n/index.ts);
      // a latin run typed into the string itself is not, and reordered
      // to "(8601 ISO)" in right-to-left.
      sinceIso8601: "Since ({{format}})",
      untilIso8601: "Until ({{format}})",
      refresh: "Refresh",
      prev: "Prev",
      next: "Next",
      failedToLoad: "Failed to load:",
      noAuditEventsMatchTheseFilters: "No audit events match these filters.",
      when: "When",
      actor: "Actor",
      eventType: "Event type",
      entity: "Entity",
      system: "system",
      viewSource: "View source",
      ipAddress: "IP address",
      userAgent: "User agent",
      payload: "Payload",
    },
    dataImportView: {
      // Round 20 W4 -- what the picker does not offer, and where to read why.
      notImportable: "There is no file route for {{kinds}}.",
      notImportableWhy: "Why, and what to do instead",
      // Round 20 W8 -- every row's verdict, target and message, as a file.
      downloadOutcome_one: "Download the row's outcome (CSV)",
      downloadOutcome_other: "Download all {{count}} rows' outcomes (CSV)",
      outcomeDownloadFailed: "The outcome file could not be downloaded.",
      notSent:
        "The file was not sent: the browser could not read or send it, so nothing was staged. If the file changed after you chose it, choose it again.",
      /* Round 19 I24 -- the register names came from the server in
         English ("Important Business Service (IBS)"). */
      entityLabel: {
        ibs_service: "{{serviceTermTitle}} ({{serviceTermShort}})",
        service: "Service",
        capability: "Capability",
        product: "Product",
        vendor: "Vendor",
        configuration_item: "Configuration Item (CI)",
        channel: "Channel",
        outsourcing_contract: "Outsourcing contract",
        recovery_team: "Recovery team",
        facility: "Facility (site)",
        data_asset: "Data asset",
        funding_resource: "Funding resource",
        regulatory_commitment: "Regulatory commitment",
        ibs_group: "{{serviceTermShort}} group",
        journey_step: "Customer journey step",
      },
      mappingWithEngine: "Mapping with Engine",
      commitRows_one: "Commit {{count}} OK row",
      commitRows_other: "Commit {{count}} OK rows",
      seeWhatLanded_one: "See the row you imported",
      seeWhatLanded_other: "See the {{count}} rows you imported",
      /* Round 12 A6 -- a graph edge lands in the drawer of the entity
         it hangs off, not in a register of its own, so the sentence
         says where rather than repeating "rows". */
      seeWhereEdgesLanded_one: "See where the edge landed",
      seeWhereEdgesLanded_other: "See where the {{count}} edges landed",
      committed: "Committed",
      committing: "Committing...",
      csvJsonXlsxFile: "CSV / JSON / XLSX file",
      csvFile: "CSV file",
      validating: "Validating...",
      mapWithEngineValidate: "Map with Engine + validate",
      uploadValidate: "Upload + validate",
      journeyStepsAttachToAnExistingJourney:
        "Journey steps attach to an existing journey by its {{serviceTermShort}} + channel codes, plus a step number. Each row needs those; the structured template has them.",
      graphEdgesLinkTwoExistingEntitiesBy:
        "Graph edges link two existing entities by their codes. Each row needs both code columns; the structured template has them.",
      samaMandatedFieldCompleteness: "SAMA-mandated field completeness",
      howMuchOfTheLiveEstateHas:
        "How much of the live estate has the SAMA-mandated fields populated.",
      // Round 18 F10 -- an empty register has no percentage.
      nothingLoaded: "Nothing loaded yet",
      rows: "rows",
      engineColumnMapping: "Engine column mapping",
      howTheMarsadEngine: "How the Marsad Engine",
      mappedYourSourceColumnsOntoTheField:
        "mapped your source columns onto the field spec. Review before committing.",
      ignoredSourceColumns: "Ignored source columns:",
      /* Round 13 D1 -- the plain path's own count of what it did not
         read. Pluralised because "1 source columns" beside a clean
         verdict reads like a rounding error rather than a loss. */
      ignoredColumnsWarning_one:
        "1 source column was not recognised and will not be imported:",
      ignoredColumnsWarning_other:
        "{{count}} source columns were not recognised and will not be imported:",
      didYouMean: "(did you mean {{field}}?)",
      retiredColumn: "(retired -- set {{field}} instead)",
      /* Round 13 D3 -- the ceiling before a file is chosen, rather than
         a web server's error page after one is. */
      uploadLimits: "Up to {{megabytes}} MB and {{rows}} rows per file.",
      // ROUND 15 E8 -- said before a byte is sent, in the words the edge
      // uses for the same refusal (round 13 D3).
      tooLargeToSend:
        "That file is larger than this deployment accepts ({{megabytes}} MB). Split it and upload the parts. Nothing was sent.",
      /* Round 13 D9 -- a table that showed a fifth of a file without
         saying so would be the same defect D1 is about. */
      rowsNotListed_one: "1 more row imported cleanly and is not listed below.",
      rowsNotListed_other:
        "{{count}} more rows imported cleanly and are not listed below.",
      /* Round 13 D8 -- the same file, twice. "0 insert, 1 update" is
         indistinguishable from "somebody edited a row"; the hash that
         answers it was already stored and nothing read it. */
      previouslyCommitted:
        "This exact file was already committed for this entity on {{when}}. Committing again will re-apply every row; an update count here may mean nothing has changed.",
      /* Round 13 D8 -- the importer's history and the two verbs it
         lacked. Fifteen staged batches sat on one estate invisible to
         every screen, with no discard and no undo. */
      historyHeading: "Earlier uploads",
      historyFile: "File",
      historyWhen: "Uploaded",
      historyOutcome: "Outcome",
      historyNoFilename: "(no filename)",
      historyStatus_staged: "Staged, not committed",
      historyStatus_committed: "Committed",
      historyStatus_undone: "Undone",
      historyStatus_failed: "Failed",
      historyCounts: "{{ok}} ok of {{rows}}, {{errors}} with errors",
      historyOpen: "Open",
      historyDiscard: "Discard",
      historyDiscarded: "Discarded. Nothing had been written.",
      historyDiscardFailed: "Could not discard that upload",
      historyUndo: "Undo",
      // Round 14 F1 -- asked, not done on one press.
      historyUndoConfirmBody:
        "Undo {{filename}}? Records it added are removed and records it changed are put back, unless something has built on them since.",
      historyUndoConfirm: "Undo it",
      historyUndoCancel: "Keep it",
      // ROUND 15 F5 -- "1 records removed": two counts, two plurals.
      historyUndoneRemoved_one: "1 record removed",
      historyUndoneRemoved_other: "{{count}} records removed",
      historyUndoneRestored_one: "1 put back as it was",
      historyUndoneRestored_other: "{{count}} put back as they were",
      historyUndone: "Undone: {{removed}}, {{restored}}.",
      stagedRows_one: "Staged {{ok}}/1 row",
      stagedRows_other: "Staged {{ok}}/{{count}} rows",
      committedRows_one: "Committed {{ok}}/1 row",
      committedRows_other: "Committed {{ok}}/{{count}} rows",
      // ROUND 15 E9 -- grouped, as every count now is.
      committedInsertedUpdated: "Committed: {{inserted}} inserted, {{updated}} updated",
      commitFailed: "Commit failed",
      rowsHaveErrors_one: "1 row has errors -- review before commit",
      rowsHaveErrors_other: "{{count}} rows have errors -- review before commit",
      historyUndoRefused_one: "1 record was left as it is: {{reason}}",
      historyUndoRefused_other:
        "{{count}} records were left as they are. The first: {{reason}}",
      historyUndoFailed: "Could not undo that upload",
      /* Round 16 E10 -- the edge gave up waiting and the batch, read
         again, has not finished undoing. */
      historyUndoUnsure:
        "The server took too long to answer. This upload still reads as committed; it may still be undoing. Look at the history again in a moment before undoing it again.",
      /* Round 13 D8 -- two things under one name. Named in the row
         detail; counted here so it is visible before Commit. */
      duplicateNames_one:
        "1 row carries a name something else already carries. It is listed below and will commit as a separate record.",
      duplicateNames_other:
        "{{count}} rows carry a name something else already carries. They are listed below and will commit as separate records.",
      /* Round 13 D2 -- the reference this file satisfies itself. */
      selfReferencingRows_one:
        "1 row points at a parent this same file creates. It is resolved on commit; no second upload is needed.",
      selfReferencingRows_other:
        "{{count}} rows point at a parent this same file creates. They are resolved on commit; no second upload is needed.",
      resolvedEnumSynonyms: "Resolved enum synonyms:",
      quot: ': "',
      quot2: '"',
      rowsWord_one: "row ·",
      rowsWord_other: "rows ·",
      errorsWord_one: "error",
      errorsWord_other: "errors",
      ok: "ok",
      insert: "insert ·",
      update: "update",
      verdict: "Verdict",
      action: "Action",
      key: "Key",
      detail: "Detail",
      dataImport: "Data import",
      uploadACsvToBulkCreateOr:
        "Upload a CSV to bulk-create or update Foundation entities. Every upload is validated and staged for review -- nothing is written until you commit.",
      // Round 17 F9.
      openDictionary: "Templates are in the data dictionary.",
      loadOrder:
        "Load the entities before the links that name them: services, business services and vendors first, then their links, funding and group memberships.",
      entity: "Entity",
      selectAnEntity: "Select an entity...",
      engineAssistedLetTheMarsadEngineMap:
        "Engine-assisted: let the Marsad Engine map a messy file’s columns onto the field spec (CSV or JSON; the source headers need not match the template).",
      autoCommitWhenCleanApplyTheMapped:
        "Auto-commit when clean: apply the mapped rows immediately if every row validates (any error keeps the batch staged for review).",
      resolveEnumSynonymsWithTheEngineMap:
        'Resolve enum synonyms with the Engine: map messy category values that don’t match (e.g. "crit" → "Critical") onto the allowed set (an extra Engine call per enum field).',
    },
    settingsAdminView: {
      /* Round 10 F6 -- the sentence between the counts and the
         escalation. Pending is a queue the drain worker empties; the
         number only matters if it stops falling. */
      readingPending_one:
        "1 message is queued for the drain worker. A count that does not fall between refreshes means nothing is draining it.",
      readingPending_other:
        "{{count}} messages are queued for the drain worker. A count that does not fall between refreshes means nothing is draining it.",
      readingFailed_one:
        "1 message has failed after its retries. It will not be sent again; the error below says why.",
      readingFailed_other:
        "{{count}} messages have failed after their retries. They will not be sent again; the errors below say why.",
      readingClear: "Nothing is queued and nothing has failed.",
      /* Round 10 A5 -- the app_settings table was one flat key-sorted
         list, so a figure driving a 3D scene sat between an admission
         threshold and an MVB rubric bar with nothing to say what it
         moved. Each heading names the SURFACE the group governs, in
         the administrator's words rather than the column's. */
      appGroup: {
        twin3d: {
          title: "Twin 3D views",
          blurb:
            "Figures the 3D scenes compute with: the fragility landscape's weights and the cascade's hop delay. Weights are stored x100 -- 60 means 0.6.",
          back: "Back to the 3D views",
        },
        fragility: {
          title: "Fragility radar (2D)",
          blurb:
            "Weights for the flat fragility radar. A neighbouring question to the 3D landscape, scored on its own inputs -- the two are not meant to agree.",
        },
        ibsThresholdSama: {
          title: "{{serviceTermShort}} admission thresholds -- SAMA rubric",
          blurb:
            "The bars the SAMA admission rubric applies (customer-exposed and indirect services). Read only while the bank profile records SAMA or no regime.",
        },
        ibsThresholdOsfi: {
          title: "Critical-operation thresholds -- OSFI E-21 rubric",
          blurb:
            "The bars the OSFI critical-operation rubric applies. Proposed defaults, for the bank to calibrate. Read only while the bank profile records OSFI.",
        },
        ibsThreshold: {
          title: "{{serviceTermShort}} admission thresholds",
          blurb:
            "The bars the admission methodology applies when deciding whether a service is important enough to be {{serviceTermShortA}}.",
        },
        mvbRubric: {
          title: "{{mvbTerm, capitalize}} rubric",
          blurb: "The cut-offs the {{mvbTermShort}} candidate rubric scores against.",
        },
        recovery: {
          title: "Recovery modelling",
          blurb:
            "Severity factors and the restore threshold the recovery model projects with.",
        },
        recommendations: {
          title: "Recommendation priority",
          blurb:
            "Revenue bands and bonuses weighting the recommendation priority score.",
        },
        roadmapThresholds: {
          title: "Roadmap thresholds",
          blurb: "Bars used when reporting roadmap readiness.",
        },
        bank: {
          title: "Bank profile",
          blurb: "Deployment-level facts about the bank itself.",
        },
      },
      /* Round 12 H3 -- accessible names for the webhook form, which
         was the one form in the product carrying unnamed fields, and
         two of these were hard-coded English besides. */
      webhookName: "Endpoint name",
      webhookUrl: "Endpoint URL",
      webhookSecret: "Signing secret (optional)",
      kindPrefixOptional: "Kind prefix (optional)",
      unknown: "unknown",
      resetting: "Resetting…",
      resetOnboarding: "Reset onboarding",
      // D10 -- "Getting Started is visible again" when it had never
      // gone away, which reads as a change that did not happen.
      onboardingReset: "Bank lifecycle stage set to fresh",
      onboardingResetDetail:
        "The setup ladder counts from the start again, for the administrator " +
        "and the resilience lead.",
      onboardingResetFailed: "Reset failed",
      // Round 14 F4 -- nothing to reset.
      onboardingAlreadyFresh:
        "Nothing changed: the bank is already at the fresh stage, so the setup ladder is already counting from the start.",
      saving: "Saving...",
      save: "Save",
      hideHistory: "Hide history",
      history: "History",
      unknown2: "(unknown)",
      disable: "Disable",
      enable: "Enable",
      settingsAudit: "Settings audit",
      readOnlyViewOfTheRuntimeConfig:
        "Read-only view of the runtime config the bank is operating under. Environment variables come first, then the tunable app_settings rows. Secrets show “configured” / “not set” without leaking the value.",
      loading: "Loading…",
      failedToLoad: "Failed to load:",
      marsadEnvironmentVariables: "MARSAD_* environment variables",
      configured: "configured",
      notSet: "not set",
      /* Round 12 G2 -- "not set" is half an answer: what an operator
         needs is that the CODE'S OWN DEFAULT is in force. Fifty-seven
         settings reached nothing at all and thirty-four of them
         rendered here with a value, which is the state this exists to
         make distinguishable. */
      notSetDefaultApplies: "not set -- the code's default applies",
      // Round 16 K11 -- what the process is doing, not what was typed.
      apiDocsServed: "the API explorer and schema ARE being served by this process",
      apiDocsOff: "0 -- the API explorer and schema are not served",
      envLegend:
        'A value here reached the container. "Not set" means the variable never arrived and the code\'s own default is in force -- the description says what that default is.',
      envSearchPlaceholder: "Find a setting by name, group or description",
      /* ROUND 13 H -- the fix for the three-round-old complaint
         arrived without a label, so this became the one unnamed field
         on the console. A placeholder is not a name. */
      envSearchLabel: "Find a setting",
      envSearchCount_one: "{{count}} match",
      envSearchCount_other: "{{count}} matches",
      proofSigning: "Proof signing",
      howDossiersAnswerPacksAndRfiBundles:
        "How dossiers, answer packs and RFI bundles are signed for third-party verification.",
      keyId: "key_id:",
      publishedKeys: "published keys:",
      ed25519Required: "Ed25519 required",
      thisInstanceSignsWithTheDefaultSentinel:
        "This instance signs with the default sentinel key. Generate a keypair (",
      scriptsGenProofKeyPy: "scripts/gen_proof_key.py",
      setThePrivateSeedInEnvAnd: "), set the private seed in env, and set",
      marsadProofRequireEd255191: "MARSAD_PROOF_REQUIRE_ED25519=1",
      beforeIssuingArtefactsToARegulator: "before issuing artefacts to a regulator.",
      publishedPublicKeys: "Published public keys:",
      v1ProofPublicKeys: "/v1/proof/public-keys",
      emailDelivery: "Email delivery",
      pending: "Pending:",
      digestHeld: "Digest held:",
      failed: "Failed:",
      attempt: "(attempt",
      onboarding: "Onboarding",
      bringBackTheGettingStartedLadderFor:
        "Bring back the Getting Started ladder for a fresh team.",
      appSettingsRows: "app_settings rows (",
      noAppSettingsRowsYetSeededBy:
        "No app_settings rows yet. Seeded by Alembic migrations.",
      key: "Key",
      type: "Type",
      value: "Value",
      default: "Default",
      by: "By",
      seed: "seed",
      cancel: "Cancel",
      edit: "Edit",
      loadingHistory: "Loading history...",
      failedToLoadHistory: "Failed to load history:",
      /* Round 19 H55 -- "No changes yet" over a value that is set: say
         where the value came from. */
      // Round 19 I24 -- the signing posture, catalogued.
      signing: {
        ed25519: "Third-party verifiable (Ed25519)",
        sentinel: "Sentinel key -- NOT production-trustworthy",
        hmac: "Internal HMAC (no third-party verify)",
      },
      noChangesYetTheFirstEditWill:
        "Unchanged since this deployment was installed: the value shown is the one it shipped with. The first edit will be recorded here.",
      value2: "value:",
      rarr: "→",
      latest: "latest",
      genesis: "genesis",
      by2: "by:",
      true: "true",
      false: "false",
      webhookEndpoints: "Webhook endpoints",
      outboundJsonOnEveryMatchingNotificationMs:
        "Outbound JSON on every matching notification -- MS Teams, Slack and generic endpoints all render it. Optional secret signs each delivery (X-Marsad-Signature, HMAC-SHA256).",
      kinds: "kinds:",
      signed: "signed",
      delete: "Delete",
      disabled: "disabled",
      noEndpointsRegistered: "No endpoints registered.",
      addEndpoint: "Add endpoint",
    },
    // Round 16 K12 -- the restore, typed and recorded, as the dossier
    // cover prints it.
    restoreRecord: {
      stated: "Restore last exercised, as stated: {{line}}",
      recorded: "Restore recorded by the product: {{date}}",
      notRecorded: "The product has no record of a restore.",
      disagree:
        "These disagree: the stated line and the product's record name different days.",
      notStated: "not stated",
      backup: "Backup, as stated: {{line}}",
    },
    bankSetupView: {
      /* Round 12 G1 -- the Azure deployment work is careful and honest
         and is entirely invisible: one region, HSTS with its two-year
         commitment spelled out, a real certificate, published proof
         keys, single tenant by construction, and a section headed
         "Known limits, stated rather than discovered". None of it
         reached a screen, on a product sold on sovereignty. */
      whereItRuns: "Where this deployment runs",
      whereItRunsSource:
        "Stated by whoever deployed this, and repeated here. MARSAD does not infer where it is running: residency is a claim the bank makes to its regulator.",
      whereItRunsUnstated:
        "Not stated. This deployment has not been told where it runs, so nothing here claims a region. Set MARSAD_DEPLOYMENT_REGION and MARSAD_DEPLOYMENT_HOSTING on the deployment to say so.",
      locationWhereItLives: "Location (where it lives)",
      marsad: "MARSAD",
      saving: "Saving…",
      saveBranding: "Save branding",
      close: "Close",
      addSource2: "Add source",
      has: "has",
      sHave: "s have",
      branding: "Branding",
      brandsEveryGeneratedPdfDossierBoardSnapshot:
        "Brands every generated PDF -- dossier, board snapshot, evidence / answer / RFI / war-room packs, entity exports.",
      bankDisplayName: "Bank display name",
      accentColour: "Accent colour",
      homeJurisdiction: "Home jurisdiction",
      notSetShowEveryJurisdiction: "Not set -- show every jurisdiction",
      saudiArabiaSama: "Saudi Arabia (SAMA)",
      unitedArabEmiratesCbuae: "United Arab Emirates (CBUAE)",
      europeanUnionDora: "European Union (DORA)",
      yourPrimaryRegulatorObligationsFromOtherJuri:
        "Your primary regulator. Obligations from other jurisdictions stay available, collapsed behind a count.",
      totalCustomers: "Total customers",
      theDenominatorIbsBreadthIsScoredAgainst:
        "The denominator {{serviceTermShort}} breadth is scored against. The methodology states its bands as a share of the customer base (under 1% up to over 50%); without this, breadth falls back to absolute headcounts, which are not comparable between banks.",
      uploadLogoPngJpeg256Kb: "Upload logo (PNG/JPEG, ≤256 KB)",
      clearLogo: "Clear logo",
      outputPreview: "Output preview",
      auditDossier: "Audit Dossier",
      class: "Class",
      method: "Method",
      status: "Status",
      canonical: "Canonical",
      addSource: "Add source",
      cancel: "Cancel",
      canonical2: "canonical",
      needsRemapping: "needs remapping",
      delete: "Delete",
      /* ROUND 15 F6 -- one press asks, the second acts and says so. */
      deleteConfirm: "Click again to confirm",
      deleted: "Removed {{name}} from the register.",
      deleteFailed: "{{name}} could not be removed.",
      dataSources: "Data sources",
      declareWhereEachKindOfDataComes:
        "Declare where each kind of data comes from, its format, and whether it's wired yet -- your Day-1 checklist + readiness board.",
      loading: "Loading…",
      noDataSourcesDefinedYet: "No data sources defined yet.",
      source: "Source",
      feeds: "Feeds",
      classMethod: "Class / method",
      shape: "Shape",
      setStatus: "Set status",
      loadingConnectedSystems: "Loading connected systems...",
      connectedSystemsCouldNotBeLoaded: "Connected systems could not be loaded.",
      connectedSystems: "Connected systems",
      of: "of",
      enabledConnector: "enabled connector",
      deliveredData: "delivered data.",
      credentialsCannotBeStoredYet: "Credentials cannot be stored yet.",
      marsadCredentialEncryptionKeyIsNotSet:
        "MARSAD_CREDENTIAL_ENCRYPTION_KEY is not set, so connector credentials entered here are rejected and every connector falls back to its environment variables. Generate a key with:",
      dockerComposeExecBackendPythonCQuot:
        'docker compose exec backend python -c "from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())"',
      connectASystem: "Connect a system",
      walksYouThroughCredentialsAConnectionTest:
        "walks you through credentials, a connection test, and turning it on.",
      noConnectorIsEnabledYet: "No connector is enabled yet.",
      needs: "needs",
      bankSetup: "Bank Setup",
      day1OnboardingTheBankSIdentity:
        "Day-1 onboarding: the bank's identity for branded outputs, and the register of where its data comes from.",
    },
    liveRuns: {
      heading: "Running now",
      queued: "waiting for the run in progress",
      running: "running for",
      stale: "stopped reporting {{ago}} ago -- it may have ended without clearing",
      counts:
        "{{batches}} batches, {{inserted}} new, {{updated}} updated, {{skipped}} skipped so far",
      idle: "Nothing running.",
      elapsedMinutes: "{{m}}m {{s}}s",
      elapsedSeconds: "{{s}}s",
      lastSucceeded:
        "Latest run finished: {{inserted}} new, {{updated}} updated, {{skipped}} skipped.",
      lastFailed: "Latest run failed: {{reason}}",
    },
    connectorsView: {
      skippedBecause: "Skipped because:",
      skipReason: {
        no_linked_asset: "link no asset",
        no_marsad_target: "link only an asset MARSAD has nowhere to put",
        unresolved_asset: "name an asset MARSAD does not hold",
        unnamed_asset: "link an asset with no name",
        unmapped_severity: "carry a severity with no mapping",
        unmapped_status: "carry a status with no mapping",
        unreadable_record: "could not be read",
        unresolved_host: "name a host MARSAD does not hold",
      },
      /* Round 10 F6 -- an administrator arriving from "1 need
         attention" read fourteen rows to find the one he was sent for,
         third round. */
      attentionOnly_one: "Show the 1 needing attention",
      attentionOnly_other: "Show only the {{count}} needing attention",
      attentionShowAll: "Show all {{count}}",
      /* ROUND 13 H -- a bare "13 connectors" is the number the
         migrations seed and says nothing about the estate. */
      countWithEnabled_one: "{{count}} connector, {{enabled}} switched on",
      countWithEnabled_other: "{{count}} connectors, {{enabled}} switched on",
      testingConnection: "Testing connection",
      configPlaceholder: '{"key": "value"}  -- leave empty to clear',
      editCadence: "Edit cadence",
      // D2 -- fourth round. A repaired connector showed the new config
      // beside the old error and offered nothing; the escalation line
      // named the administrator to himself.
      settingsChangedSinceRun: "Settings changed since the last run.",
      runNowToCheck: "Run now to check",
      openGuide: "What this connector needs",
      noOwnerRecorded: "No owner is recorded for this data source.",
      recordAnOwner: "Record one in Bank Setup",
      loading: "Loading…",
      updating: "Updating…",
      enableSelected: "Enable selected",
      disableSelected: "Disable selected",
      unknown: "unknown",
      queueing: "Queueing…",
      runNow: "Run now",
      disable: "Disable",
      enable: "Enable",
      saving: "Saving…",
      set: "Set",
      stored: "stored",
      connectors: "Connectors",
      everyRecurringDataFeedWiredToThe:
        "Every recurring data feed wired to the bank. Status pill reflects last-run posture against the connector cadence. Click a row to see run history; click “Run now” to fire a one-shot import.",
      connectASystem: "Connect a system",
      refresh: "Refresh",
      selected: "selected",
      clear: "Clear",
      failedToLoad: "Failed to load:",
      noConnectorsRegisteredYet: "No connectors registered yet.",
      status: "Status",
      source: "Source",
      kind: "Kind",
      cadence: "Cadence",
      lastRun: "Last run",
      lastSuccess: "Last success",
      errors: "Errors",
      disabled: "disabled",
      audit: "Audit",
      lastError: "Last error:",
      loadingRunHistory: "Loading run history…",
      failedToLoadHistory: "Failed to load history:",
      // Round 9 B3: a flat denial sat under a header reading "just now

      // (failed)". If the view has not got the run yet, say that.

      noRunsYetForThisConnector:
        "No runs recorded in this view yet -- reopen the row if one has just been triggered.",
      started: "Started",
      trigger: "Trigger",
      total: "Total",
      ins: "Ins",
      upd: "Upd",
      skip: "Skip",
      err: "Err",
      edit: "Edit",
      save: "Save",
      cancel: "Cancel",
      configNonSecret: "Config (non-secret)",
      noConfig: "no config",
      credentials: "Credentials",
      encryptionKeyNotSet: "encryption key not set",
      testConnection: "Test connection",
      secretsCanAposTBeStoredUntil:
        "Secrets can’t be stored until MARSAD_CREDENTIAL_ENCRYPTION_KEY is set on the backend. Until then, set values as env vars.",
      text: " *",
      secret: "secret",
    },
    connectionSetupView: {
      turnItOn: "Turn it on",
      credentialsAndProveTheyWork: "Credentials, and prove they work",
      testingConnection: "Testing connection",
      noThirdPartyVendorTo:
        "No third-party vendor to connect to: MARSAD publishes this payload contract and ships a reference exporter. The full path is exercised against that exporter.",
      theFieldListIsExact:
        "The field list is exact, but these vendor-side steps come from documentation rather than a connection we have made ourselves.",
      unverified: "unverified",
      contract: "contract",
      set: "set",
      notSet: "not set",
      stepsNotYetVerifiedAgainstALive: "steps not yet verified against a live system",
      contractWePublishExercisedEndToEnd: "contract we publish -- exercised end to end",
      // C5 -- "configured" oversells a connector that is switched off.
      readinessOff: "{{state}}, switched off",
      /* ROUND 13 H -- the four readiness states, in words rather than
         as their own enum tokens. Eleven of thirteen read "configured"
         on a morning when the bank had configured nothing: the state
         means credentials RESOLVE, which they do from the deployment's
         environment, and the word claimed an act nobody had performed.
         It is also the only place on this console where an identifier
         was reaching the reader, so no catalogue could translate it. */
      readiness: {
        not_configured: "credentials missing",
        configured: "credentials resolve, never run",
        verified: "delivered data",
        failing: "failing",
      },
      verifiedAgainstALiveSystem: "verified against a live system",
      knownTraps: "Known traps",
      cannotReachARealSystemYet: "This connector cannot reach a real system yet",
      vendorDocumentation: "Vendor documentation ↗",
      optional: "(optional)",
      saveConfiguration: "Save configuration",
      clear: "Clear",
      cleared: "{{label}} cleared",
      currently: "Currently: {{value}}",
      marsadCredentialEncryptionKeyIsNotSet:
        "MARSAD_CREDENTIAL_ENCRYPTION_KEY is not set, so nothing entered here can be stored. Set it and restart the backend first — see Bank Setup.",
      save: "Save",
      testConnection: "Test connection",
      saveAndTestConnection: "Save and test connection",
      savingBeforeTest: "Saving what you typed...",
      notTestedSaveFailed: "Not tested: saving what you typed failed ({{reason}}).",
      mustBeValidJson: "{{label}} must be valid JSON",
      // E1 -- a failed test left only a toast; once it faded the panel
      // said the step "has not passed yet", which reads as "you have
      // not run it" and blames the credentials by implication.
      testFailedHeading: "The connection test ran and did not pass.",
      /* D9 -- when the system that refused us is THIS deployment, the
         permission to fix is ours and sending the operator to find its
         administrator sends him to himself. */
      testFailedOurs:
        "This request went to MARSAD's own API. If it names a permission or a role, it is one of ours -- check the service account and its scopes in Admin > Service accounts.",
      testFailedEscalate:
        "This is what the system answered. If it names a permission or a role, the person who administers that system is who unblocks it -- not MARSAD.",
      theConnectionTestHasNotPassedYet:
        "The connection test has not passed yet, so there is nothing worth turning on. Fix the credentials above and test again.",
      connectedIn: "✓ Connected in",
      ms: "ms",
      pollEvery: "Poll every",
      seconds: "seconds",
      enableOnThisCadence: "Enable on this cadence",
      runItNow: "Run it now",
      connectASystem: "Connect a system",
      credentialsThenProofItWorksThenTurn:
        "Credentials, then proof it works, then turn it on. Manage everything already connected in",
      connectors: "Connectors",
      runAConnectionTestFirstEnablingA:
        "Run a connection test first — enabling a connector that cannot reach its system just fails quietly on a timer.",
    },
    dataDictionaryView: {
      // Round 18 F10 -- the template toasts, from the catalogue.
      templateDownloaded: "Template downloaded: {{entity}}",
      templateDownloadFailed: "Template download failed: {{entity}}",
      /* Round 10 D2 -- a graph edge's heading is the pair of ends it
         joins. It was composed server-side as English prose with an
         ASCII arrow between them; the ends are catalogue keys now,
         joined by the mirroring separator. */
      hop: {
        ibs: "Business service",
        service: "Service",
        capability: "Capability",
        data_asset: "Data asset",
        funding_resource: "Funding resource",
        cif: "Funding function",
        regulatory_commitment: "Regulatory commitment",
        ibs_group: "{{serviceTermShort}} group",
        // Round 14 F3 -- three import kinds rendered the raw key.
        configurationItem: "Configuration item",
        vendor: "Vendor",
        channel: "Channel",
      },
      downloading: "Downloading...",
      downloadCsvTemplate: "Download CSV template",
      required: "required",
      optional: "optional",
      sama: "SAMA",
      eG: "e.g.",
      graphEdge: "Graph edge",
      childRecord: "Child record",
      naturalKey: "Natural key:",
      fields: "fields ·",
      required2: "required ·",
      samaMandated: "SAMA-mandated",
      field: "Field",
      type: "Type",
      req: "Req",
      description: "Description",
      dataDictionary: "Data dictionary",
      theFieldsMarsadExpectsForEachFoundation:
        "The fields MARSAD expects for each Foundation entity, the formats, and which are SAMA-mandated. Download a CSV template to prepare a bulk upload, then bring it in through Data import.",
      goToDataImport: "Go to data import →",
      // Round 20 W4 -- what does not import from a file, and why.
      notImportable: {
        heading: "Not imported from a file",
        open: "Open →",
        risk: {
          short: "risks",
          label: "Risks (the risk register)",
          reason:
            "MARSAD holds no risk register. The bank's GRC tool stays the system of record for risks, their ratings and their treatment; MARSAD reads what they rest on -- services, configuration items, suppliers and controls -- and answers for resilience and compliance against them.",
          route:
            "Record the appetite the bank has set, by category, in Risk appetite statements.",
        },
        finding: {
          short: "findings",
          label: "Findings (audit, inspection and self-identified)",
          reason:
            "A finding is an event on the estate, not a register row: it lands on the configuration item, service or supplier it concerns and drives the cascade from there. Findings arrive from a connected system, so each keeps its source and its reference and updates in place when the source changes; a file import would land them once and leave them to go stale.",
          route:
            "Connect the GRC tool (the Archer connector reads Findings and lands each on the record it links), or raise a finding by hand from Raise a finding.",
        },
      },
      loadingDictionary: "Loading dictionary...",
      couldNotLoadTheDataDictionary: "Could not load the data dictionary.",
    },
    adminHome: {
      // Round 15 I7 -- the console's tiles were component literals a
      // translator could not reach.
      sections: {
        observability: "Observability",
        ceremony: "Ceremony",
        catalogue: "Catalogue",
      },
      tiles: {
        edgeDelays: {
          name: "Edge propagation delays",
          desc: "How long a failure takes to cross an edge; undeclared edges use the tenant hop delay",
        },
        triggers: {
          name: "Trigger rules",
          desc: "Metric, comparator and threshold, raising an edge-triggered alert on every channel",
        },
        mockExam: {
          name: "Mock field exam",
          desc: "Rehearse the {{regulatorShort}} exam: ranked likely findings + Engine working-paper notes",
        },
        auditEvents: {
          name: "Audit events",
          desc: "Every admin action + system-derived write",
        },
        connectors: {
          name: "Connectors",
          desc: "Recurring data feeds + manual trigger",
        },
        users: {
          name: "Users and roles",
          desc: "Who holds admin, a position, a job role, a module licence",
        },
        settings: {
          name: "Settings audit",
          desc: "Live env config + app_settings rows",
        },
        serviceAccounts: {
          name: "Service accounts",
          desc: "B2B API keys + scopes for the external read API",
        },
        obligationEngine: {
          name: "Obligation Engine",
          desc: "Run + watch the catalogue-wide Engine passes (summarise / tag)",
        },
        twinDerive: {
          name: "Chain derivers",
          desc: "Run + watch the twin chain derivers (blast / resilience / process / obligation)",
        },
        engineRuns: {
          name: "Engine batch runs",
          desc: "Unified hub: trigger / watch / cancel every Engine batch across modules",
        },
        engineActivity: {
          name: "Engine activity ledger",
          desc: "Tamper-evident, grounded record of every sealed Engine output",
        },
        twinCalibration: {
          name: "Twin calibration",
          desc: "Modelled recovery vs actuals from closed incidents + completed drills",
        },
        bankSetup: {
          name: "Bank Setup",
          desc: "Day-1 onboarding: branding for outputs + the data-source register",
        },
        license: {
          name: "Deployment license",
          desc: "Module-entitlement status + install/renew the signed license",
        },
        examinerGrants: {
          name: "Examiner access",
          desc: "Mint time-boxed examiner grants + export the offline examiner pack",
        },
        mvb: {
          name: "{{mvbTermShort}} declarations",
          desc: "{{mvbTerm, capitalize}} ceremony register",
        },
        board: {
          name: "Board snapshot",
          desc: "The resilience picture for executives, on one page",
        },
        dossier: {
          name: "Audit dossier",
          desc: "Signed regulator zip (trigger from Home)",
        },
        dataDictionary: {
          name: "Data dictionary",
          desc: "Expected fields + formats + CSV templates for bulk import",
        },
        dataImport: {
          name: "Data import",
          desc: "Upload a CSV, review the dry-run, commit; SAMA completeness",
        },
        dataBaseline: {
          name: "Data baseline",
          desc: "Linkage + completeness + discrimination composed; feeds score confidence",
        },
        frameworkImport: {
          name: "Framework import",
          desc: "Bring your own framework + control catalogue via CSV",
        },
        documents: {
          name: "Documents",
          desc: "The document locker -- upload, hash-chain + extract the bank's corpus",
        },
        ibses: {
          name: "{{serviceTermShort}} services",
          desc: "The {{serviceTermPluralTitle}} register",
        },
        services: {
          name: "Services",
          desc: "Internal service register + classifications",
        },
        capabilities: {
          name: "Capabilities",
          desc: "BIAN-aligned capability taxonomy",
        },
        vendors: {
          name: "Vendors",
          desc: "Third-party register + dependencies",
        },
        products: {
          name: "Products",
          desc: "Product catalogue + annual revenue exposure",
        },
        cis: {
          name: "Configuration items",
          desc: "CIs linking services to hosts + connectors",
        },
        channels: {
          name: "Channels",
          desc: "Customer channels the {{serviceTermShortPlural}} are delivered via",
        },
        journeys: {
          name: "Customer journeys",
          desc: "End-to-end customer flows over {{serviceTermShortPlural}}",
        },
        recoveryTeams: {
          name: "Recovery teams",
          desc: "Who restores a service, and how much at once",
        },
        facilities: {
          name: "Facilities",
          desc: "Sites services run in, and which are recovery sites",
        },
      },
      readingAdminSignals: "Reading admin signals...",
      connectors: "connectors",
      needAttention: "need attention",
      auditEvents24h: "audit events / 24h",
      appSettings: "app_settings",
      framingAdmin: "Framing · Admin",
      adminConsole: "Admin console",
      crossCuttingToolsForRunningTheBank:
        "Cross-cutting tools for running the bank’s MARSAD deployment: live observability over data feeds + audit trail, regulator-facing ceremony surfaces, and the Foundation catalogue editors.",
    },
    iBSPortfolioView: {
      // Round 16 L1 -- functions under their own heading and count.
      functionsHeading: "Critical internal functions",
      function: "Critical internal function",
      // Stitching review 5.4 -- the posture's words: in the register is
      // admitted, under assessment is candidates.
      functionsCount_one:
        "{{count}} critical internal function admitted, {{under}} under assessment",
      functionsCount_other:
        "{{count}} critical internal functions admitted, {{under}} under assessment",
      // P428.2 -- the group a service is governed in.
      inGroup: "in {{group}}",
      never: "never",
      /* Round 10 F3 -- a dash beside "at risk" and "within" read as a
         clean bill of health on an IBS nothing had judged. */
      notJudged: "no tolerance declared",
      /* Round 11 F1 -- the same cell, for an IBS whose tolerance IS
         declared and is waiting on a signature. The monitor judges
         against the number in force; a proposal is not one yet. */
      notInForce: "declared, not yet in force",
      /* ...and the resilience column's own empty state, which is a
         different fact: nothing has been sealed for this IBS yet. */
      notDerived: "not yet derived",
      /* Round 14 B1 -- a sealed "unknown": the deriver ran and found
         nothing watching this IBS, so it would not call it green. */
      notAssessed: "not assessed",
      ibsPortfolio: "{{serviceTermShort}} portfolio",
      everyImportantBusinessServiceOnOneScreen:
        "Every {{serviceTerm}} on one screen: admission, tier, resilience, and the",
      standing: "standing",
      liveToleranceVerdictFromContinuousMonitoring:
        "live-tolerance verdict from continuous monitoring -- worst first.",
      loading: "Loading…",
      portfolioUnavailable: "Portfolio unavailable.",
      inRegister: "{{serviceTermShortPlural}} admitted",
      underAssessment_one: "{{count}} under assessment ·",
      underAssessment_other: "{{count}} under assessment ·",
      mvb: "{{mvbTermShort}}",
      mvbAdmitted_one: "{{count}} {{mvbTermShort}} service admitted",
      mvbAdmitted_other: "{{count}} {{mvbTermShort}} services admitted",
      inLiveToleranceBreach: "in live tolerance breach",
      resilienceRed: "resilience red",
      warRoom: "War room",
      ibs: "{{serviceTermShort}}",
      admission: "Admission",
      tier: "Tier",
      resilience: "Resilience",
      tolerance: "Tolerance",
      livePosture: "Live posture",
      findings: "Findings",
      lastDrill: "Last drill",
    },
    mvbFindingsPanel: {
      hurtingTheMvbNow: "Hurting the {{mvbTermShort}} now",
      // Round 17 H40.
      summary_one:
        "{{count}} open finding across the declared {{mvbTermShort}} scope -- {{critical}} critical, {{high}} high, touching {{affected}}.",
      summary_other:
        "{{count}} open findings across the declared {{mvbTermShort}} scope -- {{critical}} critical, {{high}} high, touching {{affected}}.",
      affected_one: "{{count}} {{mvbTermShort}} {{serviceTermShort}}",
      affected_other: "{{count}} {{mvbTermShort}} {{serviceTermShortPlural}}",
      // Round 17 P1 -- of those, the admitted ones (home's figure).
      admittedOfThem: "{{count}} of them admitted.",
      /* Round 18 P7 -- the declaration's scope split three ways, so a
         function admitted moves the figure ("3 of them admitted" counted
         services only and did not move when Treasury was admitted). */
      affectedScope_one: "{{count}} in the declaration's scope: {{parts}}",
      affectedScope_other: "{{count}} in the declaration's scope: {{parts}}",
      scopeAdmittedServices_one: "{{count}} admitted service",
      scopeAdmittedServices_other: "{{count}} admitted services",
      scopeAdmittedFunctions_one: "{{count}} admitted function",
      scopeAdmittedFunctions_other: "{{count}} admitted functions",
      scopeCandidates_one: "{{count}} candidate",
      scopeCandidates_other: "{{count}} candidates",
      on: "on",
      // D2 -- was baked into the sentence, so it reordered away from
      // the host it qualifies.
      via: "via",
      reaches: "reaches",
      reachesMembers_one: "reaches {{count}} {{mvbTermShort}} member",
      reachesMembers_other: "reaches {{count}} {{mvbTermShort}} members",
      more: "more",
    },
    iBSAdmissionView: {
      stable: "stable",
      new: "new",
      admitted: "admitted",
      bizCrit: "· biz-crit",
      refresh: "· refresh",
    },
    mvbCandidatesView: {
      defaultsToWorstCaseUntil:
        "Defaults to worst case until Business Continuity records a validated workaround",
      notACandidate: "not a candidate",
      manual: "(set by hand)",
      /* Round 19 H55 -- the recommendation in words. */
      recommendation: {
        include: "Include",
        defer: "Defer",
        exclude: "Exclude",
        escalate: "Escalate",
      },
      pendingBc: "pending BC",
      gate1: "Gate 1",
      gate2: "Gate 2",
      gate2Function:
        "(a critical internal function is not seen by customers: this gate reads its financial throughput)",
      applyManualScores: "Apply manual scores",
      reScoreFromSignals: "Re-score from signals",
      approved: "Approved -- challenged and dual-signed.",
      approvedChallengedBy: "Approved -- challenged by",
      signedBy: ", signed by",
      cro: "(CRO) +",
      ae: "(AE).",
      challengedBy: "Challenged by",
      cro2: "CRO:",
      pending: "pending",
      ae2: "AE:",
      ibs: "{{serviceTermShort}} ·",
      servicesClosureGaps: "services) · Closure gaps:",
      journeysCovered: "· Journeys covered:",
      aligned: "aligned ·",
      pendingApproval: "pending approval ·",
      demotionCandidates: "demotion candidates",
      pending2: "Pending:",
      demotion: "Demotion:",
      dependencyClosureGaps: "Dependency-closure gaps",
      dependsOn: "depends on",
      escalate: "Escalate",
      loadingPack: "Loading pack...",
      noEvidencePackYetReScoreTo: "No evidence pack yet -- re-score to generate one.",
      drift: "Drift:",
      was: "(was",
      v: "v",
    },
    fieldExamView: {
      /* Round 12 B1 -- this awarded an empty bank seven OK ticks out
         of eleven checks, each reasoned "register empty". The reason is
         honest and the tick beside it was not: a check that could not
         run has not passed, and of the five surfaces that painted green
         over an unassessed estate this is the one that turned it into a
         supervisory pass. */
      notAssessed: "NOT ASSESSED",
      notAssessedCount_one: "{{count}} could not be assessed",
      notAssessedCount_other: "{{count}} could not be assessed",
      ok: "OK",
      flagged: "Flagged",
      /* ROUND 13 H, thirteenth round -- the tab is in the Service
   Owner's navigation and the page asks nothing of him. It is a
   read surface by design and the hostnames are the bank's own;
   what was missing was the sentence saying whose instrument it
   is, and where his own answer lives. */
      whoseInstrument:
        "This is the resilience lead's instrument: it asks the questions a supervisory examiner asks of the whole estate, and there is nothing on it to fill in.",
      whoseInstrumentYours: "See how your own services stand →",
      fieldExamReadiness: "Field-exam readiness",
      theQuestionsASamaOperationalResilienceField:
        "The questions a SAMA operational-resilience field examination actually asks, answered over the live estate: do your classifications",
      discriminate: "discriminate",
      doThey: ", do they",
      cohere: "cohere",
      acrossLayersAndIsEveryCriticalComponent:
        "across layers, and is every critical component",
      tested: "tested",
      withItsImpactOnDeliveryChannelsAssessed:
        "with its impact on delivery channels assessed. Every flag names its rows.",
      loading: "Loading…",
      checksUnavailable: "Checks unavailable.",
      of: "of",
      supervisoryChecksFlagged: "supervisory checks flagged",
      // D4 -- stitched from three renders, so the total drifted away
      // from the word it counts.
      ofChecksFlagged: "of {{total}} supervisory checks flagged",
      // ROUND 15 B6 -- the header is the sum of the rows, per section.
      ofDistributionChecksFlagged: "of {{total}} distribution checks flagged",
      coherenceFlagsCount_one: "{{count}} coherence flag",
      coherenceFlagsCount_other: "{{count}} coherence flags",
      // ROUND 15 B8 -- not an all-clear over empty registers.
      nothingToReconcileYet:
        "Nothing to reconcile yet: the registers this check joins are empty.",
      criticalComponentTestCoverage: "critical-component test coverage",
      classificationDiscrimination: "Classification discrimination",
      crossLayerCoherence: "Cross-layer coherence",
      noCoherenceContradictionsClassificationsReco:
        "No coherence contradictions -- classifications reconcile across the joins.",
      criticalComponentTestCoverage2:
        "What exists per critical service: a scenario defined, a drill passed",
      scenarioDefined: "Scenario defined",
      // Round 14 D2 -- one coverage figure, P419's, on every surface.
      testedCoverage:
        "tested coverage {{pct}}% ({{tested}} of {{required}} required operation-and-scenario pairs tested).",
      testedCoverageLink: "Test runs",
      criticalService: "Critical service",
      scenario: "Scenario",
      drill: "Drill",
      blastSealed: "Blast sealed",
      channelsReached: "Channels reached",
      ciGrainTargets: "CI-grain targets",
      ciGrainTargetsThisServiceSTechnology:
        "CI-grain targets = this service’s technology components directly targeted by a scenario -- the component grain a field examination tests at.",
    },
    warRoomView: {
      sar: "sar",
      mvb: "{{mvbTermShort}}",
    },
    scenariosView: {
      // Round 19 H55 -- where a scenario came from, in words.
      fromRegulatorTemplate: "From a {{regulator}} template",
      fromTemplate: "From a template",
      // Round 18 R8 -- the regulator's ask a scenario answers.
      regimeRefs: {
        heading: "The regulator's ask it answers",
        none: "No citation is recorded.",
        label: "Citation",
        placeholder: "e.g. SAMA BCM Framework, 2.6",
        add: "Cite",
        remove: "Withdraw",
        failed: "The citation was not recorded: {{reason}}",
      },
      // P418 -- the severe-but-plausible record, written at last.
      basis: {
        heading: "Severe but plausible",
        record: "Record the basis",
        edit: "Edit",
        none: "No basis is recorded for why this scenario is severe but plausible.",
        owner: "Owner",
        notNamed: "not named",
        approvedBy: "Approved by {{name}} on {{date}}",
        notApproved: "Nobody is named as approving it.",
        nextReview: "Next review",
        notSet: "not set",
        basisLabel: "Why it is severe but plausible",
        clearsApproval:
          "Changing the basis clears its approval; it will need approving again.",
        approverLabel: "Approved by",
        approverHint: "The approval date is recorded when you save, not typed.",
        save: "Save",
        cancel: "Cancel",
        error: "The basis could not be saved.",
      },
      // E2 -- deleting a scenario finished in silence, and the one
      // action that removes something is where a confirmation
      // matters most.
      runInWarRoom: "Run this in the war room",
      recordTest: "Record a test of it",
      deletedToast: "Deleted the scenario {{name}}.",
      deleteFailed: "That scenario could not be deleted.",
      unknown: "unknown",
      none: "none",
      deleted: "(deleted)",
      scenarioLibrary: "Scenario library",
      instantiate: "+ Instantiate",
      loadingScenarios: "Loading scenarios…",
      noScenariosYet: "No scenarios yet.",
      click: "Click",
      toPickATemplate: "to pick a template.",
      impacts: "impacts",
      pickAScenarioFromTheLeftRail:
        "Pick a scenario from the list to see the counterfactual cascade.",
      couldNotLoadScenario: "Could not load scenario:",
      loading: "Loading…",
      delete: "Delete",
      members: {
        heading: "Concurrent members",
        none: "This scenario carries no members, so it runs on its own impacts.",
        pick: "Add a member scenario",
        offsetLabel: "Hours after onset",
        offset: "+{{n}}h",
        add: "Add member",
        /* ROUND 15 D12 -- it has not left the library; it is marked
           there with where it sits. */
        added: "Added. It is part of {{composite}} now, and the library marks it so.",
        addFailed: "That member could not be added.",
        remove: "Remove",
        removedStill: "Removed. It is still part of {{names}}.",
        removed: "Removed, and returned to the library.",
        /* ROUND 15 D12 -- it does not leave the library; it is marked
           there with where it sits, so a register of nine mandated
           scenarios still counts nine. */
        note: "A composite reports on its members' impacts, each offset from THIS scenario's onset. Joining marks a scenario as part of this one: the library still lists it, marked with where it sits, so a register of nine mandated scenarios reads as nine and not as eleven. Composition is one level deep.",
      },
      narrative: "Narrative",
      regulatorAlignment: "Regulator alignment:",
      impacts2: "Impacts (",
      noImpactsOnThisScenario: "No impacts on this scenario.",
      /* ROUND 15 D12 -- a member is marked with where it sits rather
         than pretended out of the library. */
      partOf: "Part of {{names}}",
      /* ROUND 15 D14 -- what the source states about a target's time,
         readable by everyone and settable by an operator. Criterion
         22's controlled case (clear the 8h, watch the floored verdicts
         move back) was an UPDATE nobody could run from the product. */
      duration: {
        stated: "{{hours}}h stated",
        statedRange: "{{hours}} to {{max}}h stated",
        // Round 17 D23 -- the bank's own figure.
        set: "{{hours}}h set by the bank",
        setRange: "{{hours}} to {{max}}h set by the bank",
        untilRecovered: "until recovered",
        unstated: "no duration stated",
        capacity: "{{pct}}% capacity lost",
        edit: "Set duration",
        basisLabel: "Basis",
        basis: {
          stated: "Stated by the source",
          statedByBank: "Set by the bank",
          until_recovered: "Until recovered",
          unstated: "Not stated",
        },
        hours: "Hours",
        max: "Upper bound (hours)",
        save: "Save duration",
        cancel: "Cancel",
        saved: "Duration saved. The verdicts read a stated duration as a floor.",
        failed: "That duration could not be saved.",
      },
      counterfactualCascade: "Counterfactual cascade",
      computingCascade: "Computing cascade…",
      noCascadeNodesTheScenarioSImpacts:
        "No cascade nodes -- the scenario's impacts don't connect through the live entity graph at this as_of.",
      /* Round 19 P18 -- the cards state their own reach; the war room
         judges their union, which no card stated. */
      reachUnion_one:
        "Across these cards: {{count}} admitted {{serviceTermShort}} reached ({{all}} with candidates{{fn}}) -- the set the war room judges.",
      reachUnion_other:
        "Across these cards: {{count}} admitted {{serviceTermShortPlural}} reached ({{all}} with candidates{{fn}}) -- the set the war room judges.",
      /* Round 20 P24 -- what the scenario strikes, then what it reaches
         beyond: "3 admitted reached" sat beside five admitted cards,
         because the reach counted only what lies downstream of them. */
      struckReached:
        "Struck directly: {{struck}}{{fnStruck}}. Reached downstream of them: {{reached}} admitted ({{all}} with candidates{{fn}}).",
      struckServices_one: "{{count}} admitted service",
      struckServices_other: "{{count}} admitted services",
      struckFunctions_one: " and {{count}} critical internal function{{status}}",
      struckFunctions_other: " and {{count}} critical internal functions{{status}}",
      struckUnderAssessment: " under assessment",
      reachUnionFunctions_one: ", and {{count}} critical internal function",
      reachUnionFunctions_other: ", and {{count}} critical internal functions",
    },
    cAPDetailView: {
      middot: "·",
      times: "×",
      loadingCap: "Loading CAP...",
    },
    letterIntakeView: {
      loading: "Loading...",
    },
    queueView: {
      unknownQueuePosition: "Unknown queue position:",
      expectedOneOfPreparerReviewerApprover:
        ". Expected one of preparer / reviewer / approver.",
      loadingQueue: "Loading queue...",
    },
    analyserPanel: {
      dispatchNeedsBoth: "Pick a control and paste an excerpt to dispatch.",
      dispatchNeedsControl: "Pick a control to dispatch.",
      dispatchNeedsExcerpt: "Paste an excerpt to dispatch.",
      // Round 15 A11 -- a queued row names who queued it and offers cancel.
      queuedBy: "queued by {{name}}",
      // Round 19 A45 -- a run this reader dispatched.
      yours: "yours",
      cancelRun: "Cancel",
      cancelledToast: "Run cancelled before it started. Nothing was recorded.",
      cancelFailedToast: "That run could not be cancelled",
      // Round 14 G -- the run toasts, catalogued.
      toastSucceeded: "Analyser run succeeded",
      toastAutoSucceeded: "Auto-analyser run succeeded",
      toastFailed: "Analyser run failed",
      toastQueued: "Analyser queued",
      toastQueuedDetail:
        "If it has not started within two minutes it is failed and you can dispatch it again.",
      toastBatchQueued: "Batch analyser queued",
      toastBatchQueuedDetail:
        "Will grade up to {{count}} controls in display order. If it has not started within two minutes it is failed and you can dispatch it again.",
      toastAutoFailed: "Auto-analyser run failed",
      toastBatchGraded_one: "Batch graded {{count}} control.",
      toastBatchGraded_other: "Batch graded {{count}} controls.",
      toastOpenRun: "Open the run to review the suggestion.",
      askAnAdministratorToConfigure:
        "Ask an administrator to configure the Marsad Engine for this environment.",
      cycle: "Cycle",
      singleControl: "single_control",
      singleControl2: "Single control",
      batchCycleWalk: "Batch (cycle walk)",
      batchCycle: "batch_cycle",
      unknown: "unknown",
      dispatching: "Dispatching…",
      queued: "Queued…",
      dispatchAnalyser: "Dispatch analyser",
      dispatchBatchRun: "Dispatch batch run",
      batch: "Batch",
      single: "Single",
      unknownControl: "(unknown control)",
      // Round 16 A26 -- a design test in the run list.
      designTestRun: "Design test",
      designTestNote:
        "This is a Test of Design. Its proposal is accepted or declined on the control, not here.",
      designTestOpen: "Open the control",
      failed: "failed",
      analyserFailed: "Analyser failed.",
      succeeded: "succeeded",
      accepting: "Accepting…",
      // D1 -- Accept overwrote a preparer's judgement in silence.
      confirmHeading: "This replaces what is recorded. Compare before you accept.",
      /* Round 12 D2 -- the excerpt the judgement was made FROM, printed
         before the accept. This is the one Engine surface whose output
         goes word for word onto a bank's assessment row, and it was the
         only one with no guard on it at all: one unverified typed
         sentence and a whole policy document produced the same-looking
         panel. */
      judgedFromTyped: "Judged from this text, typed here:",
      judgedFromArtefact: "Judged from this text, drawn from the attached evidence:",
      judgedFromDocuments:
        "Judged from this text, drawn from the documents covering the control:",
      columnCurrent: "Recorded now",
      columnSuggested: "The Engine suggests",
      confirmAccept: "Replace with the suggestion",
      nothingRecorded: "nothing recorded",
      acceptedToast: "Suggestion accepted and recorded as {{name}}.",
      undo: "Undo",

      /* C3 -- Undo restored in silence, so it read as a click that

         did nothing. */

      undoneToast: "Restored your judgement.",
      field: {
        judgement: "Judgement",
        maturity: "Maturity",
        summary: "Summary",
        gaps: "Gaps",
      },
      acceptSuggestion: "Accept suggestion",
      testing: "Testing...",
      testConnection: "Test connection",
      queueing: "Queueing…",
      runningTheMarsadEngineNeedsThePreparer:
        "Running the Marsad Engine needs the preparer position (or admin). Ask for the preparer role to dispatch a run from this page.",
      backToAssessmentGrid: "← Back to assessment grid",
      marsadEngine: "Marsad Engine --",
      singleControlAnalyserPickAControlPaste:
        // F -- "dispatch" and "UPSERT" are ours: one is a verb from the
        // job queue and the other is SQL.
        "Single-control analyser. Pick a control, paste an evidence excerpt, and run it. The Marsad Engine never writes to the assessment row -- accepting a suggestion is what records it.",
      newAnalyserRun: "New analyser run",
      searchControls: "Search controls",
      noControlsMatchTheFilter: "No controls match the filter.",
      maxControlsToGrade150: "Max controls to grade (1-50)",
      workerGradesControlsInDisplayOrderApproved:
        "Worker grades controls in display_order. Approved assessments are skipped. Materialises draft rows the preparer reviews in the grid.",
      evidenceExcerptPastePolicyParagraphControlRe:
        "Evidence excerpt (paste policy paragraph / control record)",
      dispatchFailed: "Dispatch failed:",
      analyserRuns: "Analyser runs",
      loadingRuns: "Loading runs…",
      noAnalyserRunsYetForThisCycle: "No analyser runs yet for this cycle.",
      in: "in /",
      outTokens: "out tokens",
      suggestedStatus: "Suggested status",
      ml: "· ML",
      summary: "Summary",
      gaps: "Gaps",
      recommendations: "Recommendations",
      batchRunMaterialised: "Batch run materialised",
      draftAssessment: "draft assessment",
      intoTheCycleReviewAndApproveIn:
        'into the cycle. Review and approve in the assessment grid; rows show an "AI-drafted" badge.',
      engineChecking: "Engine: checking...",
      engineNotConfigured: "Engine: not configured",
      engine: "Engine:",
    },
    cycleAssessmentGridView: {
      draft: "draft",
      // B10 -- the reading leads for anybody not holding a preparer seat.
      editThisGrade: "Edit this grade",
      open: "open",
      closed: "closed",
      nonCompliant: "non_compliant",
      nonCompliant2: "non-compliant",
      partial: "partial",
      inReview: "in_review",
      inSignoff: "in_signoff",
      approved: "approved",
      marsadEngine: "Marsad Engine",
      ai: "AI",
      thisControlIs: "This control is",
      raiseACapPreLinkedToIt:
        ". Raise a CAP pre-linked to it (the Engine drafts actions when configured), then pick it up in the preparer queue.",
      workflow: "Workflow (",
      state: ") -- state:",
    },
    frameworkAssessmentsView: {
      unknown: "unknown",
      creating: "Creating…",
      create: "Create",
      frameworkAssessments: "Framework assessments",
      samaAlignedFrameworkControlCataloguesPickA:
        "SAMA-aligned framework control catalogues. Pick a framework to open its assessment cycles.",
      loadingFrameworks: "Loading frameworks…",
      couldNotLoadFrameworks: "Could not load frameworks:",
      cycles: "cycles",
      newCycle: "+ New cycle",
      loadingCycles: "Loading cycles…",
      noCyclesYetForThisFramework: "No cycles yet for this framework.",
      click: "Click",
      newCycle2: "New cycle",
      aboveToStartOne: "above to start one.",
      newCycle3: "New cycle --",
      creatingWithDefaultWindow: "Creating with default window:",
      due: "(due",
      failed: "Failed:",
      cancel: "Cancel",
      /* Round 20 Y4 -- evidence older than its cycle, counted. */
      oldEvidence_one: "{{count}} assessment rests on evidence older than its cycle",
      oldEvidence_other:
        "{{count}} assessments rest on evidence older than their cycle",
      due2: "· due",
    },
    attestationsView: {
      draft: "draft",
      open: "open",
      submitted: "submitted",
      rejected: "rejected",
      text: "–",
    },
    adminEntityDrawer: {
      edit: "edit",
      create: "create",

      /* Round 13 C7 -- the picker offered 501 options against 2,404
   services and said nothing about the other 1,903. It still caps,
   because a 2,404-option native select is not usable either; what
   changed is that it says so. */
      optionsCapped_one:
        "Showing the first {{shown}}. 1 more exists -- search above to find it.",
      optionsCapped_other:
        "Showing the first {{shown}}. {{count}} more exist -- search above to find one.",

      noParent: "(no parent)",
      // Round 14 E4 -- a capped picker can reach the rest.
      pickerSearch: "Search by name or code",
      pickerMatches_one: "{{count}} match",
      pickerMatches_other: "{{count}} matches",
      currentSelection: "(current selection)",
      select: "Select...",
    },
    contractReviewPanel: {
      renewalReviewAttention: "Renewal / review attention",
    },
    serviceAnalyserSection: {
      dispatching: "Dispatching...",
      queued: "Queued...",
      nothingToSuggest: "Nothing to suggest",
      unknown: "(unknown)",
      applying: "Applying...",
      marsadEngineMethodologySuggestions: "Marsad Engine -- methodology suggestions",
      reviewLastSuggestions: "Review last suggestions",
      null: "NULL",
      engineSuggestionsFor: "Engine suggestions for",
      model: "Model:",
      prompt: "· prompt",
      in: "in /",
      outTokens: "out tokens",
      current: "Current",
      suggested: "Suggested",
      omittedTrackedAsOpenGaps: "Omitted (tracked as open gaps)",
      cancel: "Cancel",
    },
    twinDrillSection: {
      twin: "Twin",
      viewInTwinRarr: "View in Twin →",
    },
    ibsAdapter: {
      mvb: "{{mvbTermShort}}",
      sovereign: "sovereign",
    },
    impactToleranceDeclarations: {
      draft: "draft",
      creating: "Creating...",
      createDraft: "Create draft",
      signing: "Signing...",
      signedCro: "Signed: {{hours}}h. It now waits on the business owner.",
      signedOwner: "Signed: {{hours}}h. It now waits on the CRO.",
      signedApproved: "Signed: {{hours}}h is now the declared tolerance.",
      impactToleranceDeclarations: "Impact-tolerance declarations",
      newDeclaration: "+ New declaration",
      oneInFlight: "A declaration is already awaiting signature.",
      loadingDeclarations: "Loading declarations...",
      noImpactToleranceDeclarationsOnThisIbs:
        "No impact-tolerance declarations on this {{serviceTermShort}} yet (the war-room uses the seeded value until one is signed off).",
      hTolerance: "h tolerance",
      deleteDraft: "Delete draft",
      decided: "Decided",
      signed: "signed",
      adminRequiredToSignOff: "Admin required to sign off",
      signOff: "Sign off",
      declaredToleranceHours: "Declared tolerance (hours)",
      otherDeclaredDimensionsOptional: "Other declared dimensions (optional)",
      leaveBlankToDeclareNoToleranceOn:
        "Leave blank to declare no tolerance on a dimension. Each is evaluated against the same projected outage duration.",
      rationaleRequired: "Rationale (required)",
      declaredByOptional: "Declared by (optional)",
      cancel: "Cancel",
      signOffAs: "Sign off as",
      name: "Name",
      email: "Email",
    },
    journeyAdapter: {
      noNarrative: "no narrative",
    },
    outsourcingContractAdapter: {
      materialNocOnFile: "Material · NOC on file",
      materialNoSamaNoc: "Material · no SAMA NOC",
    },
    productAdapter: {
      notMeasured: "not measured",
    },
    serviceClassificationDecisions: {
      signingAs: "Signing as {{name}} ({{email}}) -- your session.",
      confirmSignoff: "Confirm {{role}} sign-off",
      draft: "draft",
      creating: "Creating...",
      createDraft: "Create draft",
      signing: "Signing...",
      classificationDecisions: "Classification decisions",
      newDecision: "+ New decision",
      loadingDecisions: "Loading decisions...",
      noClassificationDecisionsOnThisServiceYet:
        "No classification decisions on this service yet.",
      deleteDraft: "Delete draft",
      decided: "Decided",
      signed: "signed",
      requiresThe: "Requires the",
      role: "role",
      signOff: "Sign off",
      classification: "Classification",
      rationaleRequired: "Rationale (required)",
      evaluatorOptional: "Evaluator (optional)",
      cancel: "Cancel",
      signOffAs: "Sign off as",
      name: "Name",
      email: "Email",
    },
    vendorAdapter: {
      material: "material",
    },
    vendorPassportSection: {
      firstUse: "first_use",
      key: "key",
    },
    mvbDeclarationsRegister: {
      /* ROUND 15 C11 -- the badge reads the window, not only the ceremony. */
      inForceUntil: "in force until {{to}}",
      approvedFrom: "approved, from {{from}}",
      /* A4 -- the ceremony refuses before it runs, and says what the
         step before it is. Creating a declaration with nothing admitted
         locks the one-declaration slot over an empty set, which is
         exactly the wrong-order-then-unwind the Resilience Lead is
         afraid of. */
      nothingToDeclare:
        "Nothing to declare yet: no {{serviceTermShortPlural}} are admitted, so a declaration would cover an empty set.",
      nothingToDeclareCta: "Admit {{serviceTermShortA}} first",
      nothingToDeclareTitle:
        "Nothing to declare yet: no {{serviceTermShortPlural}} are admitted. Admit one first.",
      /* ROUND 13 H -- the other two disable reasons. The button was
         disabled with `title=None` on a fresh form, which is the
         ordinary state of one. */
      needsNameTitle: "Give the declaration a name first.",
      creatingTitle: "Creating the draft...",
      raised: "raised {{when}}",
      // Round 14 C2 -- the people on the row, and the window it held.
      signedBy: "signed by {{cro}} and {{ae}}",
      submittedBy: "submitted by {{who}}",
      window: "in force {{from}} to {{to}}",
      from: "from {{from}}",
      mvbDeclarations: "{{mvbTermShort}} declarations",
      theBankAposSMinimumViableBank:
        "The bank’s {{mvbTermTitle}} declaration is regulator-grade. Land a draft, route it through CRO + Accountable Executive (AE) dual sign-off, and the BIAN landscape’s {{mvbTermShort}} overlay surfaces the live story.",
      larrScoreMvbCandidates: "← Score {{mvbTermShort}} candidates",
      // Round 18 G22 -- said with none in flight, so it does not tell
      // the reader to finish one that does not exist.
      oneAtATime: "One declaration at a time.",
      supersedes: "A new declaration supersedes {{name}} once both sign.",
      // B4 -- the rule, stated where it BINDS. "Already in progress:"
      // named the blocker and dropped the rule, so the page stopped
      // explaining itself at precisely the moment the explanation
      // mattered, and the rule survived only in the 409 that follows
      // the click. Round 9 said this; round 10 found it gone.
      oneAtATimeBlocked:
        "One declaration at a time. Finish or delete the one in flight before starting another. Already in progress:",
      // The same rule as the disabled button's title, so a reader who
      // reaches for the control gets the reason from the control.
      blockedTitle:
        "Finish or delete \u201c{{name}}\u201d before starting another declaration.",
      newDraftName: "New draft name",
      createDraft: "Create draft",
      draftCreated:
        "Draft {{name}} created. It is the declaration in flight until it is submitted or withdrawn.",
      loadingRegister: "Loading register…",
      failedToLoadMvbDeclarations: "Failed to load {{mvbTermShort}} declarations:",
      noMvbDeclarationsYetCreateADraft:
        "No {{mvbTermShort}} declarations yet. Create a draft above to start the dual-signoff ceremony.",
    },
    mvbScorecardCard: {
      micro: "{{mvbTermShort}} declaration",
      couldnTLoadTheMvb: "Couldn't load the {{mvbTermShort}} scorecard.",
      loading: "Loading…",
      noActiveMvbDeclarationDriveOneThrough:
        "No active {{mvbTermShort}} declaration. Drive one through the dual-signoff ceremony to enable the {{mvbTermShort}} overlay on the BIAN landscape.",
      openMvbRegister: "Open {{mvbTermShort}} register →",
      active: "active",
      ibsesInMvbSet: "{{serviceTermShortPlural}} in {{mvbTermShort}} set",
      inMvbSet: "In the {{mvbTermShort}} set",
      effectiveFrom: "Effective from",
      gate1Threshold: "Gate-1 threshold",
      h: "h",
      reviewIn: "Review",
      reviewInDays_one: "in {{count}} day",
      reviewInDays_other: "in {{count}} days",
      reviewToday: "today",
      reviewOverdueBy_one: "overdue by {{count}} day",
      reviewOverdueBy_other: "overdue by {{count}} days",
      cro: "CRO",
      ae: "· AE",
      dualSigned: "dual-signed",
    },
    dataBaselineView: {
      /* Round 13 D10 -- a rung that caps the resilience score's
         confidence should say where the gap is closed. */
      fixThis: "Fix this",
      dataBaseline: "Data baseline",
      everyDerivedSurfaceBlastCascadeWarRoom:
        "Every derived surface -- blast, cascade, war-room, the resilience score -- is only as true as the CMDB and asset register beneath it. The baseline composes",
      structuralLinkage: "structural linkage",
      samaMandatedCompleteness: "SAMA-mandated completeness",
      and: "and",
      classificationDiscrimination: "classification discrimination",
      intoOneFigureThatFeedsTheScore:
        "into one figure that feeds the score’s confidence: weak source data caps the tier instead of flattering it.",
      loading: "Loading…",
      baselineUnavailable: "Baseline unavailable.",
      composedSourceDataBaseline: "composed source-data baseline",
      coherenceFlag: "coherence flag",
      onTheFieldExamView: "on the field-exam view",
      importDataCsvAssisted: "Import data (CSV / assisted)",
      dataDictionaryTemplates: "Data dictionary + templates",
      fieldExamReadiness: "Field-exam readiness",
      resilienceReadinessScore: "Resilience readiness + score",
    },
    engineActivityLedgerView: {
      grounded: "grounded",
      /* Round 17 A32 -- every surface that seals, by name; the ledger
         named four of them by their raw key. */
      surfaces: {
        assisted_import_mapping: "Import column mapping",
        cap_draft_actions: "CAP action drafting",
        cap_suggest_controls: "CAP control suggestion",
        control_compliance_assessor: "Control assessment",
        control_test_of_design: "Test of Design",
        test_of_design: "Test of Design",
        ibs_admission_score: "{{serviceTermShort}} admission scoring",
        letter_extract: "Letter reading",
        mock_exam: "Mock field exam notes",
        obligation_summarise: "Obligation summary",
        obligation_tag: "Obligation tagging",
        obligation_find_equivalents: "Cross-framework equivalents",
        regulatory_change_assess: "Regulatory change assessment",
        unknown: "Other Engine output",
      },
      adminEngineActivityLedger: "ADMIN -- ENGINE ACTIVITY LEDGER",
      engineActivityLedger: "Engine activity ledger",
      everySealedMarsadEngineOutputNewestFirst:
        "Every sealed Marsad Engine output, newest-first -- a tamper-evident, grounded record an examiner can verify against the chain. Grounded = how many of the Engine's citations were real sources.",
      loadingTheLedger: "Loading the ledger...",
      couldNotLoadTheEngineActivityLedger: "Could not load the Engine activity ledger.",
      noSealedEngineOutputsYet: "No sealed Engine outputs yet.",
      surface: "Surface",
      model: "Model",
      configuredDiffers: "(configured: {{configured}})",
      grounding: "Grounding",
      seal: "Seal",
      when: "When",
    },
    engineBatchRunsPanel: {
      /* Round 12 C1 -- the column printed the deployment's configured
         model and headed it "Model", which reads as a reading. Where
         the value is the configuration rather than a provider's own
         word the row says so, and where the two disagree it names both.
         A provenance field that can be changed without changing what it
         describes is not provenance. */
      modelConfiguredOnly: "(configured)",
      modelConfiguredOnlyHint:
        "The provider named no model in its response, so this is what the deployment asked for rather than what answered.",
      /* ROUND 15 A15 -- with its own leading space: a margin drew the
         gap and put nothing in the text, so "13:51Answered" and
         "mistral(configured: ...)" read glued to a copy and a reader. */
      running: "Running…",
      succeeded: "succeeded",
      failed: "failed",
      runFailed: "Run failed.",
      /* Round 16 A26 -- the server's own words, behind the product's. */
      technicalDetail: "Technical detail, as the server recorded it",
      runKind: "Run: {{name}}",
      /* Round 16 A26 -- the hub printed a driver's raw keys
         ("refused_questions: 4"). Labels for the keys the drivers
         return; anything else is humanised rather than printed raw. */
      /* Round 19 A45 -- one sentence per figure. */
      resultSentence: {
        total_one: "{{count}} in all",
        total_other: "{{count}} in all",
        processed_one: "{{count}} processed",
        processed_other: "{{count}} processed",
        derived_one: "{{count}} derived",
        derived_other: "{{count}} derived",
        skipped_one: "{{count}} skipped",
        skipped_other: "{{count}} skipped",
        sealed_one: "{{count}} sealed",
        sealed_other: "{{count}} sealed",
        unchanged_one: "{{count}} unchanged",
        unchanged_other: "{{count}} unchanged",
        summarised_one: "{{count}} summarised",
        summarised_other: "{{count}} summarised",
        tagged_one: "{{count}} tagged",
        tagged_other: "{{count}} tagged",
        extracted_one: "{{count}} extracted",
        extracted_other: "{{count}} extracted",
        mapped_one: "{{count}} mapped",
        mapped_other: "{{count}} mapped",
        auto_confirmed_one: "{{count}} confirmed automatically",
        auto_confirmed_other: "{{count}} confirmed automatically",
        failed_one: "{{count}} failed",
        failed_other: "{{count}} failed",
        questions_one: "{{count}} question asked",
        questions_other: "{{count}} questions asked",
        narrated_one: "{{count}} note written",
        narrated_other: "{{count}} notes written",
        refused_questions_one: "{{count}} question the Engine could not answer",
        refused_questions_other: "{{count}} questions the Engine could not answer",
        judged_one: "{{count}} question kept as judged",
        judged_other: "{{count}} questions kept as judged",
        dropped_ungrounded_one: "{{count}} note dropped as ungrounded",
        dropped_ungrounded_other: "{{count}} notes dropped as ungrounded",
        notes_flagged_for_review_one: "{{count}} note flagged for review",
        notes_flagged_for_review_other: "{{count}} notes flagged for review",
        notes_with_unsupported_figures_one: "{{count}} note with unsupported figures",
        notes_with_unsupported_figures_other:
          "{{count}} notes with unsupported figures",
      },
      /* Round 19 A45 -- kinds and modules in words. */
      kindName: {
        mock_exam: "Mock field exam notes",
        document_extract: "Document text extraction",
        document_map: "Document mapping",
        summarise: "Summarise obligations",
        tag_applicability: "Tag applicability",
        derive_ibs_admission: "Derive {{serviceTermShort}} admission",
      },
      moduleName: {
        dossier: "Evidence and examination",
        framework: "Framework",
        twin: "Operational Twin",
        shared: "Platform",
      },
      resultKey: {
        total: "total",
        processed: "processed",
        derived: "derived",
        skipped: "skipped",
        sealed: "sealed",
        unchanged: "unchanged",
        summarised: "summarised",
        tagged: "tagged",
        extracted: "extracted",
        mapped: "mapped",
        auto_confirmed: "confirmed automatically",
        failed: "failed",
        questions: "questions",
        narrated: "narrated",
        narratives: "notes",
        refused_questions: "questions the Engine could not answer",
        dropped_ungrounded: "notes dropped as ungrounded",
        notes_flagged_for_review: "notes flagged for review",
        notes_with_unsupported_figures: "notes with unsupported figures",
      },
      cancelled: "cancelled",
      cancel: "Cancel",
      recentRuns: "Recent runs",
      noRunsYet: "No runs yet.",
      module: "Module",
      kind: "Kind",
      status: "Status",
      progress: "Progress",
      model: "Model",
      result: "Result",
      retry: "Retry",
    },
    frameworkImportView: {
      // Round 18 F10 -- the staging toast, from the catalogue.
      stagedToast: "Staged {{ok}}/{{count}} rows",
      stagedErrors_one: "{{count}} row has errors -- review before commit",
      stagedErrors_other: "{{count}} rows have errors -- review before commit",
      reviewThenCommit: "Review then commit",
      committed: "Committed",
      committing: "Committing...",
      uploading: "Uploading...",
      uploadDryRun: "Upload + dry-run",
      rows: "rows ·",
      ok: "ok",
      errors: "errors",
      insert: "insert ·",
      update: "update",
      verdict: "Verdict",
      action: "Action",
      control: "Control",
      detail: "Detail",
      importACustomFramework: "Import a custom framework",
      bringYourOwnFrameworkControlCatalogueBeyond:
        "Bring your own framework + control catalogue beyond the reference SAMA frameworks. One row per control (the domain + subdomain travel on each row). Imported controls become assessable and obligation-extractable immediately.",
      downloadCsvTemplate: "Download CSV template",
      columnsFrameworkCodeFrameworkNameDomainId:
        "Columns: framework_code, framework_name, domain_id, domain_name, subdomain_id, subdomain_name, control_natural_id, control_text, default_target_ml (1-5, optional). Re-importing a control by its (framework, natural id) updates it.",
    },
    outboundFeed: {
      heading: "Outbound feed",
      intro:
        "What a third-party system -- your GRC platform, a risk data warehouse -- can pull out of MARSAD, at what grain, and how it is watched. Read-only: keys are minted by an administrator.",
      whatItExposes: "What the feed exposes",
      scope: "Scope",
      route: "Route",
      grain: "What a consumer gets",
      baseUrl: "Base URL:",
      howAConsumerWalksIt: "How a consumer walks it",
      cursor:
        "Registers are pulled oldest first. Each response carries next_since and next_since_id; a consumer passes both back to get the next page, and stops when truncated is false. Both halves are needed -- a timestamp alone cannot separate rows that share one, which a batch import produces by the hundred.",
      isItWatched: "Is it watched?",
      audited:
        "Yes. Every authenticated request is recorded with the key that made it, the route, the scope and how many records it took. You can read the trail in Audit events, filtered to ext.data.read.",
      gettingAKey: "Getting a key",
      requestFromAdmin:
        "Ask your platform administrator to mint a service-account key with the scopes above. Keys are shown once at creation and never again, and revoking one leaves the record of what it pulled intact.",
    },
    serviceAccountsView: {
      contract: {
        heading: "How to use this key",
        baseUrl: "Base URL",
        header: "Header",
        thisKeyOpens: "THIS KEY OPENS",
        cursor:
          "Paging: follow next_since and next_since_id from each response until truncated is false. That is the whole walk -- a timestamp alone cannot separate rows that share one.",
        example: "EXAMPLE",
        copied: "Example copied",
        audited:
          "Every request this key makes is recorded. You will see it in Audit events, and as a request count beside the key below.",
      },
      requests_one: "{{count}} request",
      requests_other: "{{count}} requests",
      creating: "Creating...",
      createKey: "Create key",
      enabled: "enabled",
      disabled: "disabled",
      never: "never",
      newKeyFor: "New key for",
      copyItNowItIsShownOnce:
        "Copy it now -- it is shown once and cannot be retrieved later.",
      copy: "Copy",
      dismiss: "Dismiss",
      newServiceAccount: "New service account",
      name: "Name",
      scopesReadOnly: "Scopes (read-only)",
      rotate: "Rotate",
      confirmRevoke: "Confirm revoke",
      revoke: "Revoke",
      serviceAccounts: "Service accounts",
      apiKeysForB2bConsumersAGrc:
        "API keys for B2B consumers (a GRC tool / Power BI) to pull a curated read-only slice of MARSAD via the external API (/v1/ext). Keys are shown once at create / rotate.",
      noServiceAccountsYet: "No service accounts yet.",
      key: "Key",
      scopes: "Scopes",
      status: "Status",
      lastUsed: "Last used",
      actions: "Actions",
    },
    edgeDelaysView: {
      heading: "Edge propagation delays",
      /* Round 10 D2 -- the two ends of each edge kind, joined by the
         mirroring separator instead of a hard-coded arrow. */
      hop: {
        channel: "Channel",
        ibs: "Business service",
        service: "Service",
        vendor: "Vendor",
      },
      // Says plainly what an undeclared edge does, so nobody reads a
      // blank row as "instant".
      /* Round 10 A5 -- this page deferred to "the tenant hop delay"
         four times and never printed it, so the figure every
         undeclared edge falls back to was the one the reader could not
         see. It is interpolated now, everywhere it is mentioned. */
      blurb:
        "How long a failure takes to cross one edge. Where a bank has " +
        "measured an edge, Cascade uses that figure; where it has not, " +
        "the tenant hop delay of {{minutes}} minutes applies.",
      /* Round 12 H4 -- this read is admin-gated and the page is
         reachable by a seat that is not one, so the refusal used to
         arrive as "0 of 0 edges carry a declared delay": a claim about
         the bank's estate made out of a permission error. */
      needsAdmin:
        "These rows need the admin role to read. This is a refusal, not an empty estate -- nothing here says how many edges the bank has.",
      readFailed:
        "The edge list did not load, so no count can be given. Reload, and if it keeps failing the administrator should check the connector and audit consoles.",
      blankIsNotZero:
        "Blank is not zero: zero claims the failure crosses instantly. Leave an edge blank until somebody has measured it.",
      declaredCount_one: "{{declared}} of {{total}} edges carries a declared delay.",
      declaredCount_other: "{{declared}} of {{total}} edges carry a declared delay.",
      filter: "Filter by service, vendor or edge kind",
      undeclared: "tenant default, {{minutes}} min",
      minutes: "min",
      saved: "Declared {{minutes}} minutes for this edge.",
      cleared:
        "Cleared -- this edge is back on the tenant hop delay of {{minutes}} minutes.",
      clear: "Clear",
      clearHint:
        "Remove this declaration and put the edge back on the tenant hop delay of {{minutes}} minutes. Not the same as declaring zero.",
      saveInvalid: "Whole minutes, zero or more.",
      saveReady: "Declare this delay.",
      saveUnchanged: "Type a delay in minutes, or clear the box, to enable this.",
      seeCascade: "See these delays at work in Cascade",
      tenantHopSetting: "Tenant hop delay ({{minutes}} min) in the settings audit",
    },
    triggersView: {
      firing2: "firing",
      disable: "Disable",
      enable: "Enable",
      triggerRules: "Trigger rules",
      watchAnyCuratedEstateMetricWhenIt:
        "Watch any curated estate metric: when it crosses your threshold the rule fires ONCE across every notification channel (and can demand acknowledgement), stays silent while breached, and re-arms on recovery.",
      name: "Name",
      metric: "Metric",
      comparator: "Comparator",
      threshold: "Threshold",
      severity: "Severity",
      requireAcknowledgement: "Require acknowledgement",
      addRule: "Add rule",
      rules: "Rules",
      evaluateNow: "Evaluate now",
      noRulesYet: "No rules yet.",
      firing: "FIRING",
      ok: "ok",
      last: "last",
      ackRequired: "ack required",
      disabled: "disabled",
      delete: "Delete",
      // P421 -- a rule that watches for a change rather than a number.
      onChange: "When a significant change of kind \u201c{{kind}}\u201d is raised",
      watching: "watching",
    },
    twinCalibrationView: {
      ibsRecoveryVsDrillActuals: "{{serviceTermShort}} recovery vs drill actuals",
      serviceRtoVsLiveTelemetry: "Service RTO vs live-telemetry actuals",
      serviceRtoVsIncidentActuals: "Service RTO vs incident actuals",
      applying: "Applying...",
      noObservedHistoryYetNeedClosedIncidents:
        ": no observed history yet (need closed incidents / completed drills).",
      entity: "Entity",
      observedMean: "Observed (mean)",
      samples: "Samples",
      error: "Error",
      suggestion: "Suggestion",
      override: "Override",
      adminTwinCalibration: "ADMIN -- TWIN CALIBRATION",
      twinCalibration: "Twin calibration",
      howCloseTheModelRunsToReality:
        'How close the model runs to reality. We replay closed incidents (actual outages), completed drills (actual recovery), and live-telemetry outage episodes (observed down → restore) and score them against the modelled RTOs -- the validation loop that makes "digital twin" more than a diagram.',
      failedToLoadTheCalibrationReport: "Failed to load the calibration report.",
      scoringTheModelAgainstHistory: "Scoring the model against history...",
      services: "services ·",
      ibses: "{{serviceTermShortPlural}} ·",
      telemetrySampled: "telemetry sampled",
      flaggedForAdjustment: "flagged for adjustment",
      meanAbsError: "mean abs error",
    },
    usersView: {
      // E5 -- positions are reflected at sign-in, so an empty cell
      // for somebody who has never signed in is not "holds none".
      notSeenYet: "not signed in yet",
      // D12 -- a bare "--" gave the same mark to "we do not know" and
      // "they hold none", which are different answers.
      holdsNone: "none held",
      admin: "admin",
    },
    sectorBenchmarkView: {
      aggregator: "aggregator",
      p25: "p25",
      p50: "· p50",
      p75: "· p75",
      n: "(n=",
    },
    boardSnapshotView: {
      mvb: "{{mvbTermShort}}",
    },
    serviceClassificationPanel: {
      heading: "Classification",
      notYetClassified:
        "This service has not been through a classification ceremony yet.",
      decidedOn: "decided {{when}}",
      nextReview: "Next review due {{when}}",
    },
    documentsView: {
      // Round 9 B4. "pending" with an em dash beside it is a state, not
      // an explanation. This says what the row becomes, why it matters
      // and roughly when -- so waiting is visibly the right thing to do.
      awaitingExtraction:
        "Text not extracted yet -- the Engine cannot read this document for control testing until it is. This usually takes a few seconds.",
      extractionQueuedExplainer:
        "Text extraction is queued. Once it finishes, the document can be cited as evidence against a control and counts towards your framework's document coverage. This page updates itself while extraction runs.",
      backToDocumentGap: "Back to document gap analysis",
      uploading: "Uploading…",
      uploadStage: "Upload & stage",
      committing: "Committing…",
      commitBatch: "Commit batch",
      needsManualText: "needs_manual_text",
      uploadDocuments: "Upload documents",
      looseFilesOrZipArchivesPdfDocx:
        "Loose files or .zip archives (pdf / docx / xlsx / txt / md / csv / html). Files are staged for review -- nothing is inventoried until you commit. Every committed document is hash-chained: the file MARSAD holds is provably the file that was uploaded.",
      stagedBatch: "Staged batch —",
      entries: "entries (",
      new: "new,",
      duplicate: "duplicate)",
      file: "File",
      size: "Size",
      sha256: "sha256",
      duplicateWillSkip: "duplicate — will skip",
      discard: "Discard",
      /* Round 13 A3 -- the product held this text, hashed it, judged a
           bank's compliance position on it, and rendered it as a character
           count. */
      readText: "Read text",
      readTextNote:
        "The extracted text, as the Engine reads it. Read-only; to replace it, use Add text.",
      readTextEmpty: "No text has been extracted from this document yet.",
      manualText: "Manual text —",
      thisDocumentAposSTextCouldnApos:
        "This document’s text couldn’t be extracted automatically (image-only scan or unsupported format). Paste the text here; it feeds the same inventory + analysis as extracted text.",
      existingText: "Existing text (",
      charsSavingReplacesIt: "chars — saving replaces it.",
      saveText: "Save text",
      close: "Close",
      adminRoleRequired: "Admin role required.",
      documentLocker: "Document locker",
      documents: "Documents",
      provablyHashedProcessingCopiesOfTheBank:
        "Provably-hashed processing copies of the bank’s documents — the enabling layer for inventory, gap analysis and control testing. The bank’s own DMS stays the canonical store.",
      extraction: "Extraction",
      documents2: "documents…",
      inventory: "Inventory (",
      loading: "Loading…",
      noDocumentsYetUploadTheBankApos:
        "No documents yet — upload the bank’s corpus above.",
      status: "Status",
      text: "Text",
      addText: "Add text",
      delete: "Delete",
    },
    examinerWorkspace: {
      marsad: "MARSAD",
      ok: "OK",
      warn: "WARN",
      selfAsserted: "SELF-ASSERTED KEY:",
    },
    licenseGate: {
      uploadARenewalInAdminLicense: "Upload a renewal in Admin → License.",
      contactAnAdminToRenew: "Contact an admin to renew.",
      licenseExpiredReadOnlyGraceMode: "License expired — read-only grace mode",
      licenseRequired: "License required",
      installALicense: "Install a license",
      contactAMarsadAdministratorToInstallOr:
        "Contact a MARSAD administrator to install or renew the license.",
    },
    licensePanel: {
      /* D9 (round 8) -- these were the only Latin runs left on a fully
         pseudo-localised page, unchanged character for character across
         three rounds, because they live in a helper where the copy rule
         cannot see them: it reads literals in JSX children. */
      syncNever: "never (no management plane, or not yet synced)",
      syncJustNow: "just now",
      syncMinutesAgo_one: "{{count}} min ago",
      syncMinutesAgo_other: "{{count}} min ago",
      syncHoursAgo_one: "{{count}}h ago",
      syncHoursAgo_other: "{{count}}h ago",
      syncDaysAgo_one: "{{count}}d ago",
      syncDaysAgo_other: "{{count}}d ago",
      installing: "Installing…",
      uploadLicense: "Upload license",
      active: "active",
      grace2: "grace",
      allUnscopedLicense: "all (unscoped license)",
      none: "none",
      syncing: "Syncing…",
      syncNow: "Sync now",
      expiringSoon_one:
        "expires in {{count}} day -- contact your MARSAD contact to renew",
      expiringSoon_other:
        "expires in {{count}} days -- contact your MARSAD contact to renew",
      state: "State",
      enforcementOffEstateOpen: "(enforcement off — estate open)",
      /* Round 10 F6 -- the six licence states, in words. This rendered
         the column value, which was the only Latin run left on a fully
         localised page. */
      stateWord: {
        active: "Active",
        grace: "In grace",
        expired: "Expired",
        missing: "No licence installed",
        invalid: "Not valid",
        revoked: "Revoked",
      },
      customer: "Customer",
      expires: "Expires",
      grace: "grace:",
      daySLeft: "day(s) left",
      licensedModules: "Licensed modules",
      licenseId: "License ID",
      lastSynced: "Last synced",
      error: "Error",
      onlineSync: "Online sync",
      refreshARenewedLicenseApplyTheRevocation:
        "Refresh a renewed license + apply the revocation list from the management plane. Runs automatically when configured; a no-op when this deployment is air-gapped (upload-only).",
      licenseAdministrationRequiresAdminAccess:
        "License administration requires admin access.",
      deploymentLicense: "Deployment license",
      theSignedLicenseThatGatesWhichModules:
        "The signed license that gates which modules this deployment may use.",
      loading: "Loading…",
      // D12 -- with no licence installed the page listed a state and
      // stopped. What an administrator needs is what the deployment
      // does without one, since he cannot issue himself a licence.
      missingEnforcing:
        "No licence is installed and enforcement is ON, so licensed modules " +
        "are closed to everyone until one is uploaded.",
      missingOpen:
        "No licence is installed. Enforcement is off, so every module is open " +
        "-- this is the right posture for an evaluation and the wrong one for " +
        "a production deployment.",
      installRenew: "Install / renew",
      uploadAJwtLicenseIssuedByThe:
        "Upload a `.jwt` license issued by the license server. It is verified against the embedded public key and applied without a restart.",
    },
    moduleNotLicensed: {
      the: "The",
      moduleIsnTEnabled: "module isn't enabled",
      thisDeploymentSLicenseDoesnTInclude:
        "This deployment's license doesn't include the",
      moduleContactYourAccountTeamToUpgrade:
        "module. Contact your account team to upgrade the SKU.",
    },
    viewRail: {
      rarr: "→",
    },
    baselCrosswalkView: {
      /* ROUND 13 H, eleventh round -- an amber chip on a line of its
         own. Both words on this page were hardcoded English literals
         in a coloured box, saying nothing about what they judge or
         what rule produced them. The thresholds are the ones
         `_pillar_status` and `_overall_status` apply. */
      pillarStatus: {
        proved: "Proved",
        strong: "Strong",
        partial: "Partial",
        thin: "Thin",
      },
      pillarStatusWhy: {
        proved:
          "Every requirement under this pillar is proved by live evidence from the twin -- nothing rests on an attestation alone.",
        strong:
          "Two thirds or more of this pillar's requirements are proved by live evidence; the rest are bank-attested or unmapped.",
        partial:
          "Between a third and two thirds are proved by live evidence. The remainder are bank-attested or have no evidence route yet.",
        thin: "Fewer than a third are proved by live evidence -- or nothing under this pillar is mapped at all.",
      },
      overallStatus: {
        ready: "Ready",
        developing: "Developing",
        early: "Early",
      },
      overallStatusWhy: {
        ready:
          "Averaged across the seven pillars, two thirds or more of the requirements are proved by live evidence from the twin.",
        developing:
          "Averaged across the seven pillars, between a third and two thirds are proved by live evidence.",
        early:
          "Averaged across the seven pillars, fewer than a third are proved by live evidence. This is a statement about coverage, not about compliance.",
      },
      pillar: "Pillar",
      provedByTheTwin: "proved by the twin",
      bankAttested: "bank-attested",
      gap: "gap",
      maturity: "maturity",
      assessedVia: "Assessed via",
      ofTheMaturityModel: "of the maturity model",
      twinProvedCoverageAcrossTheSevenPillars:
        "twin-proved coverage across the seven pillars",
      provedStrong: "proved/strong ·",
      partial: "partial ·",
      thin: "thin",
      basel7OperationalResilienceReadiness:
        "{{framework}} operational-resilience readiness",
      everyDedicatedOperationalResilienceRegimeThe:
        "Every dedicated operational-resilience regime — the UK, APRA CPS 230, OSFI E-21, EU DORA — derives from the Basel Committee’s 2021 Principles for Operational Resilience, and SAMA’s and CBUAE’s next moves will too. This maps, per pillar, what the twin",
      proves: "proves",
      versusWhatTheBank: "versus what the bank",
      attests: "attests",
      itIsALiveLensOverThe:
        ". It is a live lens over the maturity model, not a separate assessment.",
      backToTheMaturityReadiness: "← Back to the maturity readiness",
      // F -- two pages, one click apart, both offering "Download signed
      // pack" for two DIFFERENT artefacts. A reader crossing between
      // them cannot tell whether they already have this one.
      downloadSignedPack: "Download the signed crosswalk pack",
      loading: "Loading…",
      readinessCrosswalkUnavailable: "Readiness crosswalk unavailable.",
    },
    readinessCard: {
      selfAssessedAgainstSama: "Self-assessed against SAMA",
      suggestedFromLiveEvidence: "Suggested from live evidence",
      // Round 18 B18 -- no suggestion over rows nobody judged.
      notSuggested: "No maturity suggested: nothing admitted",
      resilienceReadiness: "Resilience readiness",
      /* A5 -- the figure named, because there are two and they
         disagree: this card carries the maturity index, /executive the
         Marsad Resilience Score. The words are /readiness's, which
         shows both together and labels each. */
      weightedMaturityIndex: "Weighted maturity index",
      maturityTier: "Maturity tier",
      tier: "Tier",
      acrossThe10OpsresDimensions: "across the 10 OpsRes dimensions.",
      openReadiness: "Open readiness",
    },
    readinessView: {
      d6Note:
        "A maturity level from 1 to 5: how well the bank tests. Tested coverage, the share of operations a recorded test has reached, is a different figure, on Test runs.",
      forbidden:
        "The resilience score is read by the estate owner, the approver " +
        "and the reviewer. Your work reaches this number through the " +
        "assessments and drills behind it.",
      starting: "Starting…",
      startAMaturityAssessment: "Start a maturity assessment",
      approved: "approved",
      scoreEveryDimensionToEnableSignOff: "Score every dimension to enable sign-off.",
      /* Round 11 F3 -- was composed in the component with the raw
         column value inside it: "Self-assessed (v2 · cro_signed)". */
      selfAssessed: "Self-assessed (v{{version}} · {{state}})",
      suggestedFromEvidence: "Suggested from live evidence",
      // Round 18 B18 -- the empty estate read "1.00 tier E, suggested
      // from live evidence"; after the imports 2.17 tier D from
      // candidate rows and worker-sealed chains.
      notSuggested:
        "No maturity suggested: nothing has been admitted, so nothing has been judged. The evidence found is listed under each dimension.",
      weightedMaturityIndex: "Weighted maturity index",
      tier: "Tier",
      floorRuleAppliedTheTierIsCapped:
        "Floor rule applied: the tier is capped at C because governance, {{serviceTermShort}} identification or {{toleranceTermPlural}} (D1/D2/D3) are below the SAMA baseline (Level 3). Those are prerequisites for a higher classification.",
      couldNotLoadTheReadinessView: "Could not load the readiness view.",
      resilienceReadiness: "Resilience readiness",
      theBankAposSSamaOperationalResilience:
        "The bank’s SAMA operational-resilience maturity across the ten weighted dimensions. Scores are pre-filled from live MARSAD evidence; the assessor confirms each, then the CRO + Head of Operational Resilience sign it off.",
      // D2 -- "Basel-7" is a proper noun inside translated prose, so
      // it reordered. Interpolated values are bidi-isolated; a token
      // typed into the sentence is not.
      basel7OperationalResilienceReadinessCrosswal:
        "{{framework}} operational-resilience readiness crosswalk →",
      // F -- named, because the crosswalk page one click away
      // offers a different pack under the same words.
      downloadSignedPack: "Download the signed maturity pack",
      // D3 -- the button was offered to a reader who could not use it
      // and returned a silent 403.
      packIsApprovers:
        "The signed maturity pack is downloaded by an approver. Everything it contains is on this page.",
      packDownloaded: "Readiness pack downloaded.",
      noSelfAssessmentYetStartOneTo:
        "No self-assessment yet. Start one to score each dimension and capture the board sign-off.",
      approvedByBoard: "Approved by the board.",
      noteLabel: "Your comment on this signature (optional)",
      notePlaceholder: "What you are relying on, or any caveat.",
      approvedCro: "Approved · CRO",
      headOfOpsres: "· Head of OpsRes",
      dimensionsScored: "dimensions scored.",
      signOff: "Sign off",
      weight: "weight",
      // D4 -- stitched, so the percentage reordered away from its label.
      weightPct: "weight {{pct}}%",
      suggested: "suggested",
      noSuggestion: "no suggestion: no evidence found",
      set: "set…",
      set2: "set",
      external: "(external)",
      closeThisGapRarr: "Close this gap →",
      /* A5 -- the sign-off is the CRO's and the Head of Operational
         Resilience's own. The panel used to render for admins only,
         while the API admitted the realm role only, so the seat that
         could see the control was the seat the API refused. */
      signHeading: "What you are signing",
      signAs: "Sign as {{name}}",
      signing: "Signing…",
      signedToast: "Signed. {{next}}",
      signedToastDone: "The assessment is approved.",
      signedToastAwaiting: "Now waiting on the {{role}}.",
      signedToastAwaitingNamed: "Now waiting on {{who}} ({{role}}).",
      signFailed: "That signature was not recorded. Nothing has changed.",
      awaitingOther:
        "Waiting on the {{role}}; they sign this from Readiness or My Work.",
      // Round 18 G23 -- the person, or that nobody holds the seat.
      awaitingOtherNamed:
        "Waiting on {{who}} ({{role}}); they sign this from Readiness or My Work.",
      awaitingOtherUnheld:
        "Waiting on the {{role}} -- nobody signed in holds that seat yet.",
      cro: "CRO",
      opsRes: "Head of Operational Resilience",
      summaryTier: "Tier",
      summaryIndex: "Weighted index",
      summaryScored: "Dimensions scored",
      summaryFloor: "Floor rule",
      summaryFloorApplied: "Applied — the tier is capped at C",
      summaryFloorNot: "Not applied",
      commitsTo:
        "Signing records this as the bank's assessed operational-resilience maturity. Both signatures seal it into the maturity chain, and the scores can no longer be changed.",
      startAsk:
        "No self-assessment yet. An administrator starts one, then you score each dimension and capture the board sign-off.",
    },
    resilienceScoreCard: {
      lastSealed: "Last sealed {{score}} into the score chain",
      notYetSealed: "Not yet sealed into the score chain",
      /* Round 17 B15 -- a live 63.5 beside "Last sealed 6.0" did not say
         which was which. */
      liveUnsealed: "live, not yet sealed",
      sealedSame: "Sealed into the score chain",
      /* Round 17 B10 -- no score, and no seal, while nothing is admitted. */
      notScored: "Not yet scored: nothing admitted",
      notScoredPopulation:
        "Each pillar counts admitted services, and none is admitted yet. Nothing is sealed until one is.",
      decomposes: " \u00b7 every figure decomposes into the evidence below",
      tier: "Tier",
      /* Round 11 F3 -- the three pillars, out of a hard-coded English
         map inside the card and into the catalogue. */
      pillar: {
        posture: "Posture",
        exposure: "Exposure",
        outcome: "Outcome readiness",
      },
      confidence: "confidence",
      methodologyV: "methodology v",
      // D4 -- stitched, so the version reordered away from its label.
      methodologyVersion: "methodology v{{version}}",
      tierCappedAt: "Tier capped at",
      wouldBe: "(would be",
      confidenceIsBelow50InstrumentMoreOf:
        "): confidence is below 50% -- instrument more of the estate to lift the cap.",
      boardAssessedMaturityVsLiveEvidenceDrift:
        "Board-assessed maturity vs live evidence drift:",
      marsadResilienceScore: "MARSAD Resilience Score",
      /* ROUND 13 H -- this page offers three different signed packs
         (maturity here, score on this card, crosswalk one click away)
         and two of them refused the same reader with the SAME
         sentence, two paragraphs apart, so the page read as though it
         were repeating itself. Ninth round for the doubling. Two
         sentences because two artefacts, each naming which. */
      downloadSignedPack: "Download the signed score pack",
      packIsApprovers:
        "The signed score pack is downloaded by an approver. Every figure in it is on this card.",
      /* H -- "Exposure (25) 100" put a weight and a 0-100 score on one
         line with nothing saying which was which, so a pillar read as
         four times its own ceiling. */
      pillarWeight: "(weight {{weight}})",
      pillarOutOf: "{{value}} / 100",
      /* Round 20 Y3 -- the Score re-performed from the card: each
         pillar's inputs, its rule, the composite with its figures, and
         whether the assessment it rests on is signed. */
      inputValue: "{{value}} / 100",
      inputNotMeasured: "not measurable on this estate, left out of the mean",
      pillarRule: {
        posture: "Posture = (maturity index - 1) / 4 x 100.",
        exposure: "Exposure = the mean of the inputs above that have a value, x 100.",
        outcome:
          "Outcome readiness = the mean of the inputs above that have a value, x 100.",
      },
      formula:
        "Score {{score}} = {{expression}}. Each pillar counts by its weight; a pillar with no value drops out and the weights are re-divided by their sum.",
      postureUnsigned:
        "Posture rests on the bank's own maturity assessment, which is not fully signed: Chief Risk Officer {{cro}}, Head of Operational Resilience {{opsRes}}.",
      awaiting: "awaiting",
      /* Round 13 C8 -- the pack is composed, hashed and signed server
         side; 4.1 seconds passed with the button unchanged. */
      buildingPack: "Building the pack",
      loading: "Loading…",
      scoreUnavailable: "Score unavailable.",
    },
    recommendationsView: {
      mvb: "{{mvbTermShort}}",
    },
    cellDrawer: {
      openDetailRarr: "Open detail →",
      times: "×",
    },
    channelHeatmapView: {
      perChannelOverride: "Per-channel override",
      noData: "no data",
      // ROUND 15 B8 -- the pillar tokens, as words.
      pillar: {
        red: "red",
        amber: "amber",
        green: "green",
        unknown: "unknown",
        not_assessed: "not yet assessed",
      },
      noIbsesYet:
        "No {{serviceTermShortPlural}} are registered yet. The heatmap draws once the register holds one.",
      noIbsesYetLink: "Open the {{serviceTermShort}} register",
      channelResilienceHeatmap: "Channel resilience heatmap",
      whichIbsesServeCustomersThroughWhichChannels:
        "Which {{serviceTermShortPlural}} serve customers through which channels, with each row tinted by the {{serviceTermShort}}’s live resilience pillar at the selected as-of. Channels are SAMA-reportable; this view is the one-glance signal regulators look for.",
      loadingHeatmap: "Loading heatmap…",
      failedToLoadHeatmap: "Failed to load heatmap:",
      ibs: "{{serviceTermShort}}",
      mvb: "{{mvbTermShort}}",
      noIbsesSeededYetTheHeatmapRenders:
        "No {{serviceTermShortPlural}} seeded yet. The heatmap renders once Foundation admins add {{serviceTermShortPlural}} via /v1/admin/foundation/ibses.",
    },
    provenanceTrace: {
      genesis: "genesis",
      traceThisInput: "→ trace this input",
    },
    entityDetailLayout: {
      pdf: "pdf",
      exporting: "Exporting…",
      exportPdf: "Export PDF",
      json: "json",
      json2: "JSON",
      missingEntityIdInUrl: "Missing entity id in URL.",
      larrCapabilityMap: "← Capability map",
    },
    evidenceArtefactDetail: {
      middotControl: "· control",
      text: "…",
      rarr: "→",
    },
    findingDetail: {
      hide: "Hide",
      show: "Show",
      telemetryAuto: "telemetry.auto",
      view: "View",
      rarr: "→",
      connectorPayload: "Connector payload",
      copy: "Copy",
      category: "Category",
    },
    raiseFindingView: {
      raisedOn: "Finding raised on {{name}}.",
      raised: "Finding raised.",
      raising: "Raising...",
      // Round 18 G25 -- the press was answered in 1.8 s on a busy host
      // and the button said nothing more; the walker left at 2.5 s.
      stillSaving: "Saving the finding...",
      opening: "Saved; opening...",
      raiseFinding: "Raise finding",
      raiseAFinding: "Raise a finding",
      manualIntakeForObservationsNoConnectorCarrie:
        "Manual intake for observations no connector carries. The raised finding is first-class: it drives the cascade, resilience and the war-room, and closes from its detail page. Remediation workflow belongs on a CAP.",
      title: "Title",
      description: "Description",
      severity: "Severity",
      category: "Category",
      impactedEntityKind: "Impacted entity kind",
      impactedEntity: "Impacted entity",
      select: "Select...",
      couldNotRaiseTheFindingCheckThe:
        "Could not raise the finding. Check the fields and try again.",
    },
    fragilityRadarView: {
      mvb: "{{mvbTermShort}}",
    },
    bianLandscapeClassicView: {
      unknown: "unknown",
      close: "Close",
      controlRecord: "Control Record",
      behaviourQualifiers: "Behaviour Qualifiers",
      operations: "Operations",
      mappedServices: "Mapped services (",
      showingFirst: "showing first",
      noServicesMapToThisBianDomain: "No services map to this BIAN domain.",
      architectsOurStories: "Architects -- Our Stories",
      whereDoMyIbssSitInThe: "Where do my IBSs sit in the BIAN reference landscape?",
      standardBianServiceDomainMapWithThe:
        "Standard BIAN service domain map with the bank's own services and applications overlaid. Coloured tiles carry confidence-graded mappings; empty tiles flag coverage gaps against the industry taxonomy.",
      tiles: "tiles",
      zones: "zones",
      sections: "sections",
      servicesMapped: "services mapped",
      unmapped: "unmapped",
      switchToOverlayViewCascadeMvbConfidence:
        "Switch to overlay view (cascade / {{mvbTermShort}} / confidence + time-travel) →",
      loadingBianLandscape: "Loading BIAN landscape…",
      failed: "Failed:",
      filter: "Filter:",
    },
    bianLandscapeView: {
      bianLandscape: "BIAN landscape",
      bianAlignedBankingLandscape: "BIAN-aligned banking landscape.",
      zones: "zones ·",
      tiles: "tiles)",
      // "v1-style" named a build of this product nobody outside the
      // team has seen, on a route the audit had never visited -- so
      // the P124.4 jargon strip missed it. Rewording is also the RTL
      // fix: the Latin run inside Arabic text had no bidi isolation,
      // and there is no Latin run left to isolate.
      switchToClassicViewV1StyleTile:
        "Switch to classic view (tile grid with detail popovers) →",
      loadingLandscape: "Loading landscape…",
      failedToLoadLandscape: "Failed to load landscape:",
      overlay: "Overlay",
      confidence: "Confidence",
      svc: "svc",
      mvb: "{{mvbTermShort}}",
    },
    tileDetailDrawer: {
      tile: "Tile",
      loading: "Loading…",
      failedToLoadTileDetail: "Failed to load tile detail:",
      noServicesMappedToThisTileYet: "No services mapped to this tile yet.",
      conf: "conf ·",
      pillar: "pillar ·",
      mvb: "{{mvbTermShort}}",
      open: "open",
    },
    continuityPostureView: {
      drill: "drill",
      number: "number",
      ok: "ok",
      t: "T+",
      h: "h",
    },
    mvbDrillView: {
      completed: "completed",
      w: "W",
      t: "T+",
      h: "h",
    },
    mvbRecoveryView: {
      declared: "declared",
      dr: "(DR)",
      t: "T+",
      h: "h",
      w: "W",
      hT: "h → T+",
    },
    scenarioTemplatePickerModal: {
      // Round 14 D9.
      nameLabel: "Name for the new scenario (optional)",
      namePlaceholder: "The template's own name is used if left empty",
      samaHeading: "The {{count}} scenarios SAMA sent every bank",
      samaNote:
        "In SAMA's order and in SAMA's words, received 21 September 2026. Each figure shown when the scenario runs is one SAMA wrote; where SAMA gave none, none is invented.",
      unknown: "unknown",
      instantiate: "Instantiate",
      pickATemplateToInstantiate: "Pick a template to instantiate",
      close: "Close",
      loadingTemplates: "Loading templates…",
      instantiateFailed: "Instantiate failed:",
      // D9 -- it navigated silently, so "did that work?" was answered
      // by inference from the URL.
      created: "Scenario created from the template: {{name}}",
    },
  },
} as const;
